/**
 * i18n Translation Messages
 * All translatable strings for the admin interface
 * Migrated from Alpine.js i18n-messages.js
 */

export type Language = "zh" | "en";

export interface PushNotificationsMessages {
  // Page
  pageTitle: string;

  // Stats
  stats: {
    totalTokens: string;
    activeTokens: string;
    pushSent: string;
    successRate: string;
    platformBreakdown: string;
    ios: string;
    android: string;
  };

  // Send Notification Form
  sendForm: {
    title: string;
    description: string;
    notificationTitle: string;
    notificationTitlePlaceholder: string;
    body: string;
    bodyPlaceholder: string;
    target: string;
    targetAll: string;
    targetAllWarning: string;
    targetTest: string;
    targetTestDesc: string;
    targetSpecificUsers: string;
    targetSpecificUsersDesc: string;
    userIds: string;
    userIdsPlaceholder: string;
    userIdsHint: string;
    sound: string;
    soundPlaceholder: string;
    soundHint: string;
    soundDefault?: string;
    soundNone?: string;
    badgeCount: string;
    badgeCountPlaceholder: string;
    badgeCountHint: string;
    badgeCountHintFull?: string;
    customData: string;
    customDataPlaceholder: string;
    customDataHint: string;
    dataTemplates?: string;
    dataTemplateLabels?: Record<string, string>;
    clearButton: string;
    sendButton: string;
    sending: string;
    successMessage: string;
    sent: string;
    failed: string;
    errors: string;
  };

  // Device Tokens
  tokens: {
    tabTitle: string;
    title: string;
    allPlatforms: string;
    refresh: string;
    loading: string;
    noTokens: string;
    user: string;
    registered: string;
    active: string;
    inactive: string;
    page: string;
    of: string;
    total: string;
    previous: string;
    next: string;
  };

  // Notification Logs
  logs: {
    tabTitle: string;
    title: string;
    allStatuses: string;
    statusSent: string;
    statusFailed: string;
    refresh: string;
    loading: string;
    noLogs: string;
    error: string;
    page: string;
    of: string;
    total: string;
    previous: string;
    next: string;
    // Detail view fields
    user?: string;
    userId?: string;
    displayName?: string;
    platform?: string;
    deviceInfo?: string;
    sentAt?: string;
    pushToken?: string;
    fullTitle?: string;
    fullBody?: string;
    dataPayload?: string;
    actionNavigation?: string;
    actionType?: string;
    actionFortune?: string;
    // Grouped view fields
    recipients?: string;
    batches?: string;
    totalRecipients?: string;
    recipientList?: string;
  };

  // Time formatting
  time: {
    justNow: string;
    minutesAgo: string;
    hoursAgo: string;
    daysAgo: string;
  };

  // Validation errors
  validation: {
    titleRequired: string;
    bodyRequired: string;
    userIdsRequired: string;
    invalidJson: string;
  };

  // User Selector
  userSelector?: {
    searchPlaceholder: string;
    selectedCount: string;
    selectAll: string;
    deselectAll: string;
    clearAll: string;
    noUsers: string;
    devices: string;
    showingCount: string;
  };

  // Content Assist (AI-powered content generation)
  contentAssist?: {
    sectionTitle: string;
    inputModeLabel: string;
    agentMode: string;
    llmMode: string;
    manualMode: string;
    selectAgent: string;
    loading: string;
    noAgents: string;
    descriptionLabel: string;
    descriptionPlaceholder: string;
    generateButton: string;
    generating: string;
    selectAgentFirst: string;
    generateFailed: string;
    // LLM mode specific
    selectLLMModel: string;
    noModels: string;
    llmModeDescription: string;
    temperature: string;
    precise: string;
    balanced: string;
    creative: string;
    selectModelFirst: string;
  };
}

export interface MonitoringMessages {
  // Page
  pageTitle: string;
  pageDescription: string;
  lastUpdated: string;

  // Tabs
  tabs: {
    services: string;
    cache: string;
    storage: string;
    queue: string;
    mcp: string;
  };

  // Queue
  queue?: {
    title: string;
    status: string;
    running: string;
    stopped: string;
    queueSize: string;
    processed: string;
    success: string;
    failed: string;
    merged: string;
    savedCalls: string;
    mergeRate: string;
    costSaving: string;
    deadLetter: string;
    deadLetterDesc: string;
    retryAll: string;
    clearAll: string;
    retry: string;
    noTasks: string;
    taskId: string;
    userId: string;
    memoryType: string;
    error: string;
    failedAt: string;
    retryCount: string;
    confirmClear: string;
    confirmRetryAll: string;
    clearSuccess: string;
    retrySuccess: string;
    resetStats: string;
    confirmReset: string;
    resetSuccess: string;
  };

  // System Task Queue
  systemTaskQueue?: {
    title: string;
    queueSize: string;
    deadLetterSize: string;
    maxRetries: string;
    retryDelay: string;
    jobTimeout: string;
    deadLetter: string;
    deadLetterDesc: string;
    taskType: string;
    scheduledBy: string;
    scheduleId: string;
    config: string;
    lastError: string;
    finalError: string;
    failedAt: string;
    retryCount: string;
    noTasks: string;
    confirmClear: string;
    confirmRetryAll: string;
    clearSuccess: string;
    retrySuccess: string;
  };

  // Service Health
  serviceHealth: {
    title: string;
    services: string;
    healthy: string;
    unhealthy: string;
    error: string;
    unknown: string;
    allOk: string;
    issues: string;
    overallStatus: string;
    latency: string;
    openService: string;
  };

  // Cache Performance
  cache: {
    title: string;
    hitRate: string;
    hits: string;
    misses: string;
    avgLatency: string;
    totalRequests: string;
    successfulRetrievals: string;
    fetchFailures: string;
    avgResponseTime: string;
    unavailable: string;
  };

  // Charts
  charts: {
    cacheHitRate: string;
    hitRateLabel: string;
    avgLatency: string;
    total: string;
    cacheLatency: string;
    backend: string;
    requestDistribution: string;
    noData: string;
  };

  // Cache Details
  cacheDetails: {
    title: string;
    totalRequests: string;
    cachedKeys: string;
    cacheSize: string;
    errors: string;
    redisConnection: string;
    teiBackend: string;
    connected: string;
    disconnected: string;
    healthy: string;
    unhealthy: string;
    clear: string;
    clearConfirm: string;
    clearSuccess: string;
    clearError: string;
  };

  // Refresh Control
  refresh: {
    autoRefresh: string;
    interval: string;
    manual: string;
    refreshButton: string;
    refreshing: string;
  };

  // Errors
  errors: {
    loadFailed: string;
    tryAgain: string;
  };
}

export interface ClassificationAnalyticsMessages {
  // Page
  pageTitle: string;
  pageDescription: string;
  lastUpdated: string;

  // Tabs
  tabs: {
    skillExecution: string;
    classification: string;
  };

  // Time Range
  timeRange: {
    label: string;
    last24h: string;
    last7d: string;
    last30d: string;
  };

  // Skill Execution
  skillExecution: {
    title: string;
    stats: {
      totalExecutions: string;
      activeSkills: string;
      autoMatchRate: string;
      ruleMatchRate: string;
    };
    charts: {
      matchLayer: string;
      matchSource: string;
      topSkills: string;
    };
    recentExecutions: string;
    noData: string;
    skillId: string;
    query: string;
    matchSource: string;
    matchedRule: string;
    confidence: string;
    time: string;
  };

  // Classification
  classification: {
    title: string;
    stats: {
      totalClassifications: string;
      quickResponseRate: string;
      avgQueryLength: string;
      avgLatency: string;
      // Skill metrics
      ruleMatchRate: string;
      avgRuleMatchTime: string;
      avgLlmMatchTime: string;
      avgPromptInjection: string;
    };
    charts: {
      responseType: string;
      avgConfidence: string;
      methodPerformance: string;
      count: string;
      latency: string;
      // Skill charts
      skillMatchLayer: string;
      topMatchedSkills: string;
    };
    recentClassifications: string;
    noData: string;
    responseType: string;
    method: string;
    latencyMs: string;
  };

  // Common
  export: {
    button: string;
    csv: string;
    json: string;
  };
  refresh: string;
  loading: string;
  error: string;
  quick: string;
  deep: string;
}

export interface UserGrowthPageMessages {
  pageTitle: string;
  pageDescription: string;
  kpi: {
    todayNewUsers: string;
    todayActive: string;
    todayActiveSuffix: string;
    newUsers7d: string;
    d1Retention: string;
  };
  range: {
    days7: string;
    days14: string;
    days30: string;
    days90: string;
    startDate: string;
    endDate: string;
    invalidRange: string;
  };
  trend: {
    title: string;
    newUsersIos: string;
    newUsersAndroid: string;
    newUsersUnknown: string;
    activeRegular: string;
    activeTeam: string;
  };
  cumulative: {
    title: string;
    regular: string;
    total: string;
  };
  table: {
    title: string;
    excludeTeam: string;
    colDate: string;
    colUser: string;
    colClassification: string;
    colRegistered: string;
    colPlatform: string;
    colSessions: string;
    colRuns: string;
    newBadge: string;
    empty: string;
  };
  classification: {
    admin: string;
    internalTester: string;
    regularUser: string;
  };
  funnel: {
    title: string;
    registered: string;
    createdSelfChart: string;
    firstConversation: string;
    trialStarted: string;
    paid: string;
    medianHoursToChart: string;
    hoursUnit: string;
  };
  retention: {
    title: string;
    d7: string;
    d30: string;
    dauMau: string;
    cohortTitle: string;
    weekPrefix: string;
    cohortSizeSuffix: string;
  };
  loading: string;
  error: string;
}

export interface UsersMessages {
  pageTitle: string;
  pageDescription: string;
  refresh: string;
  loading: string;
  error: string;

  stats: {
    totalUsers: string;
    admins: string;
    betaTesters: string;
    roleDistribution: string;
    usersWithAstro?: string;
    pushEnabled?: string;
    recentSignups?: string;
  };

  filters: {
    searchPlaceholder: string;
    allUsers: string;
    betaTesters: string;
    nonBeta: string;
    clearFilters?: string;
  };

  batch: {
    selected: string;
    enableBeta: string;
    disableBeta: string;
    clearSelection: string;
  };

  table: {
    user: string;
    contact: string;
    role: string;
    subscription: string;
    flags: string;
    betaTester: string;
    realName: string;
    region: string;
    timezone?: string;
    active: string;
    created: string;
    actions: string;
  };

  badges: {
    admin: string;
    birthChart: string;
    gpsEnabled?: string;
  };

  roles: {
    free: string;
    vip: string;
    premium: string;
    pro: string;
    admin: string;
  };

  actions: {
    makeBeta: string;
    removeBeta: string;
    makeAdmin: string;
    revokeAdmin: string;
    resources: string;
    viewResources: string;
    subscription: string;
    manageSubscription: string;
  };

  status: {
    beta: string;
    notBeta: string;
  };

  pagination: {
    showing: string;
    page: string;
  };

  // User Detail Drawer
  drawer?: {
    profile: string;
    preferences: string;
    astroProfile: string;
    email: string;
    phone: string;
    created: string;
    updated: string;
    usernameUpdates: string;
    usernameUpdateCountFormat?: string;
    timezone: string;
    locale: string;
    language: string;
    region: string;
    currency: string;
    theme: string;
    fontSize: string;
    pushNotification: string;
    device: string;
    gpsLocation: string;
    realName: string;
    gender: string;
    birthDate: string;
    birthTime: string;
    birthLocation: string;
    birthTimezone: string;
    residenceInfo: string;
    residenceLocation: string;
    residenceTimezone: string;
    residencePlaceholder?: string;
    edit?: string;
    changeRole?: string;
  };

  // Advanced Filters
  advancedFilters?: {
    roleLevel: string;
    allRoles: string;
    hasAstroProfile: string;
    isAdmin: string;
    all: string;
    yes: string;
    no: string;
  };

  // Relative Date Formatting
  relativeDates?: {
    today: string;
    yesterday: string;
    daysAgo: string;
    weeksAgo: string;
    monthsAgo: string;
  };
}

export interface SystemSettingsMessages {
  // Page
  pageTitle: string;
  pageDescription: string;

  // Tabs
  tabs: {
    mcpServers: string;
    mem0Config: string;
    firstMessage: string;
    artifactTypes: string;
    toolEnhancement: string;
    toolCombo: string;
    promptTemplates: string;
    outputSchemas: string;
    modelDefaults: string;
    exportImport: string;
    auditLogs: string;
    htmlPage?: string;
  };

  // HTML Page tab strings (optional — gracefully degrades if missing)
  htmlPage?: {
    pageTitle: string;
    pageDescription: string;
    warningBanner: string;
    promptAddendumLabel: string;
    promptAddendumHint: string;
    cspLabel: string;
    cspHint: string;
    baseCssLabel: string;
    baseCssHint: string;
    maxBytesLabel: string;
    maxBytesHint: string;
    resetField: string;
    save: string;
    saving: string;
    saveSuccess: string;
    saveFailed: string;
    reload: string;
    versionLabel: string;
    updatedByLabel: string;
    addendumTooLong: string;
    cspRequired: string;
    cssRequired: string;
    bytesOutOfRange: string;
  };

  // Audit
  audit: {
    pageTitle: string;
    pageDescription: string;
    viewLogsFor: string;
    mcpServersLog: string;
    mem0ConfigLog: string;
    firstMessageLog: string;
    artifactTypesLog: string;
    toolEnhancementLog: string;
    toolComboLog: string;
    promptTemplatesLog: string;
    outputSchemasLog: string;
    configTypeNames: {
      mcp_servers: string;
      mem0_settings: string;
      first_message_instruction: string;
      artifact_types: string;
      tool_enhancement: string;
      tool_combo_templates: string;
      system_prompt_templates: string;
      output_schemas: string;
      feature_flags: string;
    };
  };

  // MCP Servers Panel
  mcp?: {
    title: string;
    description: string;
    warningBanner: string;
    editJson: string;
    reloadManager: string;
    addServer: string;
    noServers: string;
    addFirstServer: string;
    aboutMcp: string;
    aboutMcpItems: string[];
    version: string;
    lastUpdatedBy: string;
    jsonEditorTitle: string;
    jsonEditorHint: string;
    cancel: string;
    saving: string;
    saveConfiguration: string;
    loading: string;
    loadFailed: string;
    saveFailed: string;
    deleteFailed: string;
    reloadSuccess: string;
    reloadFailed: string;
    jsonSaveSuccess: string;
    jsonMustHaveMcpServers: string;
    // Server Card
    serverCard?: {
      disabled: string;
      transport: string;
      command: string;
      url: string;
      tools: string;
      toolsCount: string;
      testConnection: string;
      editServer: string;
      deleteServer: string;
      confirmDelete: string;
      connectionTestSuccess: string;
      connectionTestFailed: string;
      retryProgress: string;
      lastError: string;
      states: {
        connected: string;
        connecting: string;
        reconnecting: string;
        failed: string;
        disconnected: string;
        unknown: string;
      };
    };
    // Status Panel
    statusPanel?: {
      title: string;
      type: string;
      transport: string;
      tools: string;
      uptime: string;
      retryProgress: string;
      lastError: string;
      nextRetry: string;
      loading: string;
      loadFailed: string;
      retry: string;
      refreshStatus: string;
      reconnectAll: string;
      reconnecting: string;
      autoRefreshing: string;
      totalServers: string;
      connected: string;
      reconnectingCount: string;
      failed: string;
      allHealthy: string;
      degraded: string;
      unhealthy: string;
    };
  };

  // Mem0 Config Panel
  mem0?: {
    title: string;
    description: string;
    warningBanner: string;
    helpTitle: string;
    helpDescription: string;
    helpItems: string[];
    documentation: string;
    reloadFromDatabase: string;
    saving: string;
    saveSystemDefaults: string;
    userMemoryPrompt: string;
    agentMemoryPrompt: string;
    memoryUpdatePrompt: string;
    userMemoryCategories: string;
    agentMemoryCategories: string;
    resetToHardcoded: string;
    promptMinMax: string;
    promptUpdateMinMax: string;
    categoriesHint: string;
    loading: string;
    loadFailed: string;
    saveSuccess: string;
    saveFailed: string;
    resetSuccess: string;
    resetFailed: string;
    noDefaultPrompt: string;
    noDefaultCategories: string;
    version: string;
    lastUpdatedBy: string;
    validation: {
      userPromptMin: string;
      agentPromptMin: string;
      updatePromptMin: string;
      userPromptMax: string;
      agentPromptMax: string;
      updatePromptMax: string;
    };
  };

  // First Message Panel
  firstMessage?: {
    title: string;
    description: string;
    warningBanner: string;
    reloadFromDatabase: string;
    saving: string;
    saveSystemDefault: string;
    instructionTitle: string;
    instructionPlaceholder: string;
    instructionLength: string;
    instructionMinMax: string;
    howItWorksTitle: string;
    howItWorksItems: string[];
    loading: string;
    loadFailed: string;
    saveSuccess: string;
    saveFailed: string;
    version: string;
    lastUpdatedBy: string;
    validation: {
      minLength: string;
      maxLength: string;
    };
  };

  // Artifact Types Panel
  artifactTypes?: {
    title: string;
    description: string;
    addType: string;
    noTypes: string;
    addFirstType: string;
    editType: string;
    addTypeTitle: string;
    typeId: string;
    typeIdHint: string;
    icon: string;
    iconOptions: {
      package: string;
      sparkles: string;
      audio: string;
      document: string;
      image: string;
      calendar: string;
    };
    nameEnglish: string;
    nameChinese: string;
    descriptionLabel: string;
    descriptionHint: string;
    contentTypes: string;
    contentTypesHint: string;
    rotationPeriod: string;
    rotationOptions: {
      none: string;
      daily: string;
      weekly: string;
      monthly: string;
    };
    maxStored: string;
    maxStoredHint: string;
    cancel: string;
    saving: string;
    save: string;
    confirmDelete: string;
    loading: string;
    loadFailed: string;
    saveFailed: string;
    deleteFailed: string;
    version: string;
    lastUpdatedBy: string;
    // Documentation sections
    aboutTitle: string;
    aboutItems: string[];
    apiAccessTitle: string;
    workflowTitle: string;
    fieldRefTitle: string;
    adminOpsTitle: string;
    editing: string;
    fields: string;
    mediaFields: string;
    metadataFields: string;
    lifecycleMode: string;
    sortBy: string;
    retentionDays: string;
    defaultSuffix: string;
  };

  // Audit Log Viewer
  auditLog?: {
    loading: string;
    loadFailed: string;
    noLogs: string;
    noLogsHint: string;
    showing: string;
    version: string;
    reason: string;
    ip: string;
  };

  // Export/Import Panel
  exportImport?: {
    exportTitle: string;
    exportDescription: string;
    exporting: string;
    exportAll: string;
    exportSuccess: string;
    exportFailed: string;
    importTitle: string;
    importDescription: string;
    selectFile: string;
    importMode: string;
    importModeOptions: {
      createOnly: string;
      overwrite: string;
      merge: string;
    };
    importModeHints: {
      createOnly: string;
      overwrite: string;
      merge: string;
    };
    skipErrors: string;
    skipErrorsHint: string;
    importPreview: string;
    foundConfigs: string;
    importResults: string;
    imported: string;
    skipped: string;
    errors: string;
    importing: string;
    importConfigurations: string;
    aboutTitle: string;
    aboutItems: string[];
    noFileSelected: string;
    invalidJson: string;
    invalidFormat: string;
    importCompleted: string;
    importFailed: string;
  };

  // Tool Enhancement Panel
  toolEnhancement?: {
    title: string;
    description: string;
    loading: string;
    loadFailed: string;
    saveSuccess: string;
    saveFailed: string;
    save: string;
    saving: string;
    reload: string;
    searchPlaceholder: string;
    noToolSelected: string;
    configured: string;
    notConfigured: string;
    displayNameCn: string;
    displayNameEn: string;
    descriptionCn: string;
    descriptionEn: string;
    icon: string;
    displayOrder: string;
    enabled: string;
    clearConfig: string;
    toolInfo: string;
    technicalName: string;
    originalDescription: string;
    allCategories: string;
    orphanConfigs: string;
    orphanConfigsDesc: string;
    cleanupOrphans: string;
    orphanWarning: string;
    category: string;
    categoryOverride: string;
    categoryOverrideHint: string;
    originalCategory: string;
    useDefaultCategory: string;
    toolKey: string;
    // Category translations
    categories: Record<string, string>;
    // AI Generation
    aiGenerate: string;
    aiGenerateDesc: string;
    aiGenerateButton: string;
    aiGenerating: string;
    aiSelectModel: string;
    aiSelectModelPlaceholder: string;
    aiExplanation: string;
    aiNoDescription: string;
    // Reload Enhancer
    reloadEnhancer: string;
    reloadEnhancerDesc: string;
    reloadEnhancerSuccess: string;
    reloadEnhancerFailed: string;
    // Tool Testing
    testTool: string;
    testToolDesc: string;
    testParameters: string;
    testParametersPlaceholder: string;
    runTest: string;
    running: string;
    testResult: string;
    testSuccess: string;
    testFailed: string;
    executionTime: string;
    errorType: string;
    closeModal: string;
    noParameters: string;
    invalidJson: string;
    requiredFields: string;
    optionalFields: string;
    paramType: string;
    paramDefault: string;
    requiresUserContext: string;
    requiresUserContextDesc: string;
    formMode: string;
    jsonMode: string;
    useDefaultValue: string;
  };

  // Prompt Templates Panel
  promptTemplates?: {
    title: string;
    description: string;
    addTemplate: string;
    noTemplates: string;
    addFirstTemplate: string;
    editTemplate: string;
    addTemplateTitle: string;
    templateKey: string;
    templateKeyHint: string;
    nameEnglish: string;
    nameChinese: string;
    descriptionEn: string;
    descriptionZh: string;
    systemPromptZh: string;
    systemPromptEn: string;
    displayOrder: string;
    category: string;
    categoryOptions: {
      image: string;
      push: string;
      workflow: string;
    };
    isDefault: string;
    resetToDefault: string;
    resetSuccess: string;
    cancel: string;
    saving: string;
    save: string;
    confirmDelete: string;
    cannotDeleteDefault: string;
    loading: string;
    loadFailed: string;
    saveFailed: string;
    deleteFailed: string;
    version: string;
    lastUpdatedBy: string;
    searchPlaceholder: string;
    allCategories: string;
    // Preview
    previewPrompt: string;
    chinese: string;
    english: string;
  };

  // Output Schemas Panel
  outputSchemas?: {
    title: string;
    description: string;
    addSchema: string;
    addNew: string;
    editTitle: string;
    noSchemas: string;
    noSchemasMatch: string;
    addFirstSchema: string;
    editSchema: string;
    searchPlaceholder: string;
    scopeAll: string;
    scopeGlobal: string;
    scopeAgent: string;
    scopeGlobalOption: string;
    scopeAgentOption: string;
    selectAgent: string;
    statusAll: string;
    statusEnabled: string;
    statusDisabled: string;
    schemaKey: string;
    schemaKeyHint: string;
    nameEnglish: string;
    nameChinese: string;
    descriptionEn: string;
    descriptionZh: string;
    scope: string;
    agentType: string;
    jsonSchema: string;
    exampleOutput: string;
    formatInstruction: string;
    formatInstructionHint: string;
    displayOrder: string;
    enabled: string;
    loading: string;
    loadFailed: string;
    saveFailed: string;
    deleteFailed: string;
    toggleFailed: string;
    totalSchemas: string;
    showing: string;
    aboutTitle: string;
    aboutItem1: string;
    aboutItem2: string;
    aboutItem3: string;
    aboutItem4: string;
    cancel: string;
    save: string;
    saving: string;
    confirmDelete: string;
    editing: string;
  };

  // Tool Combo Panel
  toolCombo?: {
    title: string;
    description: string;
    loading: string;
    loadFailed: string;
    saveSuccess: string;
    saveFailed: string;
    save: string;
    saving: string;
    reload: string;
    searchPlaceholder: string;
    noTemplateSelected: string;
    newTemplate: string;
    deleteTemplate: string;
    confirmDelete: string;
    templateId: string;
    templateIdPlaceholder: string;
    nameCn: string;
    nameEn: string;
    descriptionCn: string;
    descriptionEn: string;
    guidance: string;
    guidancePlaceholder: string;
    enabled: string;
    tools: string;
    addTool: string;
    toolName: string;
    selectTool: string;
    usageInstruction: string;
    usageInstructionPlaceholder: string;
    removeTool: string;
    preview: string;
    previewPrompt: string;
    closePreview: string;
    closePicker: string;
    noTools: string;
    templateCount: string;
    searchTools: string;
    availableTools: string;
    selectedTools: string;
    clickToAdd: string;
    alreadyAdded: string;
  };

  // Model Defaults Panel
  modelDefaults?: {
    title: string;
    description: string;
    reload: string;
    save: string;
    saving: string;
    loading: string;
    loadFailed: string;
    saveSuccess: string;
    saveFailed: string;
    version: string;
    lastUpdatedBy: string;
    // Categories
    chatModels: string;
    visionModels: string;
    auxiliaryModels: string;
    embeddingReranking: string;
    speechModels: string;
    // Fields
    primaryChatModel: string;
    primaryChatModelDesc: string;
    quickChatModel: string;
    quickChatModelDesc: string;
    primaryVisionModel: string;
    primaryVisionModelDesc: string;
    quickVisionModel: string;
    quickVisionModelDesc: string;
    memoryModel: string;
    memoryModelDesc: string;
    classificationModel: string;
    classificationModelDesc: string;
    reasoningModel: string;
    reasoningModelDesc: string;
    taskModel: string;
    taskModelDesc: string;
    embeddingModel: string;
    embeddingModelDesc: string;
    rerankingModel: string;
    rerankingModelDesc: string;
    rerankingEnabled: string;
    sttModel: string;
    sttModelDesc: string;
    ttsModel: string;
    ttsModelDesc: string;
    audioSettings: {
      title: string;
      ttsProvider: string;
      ttsVoice: string;
      ttsSpeed: string;
      ttsPreambleTrim: string;
      ttsPreambleTrimUnit: string;
      ttsStreaming: string;
      sttModel: string;
      sttFallbackModel: string;
      sttHotwords: string;
    };
    imageGenerationModels: string;
    imageModel: string;
    imageModelDesc: string;
    qualityImageModel: string;
    qualityImageModelDesc: string;
    promptRewriteModel: string;
    promptRewriteModelDesc: string;
    mbtiModel: string;
    mbtiModelDesc: string;
    // Section headers
    globalModelsTitle: string;
    globalModelsDesc: string;
    perAgentDefaultsTitle: string;
    perAgentDefaultsDesc: string;
    allAgentsShared: string;
  };
}

export interface EvalScenariosMessages {
  // Page
  pageTitle: string;
  pageDescription: string;

  // Actions
  createScenario: string;
  promoteChat: string;
  refresh: string;
  runAll: string;
  runSingle: string;
  running: string;
  edit: string;
  delete: string;
  duplicate: string;
  duplicateSuccess: string;
  expandDetails: string;
  collapseDetails: string;
  removeTag: string;
  filterByTags: string;
  addTagFilter: string;
  clearTags: string;
  exportScenarios: string;
  importScenarios: string;
  exportSuccess: string;
  importSuccess: string;
  importFailed: string;
  importInvalidFile: string;

  // Filters
  filters: string;
  searchPlaceholder: string;
  allAgentTypes: string;
  allCategories: string;
  allSources: string;
  allStatus: string;
  sourceManual: string;
  sourceChatPromoted: string;
  statusActive: string;
  statusInactive: string;

  // Stats
  totalScenarios: string;
  active: string;
  manual: string;
  chatPromoted: string;
  neverRun: string;

  // Form Steps
  step1Title: string;
  step1Description: string;
  step2Title: string;
  step2Description: string;
  step3Title: string;
  step3Description: string;
  stepOf: string;
  next: string;
  previous: string;

  // Form Fields - Step 1
  scenarioName: string;
  scenarioNamePlaceholder: string;
  scenarioNameHint: string;
  description: string;
  descriptionPlaceholder: string;
  targetAgent: string;
  targetAgentHint: string;
  category: string;
  categoryPlaceholder: string;
  priority: string;
  priorityHint: string;
  tags: string;
  addTagPlaceholder: string;

  // Form Fields - Step 2
  testConversation: string;
  conversationHint: string;
  addTurn: string;
  userRole: string;
  assistantRole: string;
  userMessagePlaceholder: string;
  assistantMessagePlaceholder: string;
  expectedResponse: string;
  expectedResponsePlaceholder: string;
  expectedResponseHint: string;
  expectedResponseDetailTitle: string;
  expectedResponseDetail1: string;
  expectedResponseDetail2: string;
  expectedResponseDetail3: string;
  expectedResponseExample: string;

  // Form Fields - Step 3
  llmJudgeModel: string;
  llmJudgeModelHint: string;
  llmJudgeModelWarning: string;
  quickAddMetrics: string;
  evaluationCriteria: string;
  customMetric: string;
  metricNamePlaceholder: string;
  threshold: string;
  noMetricsSelected: string;
  minOverallScore: string;
  minOverallScoreHint: string;
  activeCheckbox: string;

  // Metrics
  metrics: {
    hallucination: string;
    hallucinationDesc: string;
    hallucinationHelp: string;
    relevancy: string;
    relevancyDesc: string;
    relevancyHelp: string;
    faithfulness: string;
    faithfulnessDesc: string;
    faithfulnessHelp: string;
    answer_relevancy: string;
    answer_relevancyDesc: string;
    answer_relevancyHelp: string;
    contextual_relevancy: string;
    contextual_relevancyDesc: string;
    contextual_relevancyHelp: string;
    bias: string;
    biasDesc: string;
    biasHelp: string;
    toxicity: string;
    toxicityDesc: string;
    toxicityHelp: string;
    astro_accuracy: string;
    astro_accuracyDesc: string;
    astro_accuracyHelp: string;
    interpretation_quality: string;
    interpretation_qualityDesc: string;
    interpretation_qualityHelp: string;
    helpfulness: string;
    helpfulnessDesc: string;
    helpfulnessHelp: string;
  };
  metricsGuide: string;
  metricsGuideContent: string;
  lowerIsBetter: string;
  higherIsBetter: string;

  // Dialogs
  createDialogTitle: string;
  editDialogTitle: string;
  createButton: string;
  saveChanges: string;

  // Promote Chat Dialog
  promoteChatTitle: string;
  promoteChatDescription: string;
  chatTopicId: string;
  chatTopicIdPlaceholder: string;
  chatTopicIdHint: string;
  promoteButton: string;

  // Run Evaluation Dialog
  runEvaluationTitle: string;
  runningEvaluation: string;
  runningHint: string;
  runningHintResult: string;
  evaluationStarted: string;
  evaluationStartedHint: string;
  checkRunsPanel: string;
  evaluationComplete: string;
  overallScore: string;
  passed: string;
  failed: string;
  response: string;
  responseTime: string;
  metricResults: string;
  reason: string;
  scenario: string;
  agentType: string;
  judgerModel: string;
  criteriaLabel: string;
  batchSummary: string;
  total: string;
  close: string;
  runEvaluationButton: string;
  batchInfoPrefix: string;
  batchInfoSuffix: string;

  // User Picker
  userPicker: {
    title: string;
    useCurrentUser: string;
    currentUserActive: string;
    currentUserLabel: string;
    clearSelection: string;
    searchPlaceholder: string;
    noUserSelected: string;
    noUserSelectedHint: string;
    loading: string;
    noMatch: string;
    testBadge: string;
    hasChart: string;
    noChart: string;
    hasChartTooltip: string;
    noChartTooltip: string;
    showingCount: string;
    runId: string;
    scenarios: string;
    user: string;
  };

  // Response Language
  responseLanguage: {
    title: string;
    hint: string;
    auto: string;
    zh: string;
    en: string;
  };

  // Results
  lastResult: string;
  noResults: string;

  // Confirmations
  deleteConfirm: string;

  // Success/Error messages
  createSuccess: string;
  updateSuccess: string;
  deleteSuccess: string;
  promoteSuccess: string;
  runSuccess: string;
  error: string;

  // History Panel
  historyPanel: {
    title: string;
    scoreTrend: string;
    recentRuns: string;
    noHistory: string;
    checkRunsPanelHint: string;
    expandDetails: string;
    collapseDetails: string;
    agentResponse: string;
    responseTime: string;
    executedAt: string;
    executedBy: string;
    metrics: string;
    resultsList: string;
    running: string;
    failed: string;
  };

  // Result Detail Dialog
  resultDetail: {
    title: string;
    viewDetails: string;
    overallScore: string;
    metricsTitle: string;
    judgmentReason: string;
    executedAt: string;
    model: string;
    responseTime: string;
    executedBy: string;
    runningHint: string;
    testConversation: string;
    expectedResponse: string;
  };

  // Runs Panel
  runsPanel: {
    title: string;
    runId: string;
    totalScenarios: string;
    passed: string;
    failed: string;
    skipped: string;
    averageScore: string;
    duration: string;
    viewDetails: string;
    noRuns: string;
    noResults: string;
    autoRefreshing: string;
    statusRunning: string;
    statusCompleted: string;
    statusFailed: string;
  };

  // Results Panel
  resultsPanel: {
    title: string;
    loading: string;
    noResults: string;
    noResultsHint: string;
    averageScore: string;
    totalRuns: string;
    scenarioName: string;
    description: string;
    testInput: string;
    noInput: string;
    history: string;
    detailedMetrics: string;
    agentResponse: string;
    evaluationNotes: string;
  };

  // Common texts
  optional: string;
  noDescription: string;
  createFirstScenario: string;
  input: string;

  // Time formatting
  time: {
    justNow: string;
    minutesAgo: string;
    hoursAgo: string;
    daysAgo: string;
  };

  // Metric Definitions
  metricDefinitions: {
    pageTitle: string;
    pageDescription: string;
    createMetric: string;
    builtinMetrics: string;
    customMetrics: string;
    viewPrompt: string;
    editMetric: string;
    deleteMetric: string;
    metricName: string;
    displayName: string;
    metricType: string;
    defaultThreshold: string;
    thresholdDirection: string;
    higherBetter: string;
    lowerBetter: string;
    applicableAgents: string;
    allAgents: string;
    systemMetric: string;
    customGeval: string;
    criteria: string;
    criteriaPlaceholder: string;
    criteriaHint: string;
    evaluationSteps: string;
    evaluationStepsHint: string;
    addStep: string;
    removeStep: string;
    noMetrics: string;
    builtinPromptNote: string;
    promptViewer: {
      title: string;
      type: string;
      source: string;
      threshold: string;
      prompt: string;
      readOnlyNote: string;
    };
    editor: {
      createTitle: string;
      editTitle: string;
      nameHint: string;
      displayNameHint: string;
      descriptionHint: string;
      // Evaluation Steps
      evaluationStepsTitle: string;
      optional: string;
      stepsVsCriteriaTitle: string;
      stepsExplanation1: string;
      stepsExplanation2: string;
      stepsExplanation3: string;
      exampleTitle: string;
      exampleStep1: string;
      exampleStep2: string;
      exampleStep3: string;
      exampleStep4: string;
      yourSteps: string;
      noStepsYet: string;
      stepPlaceholder: string;
      addStep: string;
      removeStep: string;
      // AI Generation
      aiAssistantTitle: string;
      aiAssistantDescription: string;
      aiDescriptionPlaceholder: string;
      generating: string;
      generateMetric: string;
      minChars: string;
      chars: string;
      aiExplanation: string;
      aiDescriptionTooShort: string;
      aiGenerationFailed: string;
      selectModel: string;
      selectModelPlaceholder: string;
      selectModelFirst: string;
      // Validation
      nameRequired: string;
      displayNameRequired: string;
      criteriaOrStepsRequired: string;
      saveFailed: string;
      namePlaceholder: string;
      displayNamePlaceholder: string;
      descriptionPlaceholder: string;
      allAgentsHint: string;
    };
    deleteConfirm: string;
    deleteSystemError: string;
    createSuccess: string;
    updateSuccess: string;
    deleteSuccess: string;
  };

  // Test Suites
  testSuites: {
    // Selection
    selectForSuite: string;
    exitSelect: string;
    createSuite: string;
    selectAll: string;
    deselectAll: string;
    selected: string;

    // Dialog
    createTitle: string;
    editTitle: string;
    name: string;
    namePlaceholder: string;
    description: string;
    descriptionPlaceholder: string;
    tags: string;
    tagsPlaceholder: string;
    priority: string;
    scenarioCount: string;
    scenarios: string;
    cancel: string;
    create: string;
    save: string;

    // Run
    selectSuite: string;
    runAllActive: string;
    runSuite: string;
    runSuiteInfo: string;

    // List
    noSuites: string;
    noSuitesHint: string;
    active: string;
    inactive: string;
    runningTitle: string;
    running: string;
    run: string;

    // Panel (TestSuitesPanel)
    panel: {
      runHistory: string;
      refresh: string;
      totalSuites: string;
      totalScenarios: string;
      howToCreate: string;
      howToCreateHint: string;
      loading: string;
      deleteConfirm: string;
      containsScenarios: string;
    };
  };
}

export interface NavigationMessages {
  appTitle: string;
  // Menu groups
  navGroupAgent: string;
  navGroupWorkflow: string;
  navGroupAnalytics: string;
  navGroupUser: string;
  navGroupSystem: string;
  navGroupContent: string;
  navGroupDev: string;
  navGroupFinance: string;
  // Menu items
  agentConfigs: string;
  financeDashboard: string;
  systemSettings: string;
  toolManagement: string;
  businessTemplates: string;
  featureFlags: string;
  audit: string;
  subscriptionSettings: string;
  promptTemplates: string;
  skills: string;
  monitoring: string;
  reports: string;
  classificationAnalytics: string;
  messageFeedback: string;
  users: string;
  pushTest: string;
  notificationTemplates: string;
  evaluation: string;
  playground: string;
  imageWorkflows: string;
  workflowSchedules: string;
  contentPacks: string;
  welcomeTitles: string;
  petTips: string;
  uiAssets: string;
  annotation: string;
  traces: string;
  performance: string;
  userGrowth: string;
  wishes: string;
  logout: string;
  expandSidebar: string;
  collapseSidebar: string;
}

export interface FinancePageMessages {
  pageTitle: string;
  pageDescription: string;
  tabs: {
    overview: string;
    expenses: string;
    contributions: string;
  };
  revenue: string;
  expense: string;
  net: string;
  committed: string;
  received: string;
  outstanding: string;
  outstandingHint: string;
  totalExpense: string;
  totalRevenue: string;
  capitalPool: string;
  capitalPoolHint: string;
  monthlyBurn: string;
  runway: string;
  breakeven: string;
  perMonth: string;
  months: string;
  monthlyPnl: string;
  cashflow: string;
  byChannel: string;
  accounts: string;
  account: string;
  contributed: string;
  spent: string;
  balance: string;
  byCategory: string;
  date: string;
  category: string;
  amount: string;
  contributor: string;
  round: string;
  note: string;
  lastSync: string;
  syncNow: string;
  syncing: string;
  reconWarn: string;
}

export interface EvaluationMessages {
  pageTitle: string;
  pageDescription: string;
  tabs: {
    scenarios: string;
    metrics: string;
    suites: string;
  };
}

export interface HooksMessages {
  // Pipeline
  pipelineTitle: string;
  loading: string;
  errorLoading: string;
  noData: string;
  enabledCount: string;
  showHidden: string;
  hideHidden: string;

  // Stages
  stages: {
    system: string;
    user: string;
    final: string;
  };
  hooksCount: string;

  // Config Panel
  selectHook: string;
  description: string;
  noDescription: string;
  stage: string;
  category: string;
  scope: string;
  sortOrder: string;
  dragToReorder: string;
  configInAgentConfig: string;

  // Hook Definitions (i18n for display_name and description)
  hookDefinitions: {
    [key: string]: {
      displayName: string;
      description: string;
      /** 依赖的前置条件（如其他 hooks 或配置） */
      requires?: string;
      /** 额外说明或注意事项 */
      note?: string;
    };
  };
}

export interface AgentConfigMessages {
  // List Page
  listTitle: string;
  listDescription: string;
  createAgent: string;
  searchPlaceholder: string;
  showInactive: string;
  noAgentsMatch: string;
  noAgentsMatchHint: string;
  noAgentsYet: string;
  noAgentsYetHint: string;
  showingAgents: string;
  activeCount: string;
  inactive: string;
  version: string;
  updated: string;

  // Detail Page
  loadingConfig: string;
  backToList: string;
  agentNotFound: string;
  copy: string;
  unsavedChanges: string;
  saving: string;
  saveSuccess: string;
  saveFailed: string;
  active: string;
  activateAgent: string;
  deactivateAgent: string;
  confirmDeactivate: string;
  confirmActivate: string;
  updatedViaAdmin: string;
  conflictModalTitle: string;
  conflictModalBody: string;
  conflictModalDiscard: string;
  conflictModalForce: string;

  // Tabs
  tabSettings: string;
  tabHooks: string;
  tabTools: string;
  tabPrompt: string;
  tabSchemas: string;
  tabSkills: string;
  tabJson: string;
  tabVersions: string;
  // New tabs
  tabBasic: string;
  tabMemory: string;
  tabKnowledge: string;
  tabAstrology: string;
  tabPersonality: string;
  tabEmotion: string;
  tabClassifier: string;
  tabTeam: string;
  tabAdvanced: string;

  // Actions (for JsonField browseTools button)
  actions?: {
    browseTools?: string;
  };

  // Output Format Section
  outputFormat?: {
    title: string;
    createSchema: string;
    manageSchemas: string;
    noSchemasWarning: string;
    createFirstSchema: string;
    noDefault: string;
    defaultSchemaHelp: string;
    availableSchemas: string;
    apiHint: string;
  };

  // Create/Copy Agent Dialog
  createAgentDialog: {
    copyAgent: string;
    createNewAgent: string;
    agentType: string;
    agentTypePlaceholder: string;
    agentTypeHint: string;
    name: string;
    namePlaceholder: string;
    description: string;
    descriptionPlaceholder: string;
    copyingFrom: string;
    creating: string;
    agentTypeRequired: string;
    nameRequired: string;
    agentTypeInvalid: string;
  };

  // Version History
  versionHistory: {
    title: string;
    loading: string;
    current: string;
    restore: string;
    noHistory: string;
    createdBy: string;
    versionConfig: string;
    confirmRestore: string;
    restoreReason: string;
    editing: string;
    clickToShowJson: string;
    viewConfig: string;
  };

  // JSON Preview
  jsonPreview: {
    title: string;
    copy: string;
    copied: string;
    download: string;
    modified?: string;
    reset?: string;
    applyChanges?: string;
    editHint?: string;
    loadToEditor?: string;
  };

  // Basic Info Section
  basicInfo?: {
    roleDefinitionPrompt: string;
    roleDefinitionHelp: string;
    configureSystemPrompts: string;
  };

  // Config Migration Dialog
  configMigration?: {
    title: string;
    versionInfo: string;
    changesTitle: string;
    warning: string;
    cancel: string;
    confirm: string;
    confirmed: string;
    before: string;
    after: string;
    changeTypes: {
      add: string;
      remove: string;
      modify: string;
    };
  };

  // Tools Browser Modal
  toolsBrowser?: {
    customTools: string;
    builtinToolkits: string;
    customToolsDesc: string;
    builtinToolkitsDesc: string;
    filterByCategory: string;
    allCategories: string;
    toolsSelected: string;
    loadingTools: string;
    availableTools: string;
    selectedTools: string;
    previewConfig: string;
    cancel: string;
    apply: string;
    noBuiltinToolkits?: string;
    noCustomTools?: string;
    noToolsSelected?: string;
    toolsCount?: string;
    builtinToolsBrowser?: string;
    customToolsBrowser?: string;
    format?: string;
    noItemsConfigured?: string;
    toolkits?: string;
    enabledTools?: string;
    servers?: string;
    modules?: string;
    allToolsEnabled?: string;
    toolNotFound?: string;
    serverNotFound?: string;
    willMigrateOnSave?: string;
    toolNotFoundRemove?: string;
    invalidToolsWarning?: string;
    removeInvalidToolsFirst?: string;
  };

  // MCP Tools Browser Modal
  mcpBrowser?: {
    title?: string;
    refresh?: string;
    systemServers?: string;
    userServers?: string;
    serversSelected?: string;
    toolsSelected?: string;
    invalidItemsWarning?: string;
    removeInvalidItemsFirst?: string;
    loadingServers?: string;
    availableServers?: string;
    selectedItems?: string;
    servers?: string;
    individualTools?: string;
    noServersSelected?: string;
    noServersAvailable?: string;
    connected?: string;
    disconnected?: string;
    failedToConnect?: string;
    previewConfig?: string;
    cancel?: string;
    apply?: string;
    toolsCount?: string;
  };

  // Prompt Template Preview
  promptPreview?: {
    title: string;
    buttonLabel: string;
    copy: string;
    copied: string;
    loading: string;
    refresh: string;
    generatePreview: string;
    usingTemplate: string;
    default: string;
    enabledSections: string;
    useRealData: string;
    livePreview: string;
    userId: string;
    enterUserId: string;
    useCurrent: string;
    usingCurrentUser: string;
    sampleMessage: string;
    enterSampleMessage: string;
    realDataHint: string;
    sampleDataHint: string;
    previewFailed: string;
    template: string;
    systemChars: string;
    userChars: string;
    user: string;
    hooksExecuted: string;
    systemStage: string;
    userStage: string;
    noHooksExecuted: string;
    dataSources: string;
    dataSourceHint: string;
    systemPrompt: string;
    enhancedUserMessage: string;
    contextItems: string;
    emptyStateTitle: string;
    emptyStateDesc: string;
  };

  // Output Schemas Tab
  outputSchemas?: {
    title: string;
    add: string;
    noSchemas: string;
    noSchemasHint: string;
    default: string;
    properties: string;
    noSelection: string;
    noSelectionHint: string;
  };

  // Skills Tab
  skills?: {
    title: string;
    add: string;
    noSkills: string;
    noSkillsHint: string;
    noSelection: string;
    noSelectionHint: string;
    enabled: string;
    disabled: string;
    priority: string;
    exclusionGroup: string;
    disabledWarning: string;
    // Dialog
    createTitle: string;
    editTitle: string;
    // Form fields
    skillId: string;
    skillIdPlaceholder: string;
    skillIdRequired: string;
    skillIdPattern: string;
    skillIdReadonlyHint: string;
    skillIdHint: string;
    name: string;
    namePlaceholder: string;
    nameRequired: string;
    description: string;
    descriptionPlaceholder: string;
    descriptionHint: string;
    command: string;
    commandPlaceholder: string;
    commandRequired: string;
    commandHint: string;
    displayOrder: string;
    displayOrderHint: string;
    displayOrderMin: string;
    enableSkill: string;
    instructions: string;
    instructionsPlaceholder: string;
    instructionsHint: string;
    priorityHint: string;
    exclusionGroupHint: string;
    forcePromptType: string;
    forcePromptAuto: string;
    forcePromptQuick: string;
    forcePromptDeep: string;
    toolCombo: string;
    toolComboNone: string;
    toolComboTools: string;
    toolComboHint: string;
    matchingRules: string;
    keywords: string;
    keywordsPlaceholder: string;
    keywordsHint: string;
    regexPatterns: string;
    regexPatternsHint: string;
    exampleQueries: string;
    exampleQueriesHint: string;
    // AI Generation
    aiGenerateRules: string;
    aiGenerateDesc: string;
    selectModel: string;
    selectModelPlaceholder: string;
    generateFromInstructions: string;
    aiAnalysis: string;
    aiGenerate: string;
    generating: string;
    mustStartWithLetter: string;
    provideInstructions: string;
    selectModelFirst: string;
    selectModelInAiPanel: string;
    generateFailed: string;
    patternGenerateFailed: string;
    deleteConfirmTitle: string;
    enableSkillsFirst: string;
    toggleEnableSkillsHint: string;
    clickAddFirst: string;
    selectOrCreateSkill: string;
    // Global Settings
    globalSettings: string;
    // Dedup Configuration
    dedupConfig: string;
    enableDedup: string;
    enableDedupDesc: string;
    dedupConfigHint: string;
    dedupConfigOverrideHint: string;
    dataQueryHours: string;
    dataQueryHoursHint: string;
    reportWindowHours: string;
    reportWindowHoursHint: string;
    maxPerWindow: string;
    maxPerWindowHint: string;
    outputPreviewLength: string;
    outputPreviewLengthHint: string;
    showUserQuery: string;
    showUserQueryHint: string;
    queryPreviewLength: string;
    queryPreviewLengthHint: string;
    hours: string;
    chars: string;
    dedupPrinciple: string;
    dedupPrincipleList: string[];
    skillTrackingRequired: string;
    skillTrackingRequiredHint: string;
    perSkillDedupEnabled?: string;
    perSkillDedupDisabledHint?: string;
    perSkillDedupEnabledHint?: string;
    dedupDisabledForSkill?: string;
    default?: string;
    // Skills Tab - Classifier relationship
    classifierDisabledTitle?: string;
    classifierDisabledDesc?: string;
    classifierRelationDesc?: string;
    // Skills Tab Settings Panel
    skillsNotAvailable?: string;
    skillsHookHidden?: string;
    skillMatchingSettings?: string;
    featureToggles?: string;
    enableSkills?: string;
    enableSkillsHint?: string;
    semanticMatching?: string;
    semanticMatchingHint?: string;
    thresholdSettings?: string;
    ruleConfidence?: string;
    semanticSimilarity?: string;
    llmTimeout?: string;
    skillToolInstructions?: string;
    customized?: string;
    resetToDefault?: string;
    skillToolInstructionsHint?: string;
    howItWorks?: string;
    threeLayerMatching?: string;
    layer1Rule?: string;
    layer1RuleDesc?: string;
    layer2Semantic?: string;
    layer2SemanticDesc?: string;
    layer3Llm?: string;
    layer3LlmDesc?: string;
    exclusionGroupExplain?: string;
    exclusionGroupRules?: string[];
    fieldPurposes?: string;
    fieldPurposesDesc?: string;
    fieldPurposesTable?: {
      field: string;
      purpose: string;
      hint: string;
    }[];
    skillTools?: string;
    skillToolsDesc?: string;
    skillToolsList?: string[];
    skillToolsNote?: string;
    exampleQueriesBestPractices?: string;
    exampleQueriesTips?: string[];
    semanticMatchingNote?: string;
    semanticMatchingDetails?: string[];
    validationErrors?: string;
    validationWarnings?: string;
    andMore?: string;
    newSkillName?: string;
    keywordsCount?: string;
    examplesCount?: string;
    errorsCount?: string;
    warningsCount?: string;
    // Classification Rules section
    classificationRules?: string;
    classificationRulesDesc?: string;
    llmFallbackEnabled?: string;
    llmFallbackEnabledHint?: string;
    customQuickKeywords?: string;
    customQuickKeywordsHint?: string;
    customQuickKeywordsPlaceholder?: string;
    customQuickPatterns?: string;
    customQuickPatternsHint?: string;
    customQuickPatternsPlaceholder?: string;
    defaultPatternsInfo?: string;
    addPattern?: string;
    invalidRegex?: string;
    builtinRules?: string;
    builtinRulesDesc?: string;
    builtinMaxLength?: string;
    builtinSkillNote?: string;
    builtinGreeting?: string;
    builtinAck?: string;
    builtinCasual?: string;
    builtinLaughter?: string;
    builtinFarewell?: string;
    builtinReaction?: string;
  };

  // Schema Editor
  schemaEditor?: {
    aiGenerate: string;
    aiGenerateDesc: string;
    selectModel: string;
    selectModelPlaceholder: string;
    inputExample: string;
    generating: string;
    generate: string;
    aiExplanation: string;
    key: string;
    keyValidHint: string;
    keyInvalidHint: string;
    name: string;
    namePlaceholder: string;
    description: string;
    descriptionPlaceholder: string;
    jsonSchema: string;
    validJson: string;
    jsonSchemaHint: string;
    exampleOutput: string;
    exampleOutputHint: string;
    formatInstruction: string;
    formatInstructionHint: string;
    defaultSchema: string;
    setAsDefault: string;
    deleteSchema: string;
    confirmDelete: string;
    alertExampleRequired: string;
    alertModelRequired: string;
    alertGenerateFailed: string;
  };

  // Template Selector
  templateSelector?: {
    title: string;
    description: string;
    useDefault: string;
    default: string;
    recommended: string;
    systemDefault: string;
    sections: string;
    sectionsCount: string;
    updated: string;
    noTemplates: string;
    loadingTemplates: string;
    previewTemplate: string;
  };

  // Knowledge Tab
  knowledge?: {
    confirmDeleteDoc: string;
    confirmClearAll: string;
  };

  // Test Chat
  testChat?: {
    confirmDeleteConversation: string;
  };
}

export interface ImageWorkflowsMessages {
  executionPanel: {
    title: string;
    execution: string;
    progress: string;
    nodes: string;
    failed: string;
    duration: string;
    error: string;
    clearAndClose: string;
    nodeDetail: string;
  };
  executionOverview: {
    retrying: string;
    retryFrom: string;
    users: string;
    viewUser: string;
    targetUserDetails: string;
    loading: string;
    totalUsers: string;
    completed: string;
    failedCount: string;
    currentView: string;
    userError: string;
  };
  // Config Renderers
  modelConfig?: {
    resolution: string;
    aspectRatio: string;
    quality: string;
    default: string;
    enableWebSearch: string;
    webSearchDescription: string;
  };
  executionHistory: {
    title: string;
    tab: string;
    executionLabel: string;
    nodes: string;
    saveFirst: string;
    noHistory: string;
    runFirst: string;
    relativeTime: {
      justNow: string;
      minutesAgo: string;
      hoursAgo: string;
      daysAgo: string;
    };
  };
  nodeDetail: {
    selectNode: string;
    noStepData: string;
    status: string;
    duration: string;
    order: string;
    startedAt: string;
    completedAt: string;
  };
  stepResult: {
    error: string;
    variables: string;
    noVariables: string;
    mode: string;
    generatedPrompt: string;
    showLess: string;
    showMore: string;
    noPrompt: string;
    model: string;
    output: string;
    imageLoadError: string;
    noImage: string;
    storage: string;
    saved: string;
    temporary: string;
    outputPending: string;
    noOutput: string;
  };
  outputNode?: {
    noArtifact: string;
  };
  runDialog: {
    title: string;
    description: string;
    targetUsers: string;
    cancel: string;
    execute: string;
    executing: string;
    selectAtLeastOneUser: string;
    workflowStarted: string;
    allValidUsers: string;
    usersCount: string;
    executeFailed: string;
  };
  // Workflow Toolbar
  toolbar?: {
    categories: {
      input: { label: string; description: string };
      image: { label: string; description: string };
      special: { label: string; description: string };
      action: { label: string; description: string };
      output: { label: string; description: string };
    };
    jsonExport: {
      title: string;
      copyToClipboard: string;
      downloadJson: string;
      tip: string;
    };
    jsonImport: {
      title: string;
      dropHint: string;
      orPaste: string;
      placeholder: string;
      tip: string;
      cancel: string;
      import: string;
      errors: {
        missingFields: string;
        missingMetadata: string;
        missingDefinition: string;
        parseError: string;
        fileReadError: string;
        dropJsonFile: string;
      };
    };
    addNode: string;
    nodePanel: string;
    dragToAdd: string;
    startNodeTip: string;
    nodeCount: string;
    importJson: string;
    exportJson: string;
    executionHistory: string;
    saveFirst: string;
    saving: string;
    save: string;
    running: string;
    run: string;
    unsaved: string;
    workflowName: string;
    workflowKey: string;
    keyValidation: {
      startWithLowercase: string;
      maxLength: string;
    };
    dragHint: string;
    unsavedWarning: string;
  };
  // Workflow List
  list?: {
    title: string;
    workflowCount: string;
    newWorkflow: string;
    loading: string;
    loadFailed: string;
    noWorkflows: string;
    noWorkflowsHint: string;
    createWorkflow: string;
    copySuccess: string;
    copyFailed: string;
    menu: {
      edit: string;
      makePrivate: string;
      makePublic: string;
      delete: string;
    };
    confirmDelete: string;
    deleteSuccess: string;
    deleteFailed: string;
    updateSuccess: string;
    updateFailed: string;
    stats: {
      nodes: string;
      runs: string;
    };
    tags: {
      public: string;
      agent: string;
      rawLlm: string;
      library: string;
      temp: string;
    };
  };
  // Workflow Editor
  editor?: {
    loading: string;
    back: string;
    editWorkflow: string;
    newWorkflow: string;
    unsavedWarning: string;
    validation: {
      keyRequired: string;
      keyInvalid: string;
      keyTooLong: string;
    };
    toast: {
      saveSuccess: string;
      createSuccess: string;
      saveFailed: string;
      saveFirst: string;
      executionComplete: string;
      executionFailed: string;
      executionPartial: string;
      executionCancelled: string;
      resumeComplete: string;
      resumeFailed: string;
      resumePartial: string;
      resumeCancelled: string;
      resumeError: string;
      modelRequired: string;
    };
  };
  // Config Field Renderer
  configField?: {
    selectPlaceholder: string;
    loadingOptions: string;
    unknownRenderer: string;
  };
  // Node Config Panel
  nodeConfigPanel?: {
    selectNodePrompt: string;
    labelInput: string;
    labelPlaceholder: string;
    inputFields: string;
    outputFields: string;
    executionResult: string;
    status: {
      pending: string;
      running: string;
      completed: string;
      failed: string;
      skipped: string;
    };
    timing: {
      started: string;
      completed: string;
      duration: string;
    };
    output: {
      title: string;
      prompt: string;
      generatedImage: string;
      savedToLibrary: string;
      fileId: string;
    };
    error: string;
    duplicate: string;
    delete: string;
    builtinVariables: {
      user_id: { label: string; description: string };
      user_name: { label: string; description: string };
      user_email: { label: string; description: string };
      execution_id: { label: string; description: string };
      workflow_id: { label: string; description: string };
      workflow_name: { label: string; description: string };
      timestamp: { label: string; description: string };
      date: { label: string; description: string };
      time: { label: string; description: string };
    };
  };
  // Input Fields Section
  inputFieldsSection?: {
    title: string;
    autoMap: string;
    noFields: string;
    usageHint: string;
    variableName: string;
    selectSource: string;
    fixedValue: string;
    inputFixedValue: string;
    switchToSelect: string;
    deleteField: string;
    extractKey: string;
    entireObject: string;
    contextVariables: string;
    customVariables: string;
    upstreamNode: string;
    selectableKey: string;
    clickToEdit: string;
    keyPathPlaceholder: string;
  };
  // Output Fields Section
  outputFieldsSection?: {
    title: string;
    noFields: string;
    variableName: string;
    deleteField: string;
    selectValueSource: string;
    noMapping: string;
    schema: string;
    clickToEdit: string;
  };
  // Field Source Toggle
  fieldSourceToggle?: {
    mapped: string;
    fixedValue: string;
    switchToFixed: string;
    fromUpstream: string;
    selectDataSource: string;
    context: string;
    input: string;
    variable: string;
    noAvailableSources: string;
    extractKey: string;
    entireObject: string;
    currentInputVars: string;
    contextVars: string;
    customVars: string;
    optionalKey: string;
    keyPathPlaceholder: string;
  };
  // Runtime Options Form
  runtimeOptions?: {
    title: string;
    disableMemoryTool: {
      label: string;
      description: string;
    };
    disableMemoryWrite: {
      label: string;
      description: string;
    };
  };
  // Input Mode Renderer
  inputModeRenderer?: {
    generationMethod: string;
    manual: string;
    agent: string;
    llm: string;
    outputFormat: string;
    loading: string;
    noConstraint: string;
    manualModeHint: string;
    agentLlmModeHint: string;
    fillByFormat: string;
    enterLabel: string;
    jsonFormat: string;
    variableHint: string;
    imageDescription: string;
    imageDescriptionPlaceholder: string;
    customOutputFields: string;
    customOutputFieldsHint: string;
    customOutputFieldsTip: string;
    selectAgent: string;
    userIntent: string;
    userIntentPlaceholder: string;
    selectLlmModel: string;
    selectModel: string;
    temperature: string;
    precise: string;
    balanced: string;
    creative: string;
    systemPrompt: string;
    customized: string;
    selectTemplate: string;
    systemPromptPlaceholder: string;
    templateTip: string;
    userMessage: string;
    userMessagePlaceholder: string;
  };
  // Artifact Config Renderer
  artifactConfig?: {
    saveToLibrary: string;
    imageSource: string;
    imageSourceDescription: string;
    imageSourcePlaceholder: string;
    category: string;
    categoryPlaceholder: string;
    variableHint: string;
    filename: string;
    filenamePlaceholder: string;
    optional: string;
    saveAsArtifact: string;
    loading: string;
    artifactDescription: string;
    artifactFieldMapping: string;
    fieldsCount: string;
    mapImagePath: string;
    mapAudioPath: string;
    mapVideoPath: string;
    mapDocumentPath: string;
    mapMediaPath: string;
    enterFieldLabel: string;
    mappingTip: string;
    artifactTitle: string;
    artifactTitlePlaceholder: string;
    artifactDescriptionField: string;
    artifactDescriptionPlaceholder: string;
  };
  // Push Target Renderer
  pushTarget?: {
    notificationTitle: string;
    variableHint: string;
    notificationTitlePlaceholder: string;
    notificationBody: string;
    notificationBodyPlaceholder: string;
    pushTarget: string;
    followStartConfig: string;
    followStartConfigDesc: string;
    currentUser: string;
    currentUserDesc: string;
    allUsers: string;
    allUsersDesc: string;
    specificUsers: string;
    specificUsersDesc: string;
    userIdList: string;
    userIdListPlaceholder: string;
    userIdListHint: string;
    notificationSound: string;
    default: string;
    silent: string;
    badgeNumber: string;
    badgePlaceholder: string;
    // Mode switching
    quickConfigTab: string;
    templateConfigTab: string;
    // Quick config - action
    tapAction: string;
    tapActionPlaceholder: string;
    tapActionNone: string;
    // Quick config - send message action
    sendMessageContent: string;
    sendMessageContentHint: string;
    sendMessageContentPlaceholder: string;
    // Template config
    selectTemplate: string;
    selectTemplatePlaceholder: string;
    variableMappings: string;
    variableMappingHint: string;
    notMapped: string;
    preview: string;
    previewTitle: string;
    previewAction: string;
    previewNoAction: string;
    templateNotSelected: string;
  };
  // Charm Options Renderer
  charmOptions?: {
    requiredFields: string;
    phraseContent: string;
    phraseHint: string;
    phrasePlaceholder: string;
    imageGenOptions: string;
    optional: string;
    emotionTrend: string;
    auto: string;
    positive: string;
    calm: string;
    hopeful: string;
    grateful: string;
    concerned: string;
    chartId: string;
    chartIdPlaceholder: string;
    backgroundTheme: string;
    loading: string;
    allThemes: string;
    imagesCount: string;
    backgroundImage: string;
    random: string;
    randomCurrentTheme: string;
    font: string;
    defaultFont: string;
    overlayBadge: string;
    imagesUnit: string;
    fontColorLabel: string;
    autoColor: string;
  };
  // Custom Variables Renderer
  customVariables?: {
    title: string;
    description: string;
    addVariable: string;
    noVariables: string;
    variableName: string;
    variableValue: string;
    usageHint: string;
  };
  // Builtin Variables Renderer
  builtinVariables?: {
    title: string;
    selectToEnable: string;
    userId: { label: string; description: string };
    userName: { label: string; description: string };
    userEmail: { label: string; description: string };
    executionId: { label: string; description: string };
    workflowId: { label: string; description: string };
    workflowName: { label: string; description: string };
    timestamp: { label: string; description: string };
    date: { label: string; description: string };
    time: { label: string; description: string };
  };
  // Node display labels
  nodes?: {
    output: {
      artifact: string;
      storage: string;
      artifactUser: string;
      libraryPermanent: string;
      temp7days: string;
    };
    luckyCharm: {
      font: string;
      theme: string;
    };
    prompt: {
      manual: string;
      agent: string;
      llm: string;
      clickToInput: string;
      clickToConfigAgent: string;
      clickToConfigLlm: string;
      agentLabel: string;
      modelLabel: string;
    };
    start: {
      builtinVariables: string;
      customVariables: string;
      notEnabled: string;
      notDefined: string;
    };
    push: {
      target: string;
      userCount: string;
      users: string;
      sound: string;
      receivesPrompt: string;
      followStart: string;
      currentUser: string;
      allUsers: string;
      specificUsers: string;
      mode: string;
      quickMode: string;
      templateMode: string;
      template: string;
    };
    artifactTypes: {
      lucky_charm: string;
      soulmate: string;
      soul_echo: string;
      daily_push: string;
      natal_report: string;
      transit_report: string;
    };
    fieldMapping: {
      artifactField: string;
      itemField: string;
      artifactFieldPlaceholder: string;
      itemFieldPlaceholder: string;
      hint: string;
    };
  };
}

// Resources module messages
export interface ResourcesMessages {
  // Page title and tabs
  pageTitle: string;
  tabs: {
    artifacts: string;
    library: string;
    temp: string;
  };

  // Resource types
  types: {
    artifact: string;
    library: string;
    temp: string;
  };

  // Stats
  stats: {
    totalFiles: string;
    totalSize: string;
    uniqueUsers: string;
    byType: string;
    bySubtype: string;
    files: string;
    users: string;
    subtypes: string;
    categories: string;
    avgPerUser: string;
    largestUsers: string;
    autoCleanup: string;
  };

  // Summary
  summary: {
    totalFiles: string;
    totalSize: string;
  };

  // Modal
  modal: {
    title: string;
    summary: string;
    noResources: string;
    loading: string;
    error: string;
    deleteWarning: string;
  };

  // Status
  status: {
    expired: string;
    current: string;
  };

  // Actions
  actions: {
    viewResources: string;
    delete: string;
    open: string;
    download: string;
    confirmDelete: string;
    cancel: string;
    browseTools: string;
  };

  // Groups
  groups?: {
    uncategorized: string;
    expandAll: string;
    collapseAll: string;
  };

  // Delete all
  deleteAll: {
    type: string;
    all: string;
    confirmType: string;
    confirmAll: string;
  };

  // Deleting state
  deleting: string;

  // Table headers
  table: {
    userId: string;
    totalFiles: string;
    totalSize: string;
  };

  // Cleanup operations
  cleanup: {
    title: string;
    tempTitle: string;
    tempDesc: string;
    tempButton: string;
    artifactsTitle: string;
    artifactsDesc: string;
    artifactsButton: string;
    daysOld: string;
    days: string;
    confirm: string;
    cleaning: string;
    error: string;
    successTemp: string;
    successArtifacts: string;
  };

  // Timestamps
  lastUpdated: string;

  // Errors
  errors: {
    loadFailed: string;
  };
}

// Traces page messages
export interface TracesPageMessages {
  // Page
  pageTitle: string;
  tabList: string;
  tabMetrics: string;
  opikConsole: string;
  notConfigured: string;
  notConfiguredDesc: string;
  loading: string;
  loadFailed: string;
  // List
  searchPlaceholder: string;
  search: string;
  colName: string;
  colTime: string;
  colDuration: string;
  colTokens: string;
  colCost: string;
  colSpans: string;
  noData: string;
  pagination: string;
  // Span tree
  loadingSpans: string;
  noSpans: string;
  showingSpans: string;
  spanInput: string;
  spanOutput: string;
  tokenPrompt: string;
  tokenCompletion: string;
  tokenTotal: string;
  // SmartContent
  toolCallLabel: string;
  reasoningLabel: string;
  emptyContent: string;
  emptyResponse: string;
  showRawJson: string;
  hideRawJson: string;
  // TraceDetail sections
  sectionInput: string;
  sectionOutput: string;
  sectionSpans: string;
  sectionMetadata: string;
  tokens: string;
  errorLabel: string;
  // Enhanced list + timeline
  spanCount: string;
  noModel: string;
  viewTimeline: string;
  viewTree: string;
  totalDuration: string;
  selectSpan: string;
  spanDetail: string;
  // Metrics
  metricsTitle: string;
  noStats: string;
  statTraceCount: string;
  statDuration: string;
  statCost: string;
  statTokens: string;
  statErrorSub: string;
  statAvgCost: string;
  statAvgTokens: string;
  errorBanner: string;
  chartTraceCount: string;
  chartDuration: string;
  chartTokenUsage: string;
  tooltipTraces: string;
  tooltipPrompt: string;
  tooltipCompletion: string;
  dataSource: string;
  // Filter toggles
  filterChatOnly: string;
  filterAll: string;
}

export interface I18nMessages {
  common: {
    appTitle: string;
    save: string;
    saving: string;
    cancel: string;
    delete: string;
    reset: string;
    loading: string;
    success: string;
    error: string;
    warning: string;
    required: string;
    previous: string;
    next: string;
    refresh: string;
    close: string;
    description: string;
    retry: string;
    clear: string;
    admin: string;
  };
  actions: {
    saveChanges: string;
    resetToDefault: string;
    resetToSystemDefault: string;
    browseTools: string;
  };
  navigation: NavigationMessages;
  sections: Record<string, string>;
  sectionDescriptions?: Record<string, string>;
  fields: Record<string, FieldTranslation>;
  pushNotifications: PushNotificationsMessages;
  evalScenarios: EvalScenariosMessages;
  evaluation: EvaluationMessages;
  monitoring: MonitoringMessages;
  classificationAnalytics: ClassificationAnalyticsMessages;
  userGrowth: UserGrowthPageMessages;
  users: UsersMessages;
  systemSettings: SystemSettingsMessages;
  agentConfig: AgentConfigMessages;
  hooks: HooksMessages;
  imageWorkflows: ImageWorkflowsMessages;
  resources: ResourcesMessages;
  toolManagement: ToolManagementMessages;
  businessTemplates: BusinessTemplatesMessages;
  // Shared components
  userSelector?: UserSelectorMessages;
  workflowTarget?: WorkflowTargetMessages;
  featureFlags?: FeatureFlagsMessages;
  login?: LoginPageMessages;
  subscriptionPage?: SubscriptionPageMessages;
  // Additional pages
  skillsPage?: SkillsPageMessages;
  promptTemplatesPage?: PromptTemplatesPageMessages;
  notificationTemplatesPage?: NotificationTemplatesPageMessages;
  reportsPage?: ReportsPageMessages;
  messageFeedbackPage?: MessageFeedbackPageMessages;
  annotationBatchesPage?: AnnotationBatchesPageMessages;
  annotationExportPage?: AnnotationExportPageMessages;
  annotationStatsPage?: AnnotationStatsPageMessages;
  annotationWorkbenchPage?: AnnotationWorkbenchPageMessages;
  annotationForm?: AnnotationFormMessages;
  playground?: PlaygroundMessages;
  batchCard?: BatchCardMessages;
  statsOverview?: StatsOverviewMessages;
  exportPreview?: ExportPreviewMessages;
  // Feature Group Card
  featureGroups?: FeatureGroupsMessages;
  // Chat Panel
  chatPanel?: ChatPanelMessages;
  // Traces page
  tracesPage: TracesPageMessages;
  // Schedules
  schedules?: SchedulesMessages;
  // Wishes page
  wishesPage?: WishesPageMessages;
  // Finance page
  financePage?: FinancePageMessages;
}

export interface SchedulesMessages {
  timezoneWindow: {
    enable: string;
    targetHour: string;
    defaultTimezone: string;
    hint: string;
    cronHint: string;
  };
}

// Wishes page messages
export interface WishesPageMessages {
  pageTitle: string;
  pageDescription: string;
  allStatus: string;
  statusOpen: string;
  statusPlanned: string;
  statusInProgress: string;
  statusDone: string;
  statusRejected: string;
  wishCount: string;
  totalClusters: string;
  selectCluster: string;
  noData: string;
  loading: string;
  save: string;
  title: string;
  summary: string;
  status: string;
  statusNote: string;
  representativeExcerpt: string;
  representativeWishes: string;
  notifyUsers: string;
  confirmNotify: string;
  confirmNotifyDesc: string;
  mergeInto: string;
  mergeTarget: string;
  confirmMerge: string;
  mergeCurrentDesc: string;
  cancel: string;
  language: string;
  notifiedAt: string;
  notNotified: string;
  deleteWish: string;
}

// Feature Groups Messages
export interface FeatureGroupsMessages {
  virtualFeatures: string;
  confirmEnableDependencies: string;
}

// Chat Panel Messages
export interface ChatPanelMessages {
  confirmDeleteTopic: string;
  confirmDeleteMemory: string;
  confirmDeleteBirthChart: string;
}

// Login page messages
export interface LoginPageMessages {
  title: string;
  subtitle: string;
  modePassword: string;
  modePhone: string;
  email: string;
  emailPlaceholder: string;
  password: string;
  passwordPlaceholder: string;
  phoneNumber: string;
  phonePlaceholder: string;
  phoneHint: string;
  signingIn: string;
  signIn: string;
  sendingCode: string;
  sendCode: string;
  verificationTitle: string;
  codeSentTo: string;
  resendIn: string;
  resendCode: string;
  backToPhone: string;
  enterAllDigits: string;
  invalidCode: string;
  footer: string;
}

// Subscription Settings page messages
export interface SubscriptionPageMessages {
  pageTitle: string;
  pageDescription: string;
  tabs: {
    settings: string;
    analytics: string;
    products: string;
  };
  settings: {
    refresh: string;
    saving: string;
    saveChanges: string;
    errorLoading: string;
    savedSuccess: string;
    saveFailed: string;
    resetChanges: string;
    paywallSettings: string;
    showOnLaunch: string;
    dismissable: string;
    softWall: string;
    delaySeconds: string;
    trialSettings: string;
    trialEnabled: string;
    trialDays: string;
    featuresByTier: string;
    featuresByTierHint: string;
    freeFeatures: string;
    proFeatures: string;
    businessFeatures: string;
    commaSeparated: string;
    smartPaywall: string;
    smartPaywallLabel: string;
    smartPaywallHardLimitHint: string;
    smartPaywallHardMessagesDaily: string;
    smartPaywallHardDeepDaily: string;
    smartPaywallHint: string;
    paywallModeTitle: string;
    paywallModeSmart: string;
    paywallModeSmartDesc: string;
    paywallModeLegacy: string;
    paywallModeLegacyDesc: string;
    journeyTitle: string;
    journeyStart: string;
    journeyDeepGuide: string;
    journeyFallbackGuide: string;
    journeyDeepBlock: string;
    journeyMsgBlock: string;
    softSection: string;
    softSectionDesc: string;
    hardSection: string;
    hardSectionDesc: string;
    freeDeepDailyLabel: string;
    freeDeepDailyHelp: string;
    fallbackDailyMessagesLabel: string;
    fallbackDailyMessagesHelp: string;
    hardMessagesHelp: string;
    hardDeepHelp: string;
    hardBelowGuideWarning: string;
    generalSection: string;
    advancedSection: string;
    advancedSectionHint: string;
    quotaSettings: string;
    quotaTotalMessages: string;
    quotaDailyMessages: string;
    quotaUnlimitedHint: string;
    quotaDisabledBySmartPaywall: string;
  };
  analytics: {
    title: string;
    refresh: string;
    totalUsers: string;
    activeSubscribers: string;
    trialToPaidRate: string;
    churnRate: string;
    totalSubscribers: string;
    trialUsers: string;
    conversionRate: string;
    monthlyRevenue: string;
    loading: string;
    errorLoading: string;
    noData: string;
    dateRanges: {
      days7: string;
      days30: string;
      days90: string;
      allTime: string;
    };
    statusDistribution: string;
    productDistribution: string;
    noProductData: string;
    dailyTrends: string;
    date: string;
    newTrials: string;
    conversions: string;
    newSubscriptions: string;
    cancellations: string;
    recentEvents: string;
    noRecentEvents: string;
    eventTypes: {
      trialStarted: string;
      subscribed: string;
      renewed: string;
      cancelled: string;
      expired: string;
      admin: string;
    };
    statusLabels: {
      free: string;
      trial: string;
      active: string;
      expired: string;
      cancelled: string;
    };
  };
  products: {
    title: string;
    subtitle: string;
    addProduct: string;
    editProduct: string;
    deleteProduct: string;
    deleteConfirm: string;
    productId: string;
    productName: string;
    description: string;
    price: string;
    displayPrice: string;
    interval: string;
    noProducts: string;
    noProductsHint: string;
    loading: string;
    errorLoading: string;
    savedSuccess: string;
    saveFailed: string;
    deletedSuccess: string;
    deleteFailed: string;
    refresh: string;
    appleProductId: string;
    googleProductId: string;
    sortOrder: string;
    available: string;
    unavailable: string;
    disableProduct: string;
    enableProduct: string;
    cancel: string;
    saving: string;
    saveChanges: string;
    name: string;
    priceCents: string;
    priceYuan: string;
    currency: string;
    alipaySection: string;
    alipayHint: string;
    appleSection: string;
    appleHint: string;
  };
}

// Skills Page messages
export interface SkillsPageMessages {
  pageTitle: string;
  pageDescription: string;
  enabledCount: string;
  createSkill: string;
  showAll: string;
  showEnabledOnly: string;
  savingOrder: string;
  errorLoading: string;
  loadingSkills: string;
  deleteSkill: string;
  deleteConfirm: string;
  cannotUndo: string;
  translateAll: string;
  translating: string;
  translateSuccess: string;
  translateError: string;
  translations: string;
  noTranslations: string;
}

// Prompt Templates page messages
export interface PromptTemplatesPageMessages {
  pageTitle: string;
  pageDescription: string;
  createTemplate: string;
  errorLoading: string;
  loadingTemplates: string;
  deleteTemplate: string;
  deleteConfirm: string;
  cannotUndo: string;
}

// Notification Templates page messages
export interface NotificationTemplatesPageMessages {
  pageTitle: string;
  pageDescription: string;
  tabs: {
    templates: string;
    sendTest: string;
  };
  sendTest: {
    title: string;
    description: string;
    target: string;
    specifyUser: string;
    allUsers: string;
    userIdPlaceholder: string;
    allUsersWarning: string;
    sendButton: string;
    selectTemplateOrContent: string;
    enterUserId: string;
    sendSuccess: string;
    sendFailed: string;
    succeeded: string;
    failed: string;
    unknownError: string;
  };
  rightPanel: {
    deviceTokens: string;
    sendLogs: string;
  };
  tips: {
    selectTemplate: string;
    templateVariable: string;
    silentPush: string;
  };
}

// Reports page messages
export interface ReportsPageMessages {
  pageTitle: string;
  pageDescription: string;
  tabs: {
    overview: string;
    users: string;
    pricing: string;
    trial: string;
    behavior: string;
    revenue?: string;
  };
  loading: string;
  loadFailed: string;
  fetchFailed: string;
  retry: string;
  dataUpdatedAt: string;
  dateRange: string;
  revenue?: {
    totalRevenue?: string;
    transactions?: string;
    payingUsers?: string;
    arpu?: string;
    dailyRevenue?: string;
    channelDistribution?: string;
    productDistribution?: string;
    conversionFunnel?: string;
    trialConversionTiming?: string;
    bestConversionDay?: string;
    expiringSoon?: string;
    within3Days?: string;
    within7Days?: string;
    within14Days?: string;
    productMigrations?: string;
    cohortRetention?: string;
    funnelRegistered?: string;
    funnelTrialed?: string;
    funnelPurchased?: string;
    funnelRenewed?: string;
    funnelChurned?: string;
    renewedAppleOnly?: string;
    churnedAppleOnly?: string;
    noData?: string;
    upgrade?: string;
    downgrade?: string;
  };
  // Overview tab
  overview: {
    totalUsers: string;
    activeUsers: string;
    totalSessions: string;
    chatCount: string;
    chatCost: string;
    dailyServiceCost: string;
    totalCost: string;
    avgUserCost: string;
    median: string;
    memberPrice: string;
    perMonth: string;
    profitableUsers: string;
    people: string;
    ratio: string;
    p75Cost: string;
    p75Subtitle: string;
    p90Cost: string;
    p90Subtitle: string;
    soulmateStats: string;
    lifetimeContract: string;
    freeMatch: string;
    times: string;
    cost: string;
  };
  // Users tab
  users: {
    costTierAnalysis: string;
    userDetails: string;
    user: string;
    phone: string;
    sessionCount: string;
    lifecycle: string;
    chatCost: string;
    serviceCost: string;
    totalCost: string;
    profitLoss: string;
    days: string;
  };
  // Pricing tab
  pricing: {
    scenarioSimulation: string;
    suggestions: string;
    tip1: string;
    tip2: string;
    tip3: string;
    tip4: string;
  };
  // Trial tab
  trial: {
    riskAnalysis: string;
  };
  // Behavior tab
  behavior: {
    posthogNotConfigured: string;
    posthogConfigHint: string;
    posthogHostHint: string;
    mobileConfigHint: string;
    loadingBehavior: string;
    fetchFailed: string;
    noData: string;
    userRetention: string;
    // BehaviorAnalysis charts
    topEvents: string;
    triggerCount: string;
    uniqueUsers: string;
    dailyTrend: string;
    eventCount: string;
    activeUsers: string;
    featureDistribution: string;
    clickCount: string;
    featureDetails: string;
    featureName: string;
    avgDuration: string;
    pageViews: string;
    viewCount: string;
    userJourneyFunnel: string;
    step: string;
    userCount: string;
    conversionRate: string;
    retention: {
      d1: string;
      d7: string;
      d30: string;
      d1Desc: string;
      d7Desc: string;
      d30Desc: string;
    };
    summary: {
      totalEvents: string;
      activeUsers: string;
      eventsPerUser: string;
      behaviorEvents: string;
      avgPerUser: string;
    };
    // Event name mappings
    eventNames: {
      appOpened: string;
      chatStarted: string;
      chatMessageSent: string;
      featureClicked: string;
      screenViewed: string;
      subscriptionViewed: string;
      soulmateStarted: string;
      pageview: string;
      autocapture: string;
    };
    // Feature name mappings
    featureNames: {
      soulmate: string;
      birthChart: string;
      dailyFortune: string;
      luckyCharm: string;
      settings: string;
      subscription: string;
    };
  };
  // DateRangeSelector
  dateRangeSelector: {
    last7Days: string;
    last30Days: string;
    last90Days: string;
    last1Year: string;
    to: string;
  };
}

// Message Feedback page messages
export interface MessageFeedbackPageMessages {
  pageTitle: string;
  pageDescription: string;
  like: string;
  dislike: string;
  likeShort: string;
  dislikeShort: string;
  total: string;
  likeRate: string;
  byAgentStats: string;
  filter: string;
  allTypes: string;
  allAgents: string;
  last7Days: string;
  last30Days: string;
  last90Days: string;
  last1Year: string;
  type: string;
  user: string;
  userMessage: string;
  aiReply: string;
  time: string;
  noData: string;
  paginationInfo: string;
  feedbackDetail: string;
  userNote: string;
  none: string;
  generatedImage: string;
  loadingImage: string;
  cannotLoadImage: string;
  contextData: string;
  title: string;
  mode: string;
  generationDate: string;
  viewRawJson: string;
  deviceInfo: string;
}

// Annotation Batches page messages
export interface AnnotationBatchesPageMessages {
  pageTitle: string;
  createBatch: string;
  pageDescription: string;
  tabs: {
    batchManagement: string;
    dataExport: string;
    statsReport: string;
  };
  status: {
    all: string;
    draft: string;
    active: string;
    completed: string;
    archived: string;
  };
  loadFailed: string;
  loadingBatches: string;
  noBatches: string;
  createFirstBatch: string;
  deleteBatch: string;
  deleteConfirm: string;
  deleteWarning: string;
  confirmDelete: string;
  activateFailed: string;
  restoreFailed: string;
}

// Annotation Export page messages
export interface AnnotationExportPageMessages {
  pageTitle: string;
  pageDescription: string;
  previewFailed: string;
  exportSuccess: string;
  exportFailed: string;
}

// Annotation Stats page messages
export interface AnnotationStatsPageMessages {
  pageTitle: string;
  pageDescription: string;
  loadFailed: string;
  loadingStats: string;
  exportFailed: string;
  exportSuccess: string;
  totalCases: string;
  andMore: string;
  close: string;
  downloadJson: string;
}

// Annotation Workbench page messages
export interface AnnotationWorkbenchPageMessages {
  pageTitle: string;
  pageDescription: string;
  allBatches: string;
  taskList: string;
  totalTasks: string;
  pending: string;
  completed: string;
  skipped: string;
  annotated: string;
  loadFailed: string;
  loadingTasks: string;
  noTasks: string;
  selectBatchHint: string;
  back: string;
  sessionCompleted: string;
  completedThisSession: string;
  batchProgress: string;
  submitFailed: string;
  tasksCompleteTitle: string;
  noTasksTitle: string;
  completedCountMessage: string;
  noTasksInBatch: string;
  returnToBatches: string;
  viewStats: string;
  pendingLabel: string;
  wordCount: string;
  session: string;
  labelContent: string;
  shortcuts: string;
  positive: string;
  neutral: string;
  negative: string;
  submit: string;
  skip: string;
}

// Annotation Form component messages
export interface AnnotationFormMessages {
  steps: {
    messageClassification: string;
    emotionIntent: string;
    skipped: string;
  };
  messageType: {
    label: string;
    shortcutHint: string;
    simplePrompt: string;
    fullAgent: string;
  };
  expectedRouting: {
    label: string;
    autoSetHint: string;
  };
  noEmotionHint: string;
  emotion: {
    valenceLabel: string;
    valenceShortcut: string;
    positive: string;
    neutral: string;
    negative: string;
    specificLabel: string;
    optional: string;
  };
  intent: {
    label: string;
    multiSelectHint: string;
  };
  needsSupport: {
    label: string;
    hint: string;
  };
  confidence: {
    label: string;
    high: string;
    highDesc: string;
    medium: string;
    mediumDesc: string;
    low: string;
    lowDesc: string;
  };
  notes: {
    label: string;
    placeholder: string;
  };
  actions: {
    skip: string;
    skipShortcut: string;
    previous: string;
    continueHint: string;
    submitHint: string;
    next: string;
    submitLabel: string;
  };
}

// Playground page messages
export interface PlaygroundMessages {
  modes: {
    generate: string;
    edit: string;
    fuse: string;
    luckyCharm: string;
  };
  modeDescriptions: {
    generate: string;
    edit: string;
    fuse: string;
    luckyCharm: string;
  };
  labels: {
    selectEditImage: string;
    selectFuseImages: string;
    selected: string;
    uploadOrSelect: string;
    inputMode: string;
    agent: string;
    llm: string;
    manual: string;
    selectAgent: string;
    selectAgentPlaceholder: string;
    selectLLMModel: string;
    selectModelPlaceholder: string;
    loading: string;
    noAgentsAvailable: string;
    noModelsAvailable: string;
    outputSchema: string;
    noSchemaConstraint: string;
    schemaDescription: string;
    tellAgentWhatYouWant: string;
    tellLLMWhatYouWant: string;
    userPromptPlaceholder: string;
    temperature: string;
    precise: string;
    balanced: string;
    creative: string;
    generateDescription: string;
    generating: string;
    imageDescription: string;
    editInstruction: string;
    fuseInstruction: string;
    describeImageToGenerate: string;
    describeEditInstruction: string;
    describeFuseInstruction: string;
    processing: string;
    operationFailed: string;
    generateFailed: string;
    required: string;
    upload: string;
    random: string;
    currentPack: string;
    defaultFont: string;
    emotionTrend: string;
    emotionPositive: string;
    emotionNeutral: string;
    emotionNegative: string;
    emotionMixed: string;
    extraPrompt: string;
    extraPromptAgentPlaceholder: string;
    extraPromptLLMPlaceholder: string;
    optional: string;
    generateText: string;
    fortunePhrase: string;
    fortunePhrasePlaceholder: string;
    clickGenerateOrInput: string;
    editableAfterGenerate: string;
    customOptions: string;
    chartId: string;
    chartIdPlaceholder: string;
    chartIdHint: string;
    backgroundPack: string;
    allPacks: string;
    backgroundImage: string;
    font: string;
    systemPrompt: string;
    rawLLMHint: string;
    userPromptSent: string;
    taskFailed: string;
    pleaseSelectAgent: string;
    pleaseSelectLLMModel: string;
    pleaseEnterPrompt: string;
    // ModelSelector and DynamicConfigSelector
    model: string;
    capabilityGenerate: string;
    capabilityEdit: string;
    capabilityFuse: string;
    maxImagesForFuse: string;
    resolution: string;
    quality: string;
    supportsWebSearch: string;
    supportsTransparency: string;
    pricePerImage: string;
    aspectRatio: string;
    background: string;
    defaultOption: string;
    transparent: string;
    white: string;
    auto: string;
    enableWebSearch: string;
    webSearchDescription: string;
    maxGenerate: string;
    maxFuse: string;
    output: string;
    aspectRatioSquare: string;
    aspectRatioPortrait: string;
    aspectRatioLandscape: string;
    aspectRatioVertical: string;
    aspectRatioWide: string;
    aspectRatioTallPortrait: string;
    aspectRatioTallLandscape: string;
    aspectRatioSocial: string;
    aspectRatioUltraWide: string;
    aspectRatioTall: string;
    aspectRatioBanner: string;
    qualityStandard: string;
    qualityHD: string;
    qualityLow: string;
    qualityMedium: string;
    qualityHigh: string;
    qualityAuto: string;
    sizeAuto: string;
    liveBadge: string;
  };
  luckyCharm: {
    config: string;
    inputMode: string;
  };
  history: {
    title: string;
    library: string;
    refresh: string;
    clear: string;
    loading: string;
    noImages: string;
    noImagesHint: string;
    libraryEmpty: string;
    libraryEmptyHint: string;
    selectForEdit: string;
    selectForFuse: string;
    selected: string;
    deselect: string;
    view: string;
    download: string;
    delete: string;
    confirmDelete: string;
    confirmClear: string;
    confirmDeleteFromLibrary: string;
    saveToLibrary: string;
    savedToLibrary: string;
    saveFailed: string;
    deleteFailed: string;
    saved: string;
    saving: string;
    generating: string;
    alreadySaved: string;
    editFork: string;
    imagePreview: string;
    close: string;
    model: string;
    time: string;
    source: string;
    description: string;
    filterAll: string;
    filterGenerate: string;
    filterEdit: string;
    filterFuse: string;
    filterLuckyCharm: string;
  };
}

// Tool Management page messages
export interface ToolPerfMessages {
  title: string;
  description: string;
  totalCalls: string;
  totalErrors: string;
  trackedTools: string;
  slowTools: string;
  noData: string;
  noDataHint: string;
  reset: string;
  resetConfirm: string;
  refresh: string;
  toolName: string;
  calls: string;
  errors: string;
  avgMs: string;
  maxMs: string;
  minMs: string;
  errorRate: string;
  lastError: string;
  slow: string;
  sortBy: string;
  loadError: string;
}

export interface ToolManagementMessages {
  pageTitle: string;
  pageDescription: string;
  tabs: {
    mcpServers: string;
    toolEnhancement: string;
    toolCombo: string;
    performance: string;
  };
  perf: ToolPerfMessages;
}

// Business Templates page messages
export interface BusinessTemplatesMessages {
  pageTitle: string;
  pageDescription: string;
  tabs: {
    promptTemplates: string;
    artifactTypes: string;
    outputPromptTemplates: string;
    outputSchemas: string;
  };
}

// User Selector component messages
export interface UserSelectorMessages {
  searchPlaceholder: string;
  selectedCount: string;
  selectAll: string;
  deselectAll: string;
  clearAll: string;
  noUsers: string;
  devices: string;
  showingCount: string;
}

// Workflow Target Selector component messages
export interface WorkflowTargetMessages {
  currentUser: string;
  currentUserDesc: string;
  specificUsers: string;
  specificUsersDesc: string;
  willRunAs: string;
}

// Feature Flags page messages
export interface FeatureFlagsMessages {
  pageTitle: string;
  pageDescription: string;
  createFlag: string;
  version: string;
  tabs: {
    flags: string;
    userOverrides: string;
    auditLog: string;
  };
  flagList: {
    noFlags: string;
    noFlagsHint: string;
    rollout: string;
    groups: string;
  };
  flagEditor: {
    createTitle: string;
    editTitle: string;
    flagName: string;
    flagNamePlaceholder: string;
    flagNameError: string;
    flagNameFormatError: string;
    flagNameExistsError: string;
    description: string;
    descriptionPlaceholder: string;
    descriptionLengthError: string;
    enabledByDefault: string;
    platforms: string;
    platformsError: string;
    rolloutPercentage: string;
    rolloutError: string;
    rolloutAllUsers: string;
    rolloutPartialUsers: string;
    targetUserGroups: string;
    targetUserGroupsHelp: string;
    addGroupPlaceholder: string;
    saveChanges: string;
  };
  deleteConfirm: {
    title: string;
    message: string;
    confirm: string;
  };
  toggleConfirm: {
    title: string;
    message: string;
    withNotification: string;
    withoutNotification: string;
  };
  userOverrides: {
    title: string;
    description: string;
    searchPlaceholder: string;
    selectFlag: string;
    selectFlagPlaceholder: string;
    noOverrides: string;
    noOverridesHint: string;
    addOverride: string;
    addTitle: string;
    userId: string;
    userIdPlaceholder: string;
    flag: string;
    overrideValue: string;
    enabled: string;
    disabled: string;
    deleteConfirm: string;
    clearAllConfirm: string;
    override: string;
    overridesCount: string;
    errorLoading: string;
    userIdRequired: string;
    selectFlagRequired: string;
    addFailed: string;
  };
  auditLog: {
    title: string;
    description: string;
    noLogs: string;
    noLogsHint: string;
    action: string;
    changedBy: string;
    reason: string;
    timestamp: string;
    version: string;
    page: string;
    errorLoading: string;
    actions: {
      create: string;
      update: string;
      delete: string;
    };
  };
  errors: {
    loadingError: string;
  };
}

export interface BatchCardMessages {
  status: {
    draft: string;
    active: string;
    completed: string;
    archived: string;
  };
  progress: string;
  stats: {
    totalTasks: string;
    completed: string;
    pending: string;
  };
  actions: {
    activate: string;
    activateTitle: string;
    noTasksTitle: string;
    startAnnotation: string;
    archive: string;
    restore: string;
  };
  metadata: {
    source: string;
    created: string;
  };
}

export interface StatsOverviewMessages {
  cards: {
    totalBatches: string;
    activeBatches: string;
    totalTasks: string;
    pendingTasks: string;
    completedTasks: string;
    completionRate: string;
    annotators: string;
    avgTime: string;
  };
  overallProgress: string;
  completed: string;
  batchStats: {
    title: string;
    batch: string;
    totalTasks: string;
    completed: string;
    pending: string;
    progress: string;
  };
  annotatorStats: {
    title: string;
    annotator: string;
    completed: string;
    skipped: string;
    avgTime: string;
    recentActivity: string;
    noData: string;
  };
  export: {
    title: string;
    description: string;
    button: string;
  };
}

export interface ExportPreviewMessages {
  presets: {
    title: string;
    balanced: {
      name: string;
      description: string;
    };
    deepConversations: {
      name: string;
      description: string;
    };
    newUsers: {
      name: string;
      description: string;
    };
    activeUsers: {
      name: string;
      description: string;
    };
    emotionalContent: {
      name: string;
      description: string;
    };
  };
  filters: {
    title: string;
    hasFilters: string;
    reset: string;
    expandAdvanced: string;
    collapseAdvanced: string;
    userActivity: string;
    conversationDepth: string;
    userStage: string;
    messagePosition: string;
    messageLength: string;
    to: string;
    characters: string;
    loadCount: string;
    maxCount: string;
    includeKeywords: string;
    keywordPlaceholder: string;
    add: string;
    excludeTemplates: string;
    randomSample: string;
    perPage: string;
    excludeExported: string;
    loadMessages: string;
  };
  userActivityLabels: {
    single: string;
    light: string;
    medium: string;
    heavy: string;
  };
  conversationDepthLabels: {
    singleTurn: string;
    short: string;
    medium: string;
    deep: string;
  };
  messagePositionLabels: {
    first: string;
    early: string;
    middle: string;
    last: string;
  };
  userStageLabels: {
    new: string;
    recent: string;
    active: string;
    veteran: string;
  };
  selection: {
    title: string;
    loaded: string;
    of: string;
    existing: string;
    searchPlaceholder: string;
    selectAll: string;
    selectNonDuplicate: string;
    clear: string;
    skipDuplicates: string;
    selected: string;
    duplicatesSkipped: string;
    effectiveExport: string;
    chars: string;
    copyMd5: string;
    alreadyExists: string;
    noMatchingMessages: string;
    noMessages: string;
  };
  pagination: {
    showing: string;
    total: string;
    excludedExported: string;
    previous: string;
    page: string;
    next: string;
  };
  createBatch: {
    title: string;
    batchTitle: string;
    batchTitleRequired: string;
    batchTitlePlaceholder: string;
    description: string;
    descriptionOptional: string;
    descriptionPlaceholder: string;
    create: string;
    enterTitle: string;
  };
}

export interface FieldTranslation {
  label: string;
  help?: string;
  placeholder?: string;
  invalidMessage?: string;
  options?: Record<string, string>;
}

const messages: Record<Language, I18nMessages> = {
  // ========== Chinese (Simplified) ==========
  zh: {
    common: {
      appTitle: "Agent 管理",
      save: "保存",
      saving: "保存中...",
      cancel: "取消",
      delete: "删除",
      reset: "重置",
      loading: "加载中...",
      success: "成功",
      error: "错误",
      warning: "警告",
      required: "必填",
      previous: "上一页",
      next: "下一页",
      refresh: "刷新",
      close: "关闭",
      description: "描述",
      retry: "重试",
      clear: "清除",
      admin: "管理员",
    },
    actions: {
      saveChanges: "保存更改",
      resetToDefault: "重置为默认值",
      resetToSystemDefault: "重置为系统默认",
      browseTools: "浏览工具",
    },
    navigation: {
      appTitle: "Dora 管理",
      // Menu groups
      navGroupAgent: "Agent 核心",
      navGroupWorkflow: "工作流",
      navGroupAnalytics: "数据分析",
      navGroupUser: "用户与商业",
      navGroupSystem: "系统管理",
      navGroupContent: "内容管理",
      navGroupDev: "开发测试",
      navGroupFinance: "财务",
      // Menu items
      agentConfigs: "Agent 配置",
      financeDashboard: "财务总览",
      systemSettings: "系统设置",
      toolManagement: "工具与MCP",
      businessTemplates: "业务模版",
      featureFlags: "功能开关",
      audit: "审计",
      subscriptionSettings: "订阅设置",
      promptTemplates: "Prompt 模板",
      skills: "技能栏配置",
      monitoring: "服务监控",
      reports: "运营报表",
      classificationAnalytics: "分类分析",
      messageFeedback: "消息反馈",
      users: "用户管理",
      pushTest: "推送测试",
      notificationTemplates: "通知模板",
      evaluation: "评测中心",
      playground: "测试广场",
      imageWorkflows: "工作流编排",
      workflowSchedules: "定时任务",
      contentPacks: "内容包",
      welcomeTitles: "欢迎语管理",
      petTips: "Tips 管理",
      uiAssets: "UI 资源管理",
      annotation: "数据标注",
      traces: '调用追踪',
      performance: '性能分析',
      userGrowth: '用户增长',
      wishes: '愿望池',
      logout: "退出登录",
      expandSidebar: "展开侧边栏",
      collapseSidebar: "收起侧边栏",
    },
    sections: {
      basic: "基本信息",
      models: "模型配置",
      models_chat: "对话模型",
      models_reasoning: "推理能力",
      models_vision: "视觉模型",
      models_memory: "记忆模型",
      models_classification: "分类模型",
      models_retrieval: "嵌入与检索",
      models_stt: "语音模型",
      llm_parameters: "LLM 采样参数",
      output_format: "输出格式",
      features: "功能开关",
      message_processing: "消息处理",
      advanced: "高级设置",
      memory: "记忆配置",
      memory_prompts: "记忆自定义提示词",
      memory_user: "用户记忆",
      memory_agent: "助手记忆",
      memory_query: "记忆查询设置",
      memory_ebbinghaus: "记忆衰减 (艾宾浩斯)",
      session_summary: "会话摘要",
      custom_tools: "自定义工具",
      builtin_tools: "内置工具",
      mcp_tools: "MCP 服务器",
      tools_execution: "工具执行",
      tools_compression: "工具结果压缩",
      team: "团队配置",
      quick_response: "快速响应",
      prompts: "系统提示词",
      first_message: "首条消息扩展",
      response_classifier: "匹配参数",
      response_classifier_prompts: "响应类型指令",
    },
    sectionDescriptions: {
      quick_response: "系统会自动判断用户消息的类型：简短问候、确认等走「快速响应」，复杂问题走「深度响应」。判断过程先用规则匹配（<10ms），置信度不足时再调用 LLM 分类。分类为快速响应时，会同时切换为轻量模型和下方的快速响应提示词，以实现更快、更自然的回复。",
    },
    fields: {
      // Basic Information
      agent_type: {
        label: "Agent 类型",
        help: "Agent 的唯一标识符",
      },
      name: {
        label: "名称",
        help: "Agent 的显示名称",
        placeholder: "请输入 Agent 名称",
      },
      description: {
        label: "描述",
        help: "Agent 的简要说明",
        placeholder: "请输入简要描述",
      },

      // Model Configuration
      chat_model_id: {
        label: "对话模型",
        help: "用于对话生成的 LLM 模型",
        invalidMessage: '警告: 模型 ID "{value}" 不在可用列表中',
      },
      memory_model_id: {
        label: "记忆模型",
        help: "用于记忆提取和管理的模型",
        invalidMessage: '警告: 模型 ID "{value}" 不在可用列表中',
      },
      embedding_model_id: {
        label: "向量模型",
        help: "用于文本向量化的嵌入模型",
        invalidMessage: '警告: 模型 ID "{value}" 不在可用列表中',
      },

      // LLM Sampling Parameters
      temperature: {
        label: "温度",
        help: "采样温度 (0=确定性, 数值越高越随机). 范围: 0.0-2.0",
      },
      top_p: {
        label: "Top P",
        help: "核采样概率 (0.0-1.0). 数值越低使用的token越少",
      },
      max_tokens: {
        label: "最大Token数",
        help: "生成的最大token数 (留空为不限). 范围: 1-128000",
        placeholder: "留空为不限",
      },
      presence_penalty: {
        label: "存在惩罚",
        help: "Token存在惩罚 (-2.0到2.0). 正值鼓励新主题",
      },
      frequency_penalty: {
        label: "频率惩罚",
        help: "Token频率惩罚 (-2.0到2.0). 正值减少重复",
      },

      // Features
      output_format: {
        label: "输出格式",
        help: "响应输出格式：默认（纯文本）、Markdown 或 JSON",
        options: {
          default: "默认",
          markdown: "Markdown",
          json: "JSON",
        },
      },
      debug_mode: {
        label: "调试模式",
        help: "启用详细日志输出",
      },
      storage_enabled: {
        label: "启用存储",
        help: "启用会话存储功能",
      },
      knowledge_enabled: {
        label: "知识库",
        help: "启用知识库检索",
      },
      enable_birth_chart_context: {
        label: "出生星盘上下文",
        help: "自动注入用户出生星盘数据（XML格式）到Agent上下文。需要用户已添加星盘。",
      },
      enable_web_search: {
        label: "网页搜索",
        help: "启用网页搜索功能",
      },

      // Message Processing
      "context.datetime_enabled": {
        label: "添加日期时间",
        help: "在系统提示词中添加当前日期和时间",
      },
      add_history_to_context: {
        label: "添加历史记录",
        help: "在Agent上下文中包含对话历史 (Agno: add_history_to_context)",
      },
      num_history_runs: {
        label: "最大历史轮数",
        help: "包含最近多少轮对话 (0-50)。与「最大历史消息数」二选一：设置消息数时此项自动忽略 (Agno: num_history_runs)",
      },
      num_history_messages: {
        label: "最大历史消息数",
        help: "按消息总数限制历史上下文。会话摘要的「首次阈值」和「再生成间隔」自动与此值保持一致（三者相等，Agno 无缝衔接规则）。留空则使用轮数模式 (Agno: num_history_messages)",
      },
      max_tool_calls_from_history: {
        label: "历史中最大工具调用数",
        help: "历史中保留的最大工具调用结果数。工具返回结果通常很大，限制数量可以有效节省上下文空间。留空则不限制 (Agno: max_tool_calls_from_history)",
      },
      read_chat_history: {
        label: "读取聊天历史",
        help: "允许Agent通过工具查询完整聊天历史 (Agno: read_chat_history)",
      },

      // Memory Configuration
      "memory.enabled": {
        label: "启用记忆",
        help: "启用长期记忆存储",
      },
      "memory.provider": {
        label: "记忆提供者",
        help: "选择记忆存储后端",
        options: {
          MEM0: "MEM0",
          POSTGRES: "PostgreSQL",
          NONE: "无",
        },
        invalidMessage:
          '警告: 提供者 "{value}" 无效。应为: MEM0, POSTGRES 或 NONE',
      },
      "memory.model_id": {
        label: "记忆模型",
        help: "用于记忆提取和操作的模型",
        invalidMessage: '警告: 模型ID "{value}" 不在可用记忆模型列表中',
      },
      "memory.enable_agentic_memory": {
        label: "Agentic 记忆",
        help: "启用Agentic记忆管理 - 为Agent添加记忆控制工具。MEM0: 添加搜索/添加/更新/删除记忆工具。PostgreSQL: 添加记忆搜索/保存工具。启用后，Agent可以主动管理自己的记忆。",
      },
      "memory.operation_mode": {
        label: "Mem0 操作模式",
        help: "选择记忆操作模式",
        options: {
          SYNC: "同步 (默认 - 等待确认)",
          FIRE_FORGET: "触发即忘 (快速 - 无需等待)",
          BATCH: "批处理 (队列批量处理)",
        },
      },
      "memory.loading_strategy": {
        label: "记忆加载策略",
        help: "选择记忆加载策略",
        options: {
          balanced: "平衡 (默认 - 3偏好, 3事实, 2计划, 2最近)",
          preference_first: "偏好优先 (5偏好, 2关键事实, 1最近)",
          context_heavy: "上下文重 (2关键偏好, 3关键, 5情景)",
          minimal: "最小化 (仅3关键, 2情景)",
          time_aware: "时间感知 (3计划, 2偏好, 3最近事实)",
        },
      },

      // Memory Custom Prompts
      "memory.prompts.enable_custom_prompts": {
        label: "启用自定义提示词",
        help: "为此Agent启用Mem0自定义提示词功能",
      },
      "memory.prompts.user_memory_extraction_mode": {
        label: "用户记忆提取模式",
        help: "选择使用系统默认或自定义提示词",
        options: {
          system: "使用系统默认",
          custom: "使用自定义提示词",
        },
      },
      "memory.prompts.user_memory_extraction_prompt": {
        label: "用户记忆提取提示词",
        help: '从用户消息中提取记忆的自定义提示词。点击"重置为系统默认"加载默认值。',
        placeholder: "输入自定义用户记忆提取提示词 (50-4000字符)...",
      },
      "memory.prompts.agent_memory_extraction_mode": {
        label: "Agent记忆提取模式",
        help: "选择使用系统默认或自定义提示词",
        options: {
          system: "使用系统默认",
          custom: "使用自定义提示词",
        },
      },
      "memory.prompts.agent_memory_extraction_prompt": {
        label: "Agent记忆提取提示词",
        help: '从Agent消息中提取记忆的自定义提示词。点击"重置为系统默认"加载默认值。',
        placeholder: "输入自定义Agent记忆提取提示词 (50-4000字符)...",
      },
      "memory.prompts.memory_update_mode": {
        label: "记忆更新模式",
        help: "选择使用系统默认或自定义提示词",
        options: {
          system: "使用系统默认",
          custom: "使用自定义提示词",
        },
      },
      "memory.prompts.memory_update_prompt": {
        label: "记忆更新提示词",
        help: '用于更新和合并记忆的自定义提示词。点击"重置为系统默认"加载默认值。',
        placeholder: "输入自定义记忆更新提示词 (50-4000字符)...",
      },

      // Memory Categories
      "memory.categories.enable_user_custom_categories": {
        label: "启用用户自定义类别",
        help: "启用用户记忆的自定义类别",
      },
      "memory.categories.user_categories_mode": {
        label: "用户类别模式",
        help: "选择使用系统默认或自定义类别",
        options: {
          system: "使用系统默认",
          custom: "使用自定义类别",
        },
      },
      "memory.categories.user_custom_categories": {
        label: "用户自定义类别",
        help: '以JSON格式定义用户记忆类别。每个类别必须包含: description (字符串), examples (数组), temporal (布尔值)。点击"重置为系统默认"加载默认类别。',
        placeholder:
          '{"preference": {"description": "用户偏好", "examples": ["喜欢披萨"], "temporal": false}}',
      },
      "memory.categories.enable_agent_custom_categories": {
        label: "启用Agent自定义类别",
        help: "启用Agent记忆的自定义类别",
      },
      "memory.categories.agent_categories_mode": {
        label: "Agent类别模式",
        help: "选择使用系统默认或自定义类别",
        options: {
          system: "使用系统默认",
          custom: "使用自定义类别",
        },
      },
      "memory.categories.agent_custom_categories": {
        label: "Agent自定义类别",
        help: '以JSON格式定义Agent记忆类别。每个类别必须包含: description (字符串), examples (数组), temporal (布尔值)。点击"重置为系统默认"加载默认类别。',
        placeholder:
          '{"recommendation": {"description": "建议记录", "examples": ["建议每天锻炼"], "temporal": false}}',
      },

      // Memory Query Settings
      "memory.query.rerank_enabled": {
        label: "启用重排序",
        help: "启用记忆查询结果的重排序功能",
      },
      "memory.query.top_k": {
        label: "Top K",
        help: "返回的最大记忆条数",
      },
      "memory.query.score_threshold": {
        label: "分数阈值",
        help: "记忆相关性分数的最低阈值 (0.0-1.0)",
      },
      "memory.query.facts_limit": {
        label: "事实查询限制",
        help: "检索的最大永久用户事实数量 (1-100)",
      },
      "memory.query.events_limit": {
        label: "事件查询限制",
        help: "检索的最大时序事件记忆数量 (1-50)",
      },
      "memory.query.events_threshold": {
        label: "事件搜索阈值",
        help: "事件搜索的相似度阈值 (0.0-1.0)。较低的值返回更多结果。",
      },
      "memory.query.agent_memories_limit": {
        label: "Agent记忆限制",
        help: "检索的最大Agent专属记忆数量 (1-20)",
      },
      "memory.query.agent_memories_threshold": {
        label: "Agent记忆阈值",
        help: "Agent记忆搜索的相似度阈值 (0.0-1.0)。较低的值返回更多结果。",
      },
      "memory.query.query_max_length": {
        label: "查询最大长度",
        help: "记忆搜索查询的最大总长度（字符数）(100-1000)",
      },
      "memory.query.user_query_length": {
        label: "用户查询长度",
        help: "搜索查询中当前用户问题的最大长度 (50-500字符)",
      },
      "memory.query.assistant_context_length": {
        label: "助手上下文长度",
        help: "搜索查询中最后助手回复的最大长度 (0-500字符)。设为0可禁用助手上下文。",
      },

      // Memory Decay (Ebbinghaus)
      "memory.ebbinghaus.enabled": {
        label: "启用记忆衰减",
        help: "启用艾宾浩斯遗忘曲线机制。时序记忆会根据访问频率随时间衰减。",
      },
      "memory.ebbinghaus.decay_factor": {
        label: "衰减系数",
        help: "控制衰减速度 (0.1=快速遗忘, 1.0=缓慢遗忘)。数值越高，记忆衰减越慢。",
      },
      "memory.ebbinghaus.min_retention": {
        label: "最小保持率",
        help: "记忆保持率下限 (0.0-1.0)。记忆永远不会衰减到此值以下。默认0.1表示记忆至少保留10%相关性。",
      },
      "memory.ebbinghaus.initial_strength": {
        label: "初始强度",
        help: "新记忆的初始强度 (0.5-2.0)。数值越高，新记忆越能抵抗衰减。",
      },
      "memory.ebbinghaus.reinforcement_boost": {
        label: "强化提升",
        help: "记忆被访问时的强度增加值 (0.1-1.0)。数值越高，每次访问对记忆的强化效果越大。",
      },
      "memory.ebbinghaus.max_strength": {
        label: "最大强度",
        help: "记忆强度上限 (1.0-5.0)。限制记忆通过强化能达到的最大强度。",
      },

      // Session Summary Configuration
      "session_summary.enabled": {
        label: "启用会话摘要",
        help: "为长对话自动生成摘要。摘要会压缩早期对话内容，注入到系统提示中，让 Agent 保持对整个对话的感知。",
      },
      "session_summary.model_id": {
        label: "摘要生成模型",
        help: "用于生成摘要的模型。建议使用快速、经济的模型（如 gpt-5-mini），因为摘要生成是后台任务。",
      },
      "session_summary.summary_frequency": {
        label: "生成时机",
        help: "选择何时生成摘要：\n• 按需生成：仅通过 API 手动触发\n• N条消息后：达到阈值后自动生成（推荐）\n• 会话结束时：会话结束时生成",
        options: {
          on_request: "按需生成",
          after_n_messages: "N条消息后（推荐）",
          end_of_session: "会话结束时",
        },
      },
      "session_summary.message_threshold": {
        label: "首次生成阈值",
        help: "对话达到多少条消息后生成第一次摘要。\n\n💡 推荐配置：设置为与「历史运行数」相同的值。\n\n例如：历史运行数=5，阈值=5\n• 消息1-5：由历史记录覆盖（无需摘要）\n• 消息6+：摘要覆盖[1, N-5]，历史覆盖[N-4, N]\n\n这样确保信息无缝衔接，不会遗漏。",
      },
      "session_summary.regenerate_interval": {
        label: "再生成间隔",
        help: "首次生成后，每隔多少条新消息重新生成摘要。\n• 0：永不重新生成（只用第一次的摘要）\n• N > 0：每N条新消息后更新摘要\n\n💡 推荐配置：设置为与「历史运行数」相同的值，实现滚动覆盖。",
      },
      "session_summary.inject_into_prompt": {
        label: "注入系统提示",
        help: "是否将摘要作为上下文信息注入到系统提示中。启用后，Agent 在回复时能感知到早期对话的要点。",
      },
      "session_summary.custom_prompt": {
        label: "自定义摘要提示词",
        help: "自定义摘要生成的提示词模板。留空则使用 Agno 框架的默认提示词。\n\n可用变量：{messages}（对话内容）",
      },

      // Advanced Settings
      session_timeout_minutes: {
        label: "会话超时 (分钟)",
        help: "会话自动超时时间",
      },
      max_cached_resources: {
        label: "最大缓存资源数",
        help: "缓存的最大资源数量",
      },
      resource_ttl_minutes: {
        label: "资源TTL (分钟)",
        help: "资源缓存的有效时间",
      },

      // Tools
      custom_tools: {
        label: "自定义工具",
        help: "自定义工具配置 - 可以是模块名（字符串）、模块列表（数组）或详细配置（对象）",
        placeholder:
          '{"modules": ["tools.astrology.astro_tools"], "enabled_tools": ["calculate_natal_chart"]}',
      },
      builtin_tools: {
        label: "内置工具",
        help: "启用的内置工具列表",
      },
      mcp_servers: {
        label: "MCP 服务器",
        help: "MCP服务器配置 - 可以是服务器ID（字符串）、ID列表（数组）或详细配置（对象）",
        placeholder:
          '{"servers": ["astro-tool"], "enabled_tools": ["mcp__astro_tool__get_birth_chart"]}',
      },

      // System Prompts
      instructions: {
        label: "系统提示词",
        help: "定义Agent的角色、行为和能力",
        placeholder: "请输入Agent的系统提示词...",
      },
      additional_context: {
        label: "额外上下文",
        help: "额外的上下文、示例或约束",
        placeholder: "请输入额外的上下文或指引...",
      },
      enable_first_message_instruction: {
        label: "启用首条消息指令",
        help: "为新话题/会话的首条消息启用特殊指令",
      },
      first_message_mode: {
        label: "指令模式",
        help: "选择使用系统默认或自定义指令",
        options: {
          system: "使用系统默认",
          custom: "使用自定义指令",
        },
      },
      first_message_custom_instruction: {
        label: "自定义首条消息指令",
        help: '首条消息的自定义指令。点击"重置为系统默认"加载默认值。',
        placeholder: "请输入自定义指令...",
      },

      // Response Classifier
      "response_classifier.enabled": {
        label: "启用分类引擎",
        help: "启用后，系统将自动分类响应类型（快速/深度）并匹配技能。",
      },
      "response_classifier.model_id": {
        label: "兜底分类模型",
        help: "当规则匹配置信度低于阈值时，使用此模型进行 LLM 分类。建议使用快速、低成本的模型。",
      },
      "response_classifier.rule_confidence_threshold": {
        label: "规则置信度阈值",
        help: "规则分类的置信度阈值。低于此值时使用LLM分类。范围: 0.0-1.0，默认: 0.8",
      },
      "response_classifier.quick_response_max_length": {
        label: "快速响应最大长度",
        help: "快速响应分类的最大消息长度（字符数）。更长的消息倾向于深度响应。",
      },
      "response_classifier.llm_timeout": {
        label: "LLM 超时 (秒)",
        help: "LLM分类的超时时间（秒）。超时后默认为深度响应。",
      },

      // Response Type Instructions
      "response_classifier.quick_response_instructions": {
        label: "快速响应提示词",
        help: "当分类为快速响应时，用此提示词替代系统提示词。此设置仅影响提示词内容，与快速响应使用哪个模型无关（模型在「模型配置」中的「快速对话模型」设置）。留空则不替换。",
        placeholder:
          "你是一个友好的助手。请简洁、自然地回复用户的问候或简单问题。保持轻松愉快的对话氛围。",
      },
      "response_classifier.deep_response_instructions": {
        label: "深度响应指令",
        help: "用于深度响应的指令（分析、咨询、情感支持）。如果为空，将使用默认指令。",
        placeholder:
          "你是一位专业的顾问。请认真分析用户的问题，提供深入、专业的见解和建议。考虑多个角度，给出有价值的分析。",
      },

      // ========== Hook Configuration Fields ==========

      // Memory Query Settings (hooks.user_facts, hooks.memory_context - unified)
      "hooks.user_facts.facts_limit": {
        label: "事实查询限制",
        help: "检索的最大永久用户事实数量 (1-100)",
      },
      "hooks.memory_context.events_limit": {
        label: "事件查询限制",
        help: "检索的最大时序事件记忆数量 (1-50)",
      },
      "hooks.memory_context.events_threshold": {
        label: "事件搜索阈值",
        help: "事件搜索的相似度阈值 (0.0-1.0)。较低的值返回更多结果。",
      },
      "hooks.memory_context.agent_memories_limit": {
        label: "Agent记忆限制",
        help: "检索的最大Agent专属记忆数量 (1-20)",
      },
      "hooks.memory_context.agent_memories_threshold": {
        label: "Agent记忆阈值",
        help: "Agent记忆搜索的相似度阈值 (0.0-1.0)。较低的值返回更多结果。",
      },
      "hooks.memory_context.query_max_length": {
        label: "查询最大长度",
        help: "记忆搜索查询的最大总长度（字符数）(100-1000)",
      },
      "hooks.memory_context.user_query_length": {
        label: "用户查询长度",
        help: "搜索查询中当前用户问题的最大长度 (50-500字符)",
      },
      "hooks.memory_context.assistant_context_length": {
        label: "助手上下文长度",
        help: "搜索查询中最后助手回复的最大长度 (0-500字符)。设为0可禁用助手上下文。",
      },

      // Memory Decay / Ebbinghaus (hooks.memory_reinforce)
      "hooks.memory_reinforce.enabled": {
        label: "启用记忆衰减",
        help: "启用艾宾浩斯遗忘曲线机制。时序记忆会根据访问频率随时间衰减。",
      },
      "hooks.memory_reinforce.decay_factor": {
        label: "衰减系数",
        help: "控制衰减速度 (1.0=快速遗忘, 30.0=缓慢遗忘)。数值越高，记忆衰减越慢。",
      },
      "hooks.memory_reinforce.min_retention": {
        label: "最小保持率",
        help: "记忆保持率下限 (0.0-0.5)。记忆永远不会衰减到此值以下。默认0.1表示记忆至少保留10%相关性。",
      },
      "hooks.memory_reinforce.initial_strength": {
        label: "初始强度",
        help: "新记忆的初始强度 (0.5-2.0)。数值越高，新记忆越能抵抗衰减。",
      },
      "hooks.memory_reinforce.reinforcement_boost": {
        label: "强化提升",
        help: "记忆被访问时的强度增加值 (0.1-1.0)。数值越高，每次访问对记忆的强化效果越大。",
      },
      "hooks.memory_reinforce.max_strength": {
        label: "最大强度",
        help: "记忆强度上限 (1.0-5.0)。限制记忆通过强化能达到的最大强度。",
      },

      // First Message Hook (hooks.first_message)
      "hooks.first_message.enabled": {
        label: "启用首条消息扩展",
        help: "为新会话的首条消息添加特殊指令",
      },
      "hooks.first_message.mode": {
        label: "首条消息模式",
        help: "选择使用系统默认或自定义指令",
        options: {
          system: "使用系统默认",
          custom: "使用自定义指令",
        },
      },
      "hooks.first_message.custom_instruction": {
        label: "自定义首条消息指令",
        help: "新会话首条消息的自定义指令",
        placeholder: "请输入自定义指令...",
      },

      // Response Classifier Hook (hooks.classifier)
      "hooks.classifier.enabled": {
        label: "启用分类引擎",
        help: "启用后，系统将自动分类响应类型（快速/深度）并匹配技能。",
      },
      "hooks.classifier.use_system_instructions": {
        label: "沿用系统提示词",
        help: "勾选后，快速响应将使用主系统提示词，不再替换为独立的快速响应提示词。",
      },
      "hooks.classifier.quick_response_instructions": {
        label: "快速响应提示词",
        help: "当分类为快速响应时，用此提示词替代系统提示词，引导模型生成简洁的回复。此设置仅影响提示词内容，与快速响应使用哪个模型无关（模型在「模型配置」中的「快速对话模型」设置）。留空则不替换。",
        placeholder:
          "你是一个友好的助手。请简洁、自然地回复用户的问候或简单问题。保持轻松愉快的对话氛围。",
      },
      "hooks.classifier.model_id": {
        label: "兜底分类模型",
        help: "当规则匹配置信度低于阈值时，使用此模型进行 LLM 分类。建议使用快速、低成本的模型。",
      },
      "hooks.classifier.rule_confidence_threshold": {
        label: "规则置信度阈值",
        help: "规则分类的置信度阈值。低于此值时使用LLM分类。范围: 0.0-1.0，默认: 0.8",
      },
      "hooks.classifier.quick_response_max_length": {
        label: "快速响应最大长度",
        help: "快速响应分类的最大消息长度（字符数）。更长的消息倾向于深度响应。",
      },
      "hooks.classifier.llm_timeout": {
        label: "LLM 超时 (秒)",
        help: "LLM分类的超时时间（秒）。超时后默认为深度响应。",
      },
      "hooks.classifier.deep_response_instructions": {
        label: "深度响应指令",
        help: "用于深度响应的指令（分析、咨询、情感支持）。如果为空，将使用默认指令。",
        placeholder:
          "你是一位专业的顾问。请认真分析用户的问题，提供深入、专业的见解和建议。考虑多个角度，给出有价值的分析。",
      },
    },
    pushNotifications: {
      // Page
      pageTitle: "推送通知测试",

      // Stats
      stats: {
        totalTokens: "总令牌数",
        activeTokens: "活跃令牌",
        pushSent: "已发送推送",
        successRate: "成功率",
        platformBreakdown: "平台分布",
        ios: "iOS",
        android: "Android",
      },

      // Send Notification Form
      sendForm: {
        title: "发送测试通知",
        description: "编写并发送测试推送通知",
        notificationTitle: "标题",
        notificationTitlePlaceholder: "通知标题",
        body: "内容",
        bodyPlaceholder: "通知消息内容",
        target: "发送目标",
        targetAll: "所有用户",
        targetAllWarning: "这将向所有已注册的用户发送通知。",
        targetTest: "测试用户",
        targetTestDesc: "发送到测试账号",
        targetSpecificUsers: "指定用户",
        targetSpecificUsersDesc: "发送到指定用户ID",
        userIds: "用户ID列表",
        userIdsPlaceholder: "输入用户ID，多个用逗号分隔",
        userIdsHint: "多个用户ID用逗号分隔",
        sound: "提示音",
        soundPlaceholder: "default",
        soundHint: "通知到达时播放的声音",
        soundDefault: "默认提示音",
        soundNone: "静音",
        badgeCount: "角标数字",
        badgeCountPlaceholder: "",
        badgeCountHint: "应用图标角标数字",
        badgeCountHintFull: "0 = 清除角标，留空 = 不修改",
        customData: "自定义数据 (JSON)",
        customDataPlaceholder: '{"type": "action", "action": "profile"}',
        customDataHint: "点击通知后触发的动作",
        dataTemplates: "快捷模板",
        dataTemplateLabels: {
          navigation: "首页",
          profile: "个人资料",
          feedback: "反馈",
          birthChart: "星盘",
          fortune: "运势",
        },
        clearButton: "清除",
        sendButton: "发送通知",
        sending: "发送中...",
        successMessage: "通知发送成功！",
        sent: "已发送",
        failed: "失败",
        errors: "错误",
      },

      // Device Tokens
      tokens: {
        tabTitle: "设备令牌",
        title: "已注册设备",
        allPlatforms: "所有平台",
        refresh: "刷新",
        loading: "加载中...",
        noTokens: "暂无注册的令牌",
        user: "用户",
        registered: "注册时间",
        active: "活跃",
        inactive: "未激活",
        page: "第",
        of: "页，共",
        total: "条",
        previous: "上一页",
        next: "下一页",
      },

      // Notification Logs
      logs: {
        tabTitle: "推送历史",
        title: "最近推送",
        allStatuses: "所有状态",
        statusSent: "已发送",
        statusFailed: "失败",
        refresh: "刷新",
        loading: "加载中...",
        noLogs: "暂无推送历史",
        error: "错误",
        page: "第",
        of: "页，共",
        total: "条",
        previous: "上一页",
        next: "下一页",
        // Detail view fields
        user: "用户",
        userId: "用户 ID",
        displayName: "用户名称",
        platform: "平台",
        deviceInfo: "设备信息",
        sentAt: "发送时间",
        pushToken: "推送令牌",
        fullTitle: "完整标题",
        fullBody: "完整内容",
        dataPayload: "自定义数据",
        actionNavigation: "导航",
        actionType: "动作",
        actionFortune: "运势",
        // Grouped view fields
        recipients: "个接收者",
        batches: "批次",
        totalRecipients: "接收者数量",
        recipientList: "接收者列表",
      },

      // Time formatting
      time: {
        justNow: "刚刚",
        minutesAgo: "{n}分钟前",
        hoursAgo: "{n}小时前",
        daysAgo: "{n}天前",
      },

      // Validation errors
      validation: {
        titleRequired: "标题为必填项",
        bodyRequired: "内容为必填项",
        userIdsRequired: "至少需要一个用户ID",
        invalidJson: "JSON格式无效",
      },

      // User Selector
      userSelector: {
        searchPlaceholder: "搜索用户名、手机号、邮箱或ID...",
        selectedCount: "已选择 {count} 个用户",
        selectAll: "全选",
        deselectAll: "取消全选",
        clearAll: "清除",
        noUsers: "未找到用户",
        devices: "个设备",
        showingCount: "显示 {from}-{to} 条，共 {total} 条",
      },

      // Content Assist (AI-powered content generation)
      contentAssist: {
        sectionTitle: "推送内容",
        inputModeLabel: "内容输入方式",
        agentMode: "Agent",
        llmMode: "LLM",
        manualMode: "手动",
        selectAgent: "选择 Agent",
        loading: "加载中...",
        noAgents: "无可用 Agent",
        descriptionLabel: "描述推送内容",
        descriptionPlaceholder: "例如：提醒用户查看今日运势",
        generateButton: "生成内容",
        generating: "生成中...",
        selectAgentFirst: "请先选择一个 Agent",
        generateFailed: "生成内容失败",
        // LLM mode specific
        selectLLMModel: "选择 LLM 模型",
        noModels: "无可用模型",
        llmModeDescription: "使用原始 LLM API 生成提示词，完全控制系统提示",
        temperature: "温度 (Temperature)",
        precise: "精确 (0)",
        balanced: "平衡 (1)",
        creative: "创意 (2)",
        selectModelFirst: "请先选择一个 LLM 模型",
      },
    },
    evalScenarios: {
      // Page
      pageTitle: "评测场景",
      pageDescription: "管理测试场景并运行 Agent 评测以确保质量",

      // Actions
      createScenario: "创建场景",
      promoteChat: "提升对话",
      refresh: "刷新",
      runAll: "全部运行",
      runSingle: "运行测试",
      running: "运行中...",
      edit: "编辑",
      delete: "删除",
      duplicate: "复制",
      duplicateSuccess: "场景复制成功",
      expandDetails: "展开详情",
      collapseDetails: "收起详情",
      removeTag: "删除标签",
      filterByTags: "标签",
      addTagFilter: "添加标签",
      clearTags: "清除",
      exportScenarios: "导出场景",
      importScenarios: "导入场景",
      exportSuccess: "已导出 {count} 个场景",
      importSuccess: "导入完成：创建 {created} 个，跳过 {skipped} 个",
      importFailed: "导入失败",
      importInvalidFile: "无效的文件格式，请选择 JSON 文件",

      // Filters
      filters: "筛选",
      searchPlaceholder: "搜索场景...",
      allAgentTypes: "所有 Agent 类型",
      allCategories: "所有分类",
      allSources: "所有来源",
      allStatus: "所有状态",
      sourceManual: "手动创建",
      sourceChatPromoted: "对话提升",
      statusActive: "已启用",
      statusInactive: "已禁用",

      // Stats
      totalScenarios: "总场景数",
      active: "已启用",
      manual: "手动创建",
      chatPromoted: "对话提升",
      neverRun: "未运行",

      // Form Steps
      step1Title: "基本信息",
      step1Description: "设置场景名称、选择目标 Agent 和分类",
      step2Title: "测试对话",
      step2Description: "定义要测试的对话（用户消息和预期流程）",
      step3Title: "评测配置",
      step3Description: "选择 LLM 评判模型和评测指标",
      stepOf: "第 {current} 步，共 {total} 步",
      next: "下一步",
      previous: "上一步",

      // Form Fields - Step 1
      scenarioName: "场景名称",
      scenarioNamePlaceholder: "例如：sagittarius_career_advice",
      scenarioNameHint: "建议使用下划线分隔的小写字母（snake_case）",
      description: "描述",
      descriptionPlaceholder:
        "此场景测试什么？例如：测试 Agent 是否为射手座用户提供有帮助的职业建议",
      targetAgent: "目标 Agent",
      targetAgentHint: "选择要运行此测试的 Agent",
      category: "分类",
      categoryPlaceholder: "例如：占星、职业",
      priority: "优先级",
      priorityHint: "数值越高越优先执行",
      tags: "标签",
      addTagPlaceholder: "输入标签后按回车添加...",

      // Form Fields - Step 2
      testConversation: "测试对话",
      conversationHint:
        "定义测试对话。至少添加一条用户消息发送给 Agent。可以添加多轮对话来模拟多轮会话。",
      addTurn: "添加对话",
      userRole: "用户",
      assistantRole: "助手",
      userMessagePlaceholder: "要测试的用户消息...",
      assistantMessagePlaceholder: "预期的助手回复（用于上下文参考）...",
      expectedResponse: "期望响应",
      expectedResponsePlaceholder:
        "填写一个理想的 Assistant 回复示例，例如：\n\n好的，考虑到你喜欢安静的环境，推荐以下小众景点：\n1. 青城后山 - 人少清幽，门票60元\n2. 街子古镇 - 无门票，约2小时车程\n\n你倾向哪种类型？",
      expectedResponseHint: "填写理想的回复文本，而非评判规则",
      expectedResponseDetailTitle: "什么是期望响应？",
      expectedResponseDetail1:
        '✅ 应填写：一个具体的理想回复示例（"Agent 应该怎么回复"）',
      expectedResponseDetail2:
        '❌ 不要填写：评判规则描述（那是"评测标准 Criteria"的作用）',
      expectedResponseDetail3:
        "用途：用于 AnswerRelevancy、Correctness 等指标做语义相似度比较",
      expectedResponseExample:
        "如果主要用 GEval 自定义指标，此字段可留空，评判主要依靠 Criteria",

      // Form Fields - Step 3
      llmJudgeModel: "LLM 评判模型",
      llmJudgeModelHint: "推荐：gpt-4o-mini 性价比高，gpt-4o 准确度更高",
      llmJudgeModelWarning:
        "⚠️ 此模型可能不被 DeepEval 支持。建议使用 OpenAI GPT 系列模型（如 gpt-4o-mini, gpt-4o）以确保评测正常运行。",
      quickAddMetrics: "快速添加指标",
      evaluationCriteria: "评测标准",
      customMetric: "自定义指标",
      metricNamePlaceholder: "指标名称",
      threshold: "阈值",
      noMetricsSelected: '未选择指标。点击上方"快速添加"按钮或添加自定义指标。',
      minOverallScore: "最低通过分数",
      minOverallScoreHint: "启用指标的加权平均分超过此阈值则通过",
      activeCheckbox: "启用（场景将包含在批量评测中）",

      // Metrics
      metrics: {
        hallucination: "幻觉检测",
        hallucinationDesc: "检测事实性错误",
        hallucinationHelp:
          "检测 AI 是否编造了不存在的信息。分数越低越好 (0=无幻觉, 1=严重幻觉)。阈值 0.3 表示幻觉分数必须低于 30% 才算通过。",
        relevancy: "相关性",
        relevancyDesc: "检查响应是否相关",
        relevancyHelp:
          "评估回答与用户问题的相关程度。分数越高越好 (0=完全无关, 1=高度相关)。阈值 0.7 表示相关性必须达到 70% 以上。",
        faithfulness: "忠实度",
        faithfulnessDesc: "检查对源内容的忠实程度",
        faithfulnessHelp:
          '检查回答是否忠实于提供的上下文/参考内容，不添加额外信息。分数越高越好。需要提供"期望响应"作为参考。',
        answer_relevancy: "答案相关性",
        answer_relevancyDesc: "检查答案质量",
        answer_relevancyHelp:
          "综合评估答案的质量：是否直接回答问题、信息是否完整、表达是否清晰。分数越高越好。",
        contextual_relevancy: "上下文相关性",
        contextual_relevancyDesc: "检查上下文使用情况",
        contextual_relevancyHelp:
          "评估 AI 是否正确使用了对话历史和上下文信息。适用于多轮对话场景。",
        bias: "偏见检测",
        biasDesc: "检测偏见内容",
        biasHelp:
          "检测回答中是否包含性别、种族、年龄等方面的偏见。分数越低越好 (0=无偏见)。",
        toxicity: "毒性检测",
        toxicityDesc: "检测有害内容",
        toxicityHelp:
          "检测回答中是否包含攻击性、侮辱性或有害内容。分数越低越好 (0=安全)。阈值 0.1 表示毒性必须低于 10%。",
        astro_accuracy: "占星准确性",
        astro_accuracyDesc: "检查占星术语准确性",
        astro_accuracyHelp:
          "评估占星相关内容的专业准确性：星座识别、行星位置、宫位解读、相位分析等是否正确。",
        interpretation_quality: "解读质量",
        interpretation_qualityDesc: "评估解读的深度和见解",
        interpretation_qualityHelp:
          "评估占星解读的质量：深度和洞察力、个人相关性、专业性与易读性的平衡、是否提供可行建议。",
        helpfulness: "有用性",
        helpfulnessDesc: "评估回答的帮助程度",
        helpfulnessHelp:
          "评估回答对用户的实际帮助：是否解决了用户问题、信息是否实用可行、语气是否恰当友好。",
      },
      metricsGuide: "评测指南",
      metricsGuideContent:
        "指标分两类：1) 越低越好 (幻觉、偏见、毒性) - 阈值代表可接受的最大分数；2) 越高越好 (相关性、忠实度等) - 阈值代表最低要求分数。所有分数范围为 0-1。",
      lowerIsBetter: "↓ 越低越好",
      higherIsBetter: "↑ 越高越好",

      // Dialogs
      createDialogTitle: "创建评测场景",
      editDialogTitle: "编辑评测场景",
      createButton: "创建场景",
      saveChanges: "保存更改",

      // Promote Chat Dialog
      promoteChatTitle: "提升对话为测试场景",
      promoteChatDescription:
        "将真实的聊天对话转换为测试场景。对话中的消息将被提取并用作测试对话内容。",
      chatTopicId: "对话/主题 ID",
      chatTopicIdPlaceholder: "输入对话或主题 ID",
      chatTopicIdHint: "可在聊天历史或数据库中查找",
      promoteButton: "提升为场景",

      // Run Evaluation Dialog
      runEvaluationTitle: "运行评测",
      runningEvaluation: "正在评测...",
      runningHint: "您可以关闭此对话框，评测将在后台继续运行",
      runningHintResult: "完成后结果将显示在评测历史中",
      evaluationStarted: "评测已启动",
      evaluationStartedHint: "评测正在后台运行中，您可以关闭此对话框",
      checkRunsPanel: "请打开「运行记录」查看实时进度",
      evaluationComplete: "评测完成",
      overallScore: "综合得分",
      passed: "通过",
      failed: "未通过",
      response: "响应内容",
      responseTime: "响应时间",
      metricResults: "指标结果",
      reason: "原因",
      scenario: "场景",
      agentType: "Agent 类型",
      judgerModel: "评判模型",
      criteriaLabel: "评测标准",
      batchSummary: "批量评测概况",
      total: "总计",
      close: "关闭",
      runEvaluationButton: "运行评测",
      batchInfoPrefix: "这将为",
      batchInfoSuffix: "个已启用的场景运行评测。",

      // User Picker
      userPicker: {
        title: "评测用户",
        useCurrentUser: "使用当前用户",
        currentUserActive: "当前用户 ✓",
        currentUserLabel: "（当前登录用户）",
        clearSelection: "清除选择",
        searchPlaceholder: "搜索用户名、昵称、手机号、邮箱或姓名...",
        noUserSelected: "未选择用户",
        noUserSelectedHint: "将使用默认测试用户（无星盘数据），Agent 可能会要求输入出生信息",
        loading: "加载中...",
        noMatch: "无匹配用户",
        testBadge: "测试",
        hasChart: "星盘",
        noChart: "无星盘",
        hasChartTooltip: "有本命星盘",
        noChartTooltip: "无本命星盘",
        showingCount: "显示 {shown} / {total} 个用户",
        runId: "运行 ID",
        scenarios: "场景",
        user: "用户",
      },

      // Response Language
      responseLanguage: {
        title: "回复语言",
        hint: "要求评测 Agent 使用指定语言回复",
        auto: "自动（跟随 UI 语言）",
        zh: "中文",
        en: "English",
      },

      // Results
      lastResult: "最近结果",
      noResults: "暂无结果",

      // Confirmations
      deleteConfirm: "确定要删除此场景吗？",

      // Success/Error messages
      createSuccess: "场景创建成功",
      updateSuccess: "场景更新成功",
      deleteSuccess: "场景删除成功",
      promoteSuccess: "对话提升成功",
      runSuccess: "评测完成",
      error: "操作失败",

      // History Panel
      historyPanel: {
        title: "评测历史",
        scoreTrend: "分数趋势",
        recentRuns: "最近 {n} 次",
        noHistory: "暂无评测记录",
        checkRunsPanelHint:
          "如果您刚启动评测，请点击页面顶部的「运行记录」按钮查看进度",
        expandDetails: "展开详情",
        collapseDetails: "收起详情",
        agentResponse: "Agent 响应",
        responseTime: "响应时间",
        executedAt: "执行时间",
        executedBy: "执行者",
        metrics: "评测指标",
        resultsList: "评测记录",
        running: "运行中...",
        failed: "执行失败",
      },

      // Result Detail Dialog
      resultDetail: {
        title: "评测结果详情",
        viewDetails: "查看详情",
        overallScore: "综合得分",
        metricsTitle: "评测指标详情",
        judgmentReason: "LLM 判断理由",
        executedAt: "执行时间",
        model: "模型",
        responseTime: "响应时间",
        executedBy: "执行者",
        runningHint: "评测正在进行中，请稍后刷新查看结果",
        testConversation: "测试对话",
        expectedResponse: "预期回复",
      },

      // Runs Panel
      runsPanel: {
        title: "评测运行记录",
        runId: "运行 ID",
        totalScenarios: "场景",
        passed: "通过",
        failed: "失败",
        skipped: "跳过",
        averageScore: "平均分",
        duration: "耗时",
        viewDetails: "查看详情",
        noRuns: "暂无运行记录",
        noResults: "暂无结果数据",
        autoRefreshing: "自动刷新中...",
        statusRunning: "运行中",
        statusCompleted: "已完成",
        statusFailed: "失败",
      },

      // Results Panel
      resultsPanel: {
        title: "评测结果",
        loading: "加载结果中...",
        noResults: "暂无结果",
        noResultsHint: "运行评测以查看结果",
        averageScore: "平均分数",
        totalRuns: "运行次数",
        scenarioName: "场景名称",
        description: "描述",
        testInput: "测试输入",
        noInput: "无输入",
        history: "评测历史",
        detailedMetrics: "详细指标",
        agentResponse: "Agent 响应",
        evaluationNotes: "评测备注",
      },

      // Common texts
      optional: "可选",
      noDescription: "暂无描述",
      createFirstScenario: "创建您的第一个场景开始使用",
      input: "输入",

      // Time formatting
      time: {
        justNow: "刚刚",
        minutesAgo: "{n} 分钟前",
        hoursAgo: "{n} 小时前",
        daysAgo: "{n} 天前",
      },

      // Metric Definitions
      metricDefinitions: {
        pageTitle: "评测指标管理",
        pageDescription:
          "管理评测指标定义，查看内置指标 Prompt，自定义 GEval 指标",
        createMetric: "新建自定义指标",
        builtinMetrics: "系统内置指标",
        customMetrics: "自定义指标 (GEval)",
        viewPrompt: "查看",
        editMetric: "编辑",
        deleteMetric: "删除",
        metricName: "指标名称",
        displayName: "显示名称",
        metricType: "指标类型",
        defaultThreshold: "默认阈值",
        thresholdDirection: "阈值方向",
        higherBetter: "越高越好 (≥)",
        lowerBetter: "越低越好 (≤)",
        applicableAgents: "适用 Agent",
        allAgents: "全部",
        systemMetric: "系统指标",
        customGeval: "自定义 GEval",
        criteria: "评测标准 (Criteria)",
        criteriaPlaceholder: "描述如何评估响应质量...",
        criteriaHint:
          "Criteria 会被传递给 LLM 模型，用于评判回复质量。写得越具体，评测结果越一致。建议使用要点列表格式。",
        evaluationSteps: "评测步骤（高级）",
        evaluationStepsHint:
          "如果需要更精确控制评测流程，可以定义具体的评测步骤。设置后会覆盖 Criteria。",
        addStep: "添加步骤",
        removeStep: "删除",
        noMetrics: "暂无自定义指标",
        builtinPromptNote:
          "这是 DeepEval 内置指标，Prompt 由框架维护，无法修改。如需自定义评测逻辑，请创建新的 GEval 自定义指标。",
        promptViewer: {
          title: "查看指标 Prompt",
          type: "类型",
          source: "来源",
          threshold: "默认阈值",
          prompt: "评测 Prompt",
          readOnlyNote: "此 Prompt 仅供查看，不可编辑",
        },
        editor: {
          createTitle: "新建自定义指标",
          editTitle: "编辑指标",
          nameHint: "唯一标识符，使用小写字母和下划线",
          displayNameHint: "在界面上显示的名称",
          descriptionHint: "描述此指标评估什么内容",
          // Evaluation Steps
          evaluationStepsTitle: "评测步骤 (高级选项)",
          optional: "可选",
          stepsVsCriteriaTitle: "评测步骤 vs 评测标准 (Criteria)",
          stepsExplanation1:
            "评测标准 (Criteria): 描述性的评测要求，适合大多数场景",
          stepsExplanation2:
            "评测步骤 (Steps): 逐步的评测指令，LLM 会按顺序执行每一步",
          stepsExplanation3: "如果设置了评测步骤，将覆盖评测标准",
          exampleTitle: "示例 - 占星准确性评测步骤:",
          exampleStep1: "检查星座和行星位置是否与用户星盘数据一致",
          exampleStep2: "验证相位解读是否符合占星学原则",
          exampleStep3: "评估术语使用的专业性和准确性",
          exampleStep4: "根据以上检查结果给出 1-5 分的综合评分",
          yourSteps: "您的评测步骤",
          noStepsYet: "暂无评测步骤，点击下方按钮添加",
          stepPlaceholder: "描述这一步要检查或评估的内容...",
          addStep: "添加步骤",
          removeStep: "删除",
          // AI Generation
          aiAssistantTitle: "AI 智能设计",
          aiAssistantDescription:
            "描述您想要评测的内容，AI 会根据 DeepEval 最佳实践自动生成指标配置",
          aiDescriptionPlaceholder:
            "例如：评测回复中占星解读的准确性，包括星座位置、行星相位、宫位解释等是否符合专业标准...",
          generating: "生成中...",
          generateMetric: "生成指标配置",
          minChars: "至少 10 个字符",
          chars: "字符",
          aiExplanation: "AI 设计说明",
          aiDescriptionTooShort: "请提供更详细的描述（至少 10 个字符）",
          aiGenerationFailed: "AI 生成失败，请重试",
          selectModel: "选择模型",
          selectModelPlaceholder: "-- 请选择模型 --",
          selectModelFirst: "请先选择模型",
          // Validation
          nameRequired: "请输入指标名称",
          displayNameRequired: "请输入显示名称",
          criteriaOrStepsRequired: "请填写评测标准或评测步骤",
          saveFailed: "保存失败：",
          namePlaceholder: "例如：custom_accuracy",
          displayNamePlaceholder: "例如：自定义准确性",
          descriptionPlaceholder: "描述此指标评估什么内容...",
          allAgentsHint: "留空表示适用于所有 Agent",
        },
        deleteConfirm: "确定要删除此指标吗？此操作不可撤销。",
        deleteSystemError: "系统内置指标不可删除",
        createSuccess: "指标创建成功",
        updateSuccess: "指标更新成功",
        deleteSuccess: "指标删除成功",
      },

      // Test Suites
      testSuites: {
        // Selection
        selectForSuite: "选择创建 Suite",
        exitSelect: "退出选择",
        createSuite: "创建 Suite",
        selectAll: "全选",
        deselectAll: "取消全选",
        selected: "已选",

        // Dialog
        createTitle: "创建 Test Suite",
        editTitle: "编辑 Test Suite",
        name: "名称",
        namePlaceholder: "输入 Suite 名称",
        description: "描述",
        descriptionPlaceholder: "输入描述（可选）",
        tags: "标签",
        tagsPlaceholder: "输入标签后按回车",
        priority: "优先级",
        scenarioCount: "包含场景数",
        scenarios: "个场景",
        cancel: "取消",
        create: "创建",
        save: "保存",

        // Run
        selectSuite: "选择 Test Suite",
        runAllActive: "运行所有激活的场景",
        runSuite: "运行 Test Suite",
        runSuiteInfo: "运行",

        // List
        noSuites: "暂无 Test Suite",
        noSuitesHint: "在评测场景页面选择场景创建 Suite",
        active: "已启用",
        inactive: "已禁用",
        runningTitle: "运行中...",
        running: "运行中",
        run: "运行",

        // Panel (TestSuitesPanel)
        panel: {
          runHistory: "运行记录",
          refresh: "刷新",
          totalSuites: "总 Suite 数",
          totalScenarios: "总场景数",
          howToCreate: "如何创建 Suite？",
          howToCreateHint:
            "前往「评测场景」Tab，点击「选择创建 Suite」按钮，勾选场景后即可创建。",
          loading: "加载中...",
          deleteConfirm: '确定要删除 Suite "{name}" 吗？',
          containsScenarios: 'Suite "{name}" 包含 {count} 个场景',
        },
      },
    },
    evaluation: {
      pageTitle: "评测中心",
      pageDescription: "管理评测场景、指标定义和测试套件",
      tabs: {
        scenarios: "评测场景",
        metrics: "评测指标",
        suites: "Test Suite",
      },
    },
    monitoring: {
      // Page
      pageTitle: "服务监控",
      pageDescription: "实时健康状态和性能指标",
      lastUpdated: "最后更新",

      // Tabs
      tabs: {
        services: "服务健康",
        cache: "缓存",
        storage: "存储",
        queue: "提取队列",
        mcp: "MCP 连接",
      },

      // Queue
      queue: {
        title: "提取队列",
        status: "队列状态",
        running: "运行中",
        stopped: "已停止",
        queueSize: "队列大小",
        processed: "已处理",
        success: "成功",
        failed: "失败",
        merged: "已合并",
        savedCalls: "节省调用",
        mergeRate: "合并率",
        costSaving: "成本节省率",
        deadLetter: "死信队列",
        deadLetterDesc: "重试失败后的任务会进入死信队列，可手动重试或清理",
        retryAll: "全部重试",
        clearAll: "清空队列",
        retry: "重试",
        noTasks: "暂无失败任务",
        taskId: "任务ID",
        userId: "用户ID",
        memoryType: "类型",
        error: "错误",
        failedAt: "失败时间",
        retryCount: "重试次数",
        confirmClear: "确定要清空死信队列吗？此操作不可恢复。",
        confirmRetryAll: "确定要重试所有失败任务吗？",
        clearSuccess: "已清空 {count} 个任务",
        retrySuccess: "已重试 {count} 个任务",
        resetStats: "重置统计",
        confirmReset: "确定要重置队列统计数据吗？",
        resetSuccess: "统计数据已重置",
      },

      // System Task Queue
      systemTaskQueue: {
        title: "系统任务队列",
        queueSize: "队列大小",
        deadLetterSize: "死信数量",
        maxRetries: "最大重试次数",
        retryDelay: "重试延迟",
        jobTimeout: "任务超时",
        deadLetter: "系统任务死信队列",
        deadLetterDesc: "系统任务（如定时清理）失败后进入此队列",
        taskType: "任务类型",
        scheduledBy: "调度来源",
        scheduleId: "计划ID",
        config: "配置",
        lastError: "最后错误",
        finalError: "最终错误",
        failedAt: "失败时间",
        retryCount: "重试次数",
        noTasks: "暂无失败任务",
        confirmClear: "确定要清空系统任务死信队列吗？",
        confirmRetryAll: "确定要重试所有失败的系统任务吗？",
        clearSuccess: "已清空 {count} 个系统任务",
        retrySuccess: "已重试 {count} 个系统任务",
      },

      // Service Health
      serviceHealth: {
        title: "服务健康状态",
        services: "服务",
        healthy: "健康",
        unhealthy: "异常",
        error: "错误",
        unknown: "未知",
        allOk: "全部正常",
        issues: "存在问题",
        overallStatus: "整体状态",
        latency: "延迟",
        openService: "打开服务",
      },

      // Cache Performance
      cache: {
        title: "缓存性能",
        hitRate: "命中率",
        hits: "命中",
        misses: "未命中",
        avgLatency: "平均延迟",
        totalRequests: "总请求数",
        successfulRetrievals: "成功缓存检索",
        fetchFailures: "缓存获取失败",
        avgResponseTime: "平均响应时间",
        unavailable: "缓存数据不可用",
      },

      // Charts
      charts: {
        cacheHitRate: "缓存命中率",
        hitRateLabel: "命中率",
        avgLatency: "平均延迟",
        total: "总计",
        cacheLatency: "缓存",
        backend: "后端",
        requestDistribution: "请求分布",
        noData: "暂无数据",
      },

      // Cache Details
      cacheDetails: {
        title: "缓存性能",
        totalRequests: "总请求数",
        cachedKeys: "缓存键数",
        cacheSize: "缓存大小",
        errors: "错误数",
        redisConnection: "Redis 连接",
        teiBackend: "TEI 后端",
        connected: "已连接",
        disconnected: "已断开",
        healthy: "健康",
        unhealthy: "异常",
        clear: "清除",
        clearConfirm: "清除所有缓存的嵌入向量？此操作不可撤销。",
        clearSuccess: "已清除 {count} 条缓存嵌入向量",
        clearError: "清除缓存失败",
      },

      // Refresh Control
      refresh: {
        autoRefresh: "自动刷新",
        interval: "刷新间隔",
        manual: "手动",
        refreshButton: "刷新",
        refreshing: "刷新中...",
      },

      // Errors
      errors: {
        loadFailed: "加载监控数据失败",
        tryAgain: "重试",
      },
    },

    // User Growth
    userGrowth: {
      pageTitle: "用户增长",
      pageDescription: "用户注册、活跃与留存趋势监控",
      kpi: {
        todayNewUsers: "今日新增注册",
        todayActive: "今日活跃",
        todayActiveSuffix: "全部",
        newUsers7d: "7 日新增",
        d1Retention: "新用户次日留存",
      },
      range: {
        days7: "7 天",
        days14: "14 天",
        days30: "30 天",
        days90: "90 天",
        startDate: "开始日期",
        endDate: "结束日期",
        invalidRange: "时间范围无效：开始日期需不晚于结束日期，且跨度不超过 180 天",
      },
      trend: {
        title: "增长趋势",
        newUsersIos: "iOS 新增",
        newUsersAndroid: "Android 新增",
        newUsersUnknown: "未知平台",
        activeRegular: "真实用户活跃",
        activeTeam: "团队活跃",
      },
      cumulative: {
        title: "累计用户增长",
        regular: "累计真实用户",
        total: "累计全部用户",
      },
      table: {
        title: "每日活跃明细",
        excludeTeam: "排除团队",
        colDate: "日期",
        colUser: "用户名",
        colClassification: "分类",
        colRegistered: "注册日",
        colPlatform: "平台",
        colSessions: "会话",
        colRuns: "轮次",
        newBadge: "新",
        empty: "暂无数据",
      },
      classification: {
        admin: "管理员",
        internalTester: "内测",
        regularUser: "真实用户",
      },
      funnel: {
        title: "新用户漏斗",
        registered: "注册",
        createdSelfChart: "建自己星盘",
        firstConversation: "首次对话",
        trialStarted: "开始试用",
        paid: "付费",
        medianHoursToChart: "中位激活耗时（注册→建盘）",
        hoursUnit: "小时",
      },
      retention: {
        title: "留存分析",
        d7: "7 日留存",
        d30: "30 日留存",
        dauMau: "DAU/MAU 粘性",
        cohortTitle: "周注册 Cohort 留存",
        weekPrefix: "W",
        cohortSizeSuffix: "人",
      },
      loading: "加载中...",
      error: "加载用户增长数据失败",
    },

    // Classification Analytics
    classificationAnalytics: {
      pageTitle: "分类分析",
      pageDescription: "Skill 匹配和 Response Type 分类性能分析",
      lastUpdated: "最后更新",

      tabs: {
        skillExecution: "Skill 执行",
        classification: "Response 分类",
      },

      timeRange: {
        label: "时间范围",
        last24h: "最近 24 小时",
        last7d: "最近 7 天",
        last30d: "最近 30 天",
      },

      skillExecution: {
        title: "Skill 执行分析",
        stats: {
          totalExecutions: "总执行次数",
          activeSkills: "活跃 Skill 数",
          autoMatchRate: "自动匹配率",
          ruleMatchRate: "规则匹配率",
        },
        charts: {
          matchLayer: "匹配层分布",
          matchSource: "匹配来源分布",
          topSkills: "Top 10 Skills",
        },
        recentExecutions: "最近执行记录",
        noData: "暂无数据",
        skillId: "Skill ID",
        query: "用户查询",
        matchSource: "匹配来源",
        matchedRule: "匹配规则",
        confidence: "置信度",
        time: "时间",
      },

      classification: {
        title: "Response 分类分析",
        stats: {
          totalClassifications: "总分类次数",
          quickResponseRate: "Quick Response 占比",
          avgQueryLength: "平均查询长度",
          avgLatency: "平均延迟",
          // Skill metrics
          ruleMatchRate: "规则匹配率",
          avgRuleMatchTime: "平均规则匹配耗时",
          avgLlmMatchTime: "平均 LLM 匹配耗时",
          avgPromptInjection: "平均注入字符数",
        },
        charts: {
          responseType: "Response Type 分布",
          avgConfidence: "平均置信度对比",
          methodPerformance: "分类方法性能",
          count: "数量",
          latency: "延迟 (ms)",
          // Skill charts
          skillMatchLayer: "Skill 匹配层分布",
          topMatchedSkills: "Top 匹配 Skill",
        },
        recentClassifications: "最近分类记录",
        noData: "暂无数据",
        responseType: "Response Type",
        method: "分类方法",
        latencyMs: "延迟 (ms)",
      },

      export: {
        button: "导出数据",
        csv: "导出 CSV",
        json: "导出 JSON",
      },
      refresh: "刷新",
      loading: "加载中...",
      error: "加载失败",
      quick: "Quick",
      deep: "Deep",
    },

    // Users Management
    users: {
      pageTitle: "用户管理",
      pageDescription: "管理用户和 Beta 测试人员",
      refresh: "刷新",
      loading: "正在加载用户...",
      error: "加载用户失败",

      // Stats
      stats: {
        totalUsers: "总用户数",
        admins: "管理员",
        betaTesters: "Beta 测试人员",
        roleDistribution: "角色分布",
        usersWithAstro: "有星盘用户",
        pushEnabled: "推送已开启",
        recentSignups: "近7天注册",
      },

      // Filters
      filters: {
        searchPlaceholder: "搜索用户名、昵称、手机号、邮箱或真实姓名...",
        allUsers: "全部用户",
        betaTesters: "Beta 测试人员",
        nonBeta: "非 Beta",
        clearFilters: "清除筛选",
      },

      // Batch Actions
      batch: {
        selected: "已选择 {count} 个用户",
        enableBeta: "启用 Beta",
        disableBeta: "禁用 Beta",
        clearSelection: "清除选择",
      },

      // Table
      table: {
        user: "用户",
        contact: "联系方式",
        role: "角色",
        subscription: "订阅/角色",
        flags: "标记",
        betaTester: "Beta 测试",
        realName: "真实姓名",
        region: "地区",
        timezone: "时区",
        active: "活跃",
        created: "创建时间",
        actions: "操作",
      },

      // Badges
      badges: {
        admin: "管理",
        birthChart: "星盘",
        gpsEnabled: "GPS 位置已启用",
      },

      // Roles
      roles: {
        free: "免费",
        vip: "VIP",
        premium: "高级",
        pro: "专业版",
        admin: "管理员",
      },

      // Actions
      actions: {
        makeBeta: "设为 Beta",
        removeBeta: "移除 Beta",
        makeAdmin: "设为管理员",
        revokeAdmin: "撤销管理员",
        resources: "资源",
        viewResources: "查看资源",
        subscription: "订阅",
        manageSubscription: "管理订阅",
      },

      // Status
      status: {
        beta: "Beta",
        notBeta: "否",
      },

      // Pagination
      pagination: {
        showing: "显示 {start} 至 {end}，共 {total} 个用户",
        page: "第 {current} 页，共 {total} 页",
      },

      // User Detail Drawer
      drawer: {
        profile: "基本信息",
        preferences: "偏好设置",
        astroProfile: "星盘信息",
        email: "邮箱",
        phone: "手机",
        created: "注册时间",
        updated: "最后更新",
        usernameUpdates: "用户名更新",
        usernameUpdateCountFormat: "{count}次 ({date})",
        timezone: "时区",
        locale: "语言区域",
        language: "语言",
        region: "地区",
        currency: "货币",
        theme: "主题",
        fontSize: "字体大小",
        pushNotification: "推送通知",
        device: "设备",
        gpsLocation: "GPS位置",
        realName: "真实姓名",
        gender: "性别",
        birthDate: "出生日期",
        birthTime: "出生时间",
        birthLocation: "出生地点",
        birthTimezone: "出生时区",
        residenceInfo: "当前居住地",
        residenceLocation: "居住地点",
        residenceTimezone: "居住时区",
        residencePlaceholder: "输入居住地，如：上海市浦东新区",
        edit: "编辑",
        changeRole: "点击修改等级",
      },

      // Advanced Filters
      advancedFilters: {
        roleLevel: "角色等级",
        allRoles: "全部角色",
        hasAstroProfile: "有星盘",
        isAdmin: "管理员",
        all: "全部",
        yes: "是",
        no: "否",
      },

      // Relative Date Formatting
      relativeDates: {
        today: "今天",
        yesterday: "昨天",
        daysAgo: "{count}天前",
        weeksAgo: "{count}周前",
        monthsAgo: "{count}个月前",
      },
    },

    // System Settings
    systemSettings: {
      pageTitle: "系统设置",
      pageDescription: "配置系统级设置和集成",

      tabs: {
        mcpServers: "MCP 服务",
        mem0Config: "记忆配置",
        firstMessage: "首消息注入",
        artifactTypes: "产物类型",
        toolEnhancement: "工具展示",
        toolCombo: "工具组合",
        promptTemplates: "提示词模版",
        outputSchemas: "输出格式",
        modelDefaults: "模型默认值",
        exportImport: "导入导出",
        auditLogs: "变更记录",
        htmlPage: "HTML 页面",
      },

      htmlPage: {
        pageTitle: "HTML 页面工具配置",
        pageDescription:
          "调整 present_html_page 工具的运行时参数：追加给 LLM 的指令、CSP 策略、基础 CSS 和最大字节数。保存后立即生效。",
        warningBanner:
          "修改这些参数会影响所有调用 present_html_page 的 Agent。保存后下一次工具调用即使用新值。",
        promptAddendumLabel: "Prompt 追加 (LLM 指令)",
        promptAddendumHint:
          "追加到工具内置 docstring 末尾的额外指令。留空则使用原始 docstring。最多 8KB。",
        cspLabel: "CSP 策略",
        cspHint: "嵌入 <meta http-equiv=\"Content-Security-Policy\"> 的策略字符串。最多 2KB。",
        baseCssLabel: "基础 CSS Reset",
        baseCssHint:
          "注入到 <style> 标签内的样式（不要包含 <style> 标签本身）。最多 4KB。",
        maxBytesLabel: "最大 HTML 字节数",
        maxBytesHint: "包装后 HTML 的字节上限。范围 10240–1048576。默认 200KB。",
        resetField: "重置为默认值",
        save: "保存配置",
        saving: "保存中...",
        saveSuccess: "保存成功",
        saveFailed: "保存失败",
        reload: "重新加载",
        versionLabel: "版本",
        updatedByLabel: "更新者",
        addendumTooLong: "Prompt 追加内容超过 8KB",
        cspRequired: "CSP 不能为空",
        cssRequired: "Base CSS 不能为空",
        bytesOutOfRange: "最大字节数必须在 10240 到 1048576 之间",
      },

      audit: {
        pageTitle: "审计",
        pageDescription: "查看系统配置变更记录",
        viewLogsFor: "查看审计日志：",
        mcpServersLog: "MCP 服务器审计日志",
        mem0ConfigLog: "Mem0 配置审计日志",
        firstMessageLog: "首条消息配置审计日志",
        artifactTypesLog: "产物类型配置审计日志",
        outputSchemasLog: "输出格式配置审计日志",
        toolEnhancementLog: "工具增强配置审计日志",
        toolComboLog: "工具组合配置审计日志",
        promptTemplatesLog: "提示词模版配置审计日志",
        configTypeNames: {
          mcp_servers: "MCP 服务器",
          mem0_settings: "Mem0 配置",
          first_message_instruction: "首条消息",
          artifact_types: "产物类型",
          tool_enhancement: "工具增强",
          tool_combo_templates: "工具组合",
          system_prompt_templates: "提示词模版",
          output_schemas: "输出格式",
          feature_flags: "功能开关",
        },
      },

      // MCP Servers Panel
      mcp: {
        title: "MCP 服务器",
        description: "管理用于工具集成的 Model Context Protocol 服务器",
        warningBanner:
          '警告：对 MCP 服务器配置的更改将影响所有用户。保存后，点击"重载管理器"立即应用更改。',
        editJson: "编辑 JSON",
        reloadManager: "重载管理器",
        addServer: "添加服务器",
        noServers: "暂无 MCP 服务器配置",
        addFirstServer: "添加您的第一个服务器",
        aboutMcp: "关于 MCP 服务器",
        aboutMcpItems: [
          "STDIO 服务器作为本地进程运行（例如 Python 脚本）",
          "HTTP 服务器连接到远程端点",
          "测试连接以验证服务器可用性",
          "更改后需要重载管理器才能生效",
        ],
        version: "版本",
        lastUpdatedBy: "最后更新者",
        jsonEditorTitle: "编辑 MCP 配置 (JSON)",
        jsonEditorHint:
          "直接编辑 JSON。每个服务器需要：command/url、transport 类型、status。",
        cancel: "取消",
        saving: "保存中...",
        saveConfiguration: "保存配置",
        loading: "正在加载 MCP 服务器...",
        loadFailed: "加载 MCP 服务器失败",
        saveFailed: "保存 MCP 服务器失败",
        deleteFailed: "删除 MCP 服务器失败",
        reloadSuccess: "MCP 管理器重载成功",
        reloadFailed: "重载 MCP 管理器失败",
        jsonSaveSuccess: "MCP 配置保存成功",
        jsonMustHaveMcpServers: 'JSON 必须包含 "mcpServers" 对象',
        // Server Card
        serverCard: {
          disabled: "已禁用",
          transport: "传输协议",
          command: "命令",
          url: "URL",
          tools: "工具",
          toolsCount: "{count} 个工具",
          testConnection: "测试连接",
          editServer: "编辑服务器",
          deleteServer: "删除服务器",
          confirmDelete: '删除 MCP 服务器 "{name}"？',
          connectionTestSuccess: "连接测试成功",
          connectionTestFailed: "连接测试失败",
          retryProgress: "重试 {current} / {max}",
          lastError: "最后错误",
          states: {
            connected: "已连接",
            connecting: "连接中...",
            reconnecting: "重连中...",
            failed: "失败",
            disconnected: "未连接",
            unknown: "未知",
          },
        },
        // Status Panel
        statusPanel: {
          title: "MCP 连接状态",
          type: "类型",
          transport: "传输",
          tools: "工具",
          uptime: "运行时间",
          retryProgress: "重试进度",
          lastError: "最后错误",
          nextRetry: "下次重试",
          loading: "加载 MCP 状态中...",
          loadFailed: "加载 MCP 状态失败",
          retry: "重试",
          refreshStatus: "刷新状态",
          reconnectAll: "重连全部",
          reconnecting: "重连中...",
          autoRefreshing: "每 10 秒自动刷新",
          totalServers: "服务器总数",
          connected: "已连接",
          reconnectingCount: "重连中",
          failed: "失败",
          allHealthy: "全部健康",
          degraded: "部分降级",
          unhealthy: "不健康",
        },
      },

      // Mem0 Config Panel
      mem0: {
        title: "Mem0 系统默认提示词",
        description:
          "配置用于记忆提取和管理的默认提示词（Agent 可以覆盖这些设置）",
        warningBanner:
          "警告：对系统默认提示词的更改将影响未启用自定义提示词的 Agent。已启用自定义提示词的 Agent 将继续使用自己的配置。",
        helpTitle: "Mem0 双记忆架构",
        helpDescription:
          "配置用户和 Agent 记忆提取的系统级默认提示词和类别。每个 Agent 可以选择使用这些默认值或用自定义值覆盖。",
        helpItems: [
          "用户记忆提取：从用户消息中提取事实",
          "Agent 记忆提取：从助手消息中提取事实",
          "记忆更新：将新事实与现有记忆合并",
          "类别：将记忆分类到结构化类别",
        ],
        documentation: "Mem0 文档",
        reloadFromDatabase: "从数据库重新加载",
        saving: "保存中...",
        saveSystemDefaults: "保存系统默认值",
        userMemoryPrompt: "用户记忆提取提示词",
        agentMemoryPrompt: "Agent 记忆提取提示词",
        memoryUpdatePrompt: "记忆更新提示词",
        userMemoryCategories: "用户记忆类别 (JSON)",
        agentMemoryCategories: "Agent 记忆类别 (JSON)",
        resetToHardcoded: "重置为硬编码默认值",
        promptMinMax:
          "用于从消息中提取记忆的提示词。最少 50 字符，最多 8000 字符。",
        promptUpdateMinMax:
          "用于更新/合并记忆的提示词。最少 50 字符，最多 4000 字符。",
        categoriesHint:
          "用于分类记忆的类别。每个类别需要：description、temporal。",
        loading: "正在加载 Mem0 配置...",
        loadFailed: "加载 Mem0 配置失败",
        saveSuccess: "Mem0 系统默认值保存成功",
        saveFailed: "保存配置失败",
        resetSuccess: "已重置为硬编码默认值",
        resetFailed: "重置失败",
        noDefaultPrompt: "API 未返回默认提示词",
        noDefaultCategories: "API 未返回默认类别",
        version: "版本",
        lastUpdatedBy: "最后更新者",
        validation: {
          userPromptMin: "用户记忆提取提示词必须至少 50 字符",
          agentPromptMin: "Agent 记忆提取提示词必须至少 50 字符",
          updatePromptMin: "记忆更新提示词必须至少 50 字符",
          userPromptMax: "用户记忆提取提示词不能超过 8000 字符",
          agentPromptMax: "Agent 记忆提取提示词不能超过 8000 字符",
          updatePromptMax: "记忆更新提示词不能超过 4000 字符",
        },
      },

      // First Message Panel
      firstMessage: {
        title: "首条消息系统默认值",
        description: "配置在新话题首条消息时注入的默认指令",
        warningBanner:
          '警告：对系统默认指令的更改将影响使用"系统"模式的 Agent。已设置自定义首条消息指令的 Agent 不受影响。',
        reloadFromDatabase: "从数据库重新加载",
        saving: "保存中...",
        saveSystemDefault: "保存系统默认值",
        instructionTitle: "系统默认指令",
        instructionPlaceholder: "输入系统默认首条消息指令...",
        instructionLength: "指令长度",
        instructionMinMax: "最少：10，最多：5000 字符",
        howItWorksTitle: "工作原理",
        howItWorksItems: [
          "此指令在新话题的首条消息时注入到系统提示词中",
          "Agent 可以选择使用此系统默认值或定义自己的自定义指令",
          "Agent 级别设置：enable_first_message_instruction 控制此功能是否启用",
          'Agent 级别设置：first_message_mode 控制"系统"与"自定义"模式',
        ],
        loading: "正在加载首条消息配置...",
        loadFailed: "加载首条消息配置失败",
        saveSuccess: "首条消息指令保存成功",
        saveFailed: "保存配置失败",
        version: "版本",
        lastUpdatedBy: "最后更新者",
        validation: {
          minLength: "首条消息指令必须至少 10 字符",
          maxLength: "首条消息指令不能超过 5000 字符",
        },
      },

      // Artifact Types Panel
      artifactTypes: {
        title: "产物类型",
        description: "配置用户生成内容的产物类型（幸运符、播客、报告等）",
        addType: "添加类型",
        noTypes: "暂无产物类型配置",
        addFirstType: "添加您的第一个产物类型",
        editType: "编辑产物类型",
        addTypeTitle: "添加产物类型",
        typeId: "类型 ID",
        typeIdHint: "小写字母、数字、下划线。必须以字母开头。",
        icon: "图标",
        iconOptions: {
          package: "包裹",
          sparkles: "闪光",
          audio: "音频",
          document: "文档",
          image: "图像",
          calendar: "日历",
        },
        nameEnglish: "名称（英文）",
        nameChinese: "名称（中文）",
        descriptionLabel: "描述",
        descriptionHint: "简短描述此产物类型",
        contentTypes: "内容类型",
        contentTypesHint: "image/png, image/jpeg, audio/mpeg",
        rotationPeriod: "轮换周期",
        rotationOptions: {
          none: "无（永久）",
          daily: "每日",
          weekly: "每周",
          monthly: "每月",
        },
        maxStored: "最大存储数",
        maxStoredHint: "例如：7（留空表示无限制）",
        cancel: "取消",
        saving: "保存中...",
        save: "保存",
        confirmDelete: '确定要删除产物类型 "{id}" 吗？',
        loading: "正在加载产物类型...",
        loadFailed: "加载产物类型失败",
        saveFailed: "保存产物类型失败",
        deleteFailed: "删除产物类型失败",
        version: "版本",
        lastUpdatedBy: "最后更新者",
        aboutTitle: "关于产物类型",
        aboutItems: [
          "产物类型定义用户生成内容的类别（幸运符、播客、报告）",
          '轮换周期决定产物何时过期以及新产物何时变为"当前"',
          "最大存储数限制每个用户保留的产物数量（最旧的将被标记为过期）",
          "产物是用户级别的，每个用户都有自己的产物",
        ],
        apiAccessTitle: "API 访问（应用集成）",
        workflowTitle: "工作流集成（创建产物）",
        fieldRefTitle: "字段参考",
        adminOpsTitle: "管理员操作（清理和管理）",
        editing: "编辑中...",
        fields: "字段",
        mediaFields: "媒体",
        metadataFields: "元数据",
        lifecycleMode: "生命周期模式",
        sortBy: "排序字段",
        retentionDays: "保留天数 (valid_from 之后)",
        defaultSuffix: "默认",
      },

      // Audit Log Viewer
      auditLog: {
        loading: "正在加载审计日志...",
        loadFailed: "加载审计日志失败",
        noLogs: "暂无审计日志",
        noLogsHint: "对此配置的更改将记录在这里",
        showing: "显示 {current} / {total} 条记录",
        version: "版本",
        reason: "原因",
        ip: "IP",
      },

      // Export/Import Panel
      exportImport: {
        exportTitle: "导出配置",
        exportDescription:
          "下载所有 Agent 配置为 JSON 文件。包括每个 Agent 的所有设置、提示词和工具配置。",
        exporting: "导出中...",
        exportAll: "导出所有配置",
        exportSuccess: "Agent 配置导出成功",
        exportFailed: "导出失败",
        importTitle: "导入配置",
        importDescription:
          "从 JSON 文件导入 Agent 配置。您可以选择如何处理现有配置。",
        selectFile: "选择文件",
        importMode: "导入模式",
        importModeOptions: {
          createOnly: "仅创建 - 跳过已存在的 Agent",
          overwrite: "覆盖 - 完全替换已存在的 Agent",
          merge: "合并 - 更新已存在的 Agent，保留未指定的字段",
        },
        importModeHints: {
          createOnly: "仅创建新 Agent。已存在的 Agent 将被跳过。",
          overwrite: "用导入的数据完全替换已存在的 Agent。",
          merge: "更新指定的字段，未指定的字段保留现有值。",
        },
        skipErrors: "跳过错误并继续导入其余配置",
        skipErrorsHint: "如果不勾选，导入将在第一个错误处停止。",
        importPreview: "导入预览",
        foundConfigs: "找到 {count} 个 Agent 配置：",
        importResults: "导入结果",
        imported: "已导入",
        skipped: "已跳过",
        errors: "错误",
        importing: "导入中...",
        importConfigurations: "导入配置",
        aboutTitle: "关于导入/导出",
        aboutItems: [
          "导出创建包含所有 Agent 配置的 JSON 文件",
          "导入文件必须包含 configurations 数组",
          "每个配置至少需要一个 agent_type",
          "用于备份、迁移或在环境间共享配置",
        ],
        noFileSelected: "未选择文件",
        invalidJson: "无效的 JSON 文件",
        invalidFormat: "无效格式：缺少 configurations 数组",
        importCompleted: "导入完成",
        importFailed: "导入失败",
      },

      // Tool Enhancement Panel
      toolEnhancement: {
        title: "工具增强",
        description: "配置工具的显示名称和描述",
        loading: "加载中...",
        loadFailed: "加载失败",
        saveSuccess: "保存成功",
        saveFailed: "保存失败",
        save: "保存",
        saving: "保存中...",
        reload: "重新加载",
        searchPlaceholder: "搜索工具...",
        noToolSelected: "从左侧面板选择一个工具进行配置",
        configured: "已配置",
        notConfigured: "未配置",
        displayNameCn: "显示名称（中文）",
        displayNameEn: "显示名称（英文）",
        descriptionCn: "描述（中文）",
        descriptionEn: "描述（英文）",
        icon: "图标",
        displayOrder: "显示顺序",
        enabled: "启用",
        clearConfig: "清除配置",
        toolInfo: "工具信息",
        technicalName: "技术名称",
        originalDescription: "原始描述",
        allCategories: "所有分类",
        orphanConfigs: "孤立配置",
        orphanConfigsDesc: "已不存在的工具的配置",
        cleanupOrphans: "清理",
        orphanWarning: "此工具已不存在于系统中",
        category: "分类",
        categoryOverride: "分类覆盖",
        categoryOverrideHint: "留空则使用默认分类",
        originalCategory: "默认分类",
        useDefaultCategory: "使用默认",
        toolKey: "工具键",
        // Category translations
        categories: {
          search: "搜索",
          math: "数学",
          development: "开发",
          filesystem: "文件系统",
          reasoning: "推理",
          astrology: "占星",
          fortune: "运势",
          memory: "记忆",
          utility: "工具",
          general: "通用",
        } as Record<string, string>,
        // AI Generation
        aiGenerate: "AI 生成描述",
        aiGenerateDesc: "分析原始描述并自动生成用户友好的显示名称和描述。",
        aiGenerateButton: "生成",
        aiGenerating: "生成中...",
        aiSelectModel: "选择模型",
        aiSelectModelPlaceholder: "选择一个模型...",
        aiExplanation: "AI 分析",
        aiNoDescription: "没有可用于 AI 生成的原始描述",
        // Reload Enhancer
        reloadEnhancer: "应用到 LLM",
        reloadEnhancerDesc: "将保存的配置应用到 LLM 工具描述",
        reloadEnhancerSuccess: "工具描述已重新加载",
        reloadEnhancerFailed: "重新加载失败",
        // Tool Testing
        testTool: "测试工具",
        testToolDesc: "使用参数直接调用工具进行调试",
        testParameters: "测试参数",
        testParametersPlaceholder: "输入 JSON 格式的参数...",
        runTest: "执行测试",
        running: "执行中...",
        testResult: "测试结果",
        testSuccess: "执行成功",
        testFailed: "执行失败",
        executionTime: "执行时间",
        errorType: "错误类型",
        closeModal: "关闭",
        noParameters: "此工具无需参数",
        invalidJson: "无效的 JSON 格式",
        requiredFields: "必填字段",
        optionalFields: "可选字段",
        paramType: "类型",
        paramDefault: "默认值",
        requiresUserContext: "需要用户上下文",
        requiresUserContextDesc:
          "此工具需要用户上下文，将使用当前管理员用户ID进行测试",
        formMode: "表单模式",
        jsonMode: "JSON 模式",
        useDefaultValue: "使用默认值",
      },

      // Tool Combo Panel
      toolCombo: {
        title: "工具组合",
        description: "定义工具使用顺序和指引文字",
        loading: "加载中...",
        loadFailed: "加载失败",
        saveSuccess: "保存成功",
        saveFailed: "保存失败",
        save: "保存",
        saving: "保存中...",
        reload: "重新加载",
        searchPlaceholder: "搜索模板...",
        noTemplateSelected: "请从左侧选择或创建一个模板",
        newTemplate: "新建模板",
        deleteTemplate: "删除模板",
        confirmDelete: "确定要删除此模板吗？",
        templateId: "模板 ID",
        templateIdPlaceholder: "例如: astrology_basic",
        nameCn: "名称（中文）",
        nameEn: "名称（英文）",
        descriptionCn: "描述（中文）",
        descriptionEn: "描述（英文）",
        guidance: "整体指引",
        guidancePlaceholder: "使用这些工具的整体指导说明...",
        enabled: "启用",
        tools: "工具列表",
        addTool: "添加工具",
        toolName: "工具名称",
        selectTool: "选择工具",
        usageInstruction: "使用说明",
        usageInstructionPlaceholder: "这个工具的使用说明...",
        removeTool: "移除",
        preview: "预览",
        previewPrompt: "预览生成的 Prompt 上下文",
        closePreview: "关闭",
        closePicker: "关闭",
        noTools: "还没有添加工具",
        templateCount: "{count} 个模板",
        searchTools: "搜索工具...",
        availableTools: "可用工具",
        selectedTools: "已选工具",
        clickToAdd: "点击添加",
        alreadyAdded: "已添加",
      },

      // Prompt Templates Panel
      promptTemplates: {
        title: "提示词模版",
        description: "配置用于图像生成、推送通知、工作流等功能的系统提示词模版",
        addTemplate: "添加模版",
        noTemplates: "暂无提示词模版配置",
        addFirstTemplate: "添加您的第一个提示词模版",
        editTemplate: "编辑模版",
        addTemplateTitle: "添加新模版",
        templateKey: "模版键",
        templateKeyHint: "格式: domain.operation (例如: image.generate)",
        nameEnglish: "英文名称",
        nameChinese: "中文名称",
        descriptionEn: "英文描述",
        descriptionZh: "中文描述",
        systemPromptZh: "中文系统提示词",
        systemPromptEn: "英文系统提示词",
        displayOrder: "显示顺序",
        category: "分类",
        categoryOptions: {
          image: "图像",
          push: "推送",
          workflow: "工作流",
        },
        isDefault: "默认模版",
        resetToDefault: "重置为默认",
        resetSuccess: "已重置为默认值",
        cancel: "取消",
        saving: "保存中...",
        save: "保存",
        confirmDelete: "确定要删除这个模版吗？",
        cannotDeleteDefault: "无法删除默认模版。请使用重置功能恢复默认值。",
        loading: "加载中...",
        loadFailed: "加载模版失败",
        saveFailed: "保存模版失败",
        deleteFailed: "删除模版失败",
        version: "版本",
        lastUpdatedBy: "最后更新者",
        searchPlaceholder: "搜索模版...",
        allCategories: "全部分类",
        previewPrompt: "预览提示词",
        chinese: "中文",
        english: "英文",
      },

      // Output Schemas Panel
      outputSchemas: {
        title: "输出格式",
        description: "管理集中式 JSON 输出格式定义，用于 Agent 响应",
        addSchema: "添加格式",
        addNew: "新建输出格式",
        editTitle: "编辑输出格式",
        noSchemas: "暂无输出格式配置",
        noSchemasMatch: "没有匹配的输出格式",
        addFirstSchema: "添加您的第一个输出格式",
        editSchema: "编辑输出格式",
        searchPlaceholder: "搜索格式...",
        scopeAll: "全部范围",
        scopeGlobal: "仅全局",
        scopeAgent: "仅特定 Agent",
        scopeGlobalOption: "全局 (所有 Agent)",
        scopeAgentOption: "指定 Agent",
        selectAgent: "选择 Agent...",
        statusAll: "全部状态",
        statusEnabled: "已启用",
        statusDisabled: "已禁用",
        schemaKey: "格式标识",
        schemaKeyHint: "小写字母、数字、下划线。必须以字母开头。",
        nameEnglish: "名称（英文）",
        nameChinese: "名称（中文）",
        descriptionEn: "描述（英文）",
        descriptionZh: "描述（中文）",
        scope: "适用范围",
        agentType: "Agent 类型",
        jsonSchema: "JSON Schema",
        exampleOutput: "示例输出",
        formatInstruction: "格式说明",
        formatInstructionHint: "使用 {schema} 作为 JSON Schema 的占位符",
        displayOrder: "显示顺序",
        enabled: "启用",
        loading: "正在加载输出格式...",
        loadFailed: "加载输出格式失败",
        saveFailed: "保存输出格式失败",
        deleteFailed: "删除输出格式失败",
        toggleFailed: "切换状态失败",
        totalSchemas: "总格式数",
        showing: "显示",
        aboutTitle: "关于输出格式",
        aboutItem1: "全局格式对所有 Agent 可用",
        aboutItem2: "特定 Agent 格式仅对指定的 Agent 类型可用",
        aboutItem3:
          "在 format_instruction 中使用 {schema} 占位符注入 JSON Schema",
        aboutItem4: "禁用的格式在 Agent 选择下拉框中隐藏",
        cancel: "取消",
        save: "保存",
        saving: "保存中...",
        confirmDelete: '删除输出格式 "{name}"？',
        editing: "编辑中...",
      },

      // Model Defaults Panel
      modelDefaults: {
        title: "模型默认值",
        description: "配置系统各功能的默认模型",
        reload: "重新加载",
        save: "保存更改",
        saving: "保存中...",
        loading: "正在加载模型默认值...",
        loadFailed: "加载模型默认值失败",
        saveSuccess: "模型默认值保存成功",
        saveFailed: "保存失败",
        version: "版本",
        lastUpdatedBy: "最后更新者",
        // Categories
        chatModels: "对话模型",
        visionModels: "视觉模型",
        auxiliaryModels: "辅助模型",
        embeddingReranking: "向量与重排",
        speechModels: "语音模型",
        // Fields
        primaryChatModel: "主对话模型",
        primaryChatModelDesc: "用于深度对话的主模型",
        quickChatModel: "快速对话模型",
        quickChatModelDesc: "快速响应使用的模型（提示词在「快速响应」区域配置）",
        primaryVisionModel: "视觉补充模型",
        primaryVisionModelDesc: "仅当主对话模型不支持视觉时启用",
        quickVisionModel: "快速视觉补充模型",
        quickVisionModelDesc: "仅当快速对话模型不支持视觉时启用",
        memoryModel: "记忆模型",
        memoryModelDesc: "用于记忆提取和管理",
        classificationModel: "分类模型",
        classificationModelDesc: "用于意图分类",
        reasoningModel: "推理模型",
        reasoningModelDesc: "用于复杂推理任务",
        taskModel: "任务模型",
        taskModelDesc: "用于子任务（压缩、摘要等）",
        embeddingModel: "向量模型",
        embeddingModelDesc: "用于向量嵌入",
        rerankingModel: "重排序模型",
        rerankingModelDesc: "用于搜索结果重排序",
        rerankingEnabled: "启用重排序",
        sttModel: "语音转文字模型",
        sttModelDesc: "用于语音转录",
        ttsModel: "文字转语音模型",
        ttsModelDesc: "用于语音合成",
        audioSettings: {
          title: "音频设置",
          ttsProvider: "TTS 服务商",
          ttsVoice: "默认音色",
          ttsSpeed: "默认语速",
          ttsPreambleTrim: "前导音裁剪",
          ttsPreambleTrimUnit: "毫秒",
          ttsStreaming: "流式播放",
          sttModel: "STT 模型",
          sttFallbackModel: "备用模型",
          sttHotwords: "领域热词",
        },
        imageGenerationModels: "图像生成模型",
        imageModel: "图像生成模型",
        imageModelDesc: "工作流和 Soulmate 使用的默认图像生成模型（速度优先）",
        qualityImageModel: "高质量图像模型",
        qualityImageModelDesc: "用于高质量图像生成的模型（质量优先）",
        promptRewriteModel: "提示词改写模型",
        promptRewriteModelDesc: "用于管理后台的 AI 提示词改写功能",
        mbtiModel: "MBTI 推理模型",
        mbtiModelDesc: "用于星盘 MBTI 性格分析的推理模型",
        // Section headers
        globalModelsTitle: "全局模型",
        globalModelsDesc: "以下模型为全局共享，所有 Agent 使用相同配置",
        perAgentDefaultsTitle: "Agent 默认值",
        perAgentDefaultsDesc:
          "创建新 Agent 时使用的默认模型配置，Agent 可单独覆盖",
        allAgentsShared: "所有 Agent 共享",
      },
    },

    // Agent Config
    agentConfig: {
      // List Page
      listTitle: "Agent 配置",
      listDescription: "管理 AI Agent 配置和设置",
      createAgent: "创建 Agent",
      searchPlaceholder: "搜索 Agent...",
      showInactive: "显示未激活的 Agent",
      noAgentsMatch: "没有匹配的 Agent",
      noAgentsMatchHint: "尝试调整搜索条件",
      noAgentsYet: "暂无 Agent 配置",
      noAgentsYetHint: "创建您的第一个 Agent 开始使用",
      showingAgents: "显示 {current} / {total} 个 Agent",
      activeCount: "{count} 个已激活",
      inactive: "未激活",
      version: "版本",
      updated: "更新时间",

      // Detail Page
      loadingConfig: "加载 Agent 配置...",
      backToList: "返回列表",
      agentNotFound: "未找到 Agent",
      copy: "复制",
      unsavedChanges: "有未保存的更改",
      saving: "保存中...",
      saveSuccess: "配置已保存成功！",
      saveFailed: "保存失败",
      active: "已激活",
      activateAgent: "启用 Agent",
      deactivateAgent: "禁用 Agent",
      confirmDeactivate: "确定要禁用此 Agent 吗？禁用后用户将无法使用此 Agent。",
      confirmActivate: "确定要重新启用此 Agent 吗？",
      updatedViaAdmin: "通过管理界面更新",
      conflictModalTitle: "配置已被其他管理员修改",
      conflictModalBody: "当前版本为 v{current}，你基于 v{expected} 编辑。请选择如何处理：",
      conflictModalDiscard: "刷新页面，放弃我的改动",
      conflictModalForce: "强制覆盖，使用我的版本",

      // Tabs
      tabSettings: "设置",
      tabHooks: "功能扩展",
      tabTools: "工具",
      tabPrompt: "提示词",
      tabSchemas: "Schemas",
      tabSkills: "技能",
      tabJson: "JSON",
      tabVersions: "版本历史",
      // New tabs
      tabBasic: "基础",
      tabMemory: "记忆",
      tabKnowledge: "知识库",
      tabAstrology: "占星增强",
      tabPersonality: "人格画像",
      tabEmotion: "情绪感知",
      tabClassifier: "技能",
      tabTeam: "团队",
      tabAdvanced: "高级",

      // Actions
      actions: {
        browseTools: "浏览工具",
      },

      // Output Format Section
      outputFormat: {
        title: "输出格式",
        createSchema: "创建 Schema",
        manageSchemas: "管理 Schemas",
        noSchemasWarning:
          "尚未定义输出 Schema。创建一个 Schema 以启用结构化 JSON 输出。",
        createFirstSchema: "创建第一个 Schema",
        noDefault: "无格式约束 (自由输出)",
        defaultSchemaHelp:
          "未在 API 请求中指定 output_schema_key 时使用的默认 Schema。",
        availableSchemas: "可用的 Schema Keys (用于 API)",
        apiHint:
          "在 API 请求中传递 output_schema_key 参数来使用特定的 Schema。",
      },

      // Create/Copy Agent Dialog
      createAgentDialog: {
        copyAgent: "复制 Agent",
        createNewAgent: "创建新 Agent",
        agentType: "Agent 类型",
        agentTypePlaceholder: "例如：my_agent",
        agentTypeHint: "唯一标识符。仅支持小写字母、数字、下划线和连字符。",
        name: "名称",
        namePlaceholder: "例如：我的 Agent",
        description: "描述",
        descriptionPlaceholder: "描述这个 Agent 的用途...",
        copyingFrom: "复制配置自",
        creating: "创建中...",
        agentTypeRequired: "Agent 类型为必填项",
        nameRequired: "名称为必填项",
        agentTypeInvalid:
          "Agent 类型必须以字母开头，仅包含小写字母、数字、下划线和连字符",
      },

      // Version History
      versionHistory: {
        title: "版本历史",
        loading: "加载版本历史...",
        current: "当前",
        restore: "恢复",
        noHistory: "暂无版本历史",
        createdBy: "创建者",
        versionConfig: "版本 {version} 配置",
        confirmRestore: "确定要恢复到版本 {version} 吗？这将替换当前配置。",
        restoreReason: "通过管理界面恢复",
        editing: "编辑中",
        clickToShowJson: "点击眼睛图标展开JSON配置",
        viewConfig: "查看配置",
      },

      // JSON Preview
      jsonPreview: {
        title: "JSON 配置",
        copy: "复制",
        copied: "已复制",
        download: "下载",
        modified: "有修改",
        reset: "重置",
        applyChanges: "应用更改",
        editHint: "直接编辑 JSON 并点击「应用更改」来更新配置",
        loadToEditor: "载入到编辑器",
      },

      // Basic Info Section
      basicInfo: {
        roleDefinitionPrompt: "角色定义提示词",
        roleDefinitionHelp: "定义 Agent 的角色和行为特征",
        configureSystemPrompts: "配置系统提示词",
      },

      // Config Migration Dialog
      configMigration: {
        title: "配置迁移",
        versionInfo:
          "检测到旧版本配置 (v{from})。需要升级到当前版本 (v{to}) 才能正常使用。",
        changesTitle: "将进行以下更改：",
        warning:
          "迁移后，您需要手动保存配置才能生效。迁移操作可以安全撤销（刷新页面）。",
        cancel: "取消",
        confirm: "确认迁移",
        confirmed: "已确认",
        before: "变更前",
        after: "变更后",
        changeTypes: {
          add: "新增",
          remove: "移除",
          modify: "修改",
        },
      },

      // Tools Browser Modal
      toolsBrowser: {
        customTools: "自定义工具",
        builtinToolkits: "内置工具包",
        customToolsDesc: "从可用分类中选择单个工具",
        builtinToolkitsDesc: "选择内置工具模块",
        filterByCategory: "按分类筛选：",
        allCategories: "全部分类",
        toolsSelected: "已选择 {count} 个工具",
        loadingTools: "加载工具中...",
        availableTools: "可用工具",
        selectedTools: "已选工具",
        previewConfig: "预览配置",
        cancel: "取消",
        apply: "应用",
        noBuiltinToolkits: "没有可用的内置工具包",
        noCustomTools: "没有可用的自定义工具",
        noToolsSelected: "未选择任何工具",
        toolsCount: "{count} 个工具",
        builtinToolsBrowser: "内置工具浏览器",
        customToolsBrowser: "自定义工具浏览器",
        format: "格式化",
        noItemsConfigured: "未配置任何项目",
        toolkits: "工具包",
        enabledTools: "已启用工具",
        servers: "服务器",
        modules: "模块",
        allToolsEnabled: "(启用全部工具)",
        toolNotFound: "工具不存在，请移除",
        serverNotFound: "服务器不存在，请移除",
        willMigrateOnSave: "保存时将自动迁移",
        toolNotFoundRemove: "工具不存在，请移除",
        invalidToolsWarning: "部分已选工具已不存在，请移除后再应用。",
        removeInvalidToolsFirst: "请先移除无效工具",
      },

      // MCP Tools Browser Modal
      mcpBrowser: {
        title: "MCP 工具浏览器",
        refresh: "刷新",
        systemServers: "系统服务器",
        userServers: "用户服务器",
        serversSelected: "{count} 个服务器已选择",
        toolsSelected: "{count} 个工具已选择",
        loadingServers: "加载 MCP 服务器中...",
        availableServers: "可用服务器",
        selectedItems: "已选项目",
        servers: "服务器",
        individualTools: "单个工具",
        noServersSelected: "未选择服务器",
        noServersAvailable: "没有可用的 MCP 服务器",
        connected: "已连接",
        disconnected: "已断开",
        failedToConnect: "连接此服务器失败",
        previewConfig: "预览配置",
        cancel: "取消",
        apply: "应用",
        toolsCount: "{count} 个工具",
        invalidItemsWarning: "部分已选服务器/工具已不存在，请移除后再应用。",
        removeInvalidItemsFirst: "请先移除无效项目",
      },

      // Prompt Template Preview
      promptPreview: {
        title: "系统提示词预览",
        buttonLabel: "预览提示词",
        copy: "复制",
        copied: "已复制！",
        loading: "加载中...",
        refresh: "刷新",
        generatePreview: "生成预览",
        usingTemplate: "使用模板：",
        default: "(默认)",
        enabledSections: "{count} 个已启用的部分",
        useRealData: "使用真实用户数据",
        livePreview: "实时预览",
        userId: "用户 ID",
        enterUserId: "输入用户 ID...",
        useCurrent: "使用当前用户",
        usingCurrentUser: "使用当前登录用户",
        sampleMessage: "示例用户消息（触发记忆搜索）",
        enterSampleMessage: "输入示例用户消息...",
        realDataHint:
          "使用真实数据运行所有 Hooks：user_facts、birth_chart、memory_context",
        sampleDataHint: "预览使用生成的测试用户和示例数据。",
        previewFailed: "预览失败",
        template: "模板：",
        systemChars: "系统：{count} 字符",
        userChars: "用户：{count} 字符",
        user: "用户：",
        hooksExecuted: "已执行 Hooks ({count})",
        systemStage: "SYSTEM 阶段：",
        userStage: "USER 阶段：",
        noHooksExecuted: "没有执行的 Hooks",
        dataSources: "数据源 ({count})",
        dataSourceHint: "real = 从数据库加载 | empty = 未找到数据",
        systemPrompt: "系统提示词：",
        enhancedUserMessage: "增强的用户消息 ({count} 字符)",
        contextItems: "上下文项 ({count})：",
        emptyStateTitle: '点击"生成预览"查看系统提示词的渲染效果',
        emptyStateDesc:
          "实时预览会运行所有 Hooks 使用真实数据，构建发送给 LLM 的完整提示词",
      },

      // Output Schemas Tab
      outputSchemas: {
        title: "Schemas",
        add: "添加",
        noSchemas: "暂无 Schema 定义",
        noSchemasHint: '点击"添加"创建您的第一个输出 Schema',
        default: "默认",
        properties: "{count} 个属性",
        noSelection: "未选择 Schema",
        noSelectionHint: "从列表中选择一个 Schema 或创建新的",
      },

      // Skills Tab
      skills: {
        title: "技能",
        add: "添加技能",
        noSkills: "暂无技能配置",
        noSkillsHint: "添加技能以启用动态指令注入",
        noSelection: "未选择技能",
        noSelectionHint: "从列表中选择一个技能或添加新的",
        enabled: "已启用",
        disabled: "已禁用",
        priority: "优先级",
        exclusionGroup: "互斥组",
        disabledWarning:
          '技能功能已禁用。请在技能匹配设置中启用"启用技能"以使用技能功能。',
        // Dialog
        createTitle: "创建技能",
        editTitle: "编辑技能",
        // Form fields
        skillId: "技能 ID",
        skillIdPlaceholder: "例如: lucky_charm",
        skillIdRequired: "技能 ID 为必填项",
        skillIdPattern: "仅允许字母、数字、下划线和连字符",
        skillIdReadonlyHint: "创建后不可更改",
        skillIdHint: "唯一标识符，创建后不可更改",
        name: "名称",
        namePlaceholder: "输入技能显示名称",
        nameRequired: "名称为必填项",
        description: "描述",
        descriptionPlaceholder: "简要描述此技能的功能",
        descriptionHint: "始终注入到提示词中让 Agent 了解其能力",
        command: "触发指令",
        commandPlaceholder: "用户点击技能时发送的触发指令",
        commandRequired: "触发指令为必填项",
        commandHint: "用户点击此技能时发送的消息内容",
        displayOrder: "显示顺序",
        displayOrderHint: "数字越小优先级越高",
        displayOrderMin: "必须大于等于 0",
        enableSkill: "启用此技能",
        instructions: "指令内容",
        instructionsPlaceholder:
          "编写此技能的详细指令...\n\n指令越详细，AI 生成的匹配规则越准确。",
        instructionsHint: "技能匹配时追加到基础指令",
        priorityHint: "数值越高在冲突时优先级越高",
        exclusionGroupHint: "相同组的技能互斥",
        forcePromptType: "强制提示词",
        forcePromptAuto: "自动",
        forcePromptQuick: "快速响应提示词",
        forcePromptDeep: "系统提示词",
        toolCombo: "工具组合",
        toolComboNone: "无（不使用工具组合）",
        toolComboTools: "个工具",
        toolComboHint: "匹配此技能时将注入选定的工具组合到提示词中",
        matchingRules: "匹配规则",
        keywords: "关键词（逗号分隔）",
        keywordsPlaceholder: "年度, 年报, 报告",
        keywordsHint: "用于规则匹配的触发词",
        regexPatterns: "正则表达式（每行一个）",
        regexPatternsHint: "用于模式匹配的正则表达式",
        exampleQueries: "示例查询（每行一个）",
        exampleQueriesHint: "用于嵌入语义匹配的示例查询",
        // AI Generation
        aiGenerateRules: "AI 生成匹配规则",
        aiGenerateDesc:
          "分析指令内容自动生成技能元数据、关键词、正则和示例查询",
        selectModel: "选择模型",
        selectModelPlaceholder: "选择一个模型...",
        generateFromInstructions: "从指令生成",
        aiAnalysis: "AI 分析",
        aiGenerate: "AI 生成",
        generating: "生成中...",
        mustStartWithLetter: "必须以小写字母开头",
        provideInstructions: "请先提供技能指令",
        selectModelFirst: "请先选择模型",
        selectModelInAiPanel: "请先在 AI 生成面板选择模型",
        generateFailed: "生成失败",
        patternGenerateFailed: "正则生成失败",
        deleteConfirmTitle: '确定要删除技能 "{name}" 吗？',
        enableSkillsFirst: "启用技能以配置基于技能的分类",
        toggleEnableSkillsHint: "在上方开启「启用技能」以开始",
        clickAddFirst: "点击「添加」创建第一个技能",
        selectOrCreateSkill: "选择要编辑的技能或创建新的",
        // Global Settings
        globalSettings: "全局设置",
        // Dedup Configuration
        dedupConfig: "去重配置",
        enableDedup: "启用技能去重",
        enableDedupDesc:
          "为激活的技能注入历史执行记录，让模型了解之前输出过什么内容，避免生成重复内容。",
        dedupConfigHint: "启用去重后，可配置历史记录的查询范围和显示方式。",
        dedupConfigOverrideHint:
          "留空表示使用全局默认值。仅填写需要覆盖的字段。",
        dataQueryHours: "数据查询范围",
        dataQueryHoursHint: "查询历史执行记录的时间范围",
        reportWindowHours: "报告时效窗口",
        reportWindowHoursHint: "每个时间窗口的大小",
        maxPerWindow: "每窗口最大条数",
        maxPerWindowHint: "每个时间窗口最多显示几条记录",
        outputPreviewLength: "输出摘要长度",
        outputPreviewLengthHint: "历史输出内容的最大显示字符数",
        showUserQuery: "显示用户问题",
        showUserQueryHint: "在历史记录中显示用户的原始问题",
        queryPreviewLength: "问题摘要长度",
        queryPreviewLengthHint: "用户问题的最大显示字符数",
        hours: "小时",
        chars: "字符",
        dedupPrinciple: "去重原理",
        dedupPrincipleList: [
          "当技能被匹配时，系统会查询该用户该技能的历史执行记录",
          "历史记录会以 <skill_history> 标签注入到技能指令中",
          "模型可以参考历史输出，生成差异化的新内容",
          "每个技能可以单独覆盖这些全局默认值",
        ],
        skillTrackingRequired: "需要启用技能追踪",
        skillTrackingRequiredHint:
          "技能去重功能需要同时启用「技能追踪」来记录执行历史。请在「功能扩展」中启用 skill_tracking。",
        perSkillDedupEnabled: "启用去重",
        perSkillDedupDisabledHint: "此技能已禁用去重，不会注入历史记录",
        perSkillDedupEnabledHint: "跟随全局设置，可自定义配置覆盖全局默认值",
        dedupDisabledForSkill: "此技能已禁用去重功能",
        default: "默认",
        // Skills Tab - Classifier relationship
        classifierDisabledTitle: "分类引擎已关闭",
        classifierDisabledDesc: "请在「功能扩展」中启用「分类引擎」开关，下方技能配置和快速响应模型才会生效。",
        classifierRelationDesc: "用户消息先经规则匹配（关键词/正则），再经语义匹配（向量检索），置信度不足时由 LLM 兜底判断。当同时开启「快速响应模型」时，LLM 会在一次调用中同时完成响应类型分类和技能匹配。",
        // Skills Tab Settings Panel
        skillsNotAvailable: "技能不可用",
        skillsHookHidden: "该 Agent 已隐藏技能 Hook。",
        skillMatchingSettings: "技能匹配设置",
        featureToggles: "功能开关",
        enableSkills: "启用技能",
        enableSkillsHint: "根据用户意图动态注入指令",
        semanticMatching: "语义匹配",
        semanticMatchingHint: "TEI 服务（bge-m3）",
        thresholdSettings: "阈值设置",
        ruleConfidence: "规则置信度",
        semanticSimilarity: "语义相似度",
        llmTimeout: "LLM 超时",
        skillToolInstructions: "技能工具使用说明",
        customized: "已自定义",
        resetToDefault: "重置为默认",
        skillToolInstructionsHint:
          "工具 get_skill_instructions、list_available_skills 的使用说明，注入到系统提示词。",
        howItWorks: "技能匹配原理",
        threeLayerMatching: "三层级联匹配",
        layer1Rule: "第一层：规则匹配（<10ms）",
        layer1RuleDesc:
          "匹配 keywords（0.85-0.95 置信度）和 patterns（0.9 置信度）。置信度 ≥ rule_confidence_threshold 时返回结果。",
        layer2Semantic: "第二层：语义匹配（~50ms）",
        layer2SemanticDesc:
          "需启用 semantic_matching_enabled。使用 TEI 服务（BAAI/bge-m3）在 Qdrant 中匹配 name、description 和 example_queries。相似度 ≥ similarity_threshold 时返回结果。",
        layer3Llm: "第三层：LLM 兜底（~200ms）",
        layer3LlmDesc:
          "使用 model_id 进行意图分类。超时：skill_llm_timeout 秒。当规则和语义匹配都失败时的兜底方案。",
        exclusionGroupExplain: "互斥组解析",
        exclusionGroupRules: [
          "同一 exclusion_group 中的技能互斥",
          "当同组多个技能匹配时，只保留 priority 最高的",
          "不同组的技能可以同时匹配",
          "没有设置 exclusion_group 的技能匹配后始终包含",
        ],
        fieldPurposes: "技能配置字段说明",
        fieldPurposesDesc:
          "keywords/patterns → 规则匹配 | name/description/example_queries → 语义匹配 | instructions → 执行指令",
        skillTools: "技能工具（注入系统提示词）",
        skillToolsDesc: "启用技能后，Agent 可使用以下工具：",
        skillToolsList: [
          "get_skill_instructions(skill_id) - 获取指定技能的详细指令",
          "list_available_skills() - 列出所有可用技能及描述",
        ],
        skillToolsNote: "可在上方「技能工具使用说明」中自定义工具使用说明。",
        exampleQueriesBestPractices: "编写示例查询的最佳实践",
        exampleQueriesTips: [
          "每个技能编写 3-10 个不同的示例，覆盖用户的不同提问方式",
          "包含变体：正式/口语、简短/详细、不同表达方式",
          "聚焦于用户意图，而非技术术语",
          "好的示例：「帮我看看明年运势」、「2025年我的运气怎么样」、「想了解一下未来一年的发展」",
          "避免：复制 instructions 内容——那是用于执行的，不是匹配的",
        ],
        semanticMatchingNote: "语义匹配详情",
        semanticMatchingDetails: [
          "使用系统配置的 TEI 服务（BAAI/bge-m3）进行 embedding",
          "name、description 和 example_queries 都会被索引到 Qdrant",
          "instructions 不参与匹配（它定义执行行为）",
          "保存配置时会自动重新索引 embeddings",
          "阈值控制最低相似度分数（默认 0.75 = 75% 相似）",
          "每个技能可匹配多个文本，只使用最高分数",
        ],
        validationErrors: "发现 {count} 个验证错误",
        validationWarnings: "{count} 个警告",
        andMore: "...还有 {count} 项",
        newSkillName: "技能 {index}",
        keywordsCount: "{count} 关键词",
        examplesCount: "{count} 示例",
        errorsCount: "{count} 错误",
        warningsCount: "{count} 警告",
        classificationRules: "分类规则",
        classificationRulesDesc: "控制消息分类行为，减少不必要的LLM调用",
        llmFallbackEnabled: "LLM 分类回退",
        llmFallbackEnabledHint:
          "当规则匹配失败时，是否使用LLM进行技能分类。关闭可减少API开销。",
        customQuickKeywords: "自定义快速回复关键词",
        customQuickKeywordsHint:
          "包含这些关键词的消息将被分类为快速回复，跳过技能匹配。",
        customQuickKeywordsPlaceholder: "输入关键词后按回车",
        customQuickPatterns: "自定义快速回复模式 (正则)",
        customQuickPatternsHint: "正则表达式，匹配的消息将被分类为快速回复。",
        customQuickPatternsPlaceholder: "例: ^(查余额|签到)[呀啊]?$",
        defaultPatternsInfo:
          "系统默认规则已覆盖常见问候、确认、闲聊、笑声、告别等模式。此处可添加额外的快速回复规则。",
        addPattern: "添加模式",
        invalidRegex: "无效的正则表达式",
        builtinRules: "系统内置规则",
        builtinRulesDesc: "以下规则始终生效，无需配置。自定义规则在内置规则之后检查。",
        builtinMaxLength: "仅当消息长度 ≤ 50 字符时才进行规则匹配，超过此长度直接进入 DEEP 路径。",
        builtinSkillNote: "快速回复仅跳过技能匹配流程和重型工具（MCP、记忆工具等），但技能工具始终保留。Agent 在快速回复中也可通过 get_skill_instructions 主动查询并调用已配置的技能。",
        builtinGreeting: "问候",
        builtinAck: "确认/感谢",
        builtinCasual: "闲聊",
        builtinLaughter: "笑声",
        builtinFarewell: "告别",
        builtinReaction: "反应/感叹",
      },

      // Schema Editor
      schemaEditor: {
        aiGenerate: "AI 智能生成 Schema",
        aiGenerateDesc:
          "输入期望的 JSON 输出示例，AI 会自动分析并生成对应的 JSON Schema",
        selectModel: "选择模型",
        selectModelPlaceholder: "-- 请选择模型 --",
        inputExample: "输入 JSON 示例",
        generating: "生成中...",
        generate: "生成 Schema",
        aiExplanation: "AI 设计说明",
        key: "Key",
        keyValidHint:
          "用于 API 调用的唯一标识符。仅支持小写字母、数字和下划线。",
        keyInvalidHint: "必须以字母开头，且仅包含小写字母、数字和下划线。",
        name: "名称",
        namePlaceholder: "Schema 名称",
        description: "描述",
        descriptionPlaceholder: "简要描述此 Schema",
        jsonSchema: "JSON Schema",
        validJson: "JSON 有效",
        jsonSchemaHint: "使用 JSON Schema 格式定义期望的输出结构",
        exampleOutput: "示例输出（可选）",
        exampleOutputHint: "提供一个示例输出供参考",
        formatInstruction: "格式化指令",
        formatInstructionHint: "使用 {schema} 占位符在运行时注入 JSON Schema",
        defaultSchema: "默认 Schema",
        setAsDefault: "设为默认",
        deleteSchema: "删除 Schema",
        confirmDelete: '确定要删除 "{name}" 吗？',
        alertExampleRequired: "请提供 JSON 示例",
        alertModelRequired: "请先选择模型",
        alertGenerateFailed: "AI 生成失败：",
      },

      // Template Selector
      templateSelector: {
        title: "Prompt 模板",
        description: "选择一个模板来定义此 Agent 的系统提示词结构",
        useDefault: "使用默认",
        default: "默认",
        recommended: "推荐",
        systemDefault: "系统默认",
        sections: "模板分段",
        sectionsCount: "{count} 个分段",
        updated: "更新于",
        noTemplates: "暂无可用模板",
        loadingTemplates: "加载模板中...",
        previewTemplate: "预览模板",
      },

      // Knowledge Tab
      knowledge: {
        confirmDeleteDoc: "确定要删除这个文档吗？",
        confirmClearAll: "确定要清空所有文档吗？此操作不可撤销！",
      },

      // Test Chat
      testChat: {
        confirmDeleteConversation: "删除这个对话？",
      },
    },

    // Chat Panel (Floating)
    chatPanel: {
      confirmDeleteTopic: "删除「{title}」？此操作无法撤销。",
      confirmDeleteMemory: "删除这条记忆？",
      confirmDeleteBirthChart: "删除星盘「{name}」？此操作无法撤销。",
    },

    // Feature Group Card
    featureGroups: {
      virtualFeatures: "虚拟功能",
      confirmEnableDependencies: "需要先启用「{dependencies}」，是否一起启用？",
    },

    // Hooks (功能扩展)
    hooks: {
      pipelineTitle: "扩展管道",
      loading: "加载功能扩展中...",
      errorLoading: "加载扩展出错",
      noData: "暂无扩展数据",
      enabledCount: "{enabled} / {total} 已启用",
      showHidden: "显示已关闭 ({count})",
      hideHidden: "隐藏已关闭",

      stages: {
        system: "系统阶段",
        user: "用户阶段",
        final: "结束阶段",
      },
      hooksCount: "{count} 个扩展",

      selectHook: "选择一个扩展查看详情",
      description: "描述",
      noDescription: "暂无描述",
      stage: "阶段",
      category: "分类",
      scope: "作用域",
      sortOrder: "排序",
      dragToReorder: "拖拽以重新排序",
      configInAgentConfig: "扩展参数配置在 Agent 设置对应的扩展设置中管理",

      hookDefinitions: {
        // ═══ SYSTEM 阶段 ═══
        user_facts: {
          displayName: "用户事实",
          description:
            "加载用户的永久偏好、信息和信念（如：喜欢简洁回答、是程序员）",
          requires: "记忆系统 (memory.enabled)",
          note: "注入到 System Prompt，位于最前端以优化 KV Cache",
        },
        datetime_context: {
          displayName: "日期时间",
          description:
            "注入当前日期时间上下文，让 Agent 知道今天是几月几号星期几",
        },
        astrology_plus: {
          displayName: "占星增强",
          description: "加载用户出生星盘数据和当前行运信息",
          note: "需要用户已设置出生信息",
        },
        first_message: {
          displayName: "首条消息",
          description: "检测是否为新会话的首条消息，用于触发欢迎语或特殊处理",
        },
        classifier: {
          displayName: "分类引擎",
          description: "响应类型分类（快速/深度）+ 技能匹配，统一决策引擎",
          note: "默认启用，关闭后技能配置和快速响应模型均不生效",
        },
        skill_dedup: {
          displayName: "技能去重",
          description: "检查当前对话是否重复调用相同技能，避免重复执行",
          requires: "依赖 classifier 的分类结果",
        },

        // ═══ USER 阶段 ═══
        emotion_enhance: {
          displayName: "情绪感知",
          description: "分析用户消息的情绪状态和意图，用于调整回复风格",
        },
        memory_context: {
          displayName: "记忆检索",
          description: "根据用户消息搜索相关的用户事件和 Agent 交互记忆",
          requires: "记忆系统 (memory.enabled)",
          note: "检索结果注入到 System Prompt 供 Agent 参考",
        },
        knowledge_context: {
          displayName: "知识检索",
          description: "从知识库中检索与用户问题相关的文档片段",
          requires: "知识库 (knowledge_enabled)",
        },
        memory_reinforce: {
          displayName: "记忆强化",
          description: "基于艾宾浩斯遗忘曲线，强化被访问的记忆以延缓遗忘",
          requires: "依赖 memory_context 的检索结果",
          note: "被检索到的记忆会增加强度，长期不访问的记忆会逐渐衰减",
        },

        // ═══ FINAL 阶段 ═══
        memory_save: {
          displayName: "记忆保存",
          description:
            "对话结束后，从对话中提取并保存用户记忆和 Agent 交互记忆",
          requires: "记忆系统 (memory.enabled)",
          note: "双提取模式：分别提取用户事实和 Agent 承诺/建议",
        },
        skill_tracking: {
          displayName: "技能追踪",
          description: "记录本次对话中执行的技能，用于统计和去重",
        },
      },
    },
    imageWorkflows: {
      executionPanel: {
        title: "执行",
        execution: "执行",
        progress: "进度",
        nodes: "节点",
        failed: "失败",
        duration: "耗时",
        error: "错误",
        clearAndClose: "清除并关闭",
        nodeDetail: "节点详情",
      },
      executionOverview: {
        retrying: "重试中...",
        retryFrom: "从 {node} 重试",
        users: "{count} 个用户",
        viewUser: "查看用户: {user}",
        targetUserDetails: "目标用户执行详情:",
        loading: "加载中...",
        totalUsers:
          "共 {total} 个用户，已完成 {completed} 个，失败 {failed} 个",
        completed: "已完成",
        failedCount: "失败",
        currentView: "当前查看: {user}",
        userError: "用户 {user} 错误",
      },
      // Config Renderers
      modelConfig: {
        resolution: "分辨率",
        aspectRatio: "宽高比",
        quality: "质量",
        default: "默认",
        enableWebSearch: "启用网络搜索",
        webSearchDescription: "允许模型搜索网络获取参考素材",
      },
      executionHistory: {
        title: "执行历史",
        tab: "历史",
        executionLabel: "执行",
        nodes: "节点",
        saveFirst: "请先保存工作流以查看执行历史",
        noHistory: "暂无执行历史",
        runFirst: "运行工作流后可查看执行历史",
        relativeTime: {
          justNow: "刚刚",
          minutesAgo: "{n} 分钟前",
          hoursAgo: "{n} 小时前",
          daysAgo: "{n} 天前",
        },
      },
      nodeDetail: {
        selectNode: "点击节点查看详情",
        noStepData: "该节点无执行数据",
        status: "状态",
        duration: "耗时",
        order: "执行顺序",
        startedAt: "开始时间",
        completedAt: "完成时间",
      },
      stepResult: {
        error: "错误",
        variables: "变量",
        noVariables: "无变量",
        mode: "模式",
        generatedPrompt: "生成的提示词",
        showLess: "收起",
        showMore: "展开",
        noPrompt: "未生成提示词",
        model: "模型",
        output: "输出",
        imageLoadError: "图片加载失败",
        noImage: "未生成图片",
        storage: "存储",
        saved: "已保存到图库",
        temporary: "临时",
        outputPending: "输出待处理",
        noOutput: "无输出数据",
      },
      outputNode: {
        noArtifact: "不保存为产物 (临时文件)",
      },
      runDialog: {
        title: "执行工作流",
        description: "为选定的用户执行",
        targetUsers: "目标用户",
        cancel: "取消",
        execute: "执行工作流",
        executing: "启动中...",
        selectAtLeastOneUser: "请选择至少一个用户",
        workflowStarted: "工作流已启动，目标",
        allValidUsers: "所有有效用户",
        usersCount: "{count} 个用户",
        executeFailed: "启动工作流失败",
      },
      // Workflow Toolbar
      toolbar: {
        categories: {
          input: { label: "输入", description: "文案和提示词生成" },
          image: { label: "图像处理", description: "图像生成、编辑、融合" },
          special: { label: "特殊功能", description: "特色图像功能" },
          action: { label: "动作", description: "推送通知等操作" },
          output: { label: "输出", description: "保存和导出" },
        },
        jsonExport: {
          title: "导出工作流 JSON",
          copyToClipboard: "复制到剪贴板",
          downloadJson: "下载 JSON",
          tip: "此 JSON 包含完整的工作流定义，可用于导入到其他环境或作为备份",
        },
        jsonImport: {
          title: "导入工作流 JSON",
          dropHint: "拖放 JSON 文件到此处，或点击选择文件",
          orPaste: "或直接粘贴 JSON",
          placeholder: "粘贴工作流 JSON...",
          tip: "导入将创建新工作流，原有工作流不会被覆盖",
          cancel: "取消",
          import: "导入",
          errors: {
            missingFields:
              "JSON 格式不正确：缺少 metadata、settings 或 definition",
            missingMetadata: "JSON 格式不正确：metadata 缺少 key 或 name",
            missingDefinition:
              "JSON 格式不正确：definition 缺少 nodes 或 edges 数组",
            parseError: "JSON 解析错误：{error}",
            fileReadError: "文件读取失败",
            dropJsonFile: "请拖入 JSON 文件",
          },
        },
        addNode: "添加节点",
        nodePanel: "节点面板",
        dragToAdd: "拖拽到画布添加",
        startNodeTip: "Start 节点会自动创建",
        nodeCount: "{count} 个节点",
        importJson: "导入 JSON",
        exportJson: "导出 JSON",
        executionHistory: "执行历史",
        saveFirst: "请先保存工作流",
        saving: "保存中...",
        save: "保存",
        running: "运行中...",
        run: "运行",
        unsaved: "未保存",
        workflowName: "工作流名称",
        workflowKey: "workflow_key",
        keyValidation: {
          startWithLowercase:
            "必须以小写字母开头，只能包含小写字母、数字和下划线",
          maxLength: "最长64个字符",
        },
        dragHint: "拖拽添加 {label} 节点",
        unsavedWarning: "您有未保存的更改，确定要离开吗？",
      },
      // Workflow List
      list: {
        title: "图像工作流",
        workflowCount: "{count} 个工作流",
        newWorkflow: "新建工作流",
        loading: "加载中...",
        loadFailed: "加载工作流失败",
        noWorkflows: "还没有工作流",
        noWorkflowsHint: "创建您的第一个工作流",
        createWorkflow: "创建工作流",
        copySuccess: "ID 已复制到剪贴板",
        copyFailed: "复制失败",
        menu: {
          edit: "编辑",
          makePrivate: "设为私有",
          makePublic: "设为公开",
          delete: "删除",
        },
        confirmDelete: '确定要删除 "{name}" 吗？此操作无法撤销。',
        deleteSuccess: "工作流已删除",
        deleteFailed: "删除工作流失败",
        updateSuccess: '"{name}" 现在是{status}',
        updateFailed: "更新工作流失败",
        stats: {
          nodes: "节点",
          runs: "次运行",
        },
        tags: {
          public: "公开",
          agent: "Agent",
          rawLlm: "原始 LLM",
          library: "图库",
          temp: "临时",
        },
      },
      // Workflow Editor
      editor: {
        loading: "加载工作流中...",
        back: "返回",
        editWorkflow: "编辑工作流",
        newWorkflow: "新建工作流",
        unsavedWarning: "您有未保存的更改。确定要离开吗？",
        validation: {
          keyRequired: "Key 是必填项",
          keyInvalid: "Key 必须以小写字母开头，只能包含小写字母、数字和下划线",
          keyTooLong: "Key 最长64个字符",
        },
        toast: {
          saveSuccess: "工作流已保存",
          createSuccess: "工作流已创建",
          saveFailed: "保存工作流失败",
          saveFirst: "请先保存工作流再运行",
          executionComplete: "工作流执行完成！",
          executionFailed: "工作流执行失败",
          executionPartial: "工作流部分完成",
          executionCancelled: "工作流已取消",
          resumeComplete: "工作流恢复完成！",
          resumeFailed: "工作流恢复失败",
          resumePartial: "工作流恢复部分完成",
          resumeCancelled: "工作流恢复已取消",
          resumeError: "恢复执行失败",
          modelRequired: "节点「{node}」必须选择模型，不支持自动选择",
        },
      },
      // Config Field Renderer
      configField: {
        selectPlaceholder: "请选择...",
        loadingOptions: "加载中...",
        unknownRenderer: "未实现的渲染器: {renderer}",
      },
      // Node Config Panel
      nodeConfigPanel: {
        selectNodePrompt: "选择一个节点进行配置",
        labelInput: "标签",
        labelPlaceholder: "节点标签",
        inputFields: "输入字段",
        outputFields: "输出字段",
        executionResult: "执行结果",
        status: {
          pending: "等待中",
          running: "执行中",
          completed: "已完成",
          failed: "失败",
          skipped: "已跳过",
        },
        timing: {
          started: "开始",
          completed: "完成",
          duration: "耗时",
        },
        output: {
          title: "输出",
          prompt: "Prompt",
          generatedImage: "生成的图像",
          savedToLibrary: "已保存到图库",
          fileId: "文件 ID",
        },
        error: "错误",
        duplicate: "复制",
        delete: "删除",
        builtinVariables: {
          user_id: { label: "用户 ID", description: "目标用户的唯一标识" },
          user_name: { label: "用户名", description: "目标用户的显示名称" },
          user_email: { label: "用户邮箱", description: "目标用户的邮箱地址" },
          execution_id: { label: "执行 ID", description: "当前执行的唯一标识" },
          workflow_id: {
            label: "工作流 ID",
            description: "当前工作流的唯一标识",
          },
          workflow_name: {
            label: "工作流名称",
            description: "当前工作流的名称",
          },
          timestamp: {
            label: "时间戳",
            description: "执行开始时间 (ISO 格式)",
          },
          date: { label: "日期", description: "执行日期 (YYYY-MM-DD)" },
          time: { label: "时间", description: "执行时间 (HH:mm:ss)" },
        },
      },
      // Input Fields Section
      inputFieldsSection: {
        title: "输入字段",
        autoMap: "自动映射",
        noFields: "暂无自定义输入，点击右上角 + 添加",
        usageHint: "使用 {{字段名}} 在下方配置选项中引用",
        variableName: "变量名",
        selectSource: "选择来源...",
        fixedValue: "输入固定值",
        inputFixedValue: "输入固定值...",
        switchToSelect: "选择",
        deleteField: "删除字段",
        extractKey: "提取 key",
        entireObject: "（整个对象）",
        contextVariables: "Context 变量",
        customVariables: "自定义变量 (var)",
        upstreamNode: "上游节点",
        selectableKey: "[可选key]",
        clickToEdit: "点击编辑: {key}",
        keyPathPlaceholder: "如: user_id, preferences.theme",
      },
      // Output Fields Section
      outputFieldsSection: {
        title: "输出字段",
        noFields: "暂无输出字段，点击右上角 + 添加",
        variableName: "变量名",
        deleteField: "删除字段",
        selectValueSource: "选择值来源...",
        noMapping: "不映射",
        schema: "[schema]",
        clickToEdit: "点击编辑: {key}",
      },
      // Field Source Toggle
      fieldSourceToggle: {
        mapped: "已映射",
        fixedValue: "固定值",
        switchToFixed: "切换为固定值",
        fromUpstream: "从上游节点获取",
        selectDataSource: "选择数据来源...",
        context: "Context",
        input: "输入",
        variable: "Variable",
        noAvailableSources: "没有可用的数据源",
        extractKey: "提取 key",
        entireObject: "（整个对象）",
        currentInputVars: "当前输入变量",
        contextVars: "Context 变量",
        customVars: "自定义变量 (var)",
        optionalKey: "[可选key]",
        keyPathPlaceholder: "如: user_id, preferences.theme",
      },
      // Runtime Options Form
      runtimeOptions: {
        title: "运行时选项",
        disableMemoryTool: {
          label: "禁用记忆工具",
          description: "Agent 不会搜索/调用用户记忆",
        },
        disableMemoryWrite: {
          label: "禁用记忆写入",
          description: "对话不会保存到用户记忆",
        },
      },
      // Input Mode Renderer
      inputModeRenderer: {
        generationMethod: "生成方式",
        manual: "手动",
        agent: "Agent",
        llm: "LLM",
        outputFormat: "输出格式",
        loading: "加载中...",
        noConstraint: "无格式约束 (自由输出)",
        manualModeHint: "手动模式: 选择格式后，将显示对应的输入表单",
        agentLlmModeHint: "Agent/LLM 模式: 选择格式后，AI 将按指定结构输出",
        fillByFormat: "按格式填写",
        enterLabel: "输入 {label}...",
        jsonFormat: "输入 {label} (JSON 格式)...",
        variableHint: "支持变量: {{变量名}} 引用输入字段",
        imageDescription: "图像描述",
        imageDescriptionPlaceholder:
          "描述你想要生成的图像...\n例如：一个穿着白裙子的女孩在樱花树下\n支持变量: {{变量名}}",
        customOutputFields: "自定义输出字段",
        customOutputFieldsHint: "(无数据来源，需手动填写)",
        customOutputFieldsTip:
          "提示: 在「输出字段」中为这些字段设置数据来源可跳过手动输入",
        selectAgent: "选择 Agent",
        userIntent: "用户意图",
        userIntentPlaceholder: "告诉 Agent 你想要什么...\n支持变量: {{变量名}}",
        selectLlmModel: "选择 LLM 模型",
        selectModel: "选择模型...",
        temperature: "温度 (Temperature)",
        precise: "精确 (0)",
        balanced: "平衡 (1)",
        creative: "创意 (2)",
        systemPrompt: "系统提示词",
        customized: "已自定义",
        selectTemplate: "选择模版加载或直接编辑",
        systemPromptPlaceholder: "系统提示词...",
        templateTip: "提示: 选择模版后可继续编辑，或直接输入自定义提示词",
        userMessage: "用户消息",
        userMessagePlaceholder: "输入你的请求...\n支持变量: {{变量名}}",
      },
      // Artifact Config Renderer
      artifactConfig: {
        saveToLibrary: "保存到图库",
        imageSource: "图片来源",
        imageSourceDescription: "选择要保存到图库的图片",
        imageSourcePlaceholder: "选择输入变量映射图片",
        category: "分类",
        categoryPlaceholder: "例如：portrait, landscape",
        variableHint: "支持变量: {{变量名}}",
        filename: "文件名",
        filenamePlaceholder: "例如：{{user_name}}_portrait",
        optional: "可选，支持变量",
        saveAsArtifact: "保存为产物类型",
        loading: "加载中...",
        artifactDescription: "产物会自动关联到目标用户，支持轮换和过期管理",
        artifactFieldMapping: "产物字段映射",
        fieldsCount: "{count} 个字段",
        mapImagePath: "映射图片文件路径",
        mapAudioPath: "映射音频文件路径",
        mapVideoPath: "映射视频文件路径",
        mapDocumentPath: "映射文档文件路径",
        mapMediaPath: "映射媒体文件路径",
        enterFieldLabel: "输入 {label}...",
        mappingTip:
          "💡 从上方「输入字段」定义变量，然后在此处选择「当前输入变量」映射",
        artifactTitle: "产物标题",
        artifactTitlePlaceholder: "例如：{{date}} 幸运符",
        artifactDescriptionField: "产物描述",
        artifactDescriptionPlaceholder: "基于你的星盘生成",
      },
      // Push Target Renderer
      pushTarget: {
        notificationTitle: "通知标题",
        variableHint: "支持变量: {{变量名}} 引用输入字段",
        notificationTitlePlaceholder: "通知标题",
        notificationBody: "通知内容",
        notificationBodyPlaceholder: "通知内容...",
        pushTarget: "推送目标",
        followStartConfig: "跟随启动配置",
        followStartConfigDesc: "使用启动项中的「目标用户」设置",
        currentUser: "当前用户",
        currentUserDesc: "推送给工作流执行时的登录用户",
        allUsers: "所有用户",
        allUsersDesc: "推送给所有注册了推送 Token 的用户",
        specificUsers: "指定用户",
        specificUsersDesc: "推送给指定的用户列表",
        userIdList: "用户 ID 列表",
        userIdListPlaceholder:
          "每行一个用户 ID\n例如:\nuser_abc123\nuser_xyz456",
        userIdListHint: "每行一个用户ID，或映射数组类型变量",
        notificationSound: "通知声音",
        default: "默认",
        silent: "静音",
        badgeNumber: "角标数字 (iOS)",
        badgePlaceholder: "留空不更新角标",
        quickConfigTab: "快速配置",
        templateConfigTab: "模板配置",
        tapAction: "点击动作",
        tapActionPlaceholder: "选择点击通知后的跳转动作",
        tapActionNone: "无动作",
        sendMessageContent: "消息内容",
        sendMessageContentHint: "用户点击通知后将自动发送此消息开始新对话。可用 {{字段名}} 引用输入字段，{{relative_time}} 由客户端解析为「今天/昨天/X月X日」",
        sendMessageContentPlaceholder: "输入要发送的消息内容...",
        selectTemplate: "选择模板",
        selectTemplatePlaceholder: "选择通知模板...",
        variableMappings: "变量映射",
        variableMappingHint: "将模板变量映射到上游节点的输出字段",
        notMapped: "未映射",
        preview: "预览",
        previewTitle: "通知预览",
        previewAction: "点击动作",
        previewNoAction: "无",
        templateNotSelected: "请先选择模板",
      },
      // Charm Options Renderer
      charmOptions: {
        requiredFields: "必填项",
        phraseContent: "签文内容",
        phraseHint: "支持变量: {{变量名}} 引用输入字段",
        phrasePlaceholder:
          "输入签文内容，如：今日宜静心思考...\n支持变量替换: {{phrase}}",
        imageGenOptions: "图片生成选项",
        optional: "(可选)",
        emotionTrend: "情绪趋势",
        auto: "自动",
        positive: "积极 (Positive)",
        calm: "平静 (Calm)",
        hopeful: "希望 (Hopeful)",
        grateful: "感恩 (Grateful)",
        concerned: "忧虑 (Concerned)",
        chartId: "星盘 ID",
        chartIdPlaceholder: "留空使用用户默认星盘",
        backgroundTheme: "背景套件",
        loading: "加载中...",
        allThemes: "所有套件 (随机)",
        imagesCount: "{count}张",
        backgroundImage: "背景图",
        random: "随机",
        randomCurrentTheme: "随机 (当前套件)",
        font: "字体",
        defaultFont: "默认字体",
        overlayBadge: "遮罩",
        imagesUnit: "张",
        fontColorLabel: "字色",
        autoColor: "自动",
      },
      // Custom Variables Renderer
      customVariables: {
        title: "自定义变量",
        description: "定义可在后续节点使用的变量",
        addVariable: "添加变量",
        noVariables: "暂无自定义变量，点击上方按钮添加",
        variableName: "变量名",
        variableValue: "变量值",
        usageHint: "在后续节点中使用 {{var.变量名}} 引用自定义变量",
      },
      // Builtin Variables Renderer
      builtinVariables: {
        title: "内置变量",
        selectToEnable: "选择要启用的变量",
        userId: { label: "用户 ID", description: "目标用户的唯一标识" },
        userName: { label: "用户名", description: "目标用户的显示名称" },
        userEmail: { label: "用户邮箱", description: "目标用户的邮箱地址" },
        executionId: { label: "执行 ID", description: "当前执行的唯一标识" },
        workflowId: { label: "工作流 ID", description: "当前工作流的唯一标识" },
        workflowName: { label: "工作流名称", description: "当前工作流的名称" },
        timestamp: { label: "时间戳", description: "执行开始时间 (ISO 格式)" },
        date: { label: "日期", description: "执行日期 (YYYY-MM-DD)" },
        time: { label: "时间", description: "执行时间 (HH:mm:ss)" },
      },
      // Node display labels
      nodes: {
        output: {
          artifact: "产物:",
          storage: "Storage:",
          artifactUser: "Artifact (User)",
          libraryPermanent: "Library (Permanent)",
          temp7days: "Temp (7 days)",
        },
        luckyCharm: {
          font: "字体",
          theme: "套件:",
        },
        prompt: {
          manual: "手动输入",
          agent: "Agent 生成",
          llm: "LLM 生成",
          clickToInput: "点击输入描述文本",
          clickToConfigAgent: "点击配置 Agent 生成",
          clickToConfigLlm: "点击配置 LLM 生成",
          agentLabel: "Agent:",
          modelLabel: "模型:",
        },
        start: {
          builtinVariables: "内置变量:",
          customVariables: "自定义变量:",
          notEnabled: "未启用",
          notDefined: "未定义",
        },
        push: {
          target: "目标:",
          userCount: "用户数:",
          users: "人",
          sound: "声音:",
          receivesPrompt: "← 接收 Prompt 节点输出",
          followStart: "跟随启动",
          currentUser: "当前用户",
          allUsers: "所有用户",
          specificUsers: "指定用户",
          mode: "模式:",
          quickMode: "快速配置",
          templateMode: "模板配置",
          template: "模板:",
        },
        artifactTypes: {
          lucky_charm: "每日幸运符",
          soulmate: "灵魂伴侣",
          soul_echo: "每日回响",
          daily_push: "每日推送",
          natal_report: "占星报告",
          transit_report: "行运报告",
        },
        fieldMapping: {
          artifactField: "产物字段",
          itemField: "批次结果字段",
          artifactFieldPlaceholder: "产物字段名",
          itemFieldPlaceholder: "批次结果字段名",
          hint: "将批次结果字段映射到产物 schema 字段（如 push_title → push_title）",
        },
      },
    },

    // Resources
    resources: {
      pageTitle: "用户资源",
      tabs: {
        artifacts: "产物",
        library: "图库",
        temp: "临时文件",
      },
      types: {
        artifact: "产物",
        library: "图库",
        temp: "临时文件",
      },
      stats: {
        totalFiles: "文件总数",
        totalSize: "总大小",
        uniqueUsers: "用户数",
        byType: "按类型分布",
        bySubtype: "按类型",
        files: "个文件",
        users: "个用户",
        subtypes: "子类型",
        categories: "分类",
        avgPerUser: "人均占用",
        largestUsers: "存储占用最多的用户",
        autoCleanup: "7天后自动清理",
      },
      summary: {
        totalFiles: "总文件数",
        totalSize: "总大小",
      },
      modal: {
        title: "用户资源",
        summary: "资源摘要",
        noResources: "暂无资源",
        loading: "加载中...",
        error: "加载失败",
        deleteWarning: "删除操作不可撤销",
      },
      status: {
        expired: "已过期",
        current: "当前",
      },
      actions: {
        viewResources: "查看资源",
        delete: "删除",
        open: "打开",
        download: "下载",
        confirmDelete: "确认删除",
        cancel: "取消",
        browseTools: "浏览工具",
      },
      groups: {
        uncategorized: "未分类",
        expandAll: "展开全部",
        collapseAll: "折叠全部",
      },
      deleteAll: {
        type: "删除所有{type}",
        all: "删除全部资源",
        confirmType: "确定要删除该用户的所有{type}吗？",
        confirmAll: "确定要删除该用户的所有资源吗？此操作不可撤销。",
      },
      deleting: "删除中...",
      table: {
        userId: "用户ID",
        totalFiles: "文件数",
        totalSize: "大小",
      },
      cleanup: {
        title: "清理操作",
        tempTitle: "清理临时文件",
        tempDesc: "删除超过指定天数的临时文件",
        tempButton: "清理临时文件",
        artifactsTitle: "清理过期产物",
        artifactsDesc: "删除已过期的产物文件及数据库记录",
        artifactsButton: "清理过期产物",
        daysOld: "文件天数",
        days: "天",
        confirm: "确认清理",
        cleaning: "清理中...",
        error: "清理失败",
        successTemp: "已删除 {count} 个临时文件，释放 {size} 空间",
        successArtifacts: "已删除 {count} 个过期产物，释放 {size} 空间",
      },
      lastUpdated: "最后更新",
      errors: {
        loadFailed: "加载失败",
      },
    },

    // Tool Management page
    toolManagement: {
      pageTitle: "工具与MCP",
      pageDescription: "管理MCP服务、工具展示和工具组合配置",
      tabs: {
        mcpServers: "MCP 服务",
        toolEnhancement: "工具展示",
        toolCombo: "工具组合",
        performance: "性能监控",
      },
      perf: {
        title: "工具性能监控",
        description: "实时追踪工具执行性能（自上次重启后）",
        totalCalls: "总调用",
        totalErrors: "总错误",
        trackedTools: "监控工具数",
        slowTools: "慢工具",
        noData: "暂无性能数据",
        noDataHint: "工具被调用后数据将自动收集",
        reset: "重置",
        resetConfirm: "确认重置所有性能统计数据？",
        refresh: "刷新",
        toolName: "工具名称",
        calls: "调用次数",
        errors: "错误数",
        avgMs: "平均耗时",
        maxMs: "最大耗时",
        minMs: "最小耗时",
        errorRate: "错误率",
        lastError: "最近错误",
        slow: "慢",
        sortBy: "排序",
        loadError: "加载性能数据失败",
      },
    },

    // Business Templates page
    businessTemplates: {
      pageTitle: "业务模版",
      pageDescription: "管理Prompt模版、产物类型和输出格式定义",
      tabs: {
        promptTemplates: "Prompt 模版",
        artifactTypes: "产物类型",
        outputPromptTemplates: "提示词模版",
        outputSchemas: "输出格式",
      },
    },

    // Shared: User Selector
    userSelector: {
      searchPlaceholder: "搜索用户名或ID...",
      selectedCount: "已选择 {count} 个用户",
      selectAll: "全选",
      deselectAll: "取消全选",
      clearAll: "清除",
      noUsers: "未找到用户",
      devices: "个设备",
      showingCount: "显示 {from}-{to} 条，共 {total} 条",
    },

    // Shared: Workflow Target Selector
    workflowTarget: {
      currentUser: "当前用户",
      currentUserDesc: "使用创建者/执行者的身份运行",
      specificUsers: "指定用户",
      specificUsersDesc: "选择特定用户作为执行目标",
      willRunAs: "将以此用户身份运行",
    },

    // Feature Flags
    featureFlags: {
      pageTitle: "功能开关",
      pageDescription: "管理功能开关、用户覆盖和查看变更历史",
      createFlag: "创建开关",
      version: "版本",
      tabs: {
        flags: "功能开关",
        userOverrides: "用户覆盖",
        auditLog: "审计日志",
      },
      flagList: {
        noFlags: "暂无功能开关",
        noFlagsHint: "点击「创建开关」添加第一个功能开关",
        rollout: "灰度发布",
        groups: "用户组",
      },
      flagEditor: {
        createTitle: "创建开关",
        editTitle: "编辑开关",
        flagName: "开关名称",
        flagNamePlaceholder: "my_new_feature",
        flagNameError: "开关名称为必填项",
        flagNameFormatError:
          "开关名称必须以字母开头，只能包含字母、数字和下划线",
        flagNameExistsError: "该名称的开关已存在",
        description: "描述",
        descriptionPlaceholder: "简要描述此开关控制的功能...",
        descriptionLengthError: "描述不能超过500个字符",
        enabledByDefault: "默认启用",
        platforms: "平台",
        platformsError: "至少需要选择一个平台",
        rolloutPercentage: "灰度比例",
        rolloutError: "灰度比例必须在 0 到 100 之间",
        rolloutAllUsers: "所有用户都将看到此开关启用",
        rolloutPartialUsers:
          "仅 {percentage}% 的用户将看到此开关启用（使用一致性哈希）",
        targetUserGroups: "目标用户组",
        targetUserGroupsHelp:
          "仅这些用户组中的用户会看到此开关启用。留空表示针对所有用户。",
        addGroupPlaceholder: "例如：beta_testers, vip_users",
        saveChanges: "保存更改",
      },
      deleteConfirm: {
        title: "删除开关",
        message: "确定要删除 {name} 吗？此操作无法撤销。",
        confirm: "删除",
      },
      toggleConfirm: {
        title: "切换开关",
        message: "是否向所有客户端推送通知以立即生效？",
        withNotification: "切换并推送通知",
        withoutNotification: "仅切换",
      },
      userOverrides: {
        title: "用户覆盖",
        description: "为特定用户覆盖功能开关的状态",
        searchPlaceholder: "搜索用户 ID...",
        selectFlag: "选择开关",
        selectFlagPlaceholder: "选择要覆盖的开关...",
        noOverrides: "暂无用户覆盖",
        noOverridesHint: "添加覆盖以为特定用户启用/禁用功能开关",
        addOverride: "添加覆盖",
        addTitle: "添加用户覆盖",
        userId: "用户 ID",
        userIdPlaceholder: "user_123",
        flag: "功能开关",
        overrideValue: "覆盖值",
        enabled: "启用",
        disabled: "禁用",
        deleteConfirm: "确定要删除此用户覆盖吗？",
        clearAllConfirm: "确定要删除用户 {userId} 的所有覆盖吗？",
        override: "个覆盖",
        overridesCount: "{count} 个覆盖",
        errorLoading: "加载覆盖失败",
        userIdRequired: "用户 ID 为必填项",
        selectFlagRequired: "请选择一个功能开关",
        addFailed: "添加覆盖失败",
      },
      auditLog: {
        title: "审计日志",
        description: "查看功能开关的变更历史",
        noLogs: "暂无日志",
        noLogsHint: "功能开关的变更记录将显示在这里",
        action: "操作",
        changedBy: "操作人",
        reason: "原因",
        timestamp: "时间",
        version: "版本",
        page: "第 {page} 页",
        errorLoading: "加载审计日志失败",
        actions: {
          create: "创建",
          update: "更新",
          delete: "删除",
        },
      },
      errors: {
        loadingError: "加载功能开关失败",
      },
    },
    login: {
      title: "管理员登录",
      subtitle: "登录以管理 Agent 配置",
      modePassword: "密码登录",
      modePhone: "手机登录",
      email: "邮箱地址",
      emailPlaceholder: "请输入邮箱",
      password: "密码",
      passwordPlaceholder: "请输入密码",
      phoneNumber: "手机号码",
      phonePlaceholder: "请输入手机号",
      phoneHint: "请输入已注册的管理员手机号",
      signingIn: "登录中...",
      signIn: "登录",
      sendingCode: "发送中...",
      sendCode: "发送验证码",
      verificationTitle: "输入验证码",
      codeSentTo: "验证码已发送至 {phone}",
      resendIn: "{seconds}秒后重新发送",
      resendCode: "重新发送",
      backToPhone: "返回手机号输入",
      enterAllDigits: "请输入完整的6位验证码",
      invalidCode: "验证码无效",
      footer: "受保护区域，仅限授权访问",
    },
    subscriptionPage: {
      pageTitle: "订阅管理",
      pageDescription: "配置订阅、查看分析和管理产品",
      tabs: {
        settings: "设置",
        analytics: "分析",
        products: "产品",
      },
      settings: {
        refresh: "刷新",
        saving: "保存中...",
        saveChanges: "保存更改",
        errorLoading: "加载配置失败",
        savedSuccess: "设置保存成功",
        saveFailed: "保存失败",
        resetChanges: "重置更改",
        paywallSettings: "付费墙设置",
        showOnLaunch: "启动时显示",
        dismissable: "可关闭",
        softWall: "软付费墙（可跳过）",
        delaySeconds: "延迟（秒）",
        trialSettings: "试用设置",
        trialEnabled: "启用试用",
        trialDays: "试用天数",
        featuresByTier: "功能分层",
        featuresByTierHint:
          "仅用于客户端展示，未做实际权限拦截。真正的访问控制由用户角色（role_level）和消息额度决定。",
        freeFeatures: "免费功能（逗号分隔）",
        proFeatures: "专业版功能（逗号分隔）",
        businessFeatures: "商业版功能（逗号分隔）",
        commaSeparated: "逗号分隔",
        smartPaywall: "Smart Paywall",
        smartPaywallLabel: "启用 Smart Paywall（AI 智能引导付费）",
        smartPaywallHardLimitHint: "每日硬上限：免费用户超过后直接拦截（403），次日自动恢复。0 = 关闭硬上限",
        smartPaywallHardMessagesDaily: "每日消息硬上限",
        smartPaywallHardDeepDaily: "每日深度分析硬上限",
        paywallModeTitle: "付费引导模式",
        paywallModeSmart: "智能引导（推荐）",
        paywallModeSmartDesc: "AI 在对话中自然引导付费，免费用户超量才拦截。引导时机由 AI 判断 + 系统兜底",
        paywallModeLegacy: "传统额度",
        paywallModeLegacyDesc: "达到免费消息额度后直接拦截，不做 AI 引导",
        journeyTitle: "免费用户每日旅程（按当前配置实时生成）",
        journeyStart: "开始聊天",
        journeyDeepGuide: "深度分析达 {n} 次 → 弹升级提示",
        journeyFallbackGuide: "消息达 {n} 条 → 保底弹提示",
        journeyDeepBlock: "深度分析达 {n} 次 → 拦截深度功能",
        journeyMsgBlock: "消息达 {n} 条 → 当日停止服务",
        softSection: "第一步 · 软引导（不拦截）",
        softSectionDesc: "达到阈值后向用户弹一次升级提示，用户可关闭继续聊。每人每天最多弹 1 次，新对话前 2 轮不弹（系统内置，无需配置）",
        hardSection: "第二步 · 硬上限（直接拦截）",
        hardSectionDesc: "达到上限后当天无法继续发送，次日零点（用户本地时区）自动恢复。填 0 = 关闭该项拦截",
        freeDeepDailyLabel: "深度分析引导线（次/日）",
        freeDeepDailyHelp: "超过后开始引导升级，不影响使用",
        fallbackDailyMessagesLabel: "保底引导阈值（条/日）",
        fallbackDailyMessagesHelp: "即使 AI 没主动引导，消息数达到后也会弹一次提示。0=关闭保底",
        hardMessagesHelp: "包含所有消息。建议设为保底阈值的 3 倍",
        hardDeepHelp: "仅深度分析消息。建议设为引导线的 3 倍",
        hardBelowGuideWarning: "硬上限低于引导线：用户会在收到升级提示前被直接拦截，体验突兀。建议硬上限 ≥ 引导线",
        generalSection: "通用设置",
        advancedSection: "高级设置（功能开关列表）",
        advancedSectionHint: "控制客户端各功能对免费/Pro 用户的可用性，与付费引导无关。修改前请确认功能 key 与客户端代码一致，配置错误会导致功能整体不可用",
        smartPaywallHint: "开启后，AI 会在合适的时机自然引导用户付费，消息额度限制将不生效。关闭后则使用下方的消息额度限制。两种策略互斥。",
        quotaSettings: "免费额度设置",
        quotaTotalMessages: "总消息限额",
        quotaDailyMessages: "每日消息限额",
        quotaUnlimitedHint: "设为 0 表示不限制",
        quotaDisabledBySmartPaywall: "Smart Paywall 已启用，额度限制不生效",
      },
      analytics: {
        title: "订阅分析",
        refresh: "刷新",
        totalUsers: "总用户数",
        activeSubscribers: "活跃订阅者",
        trialToPaidRate: "试用转付费率",
        churnRate: "流失率",
        totalSubscribers: "总订阅数",
        trialUsers: "试用用户",
        conversionRate: "转化率",
        monthlyRevenue: "月收入",
        loading: "加载中...",
        errorLoading: "加载分析数据失败",
        noData: "暂无数据",
        dateRanges: {
          days7: "7 天",
          days30: "30 天",
          days90: "90 天",
          allTime: "全部时间",
        },
        statusDistribution: "状态分布",
        productDistribution: "产品分布",
        noProductData: "暂无产品数据",
        dailyTrends: "每日趋势",
        date: "日期",
        newTrials: "新试用",
        conversions: "转化",
        newSubscriptions: "新订阅",
        cancellations: "取消",
        recentEvents: "最近事件",
        noRecentEvents: "暂无最近事件",
        eventTypes: {
          trialStarted: "开始试用",
          subscribed: "已订阅",
          renewed: "已续费",
          cancelled: "已取消",
          expired: "已过期",
          admin: "管理员",
        },
        statusLabels: {
          free: "免费",
          trial: "试用",
          active: "活跃",
          expired: "已过期",
          cancelled: "已取消",
        },
      },
      products: {
        title: "产品管理",
        subtitle: "管理订阅产品和商店 ID",
        addProduct: "添加产品",
        editProduct: "编辑产品",
        deleteProduct: "删除产品",
        deleteConfirm: "确定要删除此产品吗？",
        productId: "产品 ID",
        productName: "产品名称",
        description: "描述",
        price: "价格",
        displayPrice: "显示价格",
        interval: "周期",
        noProducts: "暂无产品",
        noProductsHint: "用户购买时会自动创建产品",
        loading: "加载中...",
        errorLoading: "加载产品失败",
        savedSuccess: "产品已更新",
        saveFailed: "更新失败",
        deletedSuccess: "产品已删除",
        deleteFailed: "删除失败",
        refresh: "刷新",
        appleProductId: "Apple 产品 ID",
        googleProductId: "Google 产品 ID",
        sortOrder: "排序",
        available: "可用",
        unavailable: "不可用",
        disableProduct: "禁用产品",
        enableProduct: "启用产品",
        cancel: "取消",
        saving: "保存中...",
        saveChanges: "保存更改",
        name: "名称",
        priceCents: "扣款金额(分)",
        priceYuan: "扣款金额",
        currency: "货币",
        alipaySection: "支付宝 (Android)",
        alipayHint: "改价即时生效，无需发版",
        appleSection: "Apple IAP (iOS)",
        appleHint: "价格由 App Store Connect 管理",
      },
    },

    skillsPage: {
      pageTitle: "技能管理",
      pageDescription: "管理技能，支持拖拽排序",
      enabledCount: "{enabled} / {total} 个技能已启用",
      createSkill: "创建技能",
      showAll: "显示全部",
      showEnabledOnly: "仅显示启用",
      savingOrder: "保存排序中...",
      errorLoading: "加载技能失败",
      loadingSkills: "加载技能中...",
      deleteSkill: "删除技能",
      deleteConfirm: "确定要删除",
      cannotUndo: "此操作不可撤销。",
      translateAll: "一键翻译为英文",
      translating: "正在翻译...",
      translateSuccess: "翻译完成",
      translateError: "翻译失败",
      translations: "翻译",
      noTranslations: "无翻译",
    },

    promptTemplatesPage: {
      pageTitle: "提示词模板",
      pageDescription:
        "管理系统提示词模板。模板定义了 Agent 提示词的结构和组装方式。",
      createTemplate: "创建模板",
      errorLoading: "加载模板失败",
      loadingTemplates: "加载模板中...",
      deleteTemplate: "删除模板",
      deleteConfirm: "确定要删除",
      cannotUndo: "此操作不可撤销。",
    },

    notificationTemplatesPage: {
      pageTitle: "通知模板管理",
      pageDescription: "管理通知模板、配置渠道和内容、发送测试通知",
      tabs: {
        templates: "模板列表",
        sendTest: "发送测试",
      },
      sendTest: {
        title: "发送测试通知",
        description: "选择模板或自定义内容发送测试通知",
        target: "发送目标",
        specifyUser: "指定用户",
        allUsers: "所有用户",
        userIdPlaceholder: "输入用户 ID (UUID)",
        allUsersWarning: "将向所有注册了推送的用户发送通知，请谨慎操作！",
        sendButton: "发送测试通知",
        selectTemplateOrContent: "请选择模板或填写通知内容",
        enterUserId: "请输入目标用户 ID",
        sendSuccess: "发送成功!",
        sendFailed: "发送失败",
        succeeded: "成功",
        failed: "失败",
        unknownError: "未知错误",
      },
      rightPanel: {
        deviceTokens: "设备令牌",
        sendLogs: "发送日志",
      },
      tips: {
        selectTemplate: "选择模板自动应用渠道和内容配置",
        templateVariable: "模板变量 {name} 会被上下文值替换",
        silentPush: "静默推送仅后台更新，无界面显示",
      },
    },

    reportsPage: {
      pageTitle: "运营报表",
      pageDescription: "成本分析与运营洞察",
      tabs: {
        overview: "总览",
        users: "用户成本",
        pricing: "定价分析",
        trial: "试用期风险",
        behavior: "用户行为",
        revenue: '收入分析',
      },
      loading: "加载报表数据...",
      loadFailed: "加载失败",
      fetchFailed: "获取报表数据失败",
      retry: "重试",
      dataUpdatedAt: "数据更新时间",
      dateRange: "日期范围",
      overview: {
        totalUsers: "总用户数",
        activeUsers: "活跃用户",
        totalSessions: "总会话数",
        chatCount: "聊天次数",
        chatCost: "聊天成本",
        dailyServiceCost: "每日服务成本",
        totalCost: "总成本",
        avgUserCost: "平均用户成本",
        median: "中位数",
        memberPrice: "会员价格",
        perMonth: "每月",
        profitableUsers: "盈利用户",
        people: "人",
        ratio: "占比",
        p75Cost: "P75成本",
        p75Subtitle: "75%用户成本",
        p90Cost: "P90成本",
        p90Subtitle: "90%用户成本",
        soulmateStats: "Soulmate 功能统计",
        lifetimeContract: "终身契约",
        freeMatch: "自由配对",
        times: "次",
        cost: "成本",
      },
      users: {
        costTierAnalysis: "用户成本分层分析",
        userDetails: "用户详情 (Top 50)",
        user: "用户",
        phone: "手机号",
        sessionCount: "会话数",
        lifecycle: "生命周期",
        chatCost: "聊天成本",
        serviceCost: "服务成本",
        totalCost: "总成本",
        profitLoss: "盈亏",
        days: "天",
      },
      pricing: {
        scenarioSimulation: "定价方案模拟",
        suggestions: "定价建议",
        tip1: "当前方案毛利率较低,建议优化成本结构或调整定价",
        tip2: "每日服务成本占总成本比例较高,可考虑智能化推送降低成本",
        tip3: "高成本用户群体需要重点关注,避免无限聊天",
        tip4: "建议设置单用户成本上限或会话数限制",
      },
      trial: {
        riskAnalysis: "5天试用期风险分析",
      },
      behavior: {
        posthogNotConfigured: "PostHog 未配置",
        posthogConfigHint: "用户行为分析需要配置 PostHog。请设置以下环境变量：",
        posthogHostHint: "PostHog 服务地址 (默认: https://us.i.posthog.com)",
        mobileConfigHint: "同时在移动端 App 中配置：",
        loadingBehavior: "加载行为数据...",
        fetchFailed: "获取行为数据失败",
        noData: "暂无数据",
        userRetention: "用户留存率",
        // BehaviorAnalysis charts
        topEvents: "Top 10 事件",
        triggerCount: "触发次数",
        uniqueUsers: "独立用户",
        dailyTrend: "每日活跃趋势",
        eventCount: "事件数",
        activeUsers: "活跃用户",
        featureDistribution: "功能使用分布",
        clickCount: "点击次数",
        featureDetails: "功能使用详情",
        featureName: "功能名称",
        avgDuration: "平均时长",
        pageViews: "页面浏览排行",
        viewCount: "浏览次数",
        userJourneyFunnel: "用户旅程漏斗",
        step: "步骤",
        userCount: "用户数",
        conversionRate: "转化率",
        retention: {
          d1: "D1 留存",
          d7: "D7 留存",
          d30: "D30 留存",
          d1Desc: "次日留存率",
          d7Desc: "7日留存率",
          d30Desc: "30日留存率",
        },
        summary: {
          totalEvents: "总事件数",
          activeUsers: "活跃用户",
          eventsPerUser: "人均事件",
          behaviorEvents: "行为事件",
          avgPerUser: "平均每用户",
        },
        // Event name mappings
        eventNames: {
          appOpened: "打开App",
          chatStarted: "开始聊天",
          chatMessageSent: "发送消息",
          featureClicked: "功能点击",
          screenViewed: "页面浏览",
          subscriptionViewed: "查看订阅",
          soulmateStarted: "开始Soulmate",
          pageview: "页面浏览",
          autocapture: "自动捕获",
        },
        // Feature name mappings
        featureNames: {
          soulmate: "Soulmate",
          birthChart: "命盘",
          dailyFortune: "每日运势",
          luckyCharm: "幸运符",
          settings: "设置",
          subscription: "订阅",
        },
      },
      revenue: {
        totalRevenue: '总收入',
        transactions: '交易数',
        payingUsers: '付费用户',
        arpu: 'ARPU',
        dailyRevenue: '每日收入趋势',
        channelDistribution: '渠道分布',
        productDistribution: '产品分布',
        conversionFunnel: '转化漏斗',
        trialConversionTiming: '试用转化天数分布',
        bestConversionDay: '最佳推送日: 第{day}天',
        expiringSoon: '即将到期',
        within3Days: '3天内',
        within7Days: '7天内',
        within14Days: '14天内',
        productMigrations: '产品迁移',
        cohortRetention: '队列留存 (Apple)',
        funnelRegistered: '注册',
        funnelTrialed: '试用',
        funnelPurchased: '付费',
        funnelRenewed: '续费',
        funnelChurned: '流失',
        renewedAppleOnly: '仅Apple',
        churnedAppleOnly: '仅Apple',
        noData: '暂无数据',
        upgrade: '升级',
        downgrade: '降级',
      },
      // DateRangeSelector
      dateRangeSelector: {
        last7Days: "最近7天",
        last30Days: "最近30天",
        last90Days: "最近90天",
        last1Year: "最近1年",
        to: "至",
      },
    },

    messageFeedbackPage: {
      pageTitle: "消息反馈",
      pageDescription: "用户对 AI 回复的点赞/点踩反馈数据",
      like: "点赞",
      dislike: "点踩",
      likeShort: "赞",
      dislikeShort: "踩",
      total: "总计",
      likeRate: "点赞率",
      byAgentStats: "按 Agent 类型统计",
      filter: "筛选",
      allTypes: "全部类型",
      allAgents: "全部 Agent",
      last7Days: "最近 7 天",
      last30Days: "最近 30 天",
      last90Days: "最近 90 天",
      last1Year: "最近 1 年",
      type: "类型",
      user: "用户",
      userMessage: "用户消息",
      aiReply: "AI 回复",
      time: "时间",
      noData: "暂无数据",
      paginationInfo: "共 {total} 条，第 {current} / {pages} 页",
      feedbackDetail: "反馈详情",
      userNote: "用户备注",
      none: "无",
      generatedImage: "生成图片",
      loadingImage: "加载图片中...",
      cannotLoadImage: "无法加载图片",
      contextData: "上下文数据",
      title: "标题",
      mode: "模式",
      generationDate: "生成日期",
      viewRawJson: "查看原始 JSON",
      deviceInfo: "设备信息",
    },

    // Annotation Batches page
    annotationBatchesPage: {
      pageTitle: "标注管理",
      createBatch: "创建批次",
      pageDescription: "共 {count} 个标注批次，管理您的数据标注任务",
      tabs: {
        batchManagement: "批次管理",
        dataExport: "数据导出",
        statsReport: "统计报告",
      },
      status: {
        all: "全部",
        draft: "草稿",
        active: "进行中",
        completed: "已完成",
        archived: "已归档",
      },
      loadFailed: "加载批次失败",
      loadingBatches: "正在加载批次...",
      noBatches: "暂无批次",
      createFirstBatch: "创建您的第一个标注批次开始工作",
      deleteBatch: "删除批次",
      deleteConfirm: "确定要删除批次",
      deleteWarning: "此操作无法撤销，所有相关的标注数据都将被删除。",
      confirmDelete: "确认删除",
      activateFailed: "激活批次失败",
      restoreFailed: "恢复批次失败",
    },

    // Annotation Export page
    annotationExportPage: {
      pageTitle: "数据导出",
      pageDescription: "导出标注数据用于训练或分析",
      previewFailed: "预览数据失败",
      exportSuccess: "导出成功",
      exportFailed: "导出失败",
    },

    // Annotation Stats page
    annotationStatsPage: {
      pageTitle: "统计报告",
      pageDescription: "查看标注任务的统计数据和进度报告",
      loadFailed: "加载统计数据失败",
      loadingStats: "正在加载统计数据...",
      exportFailed: "导出失败",
      exportSuccess: "导出成功",
      totalCases: "共 {count} 条案例",
      andMore: "还有 {count} 条...",
      close: "关闭",
      downloadJson: "下载 JSON",
    },

    // Annotation Workbench page
    annotationWorkbenchPage: {
      pageTitle: "标注工作台",
      pageDescription: "对话数据标注和审核",
      allBatches: "全部批次",
      taskList: "任务列表",
      totalTasks: "共 {count} 个任务",
      pending: "待处理",
      completed: "已完成",
      skipped: "已跳过",
      annotated: "已标注",
      loadFailed: "加载任务失败",
      loadingTasks: "正在加载任务...",
      noTasks: "暂无任务",
      selectBatchHint: "选择一个批次开始标注工作",
      back: "返回",
      sessionCompleted: "本次已完成",
      completedThisSession: "本次已完成",
      batchProgress: "批次进度",
      submitFailed: "提交失败",
      tasksCompleteTitle: "太棒了！任务完成",
      noTasksTitle: "暂无待标注任务",
      completedCountMessage: "本次共完成 {count} 条标注",
      noTasksInBatch: "当前批次中没有可标注的任务",
      returnToBatches: "返回批次列表",
      viewStats: "查看统计",
      pendingLabel: "待标注",
      wordCount: "字数",
      session: "会话",
      labelContent: "标注内容",
      shortcuts: "快捷键",
      positive: "正面",
      neutral: "中性",
      negative: "负面",
      submit: "提交",
      skip: "跳过",
    },

    // Annotation Form component
    annotationForm: {
      steps: {
        messageClassification: "消息分类",
        emotionIntent: "情绪意图",
        skipped: "跳过",
      },
      messageType: {
        label: "消息类型",
        shortcutHint: "快捷键: 1-8",
        simplePrompt: "简单提示词",
        fullAgent: "完整Agent",
      },
      expectedRouting: {
        label: "预期路由",
        autoSetHint: "自动设置，可覆盖",
      },
      noEmotionHint: "此消息类型不需要情绪/意图标注，直接提交即可。",
      emotion: {
        valenceLabel: "情绪倾向",
        valenceShortcut: "快捷键: 1/2/3",
        positive: "正面",
        neutral: "中性",
        negative: "负面",
        specificLabel: "具体情绪",
        optional: "可选",
      },
      intent: {
        label: "用户意图",
        multiSelectHint: "可多选",
      },
      needsSupport: {
        label: "需要情感支持",
        hint: "用户表现出需要安慰/支持的情绪",
      },
      confidence: {
        label: "标注信心",
        high: "高",
        highDesc: "很确定",
        medium: "中",
        mediumDesc: "较确定",
        low: "低",
        lowDesc: "不太确定",
      },
      notes: {
        label: "备注",
        placeholder: "如有特殊情况可在此说明...",
      },
      actions: {
        skip: "跳过",
        skipShortcut: "Esc",
        previous: "上一步",
        continueHint: "Enter 继续",
        submitHint: "⌘S/Ctrl+S 提交",
        next: "下一步",
        submitLabel: "提交标注",
      },
    },

    // Playground page
    playground: {
      modes: {
        generate: "生成",
        edit: "编辑",
        fuse: "融合",
        luckyCharm: "幸运符",
      },
      modeDescriptions: {
        generate: "根据文字描述生成全新的图像",
        edit: "对已有图像进行修改、调整或增强",
        fuse: "将多张图像融合成一张新图像",
        luckyCharm: "生成个性化运势幸运符图片",
      },
      labels: {
        selectEditImage: "选择要编辑的图像",
        selectFuseImages: "选择要融合的图像",
        selected: "已选择",
        uploadOrSelect: "或从右侧历史/图库中选择",
        inputMode: "描述输入方式",
        agent: "Agent",
        llm: "LLM",
        manual: "手动",
        selectAgent: "选择 Agent",
        selectAgentPlaceholder: "选择 Agent...",
        selectLLMModel: "选择 LLM 模型",
        selectModelPlaceholder: "选择模型...",
        loading: "加载中...",
        noAgentsAvailable: "无可用 Agent",
        noModelsAvailable: "无可用模型",
        outputSchema: "输出格式 (Output Schema)",
        noSchemaConstraint: "无格式约束 (自由输出)",
        schemaDescription: "选择格式后，Agent 会按指定的 JSON 结构输出",
        tellAgentWhatYouWant: "告诉 Agent 你想要什么",
        tellLLMWhatYouWant: "告诉 LLM 你想要什么",
        userPromptPlaceholder: "例如：一个穿着白裙子的女孩在樱花树下...",
        temperature: "温度 (Temperature)",
        precise: "精确",
        balanced: "平衡",
        creative: "创意",
        generateDescription: "生成描述",
        generating: "生成中...",
        imageDescription: "图像描述",
        editInstruction: "编辑指令",
        fuseInstruction: "融合指令",
        describeImageToGenerate: "描述你想要生成的图像...",
        describeEditInstruction: "描述你想要如何编辑图像...",
        describeFuseInstruction: "描述你想要如何融合这些图像...",
        processing: "处理中...",
        operationFailed: "操作失败",
        generateFailed: "生成提示词失败",
        required: "必填",
        upload: "上传",
        random: "随机",
        currentPack: "当前套件",
        defaultFont: "默认字体",
        emotionTrend: "情绪趋势",
        emotionPositive: "积极 (Positive)",
        emotionNeutral: "中性 (Neutral)",
        emotionNegative: "消极 (Negative)",
        emotionMixed: "混合 (Mixed)",
        extraPrompt: "额外提示词",
        extraPromptAgentPlaceholder: "给 Agent 的额外提示，例如：突出财运...",
        extraPromptLLMPlaceholder:
          "生成幸运符文案的提示词，例如：生成一句积极正面的运势文案...",
        optional: "可选",
        generateText: "生成文案",
        fortunePhrase: "运势文案",
        fortunePhrasePlaceholder:
          "输入运势文案，例如：今日宜静心思考，贵人运旺盛...",
        clickGenerateOrInput: "点击上方「生成文案」按钮，或直接输入文案...",
        editableAfterGenerate: "生成后可以编辑修改",
        customOptions: "自定义选项",
        chartId: "星盘 ID",
        chartIdPlaceholder: "留空使用用户默认星盘",
        chartIdHint: "提供星盘数据作为生成运势文案的上下文",
        backgroundPack: "背景套件",
        allPacks: "所有套件",
        backgroundImage: "背景图",
        font: "字体",
        systemPrompt: "System Prompt",
        rawLLMHint: "使用原始 LLM API 生成提示词，完全控制系统提示",
        userPromptSent: "实际发送的 User Prompt",
        taskFailed: "任务执行失败",
        pleaseSelectAgent: "请先选择一个 Agent",
        pleaseSelectLLMModel: "请先选择一个 LLM 模型",
        pleaseEnterPrompt: "请输入额外提示词",
        // ModelSelector and DynamicConfigSelector
        model: "模型",
        capabilityGenerate: "生成",
        capabilityEdit: "编辑",
        capabilityFuse: "融合",
        maxImagesForFuse: "最多{count}张",
        resolution: "分辨率",
        quality: "质量",
        supportsWebSearch: "支持网络搜索",
        supportsTransparency: "支持透明背景",
        pricePerImage: "参考价格",
        aspectRatio: "宽高比",
        background: "背景",
        defaultOption: "默认",
        transparent: "透明",
        white: "白色",
        auto: "自动",
        enableWebSearch: "启用网络搜索",
        webSearchDescription: "允许模型搜索网络获取参考素材",
        maxGenerate: "最多生成 {count} 张",
        maxFuse: "最多融合 {count} 张",
        output: "输出",
        aspectRatioSquare: "1:1 (正方形)",
        aspectRatioPortrait: "3:4 (肖像)",
        aspectRatioLandscape: "4:3 (风景)",
        aspectRatioVertical: "9:16 (竖屏)",
        aspectRatioWide: "16:9 (宽屏)",
        aspectRatioTallPortrait: "2:3 (长肖像)",
        aspectRatioTallLandscape: "3:2 (长风景)",
        aspectRatioSocial: "4:5 (社交)",
        aspectRatioUltraWide: "21:9 (超宽)",
        aspectRatioTall: "1:2 (长图)",
        aspectRatioBanner: "2:1 (横幅)",
        qualityStandard: "标准",
        qualityHD: "高清",
        qualityLow: "低",
        qualityMedium: "中",
        qualityHigh: "高",
        qualityAuto: "自动",
        sizeAuto: "自动",
        liveBadge: "实况",
      },
      luckyCharm: {
        config: "幸运符配置",
        inputMode: "生成方式",
      },
      history: {
        title: "历史记录",
        library: "图库",
        refresh: "刷新",
        clear: "清空",
        loading: "加载中...",
        noImages: "还没有生成图像",
        noImagesHint: "生成的图像将显示在这里",
        libraryEmpty: "图库为空",
        libraryEmptyHint: "保存生成的图像到图库进行持久化存储",
        selectForEdit: "选择编辑",
        selectForFuse: "选择融合",
        selected: "已选择",
        deselect: "取消选择",
        view: "查看",
        download: "下载",
        delete: "删除",
        confirmDelete: "确定要删除此历史记录吗？",
        confirmClear: "确定要清空所有历史记录吗？此操作不可撤销。",
        confirmDeleteFromLibrary: "确定要从图库中删除此图像吗？",
        saveToLibrary: "保存到图库",
        savedToLibrary: "已保存到图库",
        saveFailed: "保存失败",
        deleteFailed: "删除失败",
        saved: "已保存",
        saving: "保存中...",
        generating: "生成中...",
        alreadySaved: "已保存到图库",
        editFork: "编辑/分叉",
        imagePreview: "图像预览",
        close: "关闭",
        model: "模型",
        time: "时间",
        source: "来源",
        description: "描述",
        filterAll: "全部",
        filterGenerate: "生成",
        filterEdit: "编辑",
        filterFuse: "融合",
        filterLuckyCharm: "幸运符",
      },
    },

    // Batch Card
    batchCard: {
      status: {
        draft: "草稿",
        active: "进行中",
        completed: "已完成",
        archived: "已归档",
      },
      progress: "进度",
      stats: {
        totalTasks: "总任务",
        completed: "已完成",
        pending: "待处理",
      },
      actions: {
        activate: "激活",
        activateTitle: "激活批次",
        noTasksTitle: "批次中没有任务",
        startAnnotation: "开始标注",
        archive: "归档",
        restore: "恢复",
      },
      metadata: {
        source: "来源",
        created: "创建",
      },
    },

    // Stats Overview
    statsOverview: {
      cards: {
        totalBatches: "总批次",
        activeBatches: "个进行中",
        totalTasks: "总任务",
        pendingTasks: "个待处理",
        completedTasks: "已完成",
        completionRate: "完成率",
        annotators: "标注者",
        avgTime: "平均",
      },
      overallProgress: "整体进度",
      completed: "已完成",
      batchStats: {
        title: "批次统计",
        batch: "批次",
        totalTasks: "总任务",
        completed: "已完成",
        pending: "待处理",
        progress: "进度",
      },
      annotatorStats: {
        title: "标注者统计",
        annotator: "标注者",
        completed: "已完成",
        skipped: "已跳过",
        avgTime: "平均耗时",
        recentActivity: "最近活动",
        noData: "暂无标注数据",
      },
      export: {
        title: "导出测试数据集",
        description: "将已完成的标注结果导出为 emotion-service 测试用例格式",
        button: "导出 Golden Cases",
      },
    },

    // Export Preview
    exportPreview: {
      presets: {
        title: "快速选择采样策略",
        balanced: {
          name: "均衡采样",
          description: "从各类用户和对话中均衡抽取",
        },
        deepConversations: {
          name: "深度对话",
          description: "来自多轮深入交流的消息",
        },
        newUsers: {
          name: "新用户体验",
          description: "新用户的首次交互",
        },
        activeUsers: {
          name: "活跃用户",
          description: "来自高频使用用户的消息",
        },
        emotionalContent: {
          name: "情感表达",
          description: "包含情感关键词的消息",
        },
      },
      filters: {
        title: "筛选条件",
        hasFilters: "已设置",
        reset: "重置筛选",
        expandAdvanced: "展开高级选项",
        collapseAdvanced: "收起高级选项",
        userActivity: "用户活跃度",
        conversationDepth: "对话深度",
        userStage: "用户阶段",
        messagePosition: "消息位置",
        messageLength: "消息长度",
        to: "至",
        characters: "字符",
        loadCount: "加载数量",
        maxCount: "最多500",
        includeKeywords: "包含关键词",
        keywordPlaceholder: "输入关键词...",
        add: "添加",
        excludeTemplates: "排除模板消息",
        randomSample: "随机采样",
        perPage: "每页数量:",
        excludeExported: "只显示未导出的",
        loadMessages: "加载消息",
      },
      userActivityLabels: {
        single: "单次用户",
        light: "轻度用户(2-5次)",
        medium: "中度用户(6-20次)",
        heavy: "重度用户(20+次)",
      },
      conversationDepthLabels: {
        singleTurn: "单轮对话",
        short: "短对话(2-3轮)",
        medium: "中等(4-10轮)",
        deep: "深度对话(10+轮)",
      },
      messagePositionLabels: {
        first: "首轮消息",
        early: "前期(2-3轮)",
        middle: "中期消息",
        last: "末轮消息",
      },
      userStageLabels: {
        new: "新用户(0-1天)",
        recent: "近期(2-7天)",
        active: "活跃(8-30天)",
        veteran: "老用户(30+天)",
      },
      selection: {
        title: "选择消息",
        loaded: "已加载",
        of: "/",
        existing: "条已存在",
        searchPlaceholder: "搜索消息内容...",
        selectAll: "全选",
        selectNonDuplicate: "选择非重复",
        clear: "清除",
        skipDuplicates: "自动跳过已标注的重复消息",
        selected: "已选择:",
        duplicatesSkipped: "条重复将被跳过",
        effectiveExport: "实际导出:",
        chars: "字",
        copyMd5: "复制 MD5",
        alreadyExists: "已存在",
        noMatchingMessages: "未找到匹配的消息",
        noMessages: "没有可用的消息",
      },
      pagination: {
        showing: "显示",
        total: "共",
        excludedExported: "已排除已导出",
        previous: "上一页",
        page: "第",
        next: "下一页",
      },
      createBatch: {
        title: "创建标注批次",
        batchTitle: "批次标题",
        batchTitleRequired: "*",
        batchTitlePlaceholder: "例如: 情绪标注 - 2024年12月",
        description: "描述",
        descriptionOptional: "可选",
        descriptionPlaceholder: "批次描述...",
        create: "创建批次",
        enterTitle: "请输入批次标题",
      },
    },

    // Traces page
    tracesPage: {
      pageTitle: '调用追踪',
      tabList: 'Trace 列表',
      tabMetrics: '指标概览',
      opikConsole: 'Opik 控制台',
      notConfigured: 'Opik 未配置',
      notConfiguredDesc: '请设置 OPIK_API_KEY 和 OPIK_WORKSPACE 环境变量以启用 trace 查看器。',
      loading: '加载中...',
      loadFailed: '加载失败',
      searchPlaceholder: '搜索 trace 名称...',
      search: '搜索',
      colName: '名称',
      colTime: '时间',
      colDuration: '耗时',
      colTokens: 'Tokens',
      colCost: '费用',
      colSpans: 'Spans',
      noData: '暂无 trace 数据',
      pagination: '共 {total} 条，第 {page}/{pages} 页',
      loadingSpans: '加载 spans...',
      noSpans: '无 span 数据',
      showingSpans: '显示 {shown}/{total} 个 span',
      spanInput: '输入:',
      spanOutput: '输出:',
      tokenPrompt: 'Prompt:',
      tokenCompletion: 'Completion:',
      tokenTotal: '合计:',
      toolCallLabel: '工具调用',
      reasoningLabel: '推理过程',
      emptyContent: '空',
      emptyResponse: '空响应',
      showRawJson: '显示原始 JSON',
      hideRawJson: '隐藏原始 JSON',
      sectionInput: '输入',
      sectionOutput: '输出',
      sectionSpans: 'Spans ({count})',
      sectionMetadata: '元数据',
      tokens: 'tokens',
      errorLabel: '错误',
      spanCount: '{count} 个 span',
      noModel: '未知',
      viewTimeline: '时间线',
      viewTree: '树状图',
      totalDuration: '总耗时: {duration}',
      selectSpan: '点击 span 查看详情',
      spanDetail: 'Span 详情',
      metricsTitle: '项目指标概览',
      noStats: '暂无统计数据',
      statTraceCount: 'Trace 总数',
      statDuration: '耗时 P50 / P90',
      statCost: '累计费用',
      statTokens: '累计 Tokens',
      statErrorSub: '错误: {count}',
      statAvgCost: '均 ${cost}/trace',
      statAvgTokens: '均 {count}/trace',
      errorBanner: '{count} 个 trace 包含错误',
      chartTraceCount: '每日 Trace 数量',
      chartDuration: '每日耗时分布',
      chartTokenUsage: '每日 Token 用量',
      tooltipTraces: 'Traces',
      tooltipPrompt: 'Prompt',
      tooltipCompletion: 'Completion',
      dataSource: '数据来源: Opik (Comet) — 更详细的图表请访问 Opik 控制台',
      filterChatOnly: '会话请求',
      filterAll: '全部 Trace',
    },
    schedules: {
      timezoneWindow: {
        enable: '按用户时区分批执行',
        targetHour: '目标时段',
        defaultTimezone: '默认时区',
        hint: '将每小时触发一次，仅处理当前本地时间在目标时段内的用户',
        cronHint: '建议 Cron 改为 "0 * * * *"（每小时触发）',
      },
    },
    wishesPage: {
      pageTitle: '愿望池管理',
      pageDescription: '管理用户愿望群组 · 状态更新 · 通知派发',
      allStatus: '全部',
      statusOpen: '开放',
      statusPlanned: '已规划',
      statusInProgress: '进行中',
      statusDone: '已完成',
      statusRejected: '已拒绝',
      wishCount: '愿望数',
      totalClusters: '共 {total} 个群组',
      selectCluster: '选择一个群组查看详情',
      noData: '暂无愿望群组',
      loading: '加载中...',
      save: '保存',
      title: '标题',
      summary: '摘要',
      status: '状态',
      statusNote: '状态说明',
      representativeExcerpt: '代表性摘录',
      representativeWishes: '代表愿望',
      notifyUsers: '通知用户',
      confirmNotify: '确认通知用户',
      confirmNotifyDesc: '将向所有订阅此愿望群组的用户发送通知，并将状态标记为已完成。确定继续？',
      mergeInto: '合并到...',
      mergeTarget: '目标群组',
      confirmMerge: '确认合并',
      mergeCurrentDesc: '当前群组将被合并到目标群组，所有愿望转移，当前群组删除。',
      cancel: '取消',
      language: '语言',
      notifiedAt: '通知时间',
      notNotified: '未通知',
      deleteWish: '删除',
    },
    financePage: {
      pageTitle: "财务",
      pageDescription: "出资、支出与收入损益（数据源：飞书费用明细，只读）",
      tabs: {
        overview: "概览",
        expenses: "支出明细",
        contributions: "出资记录",
      },
      revenue: "收入",
      expense: "支出",
      net: "净额",
      committed: "认缴总额",
      received: "实缴总额",
      outstanding: "待实缴",
      outstandingHint: "已认缴未付款",
      totalExpense: "总支出",
      totalRevenue: "总收入",
      capitalPool: "资金池余额",
      capitalPoolHint: "实缴 − 支出（真实现金）",
      monthlyBurn: "月燃烧率",
      runway: "可支撑月数",
      breakeven: "已盈亏平衡",
      perMonth: "月",
      months: "月",
      monthlyPnl: "月度损益",
      cashflow: "累计现金流（资金池走势）",
      byChannel: "收入渠道",
      accounts: "各方账户",
      account: "账户",
      contributed: "收到出资",
      spent: "支出",
      balance: "余额",
      byCategory: "支出分类",
      date: "日期",
      category: "项目",
      amount: "金额",
      contributor: "出资人",
      round: "轮次",
      note: "备注",
      lastSync: "上次同步",
      syncNow: "立即同步",
      syncing: "同步中…",
      reconWarn: "同步数据与飞书申报口径存在偏差，请核对飞书原表",
    },
  },

  // ========== English ==========
  en: {
    common: {
      appTitle: "Agent Admin",
      save: "Save",
      saving: "Saving...",
      cancel: "Cancel",
      delete: "Delete",
      reset: "Reset",
      loading: "Loading...",
      success: "Success",
      error: "Error",
      warning: "Warning",
      required: "Required",
      previous: "Previous",
      next: "Next",
      refresh: "Refresh",
      close: "Close",
      description: "Description",
      retry: "Retry",
      clear: "Clear",
      admin: "Admin",
    },
    actions: {
      saveChanges: "Save Changes",
      resetToDefault: "Reset to Default",
      resetToSystemDefault: "Reset to System Default",
      browseTools: "Browse Tools",
    },
    navigation: {
      appTitle: "Dora Admin",
      // Menu groups
      navGroupAgent: "Agent Core",
      navGroupWorkflow: "Workflows",
      navGroupAnalytics: "Analytics",
      navGroupUser: "User & Business",
      navGroupSystem: "System",
      navGroupContent: "Content",
      navGroupDev: "Dev & Test",
      navGroupFinance: "Finance",
      // Menu items
      agentConfigs: "Agent Configs",
      financeDashboard: "Dashboard",
      systemSettings: "System Settings",
      toolManagement: "Tools & MCP",
      businessTemplates: "Business Templates",
      featureFlags: "Feature Flags",
      audit: "Audit",
      subscriptionSettings: "Subscription",
      promptTemplates: "Prompt Templates",
      skills: "Skill Bar Config",
      monitoring: "Monitoring",
      reports: "Reports",
      classificationAnalytics: "Classification Analytics",
      messageFeedback: "Message Feedback",
      users: "Users",
      pushTest: "Push Test",
      notificationTemplates: "Notification Templates",
      evaluation: "Evaluation",
      playground: "Playground",
      imageWorkflows: "Workflow Editor",
      workflowSchedules: "Schedules",
      contentPacks: "Content Packs",
      welcomeTitles: "Welcome Titles",
      petTips: "Tips",
      uiAssets: "UI Assets",
      annotation: "Annotation",
      traces: 'Prompt Traces',
      performance: 'Performance',
      userGrowth: 'User Growth',
      wishes: 'Wish Pool',
      logout: "Logout",
      expandSidebar: "Expand sidebar",
      collapseSidebar: "Collapse sidebar",
    },
    sections: {
      basic: "Basic Information",
      models: "Model Configuration",
      models_chat: "Chat Model",
      models_reasoning: "Reasoning",
      models_vision: "Vision Model",
      models_memory: "Memory Model",
      models_classification: "Classification Model",
      models_retrieval: "Embedding & Retrieval",
      models_stt: "Speech Model",
      llm_parameters: "LLM Sampling Parameters",
      output_format: "Output Format",
      features: "Features",
      message_processing: "Message Processing",
      advanced: "Advanced Settings",
      memory: "Memory Configuration",
      memory_prompts: "Memory Custom Prompts",
      memory_user: "User Memory",
      memory_agent: "Agent Memory",
      memory_query: "Memory Query Settings",
      memory_ebbinghaus: "Memory Decay (Ebbinghaus)",
      session_summary: "Session Summary",
      custom_tools: "Custom Tools",
      builtin_tools: "Builtin Tools",
      mcp_tools: "MCP Servers",
      tools_execution: "Tool Execution",
      tools_compression: "Tool Results Compression",
      team: "Team Configuration",
      quick_response: "Quick Response",
      prompts: "System Prompts",
      first_message: "First Message Hook",
      response_classifier: "Matching Parameters",
      response_classifier_prompts: "Response Type Instructions",
    },
    sectionDescriptions: {
      quick_response: "The system automatically classifies user messages: brief greetings and confirmations use \"Quick Response\", while complex questions use \"Deep Response\". Classification starts with rule matching (<10ms); if confidence is low, an LLM fallback is used. When classified as quick, both the model and prompt below are switched for faster, more natural replies.",
    },
    fields: {
      // Basic Information
      agent_type: {
        label: "Agent Type",
        help: "Unique identifier for the agent",
      },
      name: {
        label: "Name",
        help: "Display name for the agent",
        placeholder: "Enter agent name",
      },
      description: {
        label: "Description",
        help: "Brief description of this agent",
        placeholder: "Enter description",
      },

      // Model Configuration
      chat_model_id: {
        label: "Chat Model",
        help: "LLM model used for conversation",
        invalidMessage:
          'Warning: Model ID "{value}" is not in the available list',
      },
      memory_model_id: {
        label: "Memory Model",
        help: "Model used for memory extraction",
        invalidMessage:
          'Warning: Model ID "{value}" is not in the available list',
      },
      embedding_model_id: {
        label: "Embedding Model",
        help: "Model used for text embeddings",
        invalidMessage:
          'Warning: Model ID "{value}" is not in the available list',
      },

      // LLM Sampling Parameters
      temperature: {
        label: "Temperature",
        help: "Sampling temperature (0=deterministic, higher=random). Range: 0.0-2.0",
      },
      top_p: {
        label: "Top P",
        help: "Nucleus sampling probability (0.0-1.0). Lower uses fewer tokens",
      },
      max_tokens: {
        label: "Max Tokens",
        help: "Maximum tokens to generate (leave empty for unlimited). Range: 1-128000",
        placeholder: "Leave empty for unlimited",
      },
      presence_penalty: {
        label: "Presence Penalty",
        help: "Token presence penalty (-2.0 to 2.0). Positive encourages new topics",
      },
      frequency_penalty: {
        label: "Frequency Penalty",
        help: "Token frequency penalty (-2.0 to 2.0). Positive reduces repetition",
      },

      // Features
      output_format: {
        label: "Output Format",
        help: "Response output format: default (plain text), markdown, or JSON",
        options: {
          default: "Default",
          markdown: "Markdown",
          json: "JSON",
        },
      },
      debug_mode: {
        label: "Debug Mode",
        help: "Enable verbose logging",
      },
      storage_enabled: {
        label: "Storage Enabled",
        help: "Enable session storage",
      },
      knowledge_enabled: {
        label: "Knowledge Base",
        help: "Enable knowledge base retrieval",
      },
      enable_birth_chart_context: {
        label: "Birth Chart Context",
        help: "Automatically inject user's birth chart data (XML format) into agent context. Requires user to have added their birth chart.",
      },
      enable_web_search: {
        label: "Web Search",
        help: "Enable web search functionality",
      },

      // Message Processing
      "context.datetime_enabled": {
        label: "Add DateTime",
        help: "Include current date and time in system prompt",
      },
      add_history_to_context: {
        label: "Add History",
        help: "Include conversation history in agent context (Agno: add_history_to_context)",
      },
      num_history_runs: {
        label: "Max History Runs",
        help: "How many recent conversation runs to include (0-50). Mutually exclusive with 'Max History Messages': when messages is set, this is ignored (Agno: num_history_runs)",
      },
      num_history_messages: {
        label: "Max History Messages",
        help: "Cap history by total message count instead of runs. Session summary's 'First Threshold' and 'Regenerate Interval' auto-sync to this value (all three equal, per Agno's seamless-coverage rule). Leave empty to use runs mode (Agno: num_history_messages)",
      },
      max_tool_calls_from_history: {
        label: "Max Tool Calls in History",
        help: "Max tool call results to keep in history. Tool results are often large and consume significant context. Leave empty for unlimited (Agno: max_tool_calls_from_history)",
      },
      read_chat_history: {
        label: "Read Chat History",
        help: "Allow agent to query full chat history via tools (Agno: read_chat_history)",
      },

      // Memory Configuration
      "memory.enabled": {
        label: "Enable Memory",
        help: "Enable long-term memory storage",
      },
      "memory.provider": {
        label: "Memory Provider",
        help: "Choose memory storage backend",
        options: {
          MEM0: "MEM0",
          POSTGRES: "PostgreSQL",
          NONE: "None",
        },
        invalidMessage:
          'Warning: Provider "{value}" is not valid. Expected: MEM0, POSTGRES, or NONE',
      },
      "memory.model_id": {
        label: "Memory Model",
        help: "Model used for memory extraction and operations",
        invalidMessage:
          'Warning: Model ID "{value}" is not in the available memory models list',
      },
      "memory.enable_agentic_memory": {
        label: "Agentic Memory",
        help: "Enable agentic memory management - adds memory control tools to the agent. For MEM0: adds search/add/update/delete memory tools. For PostgreSQL: adds memory search/save tools. When enabled, the agent can actively manage its own memories.",
      },
      "memory.operation_mode": {
        label: "Mem0 Operation Mode",
        help: "Select memory operation mode",
        options: {
          SYNC: "Sync (Default - Wait for confirmation)",
          FIRE_FORGET: "Fire & Forget (Fast - No wait)",
          BATCH: "Batch (Queue for bulk processing)",
        },
      },
      "memory.loading_strategy": {
        label: "Memory Loading Strategy",
        help: "Select memory loading strategy",
        options: {
          balanced: "Balanced (Default - 3 pref, 3 facts, 2 plans, 2 recent)",
          preference_first:
            "Preference-First (5 pref, 2 critical facts, 1 recent)",
          context_heavy: "Context-Heavy (2 key pref, 3 critical, 5 episodic)",
          minimal: "Minimal (3 critical only, 2 episodic)",
          time_aware: "Time-Aware (3 plans, 2 pref, 3 recent facts)",
        },
      },

      // Memory Custom Prompts
      "memory.prompts.enable_custom_prompts": {
        label: "Enable Custom Prompts",
        help: "Enable Mem0 custom prompts feature for this agent",
      },
      "memory.prompts.user_memory_extraction_mode": {
        label: "User Memory Extraction Mode",
        help: "Choose to use system default or custom prompt",
        options: {
          system: "Use System Default",
          custom: "Use Custom Prompt",
        },
      },
      "memory.prompts.user_memory_extraction_prompt": {
        label: "User Memory Extraction Prompt",
        help: 'Custom prompt for extracting memories from USER messages. Click "Reset to System Default" to load the default value.',
        placeholder:
          "Enter custom user memory extraction prompt (50-4000 characters)...",
      },
      "memory.prompts.agent_memory_extraction_mode": {
        label: "Agent Memory Extraction Mode",
        help: "Choose to use system default or custom prompt",
        options: {
          system: "Use System Default",
          custom: "Use Custom Prompt",
        },
      },
      "memory.prompts.agent_memory_extraction_prompt": {
        label: "Agent Memory Extraction Prompt",
        help: 'Custom prompt for extracting memories from AGENT messages. Click "Reset to System Default" to load the default value.',
        placeholder:
          "Enter custom agent memory extraction prompt (50-4000 characters)...",
      },
      "memory.prompts.memory_update_mode": {
        label: "Memory Update Mode",
        help: "Choose to use system default or custom prompt",
        options: {
          system: "Use System Default",
          custom: "Use Custom Prompt",
        },
      },
      "memory.prompts.memory_update_prompt": {
        label: "Memory Update Prompt",
        help: 'Custom prompt for updating and merging memories. Click "Reset to System Default" to load the default value.',
        placeholder:
          "Enter custom memory update prompt (50-4000 characters)...",
      },

      // Memory Categories
      "memory.categories.enable_user_custom_categories": {
        label: "Enable User Custom Categories",
        help: "Enable custom categories for user memories",
      },
      "memory.categories.user_categories_mode": {
        label: "User Categories Mode",
        help: "Choose to use system default or custom categories",
        options: {
          system: "Use System Default",
          custom: "Use Custom Categories",
        },
      },
      "memory.categories.user_custom_categories": {
        label: "User Custom Categories",
        help: 'Define custom USER memory categories in JSON format. Each category must have: description (string), examples (array), temporal (boolean). Click "Reset to System Default" to load the default categories.',
        placeholder:
          '{"preference": {"description": "User preferences", "examples": ["Likes pizza"], "temporal": false}}',
      },
      "memory.categories.enable_agent_custom_categories": {
        label: "Enable Agent Custom Categories",
        help: "Enable custom categories for agent memories",
      },
      "memory.categories.agent_categories_mode": {
        label: "Agent Categories Mode",
        help: "Choose to use system default or custom categories",
        options: {
          system: "Use System Default",
          custom: "Use Custom Categories",
        },
      },
      "memory.categories.agent_custom_categories": {
        label: "Agent Custom Categories",
        help: 'Define custom AGENT memory categories in JSON format. Each category must have: description (string), examples (array), temporal (boolean). Click "Reset to System Default" to load the default categories.',
        placeholder:
          '{"recommendation": {"description": "Recommendations", "examples": ["Exercise daily"], "temporal": false}}',
      },

      // Memory Query Settings
      "memory.query.rerank_enabled": {
        label: "Enable Reranking",
        help: "Enable reranking of memory query results",
      },
      "memory.query.top_k": {
        label: "Top K",
        help: "Maximum number of memories to return",
      },
      "memory.query.score_threshold": {
        label: "Score Threshold",
        help: "Minimum relevance score threshold for memories (0.0-1.0)",
      },

      // Memory Decay (Ebbinghaus)
      "memory.ebbinghaus.enabled": {
        label: "Enable Memory Decay",
        help: "Enable Ebbinghaus forgetting curve for temporal memories. Memories decay over time based on access frequency.",
      },
      "memory.ebbinghaus.decay_factor": {
        label: "Decay Factor",
        help: "Controls decay speed (0.1=fast forget, 1.0=slow forget). Higher values mean slower memory decay.",
      },
      "memory.ebbinghaus.min_retention": {
        label: "Min Retention",
        help: "Minimum retention floor (0.0-1.0). Memories never decay below this value. Default 0.1 means memories retain at least 10% relevance.",
      },
      "memory.ebbinghaus.initial_strength": {
        label: "Initial Strength",
        help: "Starting memory strength for new memories (0.5-2.0). Higher values make new memories more resistant to decay.",
      },
      "memory.ebbinghaus.reinforcement_boost": {
        label: "Reinforcement Boost",
        help: "Strength increase when memory is accessed (0.1-1.0). Higher values strengthen memories more on each access.",
      },
      "memory.ebbinghaus.max_strength": {
        label: "Max Strength",
        help: "Maximum memory strength cap (1.0-5.0). Limits how strong a memory can become through reinforcement.",
      },

      // Session Summary Configuration
      "session_summary.enabled": {
        label: "Enable Session Summary",
        help: "Automatically generate summaries for long conversations. Summaries compress early conversation content and inject into system prompts, helping agents maintain awareness of the full conversation.",
      },
      "session_summary.model_id": {
        label: "Summary Model",
        help: "Model used for summary generation. Recommend using a fast, cost-effective model (e.g., gpt-5-mini) since summary generation is a background task.",
      },
      "session_summary.summary_frequency": {
        label: "Generation Timing",
        help: "When to generate summaries:\n• On Request: Only via API manual trigger\n• After N Messages: Auto-generate after threshold (recommended)\n• End of Session: Generate when session ends",
        options: {
          on_request: "On Request",
          after_n_messages: "After N Messages (Recommended)",
          end_of_session: "End of Session",
        },
      },
      "session_summary.message_threshold": {
        label: "First Generation Threshold",
        help: "Number of messages before generating the first summary.\n\n💡 Recommended: Set equal to 'num_history_runs'.\n\nExample: history_runs=5, threshold=5\n• Messages 1-5: Covered by history (no summary needed)\n• Messages 6+: Summary covers [1, N-5], history covers [N-4, N]\n\nThis ensures seamless coverage with no information gaps.",
      },
      "session_summary.regenerate_interval": {
        label: "Regenerate Interval",
        help: "After first generation, regenerate summary every N new messages.\n• 0: Never regenerate (use initial summary forever)\n• N > 0: Update summary every N new messages\n\n💡 Recommended: Set equal to 'num_history_runs' for rolling coverage.",
      },
      "session_summary.inject_into_prompt": {
        label: "Inject into Prompt",
        help: "Whether to inject the summary as context into system prompts. When enabled, agents can perceive key points from earlier conversation when responding.",
      },
      "session_summary.custom_prompt": {
        label: "Custom Summary Prompt",
        help: "Custom prompt template for summary generation. Leave empty to use Agno framework's default prompt.\n\nAvailable variables: {messages} (conversation content)",
      },

      // Advanced Settings
      session_timeout_minutes: {
        label: "Session Timeout (minutes)",
        help: "Automatic session timeout duration",
      },
      max_cached_resources: {
        label: "Max Cached Resources",
        help: "Maximum number of cached resources",
      },
      resource_ttl_minutes: {
        label: "Resource TTL (minutes)",
        help: "Cache validity time for resources",
      },

      // Tools
      custom_tools: {
        label: "Custom Tools",
        help: "Custom tool configuration - can be a module name (string), list of modules (array), or detailed config (object)",
        placeholder:
          '{"modules": ["tools.astrology.astro_tools"], "enabled_tools": ["calculate_natal_chart"]}',
      },
      builtin_tools: {
        label: "Builtin Tools",
        help: "List of enabled builtin tools",
      },
      mcp_servers: {
        label: "MCP Servers",
        help: "MCP server configuration - can be a server ID (string), list of IDs (array), or detailed config (object)",
        placeholder:
          '{"servers": ["astro-tool"], "enabled_tools": ["mcp__astro_tool__get_birth_chart"]}',
      },

      // System Prompts
      instructions: {
        label: "System Instructions",
        help: "Define the agent's role, behavior, and capabilities",
        placeholder: "Enter system instructions...",
      },
      additional_context: {
        label: "Additional Context",
        help: "Extra context, examples, or constraints",
        placeholder: "Enter additional context...",
      },
      enable_first_message_instruction: {
        label: "Enable First Message Instruction",
        help: "Enable special instruction for the first message in a new topic/session",
      },
      first_message_mode: {
        label: "Instruction Mode",
        help: "Choose to use system default or custom instruction",
        options: {
          system: "Use System Default",
          custom: "Use Custom Instruction",
        },
      },
      first_message_custom_instruction: {
        label: "Custom First Message Instruction",
        help: 'Custom instruction for first message. Click "Reset to System Default" to load default value.',
        placeholder: "Enter custom instruction...",
      },

      // Response Classifier
      "response_classifier.enabled": {
        label: "Enable Classification Engine",
        help: "Classify response type (quick/deep) and match skills automatically.",
      },
      "response_classifier.model_id": {
        label: "Fallback Classification Model",
        help: "Model used for LLM-based classification when rule confidence is below threshold. Use a fast, cost-effective model.",
      },
      "response_classifier.rule_confidence_threshold": {
        label: "Rule Confidence Threshold",
        help: "Confidence threshold for rule-based classification. Below this value, LLM classification is used. Range: 0.0-1.0, default: 0.8",
      },
      "response_classifier.quick_response_max_length": {
        label: "Quick Response Max Length",
        help: "Maximum message length (characters) for quick response classification. Longer messages tend toward deep responses.",
      },
      "response_classifier.llm_timeout": {
        label: "LLM Timeout (seconds)",
        help: "Timeout for LLM classification in seconds. If exceeded, defaults to deep response.",
      },

      // Response Type Instructions
      "response_classifier.quick_response_instructions": {
        label: "Quick Response Prompt",
        help: "When classified as quick response, this prompt replaces the system prompt. This only affects the prompt — the quick response model is configured separately in Model Configuration > Quick Chat Model. Leave empty to keep the original.",
        placeholder:
          "You are a friendly assistant. Please respond to greetings or simple questions in a concise, natural way. Keep the conversation light and pleasant.",
      },
      "response_classifier.deep_response_instructions": {
        label: "Deep Response Instructions",
        help: "Instructions used for deep responses (analysis, consultation, emotional support). If empty, default instructions will be used instead.",
        placeholder:
          "You are a professional consultant. Please carefully analyze the user's question, providing in-depth, professional insights and suggestions. Consider multiple angles and provide valuable analysis.",
      },

      // ========== Hook Configuration Fields ==========

      // Memory Query Settings (hooks.user_facts, hooks.memory_context - unified)
      "hooks.user_facts.facts_limit": {
        label: "Facts Query Limit",
        help: "Maximum number of permanent user facts to retrieve (1-100)",
      },
      "hooks.memory_context.events_limit": {
        label: "Events Query Limit",
        help: "Maximum number of temporal event memories to retrieve (1-50)",
      },
      "hooks.memory_context.events_threshold": {
        label: "Events Search Threshold",
        help: "Similarity threshold for event search (0.0-1.0). Lower values return more results.",
      },
      "hooks.memory_context.agent_memories_limit": {
        label: "Agent Memories Limit",
        help: "Maximum number of agent-specific memories to retrieve (1-20)",
      },
      "hooks.memory_context.agent_memories_threshold": {
        label: "Agent Memories Threshold",
        help: "Similarity threshold for agent memory search (0.0-1.0). Lower values return more results.",
      },
      "hooks.memory_context.query_max_length": {
        label: "Query Max Length",
        help: "Maximum total length of memory search query in characters (100-1000)",
      },
      "hooks.memory_context.user_query_length": {
        label: "User Query Length",
        help: "Max length of current user question in search query (50-500 chars)",
      },
      "hooks.memory_context.assistant_context_length": {
        label: "Assistant Context Length",
        help: "Max length of last assistant response in search query (0-500 chars). Set to 0 to disable assistant context.",
      },

      // Memory Decay / Ebbinghaus (hooks.memory_reinforce)
      "hooks.memory_reinforce.enabled": {
        label: "Enable Memory Decay",
        help: "Enable Ebbinghaus forgetting curve for temporal memories. Memories decay over time based on access frequency.",
      },
      "hooks.memory_reinforce.decay_factor": {
        label: "Decay Factor",
        help: "Controls decay speed (1.0=fast forget, 30.0=slow forget). Higher values mean slower memory decay.",
      },
      "hooks.memory_reinforce.min_retention": {
        label: "Min Retention",
        help: "Minimum retention floor (0.0-0.5). Memories never decay below this value. Default 0.1 means memories retain at least 10% relevance.",
      },
      "hooks.memory_reinforce.initial_strength": {
        label: "Initial Strength",
        help: "Starting memory strength for new memories (0.5-2.0). Higher values make new memories more resistant to decay.",
      },
      "hooks.memory_reinforce.reinforcement_boost": {
        label: "Reinforcement Boost",
        help: "Strength increase when memory is accessed (0.1-1.0). Higher values strengthen memories more on each access.",
      },
      "hooks.memory_reinforce.max_strength": {
        label: "Max Strength",
        help: "Maximum memory strength cap (1.0-5.0). Limits how strong a memory can become through reinforcement.",
      },

      // First Message Hook (hooks.first_message)
      "hooks.first_message.enabled": {
        label: "Enable First Message Hook",
        help: "Add special instruction for first message in new sessions",
      },
      "hooks.first_message.mode": {
        label: "First Message Mode",
        help: "Use system default or custom instruction",
        options: {
          system: "Use System Default",
          custom: "Use Custom Instruction",
        },
      },
      "hooks.first_message.custom_instruction": {
        label: "Custom First Message Instruction",
        help: "Custom instruction shown only in first message of new sessions",
        placeholder: "Enter custom instruction for first message...",
      },

      // Response Classifier Hook (hooks.classifier)
      "hooks.classifier.enabled": {
        label: "Enable Classification Engine",
        help: "Classify response type (quick/deep) and match skills automatically.",
      },
      "hooks.classifier.use_system_instructions": {
        label: "Use System Prompt",
        help: "When checked, quick responses will use the main system prompt instead of a separate quick response prompt.",
      },
      "hooks.classifier.quick_response_instructions": {
        label: "Quick Response Prompt",
        help: "When classified as quick response, this prompt replaces the system prompt to guide concise replies. This only affects the prompt content — the quick response model is configured separately in Model Configuration > Quick Chat Model. Leave empty to keep the original system prompt.",
        placeholder:
          "You are a friendly assistant. Please respond briefly and naturally to user greetings or simple questions. Maintain a light and pleasant conversation atmosphere.",
      },
      "hooks.classifier.model_id": {
        label: "Fallback Classification Model",
        help: "Model used for LLM-based classification when rule confidence is below threshold. Use a fast, cost-effective model.",
      },
      "hooks.classifier.rule_confidence_threshold": {
        label: "Rule Confidence Threshold",
        help: "Confidence threshold for rule-based classification. Below this value, LLM classification is used. Range: 0.0-1.0, default: 0.8",
      },
      "hooks.classifier.quick_response_max_length": {
        label: "Quick Response Max Length",
        help: "Maximum message length (characters) for quick response classification. Longer messages tend toward deep responses.",
      },
      "hooks.classifier.llm_timeout": {
        label: "LLM Timeout (seconds)",
        help: "Timeout for LLM classification in seconds. If exceeded, defaults to deep response.",
      },
      "hooks.classifier.deep_response_instructions": {
        label: "Deep Response Instructions",
        help: "Instructions used for deep responses (analysis, consultation, emotional support). If empty, default instructions will be used.",
        placeholder:
          "You are a professional consultant. Please carefully analyze the user's question, providing in-depth, professional insights and suggestions. Consider multiple angles and provide valuable analysis.",
      },
    },
    pushNotifications: {
      // Page
      pageTitle: "Push Notifications",

      // Stats
      stats: {
        totalTokens: "Total Tokens",
        activeTokens: "Active Tokens",
        pushSent: "Pushes Sent",
        successRate: "Success Rate",
        platformBreakdown: "Platform Breakdown",
        ios: "iOS",
        android: "Android",
      },

      // Send Notification Form
      sendForm: {
        title: "Send Push Notification",
        description:
          "Send a push notification to test devices or specific users",
        notificationTitle: "Notification Title",
        notificationTitlePlaceholder: "Enter notification title",
        body: "Notification Body",
        bodyPlaceholder: "Enter notification content",
        target: "Target",
        targetAll: "All Users",
        targetAllWarning:
          "This will send notifications to all registered users.",
        targetTest: "Test Devices",
        targetTestDesc: "Send to devices with test tokens",
        targetSpecificUsers: "Specific Users",
        targetSpecificUsersDesc: "Send to specific user IDs",
        userIds: "User IDs",
        userIdsPlaceholder: "Enter user IDs, one per line",
        userIdsHint: "Enter one user ID per line",
        sound: "Sound",
        soundPlaceholder: "default",
        soundHint: "Sound played when notification arrives",
        soundDefault: "Default Sound",
        soundNone: "Silent",
        badgeCount: "Badge Count",
        badgeCountPlaceholder: "",
        badgeCountHint: "App icon badge number",
        badgeCountHintFull: "0 = Clear badge, empty = No change",
        customData: "Custom Data (JSON)",
        customDataPlaceholder: '{"type": "action", "action": "profile"}',
        customDataHint: "Action triggered when notification is tapped",
        dataTemplates: "Templates",
        dataTemplateLabels: {
          navigation: "Home",
          profile: "Profile",
          feedback: "Feedback",
          birthChart: "Birth Chart",
          fortune: "Fortune",
        },
        clearButton: "Clear",
        sendButton: "Send Notification",
        sending: "Sending...",
        successMessage: "Notification sent successfully",
        sent: "Sent",
        failed: "Failed",
        errors: "Errors",
      },

      // Device Tokens
      tokens: {
        tabTitle: "Device Tokens",
        title: "Registered Devices",
        allPlatforms: "All Platforms",
        refresh: "Refresh",
        loading: "Loading...",
        noTokens: "No device tokens found",
        user: "User",
        registered: "Registered",
        active: "Active",
        inactive: "Inactive",
        page: "Page",
        of: "of",
        total: "total",
        previous: "Previous",
        next: "Next",
      },

      // Notification Logs
      logs: {
        tabTitle: "Push History",
        title: "Recent Pushes",
        allStatuses: "All Statuses",
        statusSent: "Sent",
        statusFailed: "Failed",
        refresh: "Refresh",
        loading: "Loading...",
        noLogs: "No push history found",
        error: "Error",
        page: "Page",
        of: "of",
        total: "total",
        previous: "Previous",
        next: "Next",
        // Detail view fields
        user: "User",
        userId: "User ID",
        displayName: "Display Name",
        platform: "Platform",
        deviceInfo: "Device Info",
        sentAt: "Sent At",
        pushToken: "Push Token",
        fullTitle: "Full Title",
        fullBody: "Full Body",
        dataPayload: "Custom Data",
        actionNavigation: "Navigation",
        actionType: "Action",
        actionFortune: "Fortune",
        // Grouped view fields
        recipients: "recipient(s)",
        batches: "batches",
        totalRecipients: "Total Recipients",
        recipientList: "Recipient List",
      },

      // Time formatting
      time: {
        justNow: "Just now",
        minutesAgo: "{n} minutes ago",
        hoursAgo: "{n} hours ago",
        daysAgo: "{n} days ago",
      },

      // Validation errors
      validation: {
        titleRequired: "Title is required",
        bodyRequired: "Body is required",
        userIdsRequired: "At least one user ID is required",
        invalidJson: "Invalid JSON format",
      },

      // User Selector
      userSelector: {
        searchPlaceholder: "Search by name, phone, email, or ID...",
        selectedCount: "{count} users selected",
        selectAll: "Select All",
        deselectAll: "Deselect All",
        clearAll: "Clear",
        noUsers: "No users found",
        devices: "device(s)",
        showingCount: "Showing {from}-{to} of {total}",
      },

      // Content Assist (AI-powered content generation)
      contentAssist: {
        sectionTitle: "Push Content",
        inputModeLabel: "Content Input Mode",
        agentMode: "Agent",
        llmMode: "LLM",
        manualMode: "Manual",
        selectAgent: "Select Agent",
        loading: "Loading...",
        noAgents: "No agents available",
        descriptionLabel: "Describe push content",
        descriptionPlaceholder: "e.g., Remind users to check daily horoscope",
        generateButton: "Generate Content",
        generating: "Generating...",
        selectAgentFirst: "Please select an agent first",
        generateFailed: "Content generation failed",
        // LLM mode specific
        selectLLMModel: "Select LLM Model",
        noModels: "No models available",
        llmModeDescription:
          "Use raw LLM API with custom system prompt for full control",
        temperature: "Temperature",
        precise: "Precise (0)",
        balanced: "Balanced (1)",
        creative: "Creative (2)",
        selectModelFirst: "Please select an LLM model first",
      },
    },
    evalScenarios: {
      // Page
      pageTitle: "Evaluation Scenarios",
      pageDescription:
        "Manage test scenarios and run agent evaluations to ensure quality",

      // Actions
      createScenario: "Create Scenario",
      promoteChat: "Promote Chat",
      refresh: "Refresh",
      runAll: "Run All",
      runSingle: "Run Test",
      running: "Running...",
      edit: "Edit",
      delete: "Delete",
      duplicate: "Duplicate",
      duplicateSuccess: "Scenario duplicated successfully",
      expandDetails: "Show details",
      collapseDetails: "Collapse",
      removeTag: "Remove tag",
      filterByTags: "Tags",
      addTagFilter: "Add tag",
      clearTags: "Clear",
      exportScenarios: "Export",
      importScenarios: "Import",
      exportSuccess: "Exported {count} scenarios",
      importSuccess: "Import complete: {created} created, {skipped} skipped",
      importFailed: "Import failed",
      importInvalidFile: "Invalid file format, please select a JSON file",

      // Filters
      filters: "Filters",
      searchPlaceholder: "Search scenarios...",
      allAgentTypes: "All Agent Types",
      allCategories: "All Categories",
      allSources: "All Sources",
      allStatus: "All Status",
      sourceManual: "Manual",
      sourceChatPromoted: "Chat Promoted",
      statusActive: "Active",
      statusInactive: "Inactive",

      // Stats
      totalScenarios: "Total Scenarios",
      active: "Active",
      manual: "Manual",
      chatPromoted: "Chat Promoted",
      neverRun: "Never run",

      // Form Steps
      step1Title: "Basic Information",
      step1Description:
        "Set scenario name, select target agent, and categorize",
      step2Title: "Test Conversation",
      step2Description:
        "Define the conversation to test (user messages and expected flow)",
      step3Title: "Evaluation Config",
      step3Description: "Choose LLM judge model and evaluation metrics",
      stepOf: "Step {current} of {total}",
      next: "Next",
      previous: "Previous",

      // Form Fields - Step 1
      scenarioName: "Scenario Name",
      scenarioNamePlaceholder: "e.g., sagittarius_career_advice",
      scenarioNameHint: "Use lowercase with underscores (snake_case)",
      description: "Description",
      descriptionPlaceholder:
        "What does this scenario test? e.g., Test if agent provides helpful career advice for Sagittarius users",
      targetAgent: "Target Agent",
      targetAgentHint: "Select the agent to run this test against",
      category: "Category",
      categoryPlaceholder: "e.g., astrology, career",
      priority: "Priority",
      priorityHint: "Higher = run first",
      tags: "Tags",
      addTagPlaceholder: "Add tag and press Enter...",

      // Form Fields - Step 2
      testConversation: "Test Conversation",
      conversationHint:
        "Define the test conversation. At minimum, add a user message to send to the agent. You can add multiple turns to simulate a multi-turn conversation.",
      addTurn: "Add Turn",
      userRole: "User",
      assistantRole: "Assistant",
      userMessagePlaceholder: "User message to test...",
      assistantMessagePlaceholder:
        "Expected assistant response (for context)...",
      expectedResponse: "Expected Response",
      expectedResponsePlaceholder:
        "Write an ideal Assistant response example, e.g.:\n\nSure! Considering you prefer quiet places, here are some hidden gems:\n1. Qingcheng Back Mountain - peaceful, ¥60 entrance\n2. Jiezi Ancient Town - free, ~2 hours drive\n\nWhich type do you prefer?",
      expectedResponseHint:
        "Write an ideal response text, not evaluation rules",
      expectedResponseDetailTitle: "What is Expected Response?",
      expectedResponseDetail1:
        '✅ Should write: A specific ideal response example ("How Agent should respond")',
      expectedResponseDetail2:
        '❌ Do NOT write: Evaluation rules (that\'s what "Criteria" is for)',
      expectedResponseDetail3:
        "Purpose: Used for AnswerRelevancy, Correctness metrics for semantic similarity comparison",
      expectedResponseExample:
        "If using GEval custom metrics, this field can be left empty - evaluation relies mainly on Criteria",

      // Form Fields - Step 3
      llmJudgeModel: "LLM Judge Model",
      llmJudgeModelHint:
        "Recommend: gpt-4o-mini for cost-effective evaluation, gpt-4o for higher accuracy",
      llmJudgeModelWarning:
        "⚠️ This model may not be supported by DeepEval. Please use OpenAI GPT models (e.g., gpt-4o-mini, gpt-4o) to ensure evaluations run correctly.",
      quickAddMetrics: "Quick Add Metrics",
      evaluationCriteria: "Evaluation Criteria",
      customMetric: "Custom Metric",
      metricNamePlaceholder: "Metric name",
      threshold: "Threshold",
      noMetricsSelected:
        'No metrics selected. Click "Quick Add" buttons above or add custom metrics.',
      minOverallScore: "Minimum Overall Score to Pass",
      minOverallScoreHint:
        "Scenario passes if weighted average of enabled metrics exceeds this threshold",
      activeCheckbox: "Active (scenario will be included in batch evaluations)",

      // Metrics
      metrics: {
        hallucination: "Hallucination Detection",
        hallucinationDesc: "Detect factual errors",
        hallucinationHelp:
          "Detects if AI fabricates non-existent information. Lower is better (0=no hallucination, 1=severe). Threshold 0.3 means score must be below 30% to pass.",
        relevancy: "Response Relevancy",
        relevancyDesc: "Check if response is relevant",
        relevancyHelp:
          "Evaluates how relevant the response is to the user question. Higher is better (0=irrelevant, 1=highly relevant). Threshold 0.7 means 70%+ relevancy required.",
        faithfulness: "Faithfulness",
        faithfulnessDesc: "Check fidelity to source",
        faithfulnessHelp:
          'Checks if response stays faithful to provided context without adding extra information. Higher is better. Requires "Expected Response" as reference.',
        answer_relevancy: "Answer Relevancy",
        answer_relevancyDesc: "Check answer quality",
        answer_relevancyHelp:
          "Comprehensive evaluation of answer quality: directness, completeness, and clarity. Higher is better.",
        contextual_relevancy: "Contextual Relevancy",
        contextual_relevancyDesc: "Check context usage",
        contextual_relevancyHelp:
          "Evaluates if AI correctly uses conversation history and context. Best for multi-turn conversations.",
        bias: "Bias Detection",
        biasDesc: "Detect biased content",
        biasHelp:
          "Detects gender, racial, age or other biases in the response. Lower is better (0=no bias).",
        toxicity: "Toxicity Detection",
        toxicityDesc: "Detect toxic content",
        toxicityHelp:
          "Detects offensive, insulting or harmful content. Lower is better (0=safe). Threshold 0.1 means toxicity must be below 10%.",
        astro_accuracy: "Astrological Accuracy",
        astro_accuracyDesc: "Check astrology terminology accuracy",
        astro_accuracyHelp:
          "Evaluates accuracy of astrological content: zodiac identification, planet positions, house interpretation, aspect analysis.",
        interpretation_quality: "Interpretation Quality",
        interpretation_qualityDesc: "Evaluate depth and insight",
        interpretation_qualityHelp:
          "Evaluates astrology reading quality: depth and insight, personal relevance, professional vs accessible balance, actionable guidance.",
        helpfulness: "Helpfulness",
        helpfulnessDesc: "Evaluate how helpful the response is",
        helpfulnessHelp:
          "Evaluates practical helpfulness: does it solve user problem, is info practical and actionable, is tone appropriate.",
      },
      metricsGuide: "Metrics Guide",
      metricsGuideContent:
        "Metrics fall into two categories: 1) Lower is better (hallucination, bias, toxicity) - threshold is max acceptable score; 2) Higher is better (relevancy, faithfulness, etc.) - threshold is minimum required. All scores range 0-1.",
      lowerIsBetter: "↓ Lower is better",
      higherIsBetter: "↑ Higher is better",

      // Dialogs
      createDialogTitle: "Create Evaluation Scenario",
      editDialogTitle: "Edit Evaluation Scenario",
      createButton: "Create Scenario",
      saveChanges: "Save Changes",

      // Promote Chat Dialog
      promoteChatTitle: "Promote Chat to Scenario",
      promoteChatDescription:
        "Convert a real chat conversation into a test scenario. The conversation's messages will be extracted and used as the test conversation.",
      chatTopicId: "Chat/Topic ID",
      chatTopicIdPlaceholder: "Enter chat or topic ID",
      chatTopicIdHint: "Find this in the chat history or database",
      promoteButton: "Promote to Scenario",

      // Run Evaluation Dialog
      runEvaluationTitle: "Run Evaluation",
      runningEvaluation: "Running evaluation...",
      runningHint:
        "You can close this dialog, the evaluation will continue in the background",
      runningHintResult:
        "Results will appear in the evaluation history when complete",
      evaluationStarted: "Evaluation Started",
      evaluationStartedHint:
        "Evaluation is running in the background, you can close this dialog",
      checkRunsPanel: 'Open "Run Records" to view real-time progress',
      evaluationComplete: "Evaluation Complete",
      overallScore: "Overall Score",
      passed: "Passed",
      failed: "Failed",
      response: "Response",
      responseTime: "Response Time",
      metricResults: "Metric Results",
      reason: "Reason",
      scenario: "Scenario",
      agentType: "Agent Type",
      judgerModel: "Judger Model",
      criteriaLabel: "Criteria",
      batchSummary: "Batch Summary",
      total: "Total",
      close: "Close",
      runEvaluationButton: "Run Evaluation",
      batchInfoPrefix: "This will run evaluations for",
      batchInfoSuffix: "active scenario(s).",

      // User Picker
      userPicker: {
        title: "Evaluation User",
        useCurrentUser: "Use current user",
        currentUserActive: "Current user ✓",
        currentUserLabel: "(current logged-in user)",
        clearSelection: "Clear selection",
        searchPlaceholder: "Search by username, nickname, phone, email, or name...",
        noUserSelected: "No user selected",
        noUserSelectedHint: "Will use default test user (no birth chart data), Agent may ask for birth info",
        loading: "Loading...",
        noMatch: "No matching users",
        testBadge: "Test",
        hasChart: "Chart",
        noChart: "No chart",
        hasChartTooltip: "Has birth chart",
        noChartTooltip: "No birth chart",
        showingCount: "Showing {shown} / {total} users",
        runId: "Run ID",
        scenarios: "Scenarios",
        user: "User",
      },

      // Response Language
      responseLanguage: {
        title: "Response Language",
        hint: "Require the evaluation agent to respond in the specified language",
        auto: "Auto (follow UI language)",
        zh: "中文",
        en: "English",
      },

      // Results
      lastResult: "Last Result",
      noResults: "No results yet",

      // Confirmations
      deleteConfirm: "Are you sure you want to delete this scenario?",

      // Success/Error messages
      createSuccess: "Scenario created successfully",
      updateSuccess: "Scenario updated successfully",
      deleteSuccess: "Scenario deleted successfully",
      promoteSuccess: "Chat promoted to scenario successfully",
      runSuccess: "Evaluation completed",
      error: "Operation failed",

      // History Panel
      historyPanel: {
        title: "Evaluation History",
        scoreTrend: "Score Trend",
        recentRuns: "Recent {n} runs",
        noHistory: "No evaluation records",
        checkRunsPanelHint:
          'If you just started an evaluation, click the "Run Records" button at the top to check progress',
        expandDetails: "Expand details",
        collapseDetails: "Collapse details",
        agentResponse: "Agent Response",
        responseTime: "Response Time",
        executedAt: "Executed At",
        executedBy: "Executed By",
        metrics: "Metrics",
        resultsList: "Evaluation Records",
        running: "Running...",
        failed: "Execution Failed",
      },

      // Result Detail Dialog
      resultDetail: {
        title: "Evaluation Result Details",
        viewDetails: "View Details",
        overallScore: "Overall Score",
        metricsTitle: "Metric Details",
        judgmentReason: "LLM Judgment Reason",
        executedAt: "Executed At",
        model: "Model",
        responseTime: "Response Time",
        executedBy: "Executed By",
        runningHint:
          "Evaluation is in progress, please refresh later to see results",
        testConversation: "Test Conversation",
        expectedResponse: "Expected Response",
      },

      // Runs Panel
      runsPanel: {
        title: "Evaluation Runs",
        runId: "Run ID",
        totalScenarios: "scenarios",
        passed: "Passed",
        failed: "Failed",
        skipped: "Skipped",
        averageScore: "Average",
        duration: "Duration",
        viewDetails: "View Details",
        noRuns: "No run records",
        noResults: "No results available",
        autoRefreshing: "Auto-refreshing...",
        statusRunning: "Running",
        statusCompleted: "Completed",
        statusFailed: "Failed",
      },

      // Results Panel
      resultsPanel: {
        title: "Evaluation Results",
        loading: "Loading results...",
        noResults: "No results yet",
        noResultsHint: "Run an evaluation to see results here",
        averageScore: "Average Score",
        totalRuns: "Total Runs",
        scenarioName: "Scenario Name",
        description: "Description",
        testInput: "Test Input",
        noInput: "No input",
        history: "Evaluation History",
        detailedMetrics: "Detailed Metrics",
        agentResponse: "Agent Response",
        evaluationNotes: "Evaluation Notes",
      },

      // Common texts
      optional: "Optional",
      noDescription: "No description",
      createFirstScenario: "Create your first scenario to get started",
      input: "Input",

      // Time formatting
      time: {
        justNow: "Just now",
        minutesAgo: "{n} minutes ago",
        hoursAgo: "{n} hours ago",
        daysAgo: "{n} days ago",
      },

      // Metric Definitions
      metricDefinitions: {
        pageTitle: "Metric Definitions",
        pageDescription:
          "Manage evaluation metric definitions, view built-in prompts, and customize GEval metrics",
        createMetric: "Create Custom Metric",
        builtinMetrics: "Built-in Metrics",
        customMetrics: "Custom Metrics (GEval)",
        viewPrompt: "View",
        editMetric: "Edit",
        deleteMetric: "Delete",
        metricName: "Metric Name",
        displayName: "Display Name",
        metricType: "Metric Type",
        defaultThreshold: "Default Threshold",
        thresholdDirection: "Threshold Direction",
        higherBetter: "Higher is better (≥)",
        lowerBetter: "Lower is better (≤)",
        applicableAgents: "Applicable Agents",
        allAgents: "All",
        systemMetric: "System Metric",
        customGeval: "Custom GEval",
        criteria: "Evaluation Criteria",
        criteriaPlaceholder: "Describe how to evaluate response quality...",
        criteriaHint:
          "Criteria is passed to the LLM model to judge response quality. Be specific for consistent results. Use bullet points.",
        evaluationSteps: "Evaluation Steps (Advanced)",
        evaluationStepsHint:
          "Define specific evaluation steps for more precise control. Overrides Criteria when set.",
        addStep: "Add Step",
        removeStep: "Remove",
        noMetrics: "No custom metrics yet",
        builtinPromptNote:
          "This is a DeepEval built-in metric. The prompt is maintained by the framework and cannot be modified. To customize evaluation logic, create a new GEval custom metric.",
        promptViewer: {
          title: "View Metric Prompt",
          type: "Type",
          source: "Source",
          threshold: "Default Threshold",
          prompt: "Evaluation Prompt",
          readOnlyNote: "This prompt is read-only and cannot be edited",
        },
        editor: {
          createTitle: "Create Custom Metric",
          editTitle: "Edit Metric",
          nameHint: "Unique identifier, use lowercase letters and underscores",
          displayNameHint: "Name displayed in the UI",
          descriptionHint: "Describe what this metric evaluates",
          // Evaluation Steps
          evaluationStepsTitle: "Evaluation Steps (Advanced)",
          optional: "Optional",
          stepsVsCriteriaTitle: "Evaluation Steps vs Criteria",
          stepsExplanation1:
            "Criteria: Descriptive evaluation requirements, suitable for most scenarios",
          stepsExplanation2:
            "Steps: Step-by-step instructions, LLM will execute each step in order",
          stepsExplanation3:
            "If steps are set, they will override the criteria",
          exampleTitle: "Example - Astrological Accuracy Steps:",
          exampleStep1:
            "Check if zodiac signs and planet positions match user birth chart data",
          exampleStep2:
            "Verify aspect interpretations follow astrological principles",
          exampleStep3:
            "Evaluate the professionalism and accuracy of terminology",
          exampleStep4:
            "Give a comprehensive score of 1-5 based on the above checks",
          yourSteps: "Your Evaluation Steps",
          noStepsYet: "No evaluation steps yet, click the button below to add",
          stepPlaceholder: "Describe what to check or evaluate in this step...",
          addStep: "Add Step",
          removeStep: "Remove",
          // AI Generation
          aiAssistantTitle: "AI Smart Design",
          aiAssistantDescription:
            "Describe what you want to evaluate, AI will generate metric configuration based on DeepEval best practices",
          aiDescriptionPlaceholder:
            "e.g., Evaluate the accuracy of astrological interpretations, including zodiac positions, planetary aspects, house interpretations...",
          generating: "Generating...",
          generateMetric: "Generate Metric",
          minChars: "At least 10 characters",
          chars: "characters",
          aiExplanation: "AI Design Explanation",
          aiDescriptionTooShort:
            "Please provide a more detailed description (at least 10 characters)",
          aiGenerationFailed: "AI generation failed. Please try again.",
          selectModel: "Select Model",
          selectModelPlaceholder: "-- Select a model --",
          selectModelFirst: "Please select a model first",
          // Validation
          nameRequired: "Name is required",
          displayNameRequired: "Display name is required",
          criteriaOrStepsRequired: "Either criteria or evaluation steps is required",
          saveFailed: "Failed to save metric: ",
          namePlaceholder: "e.g., custom_accuracy",
          displayNamePlaceholder: "e.g., Custom Accuracy",
          descriptionPlaceholder: "Describe what this metric evaluates...",
          allAgentsHint: "Leave empty for all agents",
        },
        deleteConfirm:
          "Are you sure you want to delete this metric? This action cannot be undone.",
        deleteSystemError: "System metrics cannot be deleted",
        createSuccess: "Metric created successfully",
        updateSuccess: "Metric updated successfully",
        deleteSuccess: "Metric deleted successfully",
      },

      // Test Suites
      testSuites: {
        // Selection
        selectForSuite: "Select for Suite",
        exitSelect: "Exit Selection",
        createSuite: "Create Suite",
        selectAll: "Select all",
        deselectAll: "Deselect all",
        selected: "selected",

        // Dialog
        createTitle: "Create Test Suite",
        editTitle: "Edit Test Suite",
        name: "Name",
        namePlaceholder: "Enter suite name",
        description: "Description",
        descriptionPlaceholder: "Enter description (optional)",
        tags: "Tags",
        tagsPlaceholder: "Type and press Enter to add",
        priority: "Priority",
        scenarioCount: "Scenarios included",
        scenarios: "scenarios",
        cancel: "Cancel",
        create: "Create",
        save: "Save",

        // Run
        selectSuite: "Select Test Suite",
        runAllActive: "Run all active scenarios",
        runSuite: "Run Test Suite",
        runSuiteInfo: "Run",

        // List
        noSuites: "No Test Suites",
        noSuitesHint:
          "Select scenarios from the Eval Scenarios page to create a Suite",
        active: "Active",
        inactive: "Inactive",
        runningTitle: "Running...",
        running: "Running",
        run: "Run",

        // Panel (TestSuitesPanel)
        panel: {
          runHistory: "Run History",
          refresh: "Refresh",
          totalSuites: "Total Suites",
          totalScenarios: "Total Scenarios",
          howToCreate: "How to create a Suite?",
          howToCreateHint:
            'Go to the "Scenarios" tab, click "Select for Suite" button, select scenarios and create.',
          loading: "Loading...",
          deleteConfirm: 'Are you sure you want to delete "{name}"?',
          containsScenarios: 'Suite "{name}" contains {count} scenarios',
        },
      },
    },
    evaluation: {
      pageTitle: "Evaluation Center",
      pageDescription:
        "Manage evaluation scenarios, metric definitions, and test suites",
      tabs: {
        scenarios: "Scenarios",
        metrics: "Metrics",
        suites: "Test Suites",
      },
    },
    monitoring: {
      // Page
      pageTitle: "Service Monitoring",
      pageDescription: "Real-time health and performance metrics",
      lastUpdated: "Last updated",

      // Tabs
      tabs: {
        services: "Services",
        cache: "Cache",
        storage: "Storage",
        queue: "Extraction Queue",
        mcp: "MCP Connections",
      },

      // Queue
      queue: {
        title: "Extraction Queue",
        status: "Queue Status",
        running: "Running",
        stopped: "Stopped",
        queueSize: "Queue Size",
        processed: "Processed",
        success: "Success",
        failed: "Failed",
        merged: "Merged",
        savedCalls: "Saved Calls",
        mergeRate: "Merge Rate",
        costSaving: "Cost Saving",
        deadLetter: "Dead Letter Queue",
        deadLetterDesc:
          "Failed tasks after retries are moved to the dead letter queue for manual retry or cleanup",
        retryAll: "Retry All",
        clearAll: "Clear Queue",
        retry: "Retry",
        noTasks: "No failed tasks",
        taskId: "Task ID",
        userId: "User ID",
        memoryType: "Type",
        error: "Error",
        failedAt: "Failed At",
        retryCount: "Retries",
        confirmClear:
          "Are you sure you want to clear the dead letter queue? This action cannot be undone.",
        confirmRetryAll: "Are you sure you want to retry all failed tasks?",
        clearSuccess: "Cleared {count} tasks",
        retrySuccess: "Retried {count} tasks",
        resetStats: "Reset Stats",
        confirmReset: "Are you sure you want to reset queue statistics?",
        resetSuccess: "Statistics have been reset",
      },

      // Service Health
      serviceHealth: {
        title: "Service Health Status",
        services: "Services",
        healthy: "Healthy",
        unhealthy: "Unhealthy",
        error: "Error",
        unknown: "Unknown",
        allOk: "All OK",
        issues: "Issues",
        overallStatus: "Overall Status",
        latency: "Latency",
        openService: "Open Service",
      },

      // Cache Performance
      cache: {
        title: "Cache Performance",
        hitRate: "Hit Rate",
        hits: "Hits",
        misses: "Misses",
        avgLatency: "Avg Latency",
        totalRequests: "total requests",
        successfulRetrievals: "Successful cache retrievals",
        fetchFailures: "Cache fetch failures",
        avgResponseTime: "Average response time",
        unavailable: "Cache data unavailable",
      },

      // Charts
      charts: {
        cacheHitRate: "Cache Hit Rate",
        hitRateLabel: "hit rate",
        avgLatency: "Average Latency",
        total: "Total",
        cacheLatency: "Cache",
        backend: "Backend",
        requestDistribution: "Request Distribution",
        noData: "No data",
      },

      // Cache Details
      cacheDetails: {
        title: "Cache Performance",
        totalRequests: "Total Requests",
        cachedKeys: "Cached Keys",
        cacheSize: "Cache Size",
        errors: "Errors",
        redisConnection: "Redis Connection",
        teiBackend: "TEI Backend",
        connected: "Connected",
        disconnected: "Disconnected",
        healthy: "Healthy",
        unhealthy: "Unhealthy",
        clear: "Clear",
        clearConfirm:
          "Clear all cached embeddings? This action cannot be undone.",
        clearSuccess: "Cleared {count} cached embeddings",
        clearError: "Failed to clear cache",
      },

      // Refresh Control
      refresh: {
        autoRefresh: "Auto-refresh",
        interval: "Interval",
        manual: "Manual",
        refreshButton: "Refresh",
        refreshing: "Refreshing...",
      },

      // Errors
      errors: {
        loadFailed: "Failed to load monitoring data",
        tryAgain: "Try again",
      },
    },

    // User Growth
    userGrowth: {
      pageTitle: "User Growth",
      pageDescription: "User registration, activity, and retention trends",
      kpi: {
        todayNewUsers: "New Users Today",
        todayActive: "Active Today",
        todayActiveSuffix: "total",
        newUsers7d: "New Users (7d)",
        d1Retention: "D1 Retention",
      },
      range: {
        days7: "7d",
        days14: "14d",
        days30: "30d",
        days90: "90d",
        startDate: "Start date",
        endDate: "End date",
        invalidRange: "Invalid range: start must not be after end, and the span must not exceed 180 days",
      },
      trend: {
        title: "Growth Trend",
        newUsersIos: "New (iOS)",
        newUsersAndroid: "New (Android)",
        newUsersUnknown: "New (Unknown)",
        activeRegular: "Active (Real Users)",
        activeTeam: "Active (Team)",
      },
      cumulative: {
        title: "Cumulative User Growth",
        regular: "Cumulative Real Users",
        total: "Cumulative All Users",
      },
      table: {
        title: "Daily Active Detail",
        excludeTeam: "Exclude Team",
        colDate: "Date",
        colUser: "User",
        colClassification: "Classification",
        colRegistered: "Registered",
        colPlatform: "Platform",
        colSessions: "Sessions",
        colRuns: "Runs",
        newBadge: "New",
        empty: "No data",
      },
      classification: {
        admin: "Admin",
        internalTester: "Internal Tester",
        regularUser: "Regular User",
      },
      funnel: {
        title: "New User Funnel",
        registered: "Registered",
        createdSelfChart: "Created Self Chart",
        firstConversation: "First Conversation",
        trialStarted: "Trial Started",
        paid: "Paid",
        medianHoursToChart: "Median activation time (register -> chart)",
        hoursUnit: "h",
      },
      retention: {
        title: "Retention",
        d7: "D7 Retention",
        d30: "D30 Retention",
        dauMau: "DAU/MAU Stickiness",
        cohortTitle: "Weekly Cohort Retention",
        weekPrefix: "W",
        cohortSizeSuffix: " users",
      },
      loading: "Loading...",
      error: "Failed to load user growth data",
    },

    // Classification Analytics
    classificationAnalytics: {
      pageTitle: "Classification Analytics",
      pageDescription:
        "Skill matching and Response Type classification performance analysis",
      lastUpdated: "Last updated",

      tabs: {
        skillExecution: "Skill Execution",
        classification: "Response Classification",
      },

      timeRange: {
        label: "Time Range",
        last24h: "Last 24 Hours",
        last7d: "Last 7 Days",
        last30d: "Last 30 Days",
      },

      skillExecution: {
        title: "Skill Execution Analytics",
        stats: {
          totalExecutions: "Total Executions",
          activeSkills: "Active Skills",
          autoMatchRate: "Auto Match Rate",
          ruleMatchRate: "Rule Match Rate",
        },
        charts: {
          matchLayer: "Match Layer Distribution",
          matchSource: "Match Source Distribution",
          topSkills: "Top 10 Skills",
        },
        recentExecutions: "Recent Executions",
        noData: "No data available",
        skillId: "Skill ID",
        query: "User Query",
        matchSource: "Match Source",
        matchedRule: "Matched Rule",
        confidence: "Confidence",
        time: "Time",
      },

      classification: {
        title: "Response Classification Analytics",
        stats: {
          totalClassifications: "Total Classifications",
          quickResponseRate: "Quick Response Rate",
          avgQueryLength: "Avg Query Length",
          avgLatency: "Avg Latency",
          // Skill metrics
          ruleMatchRate: "Rule Match Rate",
          avgRuleMatchTime: "Avg Rule Match Time",
          avgLlmMatchTime: "Avg LLM Match Time",
          avgPromptInjection: "Avg Prompt Injection",
        },
        charts: {
          responseType: "Response Type Distribution",
          avgConfidence: "Average Confidence Comparison",
          methodPerformance: "Classification Method Performance",
          count: "Count",
          latency: "Latency (ms)",
          // Skill charts
          skillMatchLayer: "Skill Match Layer Distribution",
          topMatchedSkills: "Top Matched Skills",
        },
        recentClassifications: "Recent Classifications",
        noData: "No data available",
        responseType: "Response Type",
        method: "Classification Method",
        latencyMs: "Latency (ms)",
      },

      export: {
        button: "Export Data",
        csv: "Export CSV",
        json: "Export JSON",
      },
      refresh: "Refresh",
      loading: "Loading...",
      error: "Failed to load",
      quick: "Quick",
      deep: "Deep",
    },

    // Users Management
    users: {
      pageTitle: "User Management",
      pageDescription: "Manage users and beta tester access",
      refresh: "Refresh",
      loading: "Loading users...",
      error: "Failed to load users",

      // Stats
      stats: {
        totalUsers: "Total Users",
        admins: "Admins",
        betaTesters: "Beta Testers",
        roleDistribution: "Role Distribution",
        usersWithAstro: "Users with Astro",
        pushEnabled: "Push Enabled",
        recentSignups: "Recent Signups (7d)",
      },

      // Filters
      filters: {
        searchPlaceholder:
          "Search by username, nickname, phone, email, or real name...",
        allUsers: "All Users",
        betaTesters: "Beta Testers",
        nonBeta: "Non-Beta",
        clearFilters: "Clear Filters",
      },

      // Batch Actions
      batch: {
        selected: "{count} user(s) selected",
        enableBeta: "Enable Beta",
        disableBeta: "Disable Beta",
        clearSelection: "Clear Selection",
      },

      // Table
      table: {
        user: "User",
        contact: "Contact",
        role: "Role",
        subscription: "Subscription",
        flags: "Flags",
        betaTester: "Beta Tester",
        realName: "Real Name",
        region: "Region",
        timezone: "Timezone",
        active: "Active",
        created: "Created",
        actions: "Actions",
      },

      // Badges
      badges: {
        admin: "Admin",
        birthChart: "Birth Chart",
        gpsEnabled: "GPS Location Enabled",
      },

      // Roles
      roles: {
        free: "Free",
        vip: "VIP",
        premium: "Premium",
        pro: "Pro",
        admin: "Admin",
      },

      // Actions
      actions: {
        makeBeta: "Make Beta",
        removeBeta: "Remove Beta",
        makeAdmin: "Make Admin",
        revokeAdmin: "Revoke Admin",
        resources: "Resources",
        viewResources: "View Resources",
        subscription: "Sub",
        manageSubscription: "Manage Subscription",
      },

      // Status
      status: {
        beta: "Beta",
        notBeta: "No",
      },

      // Pagination
      pagination: {
        showing: "Showing {start} to {end} of {total} users",
        page: "Page {current} of {total}",
      },

      // User Detail Drawer
      drawer: {
        profile: "Profile",
        preferences: "Preferences",
        astroProfile: "Astro Profile",
        email: "Email",
        phone: "Phone",
        created: "Created",
        updated: "Last Updated",
        usernameUpdates: "Username Updates",
        usernameUpdateCountFormat: "{count} times ({date})",
        timezone: "Timezone",
        locale: "Locale",
        language: "Language",
        region: "Region",
        currency: "Currency",
        theme: "Theme",
        fontSize: "Font Size",
        pushNotification: "Push Notification",
        device: "Device",
        gpsLocation: "GPS Location",
        realName: "Real Name",
        gender: "Gender",
        birthDate: "Birth Date",
        birthTime: "Birth Time",
        birthLocation: "Birth Location",
        birthTimezone: "Birth Timezone",
        residenceInfo: "Current Residence",
        residenceLocation: "Residence Location",
        residenceTimezone: "Residence Timezone",
        residencePlaceholder: "Enter residence, e.g., Shanghai Pudong",
        edit: "Edit",
        changeRole: "Click to change role",
      },

      // Advanced Filters
      advancedFilters: {
        roleLevel: "Role Level",
        allRoles: "All Roles",
        hasAstroProfile: "Has Astro Profile",
        isAdmin: "Admin",
        all: "All",
        yes: "Yes",
        no: "No",
      },

      // Relative Date Formatting
      relativeDates: {
        today: "Today",
        yesterday: "Yesterday",
        daysAgo: "{count} days ago",
        weeksAgo: "{count} weeks ago",
        monthsAgo: "{count} months ago",
      },
    },

    // System Settings
    systemSettings: {
      pageTitle: "System Settings",
      pageDescription: "Configure system-wide settings and integrations",

      tabs: {
        mcpServers: "MCP Services",
        mem0Config: "Memory",
        firstMessage: "First Message",
        artifactTypes: "Artifacts",
        toolEnhancement: "Tool Display",
        toolCombo: "Tool Combos",
        promptTemplates: "Prompts",
        outputSchemas: "Output Schemas",
        modelDefaults: "Model Defaults",
        exportImport: "Import/Export",
        auditLogs: "Change Logs",
        htmlPage: "HTML Page",
      },

      htmlPage: {
        pageTitle: "HTML Page Tool Settings",
        pageDescription:
          "Tune runtime knobs for the present_html_page tool: addendum appended to the LLM-facing docstring, embedded CSP, base CSS reset, and max HTML byte budget. Changes apply on the next tool invocation.",
        warningBanner:
          "These settings affect every agent that calls present_html_page. Saved values take effect immediately on the next tool call.",
        promptAddendumLabel: "Prompt Addendum (LLM Instructions)",
        promptAddendumHint:
          "Appended verbatim to the tool's built-in docstring. Leave empty to keep only the built-in instructions. Max 8KB.",
        cspLabel: "CSP Policy",
        cspHint:
          "Embedded into <meta http-equiv=\"Content-Security-Policy\">. Max 2KB.",
        baseCssLabel: "Base CSS Reset",
        baseCssHint:
          "Injected inside the <style> tag (do NOT include <style> tags themselves). Max 4KB.",
        maxBytesLabel: "Max HTML Bytes",
        maxBytesHint: "Upper bound on wrapped HTML size. Range 10240–1048576. Default 200KB.",
        resetField: "Reset to default",
        save: "Save",
        saving: "Saving...",
        saveSuccess: "Saved",
        saveFailed: "Save failed",
        reload: "Reload",
        versionLabel: "Version",
        updatedByLabel: "Updated by",
        addendumTooLong: "Prompt addendum exceeds 8KB",
        cspRequired: "CSP cannot be empty",
        cssRequired: "Base CSS cannot be empty",
        bytesOutOfRange: "Max bytes must be between 10240 and 1048576",
      },

      audit: {
        pageTitle: "Audit",
        pageDescription: "View system configuration change logs",
        viewLogsFor: "View audit logs for:",
        mcpServersLog: "MCP Servers Audit Log",
        mem0ConfigLog: "Mem0 Configuration Audit Log",
        firstMessageLog: "First Message Configuration Audit Log",
        artifactTypesLog: "Artifact Types Configuration Audit Log",
        toolEnhancementLog: "Tool Enhancement Configuration Audit Log",
        toolComboLog: "Tool Combos Configuration Audit Log",
        promptTemplatesLog: "Prompt Templates Configuration Audit Log",
        outputSchemasLog: "Output Schemas Configuration Audit Log",
        configTypeNames: {
          mcp_servers: "MCP Servers",
          mem0_settings: "Mem0 Config",
          first_message_instruction: "First Message",
          artifact_types: "Artifact Types",
          tool_enhancement: "Tool Enhancement",
          tool_combo_templates: "Tool Combos",
          system_prompt_templates: "Prompt Templates",
          output_schemas: "Output Schemas",
          feature_flags: "Feature Flags",
        },
      },

      // MCP Servers Panel
      mcp: {
        title: "MCP Servers",
        description:
          "Manage Model Context Protocol servers for tool integration",
        warningBanner:
          'Warning: Changes to MCP server configuration affect ALL users. After saving, click "Reload Manager" to apply changes immediately.',
        editJson: "Edit JSON",
        reloadManager: "Reload Manager",
        addServer: "Add Server",
        noServers: "No MCP servers configured",
        addFirstServer: "Add your first server",
        aboutMcp: "About MCP Servers",
        aboutMcpItems: [
          "STDIO servers run as local processes (e.g., Python scripts)",
          "HTTP servers connect to remote endpoints",
          "Test connections to verify server availability",
          "Reload manager after making changes to apply them",
        ],
        version: "Version",
        lastUpdatedBy: "Last updated by",
        jsonEditorTitle: "Edit MCP Configuration (JSON)",
        jsonEditorHint:
          "Edit the JSON directly. Each server needs: command/url, transport type, status.",
        cancel: "Cancel",
        saving: "Saving...",
        saveConfiguration: "Save Configuration",
        loading: "Loading MCP servers...",
        loadFailed: "Failed to load MCP servers",
        saveFailed: "Failed to save MCP server",
        deleteFailed: "Failed to delete MCP server",
        reloadSuccess: "MCP manager reloaded successfully",
        reloadFailed: "Failed to reload MCP manager",
        jsonSaveSuccess: "MCP configuration saved successfully",
        jsonMustHaveMcpServers: 'JSON must have a "mcpServers" object',
        // Server Card
        serverCard: {
          disabled: "Disabled",
          transport: "Transport",
          command: "Command",
          url: "URL",
          tools: "Tools",
          toolsCount: "{count} tools",
          testConnection: "Test connection",
          editServer: "Edit server",
          deleteServer: "Delete server",
          confirmDelete: 'Delete MCP server "{name}"?',
          connectionTestSuccess: "Connection test successful",
          connectionTestFailed: "Connection test failed",
          retryProgress: "Retry {current} / {max}",
          lastError: "Last Error",
          states: {
            connected: "Connected",
            connecting: "Connecting...",
            reconnecting: "Reconnecting...",
            failed: "Failed",
            disconnected: "Disconnected",
            unknown: "Unknown",
          },
        },
        // Status Panel
        statusPanel: {
          title: "MCP Connection Status",
          type: "Type",
          transport: "Transport",
          tools: "Tools",
          uptime: "Uptime",
          retryProgress: "Retry Progress",
          lastError: "Last Error",
          nextRetry: "Next retry",
          loading: "Loading MCP status...",
          loadFailed: "Failed to load MCP status",
          retry: "Retry",
          refreshStatus: "Refresh status",
          reconnectAll: "Reconnect All",
          reconnecting: "Reconnecting...",
          autoRefreshing: "Auto-refreshing every 10s",
          totalServers: "Total Servers",
          connected: "Connected",
          reconnectingCount: "Reconnecting",
          failed: "Failed",
          allHealthy: "All Healthy",
          degraded: "Degraded",
          unhealthy: "Unhealthy",
        },
      },

      // Mem0 Config Panel
      mem0: {
        title: "Mem0 System Default Prompts",
        description:
          "Configure default prompts used for memory extraction and management (agents can override these)",
        warningBanner:
          "Warning: Changes to system default prompts affect agents that do NOT have custom prompts enabled. Agents with custom prompts will continue using their own.",
        helpTitle: "Mem0 Dual Memory Architecture",
        helpDescription:
          "Configure system-wide default prompts and categories for both User and Agent memory extraction. Each agent can choose to use these defaults or override with custom values.",
        helpItems: [
          "User Memory Extraction: Extracts facts from user messages",
          "Agent Memory Extraction: Extracts facts from assistant messages",
          "Memory Update: Merges new facts with existing memories",
          "Categories: Classifies memories into structured categories",
        ],
        documentation: "Mem0 Documentation",
        reloadFromDatabase: "Reload from Database",
        saving: "Saving...",
        saveSystemDefaults: "Save System Defaults",
        userMemoryPrompt: "User Memory Extraction Prompt",
        agentMemoryPrompt: "Agent Memory Extraction Prompt",
        memoryUpdatePrompt: "Memory Update Prompt",
        userMemoryCategories: "User Memory Categories (JSON)",
        agentMemoryCategories: "Agent Memory Categories (JSON)",
        resetToHardcoded: "Reset to Hardcoded Default",
        promptMinMax:
          "Prompt for extracting memories from messages. Minimum 50 characters, maximum 8000 characters.",
        promptUpdateMinMax:
          "Prompt for updating/merging memories. Minimum 50 characters, maximum 4000 characters.",
        categoriesHint:
          "Categories for classifying memories. Each category needs: description, temporal.",
        loading: "Loading Mem0 configuration...",
        loadFailed: "Failed to load Mem0 configuration",
        saveSuccess: "Mem0 system defaults saved successfully",
        saveFailed: "Failed to save configuration",
        resetSuccess: "Reset to hardcoded default",
        resetFailed: "Failed to reset",
        noDefaultPrompt: "No default prompt returned from API",
        noDefaultCategories: "No default categories returned from API",
        version: "Version",
        lastUpdatedBy: "Last updated by",
        validation: {
          userPromptMin:
            "User memory extraction prompt must be at least 50 characters",
          agentPromptMin:
            "Agent memory extraction prompt must be at least 50 characters",
          updatePromptMin:
            "Memory update prompt must be at least 50 characters",
          userPromptMax:
            "User memory extraction prompt cannot exceed 8000 characters",
          agentPromptMax:
            "Agent memory extraction prompt cannot exceed 8000 characters",
          updatePromptMax: "Memory update prompt cannot exceed 4000 characters",
        },
      },

      // First Message Panel
      firstMessage: {
        title: "First Message System Default",
        description:
          "Configure the default instruction injected on first message in a new topic",
        warningBanner:
          'Warning: Changes to the system default instruction affect agents using "system" mode. Agents with custom first message instructions will not be affected.',
        reloadFromDatabase: "Reload from Database",
        saving: "Saving...",
        saveSystemDefault: "Save System Default",
        instructionTitle: "System Default Instruction",
        instructionPlaceholder:
          "Enter the system default first message instruction...",
        instructionLength: "Instruction Length",
        instructionMinMax: "Minimum: 10, Maximum: 5000 characters",
        howItWorksTitle: "How It Works",
        howItWorksItems: [
          "This instruction is injected into the system prompt on the first message of a new topic",
          "Agents can choose to use this system default or define their own custom instruction",
          "Agent-level setting: enable_first_message_instruction controls if this feature is active",
          'Agent-level setting: first_message_mode controls "system" vs "custom"',
        ],
        loading: "Loading first message configuration...",
        loadFailed: "Failed to load first message configuration",
        saveSuccess: "First message instruction saved successfully",
        saveFailed: "Failed to save configuration",
        version: "Version",
        lastUpdatedBy: "Last updated by",
        validation: {
          minLength: "First message instruction must be at least 10 characters",
          maxLength: "First message instruction cannot exceed 5000 characters",
        },
      },

      // Artifact Types Panel
      artifactTypes: {
        title: "Artifact Types",
        description:
          "Configure artifact types for user-generated content (lucky charms, podcasts, reports)",
        addType: "Add Type",
        noTypes: "No artifact types configured",
        addFirstType: "Add your first artifact type",
        editType: "Edit Artifact Type",
        addTypeTitle: "Add Artifact Type",
        typeId: "Type ID",
        typeIdHint:
          "Lowercase letters, numbers, underscores. Must start with a letter.",
        icon: "Icon",
        iconOptions: {
          package: "Package",
          sparkles: "Sparkles",
          audio: "Audio",
          document: "Document",
          image: "Image",
          calendar: "Calendar",
        },
        nameEnglish: "Name (English)",
        nameChinese: "Name (Chinese)",
        descriptionLabel: "Description",
        descriptionHint: "Brief description of this artifact type",
        contentTypes: "Content Types",
        contentTypesHint: "image/png, image/jpeg, audio/mpeg",
        rotationPeriod: "Rotation Period",
        rotationOptions: {
          none: "None (Permanent)",
          daily: "Daily",
          weekly: "Weekly",
          monthly: "Monthly",
        },
        maxStored: "Max Stored",
        maxStoredHint: "e.g., 7 (leave empty for unlimited)",
        cancel: "Cancel",
        saving: "Saving...",
        save: "Save",
        confirmDelete: 'Are you sure you want to delete artifact type "{id}"?',
        loading: "Loading artifact types...",
        loadFailed: "Failed to load artifact types",
        saveFailed: "Failed to save artifact type",
        deleteFailed: "Failed to delete artifact type",
        version: "Version",
        lastUpdatedBy: "Last updated by",
        aboutTitle: "About Artifact Types",
        aboutItems: [
          "Artifact types define categories of user-generated content (lucky charms, podcasts, reports)",
          'Rotation period determines when artifacts expire and new ones become "current"',
          "Max stored limits how many artifacts to keep per user (oldest are marked expired)",
          "Artifacts are user-level, each user has their own artifacts",
        ],
        apiAccessTitle: "API Access (App Integration)",
        workflowTitle: "Workflow Integration (Creating Artifacts)",
        fieldRefTitle: "Field Reference",
        adminOpsTitle: "Admin Operations (Cleanup & Management)",
        editing: "Editing...",
        fields: "fields",
        mediaFields: "media",
        metadataFields: "metadata",
        lifecycleMode: "Lifecycle Mode",
        sortBy: "Sort By",
        retentionDays: "Retention Days (after valid_from)",
        defaultSuffix: "default",
      },

      // Audit Log Viewer
      auditLog: {
        loading: "Loading audit logs...",
        loadFailed: "Failed to load audit logs",
        noLogs: "No audit logs available",
        noLogsHint: "Changes to this configuration will be logged here",
        showing: "Showing {current} of {total} log entries",
        version: "Version",
        reason: "Reason",
        ip: "IP",
      },

      // Export/Import Panel
      exportImport: {
        exportTitle: "Export Configurations",
        exportDescription:
          "Download all agent configurations as a JSON file. This includes all settings, prompts, and tool configurations for each agent.",
        exporting: "Exporting...",
        exportAll: "Export All Configurations",
        exportSuccess: "Agent configurations exported successfully",
        exportFailed: "Export failed",
        importTitle: "Import Configurations",
        importDescription:
          "Import agent configurations from a JSON file. You can choose how to handle existing configurations.",
        selectFile: "Select File",
        importMode: "Import Mode",
        importModeOptions: {
          createOnly: "Create Only - Skip existing agents",
          overwrite: "Overwrite - Replace existing agents completely",
          merge: "Merge - Update existing agents, keep unspecified fields",
        },
        importModeHints: {
          createOnly: "Only creates new agents. Existing agents are skipped.",
          overwrite: "Completely replaces existing agents with imported data.",
          merge:
            "Updates specified fields, keeps existing values for unspecified fields.",
        },
        skipErrors: "Skip errors and continue with remaining imports",
        skipErrorsHint: "If unchecked, import will stop on first error.",
        importPreview: "Import Preview",
        foundConfigs: "Found {count} agent configurations:",
        importResults: "Import Results",
        imported: "Imported",
        skipped: "Skipped",
        errors: "Errors",
        importing: "Importing...",
        importConfigurations: "Import Configurations",
        aboutTitle: "About Import/Export",
        aboutItems: [
          "Export creates a JSON file with all agent configurations",
          "Import file must have a configurations array",
          "Each configuration needs at least an agent_type",
          "Use this for backup, migration, or sharing configurations between environments",
        ],
        noFileSelected: "No file selected",
        invalidJson: "Invalid JSON file",
        invalidFormat: "Invalid format: missing configurations array",
        importCompleted: "Import completed",
        importFailed: "Import failed",
      },

      // Tool Enhancement Panel
      toolEnhancement: {
        title: "Tool Enhancement",
        description: "Configure display names and descriptions for tools",
        loading: "Loading...",
        loadFailed: "Failed to load",
        saveSuccess: "Saved successfully",
        saveFailed: "Failed to save",
        save: "Save",
        saving: "Saving...",
        reload: "Reload",
        searchPlaceholder: "Search tools...",
        noToolSelected: "Select a tool from the left panel to configure",
        configured: "Configured",
        notConfigured: "Not configured",
        displayNameCn: "Display Name (Chinese)",
        displayNameEn: "Display Name (English)",
        descriptionCn: "Description (Chinese)",
        descriptionEn: "Description (English)",
        icon: "Icon",
        displayOrder: "Display Order",
        enabled: "Enabled",
        clearConfig: "Clear Config",
        toolInfo: "Tool Info",
        technicalName: "Technical Name",
        originalDescription: "Original Description",
        allCategories: "All Categories",
        orphanConfigs: "Orphan Configs",
        orphanConfigsDesc: "Configurations for tools that no longer exist",
        cleanupOrphans: "Clean Up",
        orphanWarning: "This tool no longer exists in the system",
        category: "Category",
        categoryOverride: "Category Override",
        categoryOverrideHint: "Leave empty to use default category",
        originalCategory: "Default Category",
        useDefaultCategory: "Use Default",
        toolKey: "Tool Key",
        // Category translations
        categories: {
          search: "Search",
          math: "Math",
          development: "Development",
          filesystem: "File System",
          reasoning: "Reasoning",
          astrology: "Astrology",
          fortune: "Fortune",
          memory: "Memory",
          utility: "Utility",
          general: "General",
        } as Record<string, string>,
        // AI Generation
        aiGenerate: "AI Generate Descriptions",
        aiGenerateDesc:
          "Analyze the original description and automatically generate user-friendly display names and descriptions.",
        aiGenerateButton: "Generate",
        aiGenerating: "Generating...",
        aiSelectModel: "Select Model",
        aiSelectModelPlaceholder: "Select a model...",
        aiExplanation: "AI Analysis",
        aiNoDescription: "No original description available for AI generation",
        // Reload Enhancer
        reloadEnhancer: "Apply to LLM",
        reloadEnhancerDesc:
          "Apply saved configurations to LLM tool descriptions",
        reloadEnhancerSuccess: "Tool descriptions reloaded",
        reloadEnhancerFailed: "Failed to reload",
        // Tool Testing
        testTool: "Test Tool",
        testToolDesc: "Directly invoke the tool with parameters for debugging",
        testParameters: "Test Parameters",
        testParametersPlaceholder: "Enter parameters in JSON format...",
        runTest: "Run Test",
        running: "Running...",
        testResult: "Test Result",
        testSuccess: "Execution Successful",
        testFailed: "Execution Failed",
        executionTime: "Execution Time",
        errorType: "Error Type",
        closeModal: "Close",
        noParameters: "This tool requires no parameters",
        invalidJson: "Invalid JSON format",
        requiredFields: "Required Fields",
        optionalFields: "Optional Fields",
        paramType: "Type",
        paramDefault: "Default",
        requiresUserContext: "Requires User Context",
        requiresUserContextDesc:
          "This tool requires user context. Your admin user ID will be used for testing.",
        formMode: "Form Mode",
        jsonMode: "JSON Mode",
        useDefaultValue: "Use Default",
      },

      // Tool Combo Panel
      toolCombo: {
        title: "Tool Combos",
        description: "Define tool usage order and guidance",
        loading: "Loading...",
        loadFailed: "Failed to load",
        saveSuccess: "Saved successfully",
        saveFailed: "Failed to save",
        save: "Save",
        saving: "Saving...",
        reload: "Reload",
        searchPlaceholder: "Search templates...",
        noTemplateSelected: "Select or create a template from the left",
        newTemplate: "New Template",
        deleteTemplate: "Delete Template",
        confirmDelete: "Are you sure you want to delete this template?",
        templateId: "Template ID",
        templateIdPlaceholder: "e.g., astrology_basic",
        nameCn: "Name (Chinese)",
        nameEn: "Name (English)",
        descriptionCn: "Description (Chinese)",
        descriptionEn: "Description (English)",
        guidance: "Overall Guidance",
        guidancePlaceholder: "Overall guidance for using these tools...",
        enabled: "Enabled",
        tools: "Tools",
        addTool: "Add Tool",
        toolName: "Tool Name",
        selectTool: "Select Tool",
        usageInstruction: "Usage Instruction",
        usageInstructionPlaceholder: "Instructions for this tool...",
        removeTool: "Remove",
        preview: "Preview",
        previewPrompt: "Preview generated prompt context",
        closePreview: "Close",
        closePicker: "Close",
        noTools: "No tools added yet",
        templateCount: "{count} templates",
        searchTools: "Search tools...",
        availableTools: "Available Tools",
        selectedTools: "Selected Tools",
        clickToAdd: "Click to add",
        alreadyAdded: "Added",
      },

      // Prompt Templates Panel
      promptTemplates: {
        title: "Prompt Templates",
        description:
          "Configure system prompt templates for image generation, push notifications, workflows, and more",
        addTemplate: "Add Template",
        noTemplates: "No prompt templates configured",
        addFirstTemplate: "Add your first prompt template",
        editTemplate: "Edit Template",
        addTemplateTitle: "Add New Template",
        templateKey: "Template Key",
        templateKeyHint: "Format: domain.operation (e.g., image.generate)",
        nameEnglish: "English Name",
        nameChinese: "Chinese Name",
        descriptionEn: "English Description",
        descriptionZh: "Chinese Description",
        systemPromptZh: "Chinese System Prompt",
        systemPromptEn: "English System Prompt",
        displayOrder: "Display Order",
        category: "Category",
        categoryOptions: {
          image: "Image",
          push: "Push",
          workflow: "Workflow",
        },
        isDefault: "Default Template",
        resetToDefault: "Reset to Default",
        resetSuccess: "Reset to default value",
        cancel: "Cancel",
        saving: "Saving...",
        save: "Save",
        confirmDelete: "Are you sure you want to delete this template?",
        cannotDeleteDefault:
          "Cannot delete default templates. Use reset to restore default values.",
        loading: "Loading...",
        loadFailed: "Failed to load templates",
        saveFailed: "Failed to save template",
        deleteFailed: "Failed to delete template",
        version: "Version",
        lastUpdatedBy: "Last Updated By",
        searchPlaceholder: "Search templates...",
        allCategories: "All Categories",
        previewPrompt: "Preview Prompt",
        chinese: "Chinese",
        english: "English",
      },

      // Output Schemas Panel
      outputSchemas: {
        title: "Output Schemas",
        description:
          "Manage centralized JSON output schema definitions for agent responses",
        addSchema: "Add Schema",
        addNew: "Add Output Schema",
        editTitle: "Edit Output Schema",
        noSchemas: "No output schemas configured",
        noSchemasMatch: "No schemas match your search",
        addFirstSchema: "Add your first schema",
        editSchema: "Edit Output Schema",
        searchPlaceholder: "Search schemas...",
        scopeAll: "All Scopes",
        scopeGlobal: "Global Only",
        scopeAgent: "Agent-Specific",
        scopeGlobalOption: "Global (All Agents)",
        scopeAgentOption: "Agent-Specific",
        selectAgent: "Select Agent...",
        statusAll: "All Status",
        statusEnabled: "Enabled",
        statusDisabled: "Disabled",
        schemaKey: "Schema Key",
        schemaKeyHint:
          "Lowercase letters, numbers, underscores. Must start with a letter.",
        nameEnglish: "Name (English)",
        nameChinese: "Name (Chinese)",
        descriptionEn: "Description (English)",
        descriptionZh: "Description (Chinese)",
        scope: "Scope",
        agentType: "Agent Type",
        jsonSchema: "JSON Schema",
        exampleOutput: "Example Output",
        formatInstruction: "Format Instruction",
        formatInstructionHint:
          "Use {schema} as a placeholder for the JSON schema",
        displayOrder: "Display Order",
        enabled: "Enabled",
        loading: "Loading output schemas...",
        loadFailed: "Failed to load output schemas",
        saveFailed: "Failed to save output schema",
        deleteFailed: "Failed to delete output schema",
        toggleFailed: "Failed to toggle status",
        totalSchemas: "Total schemas",
        showing: "Showing",
        aboutTitle: "About Output Schemas",
        aboutItem1: "Global schemas are available to all agents",
        aboutItem2:
          "Agent-specific schemas are only available to the specified agent type",
        aboutItem3:
          "Use {schema} placeholder in format_instruction to inject the JSON schema",
        aboutItem4:
          "Disabled schemas are hidden from agent selection dropdowns",
        cancel: "Cancel",
        save: "Save",
        saving: "Saving...",
        confirmDelete: 'Delete output schema "{name}"?',
        editing: "Editing...",
      },

      // Model Defaults Panel
      modelDefaults: {
        title: "Model Defaults",
        description:
          "Configure default models for different system capabilities",
        reload: "Reload",
        save: "Save Changes",
        saving: "Saving...",
        loading: "Loading model defaults...",
        loadFailed: "Failed to load model defaults",
        saveSuccess: "Model defaults saved successfully",
        saveFailed: "Failed to save",
        version: "Version",
        lastUpdatedBy: "Updated by",
        // Categories
        chatModels: "Chat Models",
        visionModels: "Vision Models",
        auxiliaryModels: "Auxiliary Models",
        embeddingReranking: "Embedding & Reranking",
        speechModels: "Speech Models",
        // Fields
        primaryChatModel: "Primary Chat Model",
        primaryChatModelDesc: "Main model for conversations",
        quickChatModel: "Quick Chat Model",
        quickChatModelDesc: "Model for quick responses (prompt configured in Quick Response section)",
        primaryVisionModel: "Vision Supplement Model",
        primaryVisionModelDesc: "Only activates when chat model lacks vision support",
        quickVisionModel: "Quick Vision Supplement Model",
        quickVisionModelDesc: "Only activates when quick chat model lacks vision support",
        memoryModel: "Memory Model",
        memoryModelDesc: "For memory extraction and management",
        classificationModel: "Classification Model",
        classificationModelDesc: "For intent classification",
        reasoningModel: "Reasoning Model",
        reasoningModelDesc: "For complex reasoning tasks",
        taskModel: "Task Model",
        taskModelDesc: "For subtasks (compression, summary, etc.)",
        embeddingModel: "Embedding Model",
        embeddingModelDesc: "For vector embeddings",
        rerankingModel: "Reranking Model",
        rerankingModelDesc: "For search result reranking",
        rerankingEnabled: "Enable Reranking",
        sttModel: "Speech-to-Text Model",
        sttModelDesc: "For voice transcription",
        ttsModel: "Text-to-Speech Model",
        ttsModelDesc: "For voice synthesis",
        audioSettings: {
          title: "Audio Settings",
          ttsProvider: "TTS Provider",
          ttsVoice: "Default Voice",
          ttsSpeed: "Default Speed",
          ttsPreambleTrim: "Preamble Trim",
          ttsPreambleTrimUnit: "ms",
          ttsStreaming: "Streaming",
          sttModel: "STT Model",
          sttFallbackModel: "Fallback Model",
          sttHotwords: "Domain Hotwords",
        },
        imageGenerationModels: "Image Generation Models",
        imageModel: "Image Generation Model",
        imageModelDesc:
          "Default model for workflow and Soulmate image generation (speed-optimized)",
        qualityImageModel: "Quality Image Model",
        qualityImageModelDesc:
          "Model for high-quality image generation (quality-optimized)",
        promptRewriteModel: "Prompt Rewrite Model",
        promptRewriteModelDesc: "For AI-powered prompt rewriting in admin console",
        mbtiModel: "MBTI Inference Model",
        mbtiModelDesc: "Model used for MBTI personality inference from birth charts",
        // Section headers
        globalModelsTitle: "Global Models",
        globalModelsDesc: "These models are shared globally across all agents",
        perAgentDefaultsTitle: "Per-Agent Defaults",
        perAgentDefaultsDesc:
          "Default model configuration when creating new agents, can be overridden per agent",
        allAgentsShared: "Shared by all agents",
      },
    },

    // Agent Config
    agentConfig: {
      // List Page
      listTitle: "Agent Configurations",
      listDescription: "Manage AI agent configurations and settings",
      createAgent: "Create Agent",
      searchPlaceholder: "Search agents...",
      showInactive: "Show inactive agents",
      noAgentsMatch: "No agents match your search",
      noAgentsMatchHint: "Try adjusting your search terms",
      noAgentsYet: "No agents configured yet",
      noAgentsYetHint: "Create your first agent to get started",
      showingAgents: "Showing {current} of {total} agents",
      activeCount: "{count} active",
      inactive: "Inactive",
      version: "Version",
      updated: "Updated",

      // Detail Page
      loadingConfig: "Loading agent configuration...",
      backToList: "Back to List",
      agentNotFound: "Agent not found",
      copy: "Copy",
      unsavedChanges: "Unsaved changes",
      saving: "Saving...",
      saveSuccess: "Configuration saved successfully!",
      saveFailed: "Failed to save",
      active: "Active",
      activateAgent: "Activate Agent",
      deactivateAgent: "Deactivate Agent",
      confirmDeactivate: "Are you sure you want to deactivate this agent? Users will no longer be able to use it.",
      confirmActivate: "Are you sure you want to reactivate this agent?",
      updatedViaAdmin: "Updated via admin interface",
      conflictModalTitle: "Config modified by another admin",
      conflictModalBody: "Current version is v{current}, you edited from v{expected}. Choose how to proceed:",
      conflictModalDiscard: "Refresh page, discard my changes",
      conflictModalForce: "Force override with my version",

      // Tabs
      tabSettings: "Settings",
      tabHooks: "Hooks",
      tabTools: "Tools",
      tabPrompt: "Prompt",
      tabSchemas: "Schemas",
      tabSkills: "Skills",
      tabJson: "JSON",
      tabVersions: "Versions",
      // New tabs
      tabBasic: "Basic",
      tabMemory: "Memory",
      tabKnowledge: "Knowledge",
      tabAstrology: "Astrology+",
      tabPersonality: "Personality",
      tabEmotion: "Emotion",
      tabClassifier: "Classifier",
      tabTeam: "Team",
      tabAdvanced: "Advanced",

      // Actions
      actions: {
        browseTools: "Browse Tools",
      },

      // Output Format Section
      outputFormat: {
        title: "Output Format",
        createSchema: "Create Schema",
        manageSchemas: "Manage Schemas",
        noSchemasWarning:
          "No output schemas defined. Create a schema to enable structured JSON output.",
        createFirstSchema: "Create First Schema",
        noDefault: "No format constraint (free output)",
        defaultSchemaHelp:
          "Default schema used when no output_schema_key is specified in API request.",
        availableSchemas: "Available Schema Keys (for API)",
        apiHint:
          "Pass output_schema_key in API request to use a specific schema.",
      },

      // Create/Copy Agent Dialog
      createAgentDialog: {
        copyAgent: "Copy Agent",
        createNewAgent: "Create New Agent",
        agentType: "Agent Type",
        agentTypePlaceholder: "e.g., my_agent",
        agentTypeHint:
          "Unique identifier. Lowercase letters, numbers, underscores, and hyphens only.",
        name: "Name",
        namePlaceholder: "e.g., My Agent",
        description: "Description",
        descriptionPlaceholder: "Describe what this agent does...",
        copyingFrom: "Copying configuration from",
        creating: "Creating...",
        agentTypeRequired: "Agent type is required",
        nameRequired: "Name is required",
        agentTypeInvalid:
          "Agent type must start with a letter and contain only lowercase letters, numbers, underscores, and hyphens",
      },

      // Version History
      versionHistory: {
        title: "Version History",
        loading: "Loading version history...",
        current: "Current",
        restore: "Restore",
        noHistory: "No version history available",
        createdBy: "Created by",
        versionConfig: "Version {version} Configuration",
        confirmRestore:
          "Activate version {version}? This will replace the current configuration.",
        restoreReason: "Activated via admin interface",
        editing: "Editing",
        clickToShowJson: "Click the eye icon to show JSON configuration",
        viewConfig: "View Config",
      },

      // JSON Preview
      jsonPreview: {
        title: "JSON Configuration",
        copy: "Copy",
        copied: "Copied",
        download: "Download",
        modified: "Modified",
        reset: "Reset",
        applyChanges: "Apply Changes",
        editHint:
          "Edit JSON directly and click 'Apply Changes' to update configuration",
        loadToEditor: "Load to Editor",
      },

      // Basic Info Section
      basicInfo: {
        roleDefinitionPrompt: "Role Definition Prompt",
        roleDefinitionHelp:
          "Define the agent's role and behavioral characteristics",
        configureSystemPrompts: "Configure System Prompts",
      },

      // Config Migration Dialog
      configMigration: {
        title: "Configuration Migration",
        versionInfo:
          "Detected legacy configuration (v{from}). Upgrade to v{to} required for compatibility.",
        changesTitle: "Migration Changes:",
        warning:
          "After migration, you need to manually save to apply changes. Migration can be safely reverted (refresh page).",
        cancel: "Cancel",
        confirm: "Confirm Migration",
        confirmed: "Confirmed",
        before: "Before",
        after: "After",
        changeTypes: {
          add: "Add",
          remove: "Remove",
          modify: "Modify",
        },
      },

      // Tools Browser Modal
      toolsBrowser: {
        customTools: "Custom Tools",
        builtinToolkits: "Builtin Toolkits",
        customToolsDesc: "Select individual tools from available categories",
        builtinToolkitsDesc: "Select builtin tool modules",
        filterByCategory: "Filter by category:",
        allCategories: "All categories",
        toolsSelected: "{count} tool(s) selected",
        loadingTools: "Loading tools...",
        availableTools: "Available Tools",
        selectedTools: "Selected Tools",
        previewConfig: "Preview Configuration",
        cancel: "Cancel",
        apply: "Apply",
        noBuiltinToolkits: "No builtin toolkits available",
        noCustomTools: "No custom tools available",
        noToolsSelected: "No tools selected",
        toolsCount: "{count} tools",
        builtinToolsBrowser: "Builtin Tools Browser",
        customToolsBrowser: "Custom Tools Browser",
        format: "Format",
        noItemsConfigured: "No items configured",
        toolkits: "Toolkits",
        enabledTools: "Enabled Tools",
        servers: "Servers",
        modules: "Modules",
        allToolsEnabled: "(all tools enabled)",
        toolNotFound: "Tool not found - please remove",
        serverNotFound: "Server not found - please remove",
        willMigrateOnSave: "will migrate on save",
        toolNotFoundRemove: "Tool not found - please remove",
        invalidToolsWarning:
          "Some selected tools no longer exist. Please remove them before applying.",
        removeInvalidToolsFirst: "Remove invalid tools first",
      },

      // MCP Tools Browser Modal
      mcpBrowser: {
        title: "MCP Tools Browser",
        refresh: "Refresh",
        systemServers: "System Servers",
        userServers: "User Servers",
        serversSelected: "{count} server(s) selected",
        toolsSelected: "{count} tool(s) selected",
        loadingServers: "Loading MCP servers...",
        availableServers: "Available Servers",
        selectedItems: "Selected Items",
        servers: "Servers",
        individualTools: "Individual Tools",
        noServersSelected: "No servers selected",
        noServersAvailable: "No MCP servers available",
        connected: "Connected",
        disconnected: "Disconnected",
        failedToConnect: "Failed to connect to this server",
        previewConfig: "Preview Configuration",
        cancel: "Cancel",
        apply: "Apply",
        toolsCount: "{count} tools",
        invalidItemsWarning:
          "Some selected servers/tools no longer exist. Please remove them before applying.",
        removeInvalidItemsFirst: "Remove invalid items first",
      },

      // Prompt Template Preview
      promptPreview: {
        title: "System Prompt Preview",
        buttonLabel: "Preview Prompt",
        copy: "Copy",
        copied: "Copied!",
        loading: "Loading...",
        refresh: "Refresh",
        generatePreview: "Generate Preview",
        usingTemplate: "Using template:",
        default: "(Default)",
        enabledSections: "{count} enabled sections",
        useRealData: "Use real user data",
        livePreview: "Live Preview",
        userId: "User ID",
        enterUserId: "Enter User ID...",
        useCurrent: "Use Current",
        usingCurrentUser: "Using current logged-in user",
        sampleMessage: "Sample User Message (triggers memory search)",
        enterSampleMessage: "Enter a sample user message...",
        realDataHint:
          "Runs all hooks with real data: user_facts, birth_chart, memory_context",
        sampleDataHint: "Preview uses generated test user with sample data.",
        previewFailed: "Preview Failed",
        template: "Template:",
        systemChars: "System: {count} chars",
        userChars: "User: {count} chars",
        user: "User:",
        hooksExecuted: "Hooks Executed ({count} total)",
        systemStage: "SYSTEM Stage:",
        userStage: "USER Stage:",
        noHooksExecuted: "No hooks executed",
        dataSources: "Data Sources ({count})",
        dataSourceHint: "real = loaded from database | empty = no data found",
        systemPrompt: "System Prompt:",
        enhancedUserMessage: "Enhanced User Message ({count} chars)",
        contextItems: "Context Items ({count}):",
        emptyStateTitle:
          'Click "Generate Preview" to see how the system prompt will be rendered',
        emptyStateDesc:
          "Live preview runs all hooks with real data and builds the exact prompt sent to LLM",
      },

      // Output Schemas Tab
      outputSchemas: {
        title: "Schemas",
        add: "Add",
        noSchemas: "No schemas defined",
        noSchemasHint: 'Click "Add" to create your first output schema',
        default: "Default",
        properties: "{count} properties",
        noSelection: "No schema selected",
        noSelectionHint: "Select a schema from the list or create a new one",
      },

      // Skills Tab
      skills: {
        title: "Skills",
        add: "Add Skill",
        noSkills: "No skills configured",
        noSkillsHint: "Add skills to enable dynamic instruction injection",
        noSelection: "No skill selected",
        noSelectionHint: "Select a skill from the list or add a new one",
        enabled: "Enabled",
        disabled: "Disabled",
        priority: "Priority",
        exclusionGroup: "Group",
        disabledWarning:
          'Skills are disabled. Enable "Enable Skills" in skill matching settings to use skills.',
        // Dialog
        createTitle: "Create New Skill",
        editTitle: "Edit Skill",
        // Form fields
        skillId: "Skill ID",
        skillIdPlaceholder: "e.g., lucky_charm",
        skillIdRequired: "Skill ID is required",
        skillIdPattern: "Only alphanumeric, underscore, and hyphen allowed",
        skillIdReadonlyHint: "Cannot be changed after creation",
        skillIdHint: "Unique identifier, cannot be changed later",
        name: "Name",
        namePlaceholder: "Enter skill display name",
        nameRequired: "Name is required",
        description: "Description",
        descriptionPlaceholder:
          "Brief description of the skill's functionality",
        descriptionHint:
          "Always injected into prompt to let agent know its capabilities",
        command: "Command",
        commandPlaceholder:
          "Trigger command text sent when user clicks the skill",
        commandRequired: "Command is required",
        commandHint: "Message content sent when user clicks this skill",
        displayOrder: "Display Order",
        displayOrderHint: "Lower number = higher priority",
        displayOrderMin: "Must be 0 or greater",
        enableSkill: "Enable this skill",
        instructions: "Instructions",
        instructionsPlaceholder:
          "Write detailed instructions for this skill...\n\nThe more detailed your instructions, the better the AI can generate matching rules.",
        instructionsHint: "Appended to base instructions when skill is matched",
        priorityHint: "Higher wins in conflicts",
        exclusionGroupHint: "Same group = mutually exclusive",
        forcePromptType: "Force Prompt Type",
        forcePromptAuto: "Auto",
        forcePromptQuick: "Quick Response Prompt",
        forcePromptDeep: "System Prompt",
        toolCombo: "Tool Combo",
        toolComboNone: "None (no tool combo)",
        toolComboTools: "tools",
        toolComboHint:
          "When this skill matches, the selected tool combo will be injected into the prompt",
        matchingRules: "Matching Rules",
        keywords: "Keywords (comma-separated)",
        keywordsPlaceholder: "annual, yearly, year report",
        keywordsHint: "Trigger words for rule-based matching",
        regexPatterns: "Regex Patterns (one per line)",
        regexPatternsHint: "Regular expressions for pattern matching",
        exampleQueries: "Example Queries (one per line)",
        exampleQueriesHint:
          "Sample queries for embedding-based semantic matching",
        // AI Generation
        aiGenerateRules: "AI Generate Matching Rules",
        aiGenerateDesc:
          "Analyze your instructions and automatically generate skill metadata, keywords, patterns, and example queries.",
        selectModel: "Select Model",
        selectModelPlaceholder: "Select a model...",
        generateFromInstructions: "Generate from Instructions",
        aiAnalysis: "AI Analysis",
        aiGenerate: "AI Generate",
        generating: "Generating...",
        mustStartWithLetter: "Must start with lowercase letter",
        provideInstructions: "Please provide instructions for the skill first",
        selectModelFirst: "Please select a model first",
        selectModelInAiPanel:
          "Please select a model in the AI Generation panel first",
        generateFailed: "Generation failed",
        patternGenerateFailed: "Pattern generation failed",
        deleteConfirmTitle: 'Are you sure you want to delete skill "{name}"?',
        enableSkillsFirst:
          "Enable Skills to configure skill-based classification",
        toggleEnableSkillsHint: 'Toggle "Enable Skills" above to get started',
        clickAddFirst: 'Click "Add" to create your first skill',
        selectOrCreateSkill: "Select a skill to edit or create a new one",
        // Global Settings
        globalSettings: "Global Settings",
        // Dedup Configuration
        dedupConfig: "Dedup Configuration",
        enableDedup: "Enable Skill Dedup",
        enableDedupDesc:
          "Inject historical execution records into active skills, letting the model know what content has been generated before to avoid duplicates.",
        dedupConfigHint:
          "After enabling dedup, you can configure the query range and display format of history records.",
        dedupConfigOverrideHint:
          "Leave empty to use global defaults. Only fill in fields that need overriding.",
        dataQueryHours: "Data Query Range",
        dataQueryHoursHint:
          "Time range for querying historical execution records",
        reportWindowHours: "Report Window",
        reportWindowHoursHint: "Size of each time window",
        maxPerWindow: "Max Per Window",
        maxPerWindowHint: "Maximum records to display per time window",
        outputPreviewLength: "Output Preview Length",
        outputPreviewLengthHint:
          "Maximum characters to display for historical output",
        showUserQuery: "Show User Query",
        showUserQueryHint: "Display user's original query in history records",
        queryPreviewLength: "Query Preview Length",
        queryPreviewLengthHint:
          "Maximum characters to display for user queries",
        hours: "hours",
        chars: "chars",
        dedupPrinciple: "How It Works",
        dedupPrincipleList: [
          "When a skill is matched, the system queries the user's historical execution records for that skill",
          "History records are injected into skill instructions with <skill_history> tags",
          "The model can reference historical output to generate differentiated new content",
          "Each skill can override these global defaults individually",
        ],
        skillTrackingRequired: "Skill Tracking Required",
        skillTrackingRequiredHint:
          'Skill dedup requires "Skill Tracking" to be enabled to record execution history. Please enable skill_tracking in Feature Extensions.',
        perSkillDedupEnabled: "Enable Dedup",
        perSkillDedupDisabledHint:
          "Dedup is disabled for this skill, no history will be injected",
        perSkillDedupEnabledHint:
          "Follow global settings, can customize config to override global defaults",
        dedupDisabledForSkill: "Dedup is disabled for this skill",
        default: "Default",
        // Skills Tab - Classifier relationship
        classifierDisabledTitle: "Classification Engine Disabled",
        classifierDisabledDesc: "Enable the Classification Engine toggle in Feature Extensions for skill configurations and quick response model to take effect.",
        classifierRelationDesc: "Messages are first matched by rules (keywords/regex), then by semantic search (vector retrieval). When confidence is low, an LLM fallback is used. If Quick Response Model is also enabled, a single LLM call handles both response type classification and skill matching.",
        // Skills Tab Settings Panel
        skillsNotAvailable: "Skills Not Available",
        skillsHookHidden: "Skills hook is hidden for this agent.",
        skillMatchingSettings: "Skill Matching Settings",
        featureToggles: "Feature Toggles",
        enableSkills: "Enable Skills",
        enableSkillsHint:
          "Dynamically inject instructions based on user intent",
        semanticMatching: "Semantic Matching",
        semanticMatchingHint: "TEI Service (bge-m3)",
        thresholdSettings: "Threshold Settings",
        ruleConfidence: "Rule Confidence",
        semanticSimilarity: "Semantic Similarity",
        llmTimeout: "LLM Timeout",
        skillToolInstructions: "Skill Tool Instructions",
        customized: "Customized",
        resetToDefault: "Reset to Default",
        skillToolInstructionsHint:
          "Usage instructions for get_skill_instructions and list_available_skills tools, injected into system prompt.",
        howItWorks: "How Skill Matching Works",
        threeLayerMatching: "Three-Layer Cascading Matching",
        layer1Rule: "Layer 1: Rule Matching (<10ms)",
        layer1RuleDesc:
          "Matches keywords (0.85-0.95 confidence) and patterns (0.9 confidence). Returns result when confidence ≥ rule_confidence_threshold.",
        layer2Semantic: "Layer 2: Semantic Matching (~50ms)",
        layer2SemanticDesc:
          "Requires semantic_matching_enabled. Uses TEI service (BAAI/bge-m3) to match name, description, and example_queries in Qdrant. Returns result when similarity ≥ similarity_threshold.",
        layer3Llm: "Layer 3: LLM Fallback (~200ms)",
        layer3LlmDesc:
          "Uses model_id for intent classification. Timeout: skill_llm_timeout seconds. Fallback when rule and semantic matching both fail.",
        exclusionGroupExplain: "Exclusion Group Rules",
        exclusionGroupRules: [
          "Skills in the same exclusion_group are mutually exclusive",
          "When multiple skills in the same group match, only the one with highest priority is kept",
          "Skills in different groups can match simultaneously",
          "Skills without exclusion_group are always included when matched",
        ],
        fieldPurposes: "Skill Configuration Field Reference",
        fieldPurposesDesc:
          "keywords/patterns → Rule Matching | name/description/example_queries → Semantic Matching | instructions → Execution Instructions",
        skillTools: "Skill Tools (Injected into System Prompt)",
        skillToolsDesc: "When skills are enabled, Agent can use these tools:",
        skillToolsList: [
          "get_skill_instructions(skill_id) - Get detailed instructions for a specific skill",
          "list_available_skills() - List all available skills and descriptions",
        ],
        skillToolsNote:
          'You can customize tool instructions in the "Skill Tool Instructions" section above.',
        exampleQueriesBestPractices: "Best Practices for Example Queries",
        exampleQueriesTips: [
          "Write 3-10 different examples per skill, covering various ways users might ask",
          "Include variations: formal/casual, brief/detailed, different expressions",
          "Focus on user intent, not technical terms",
          'Good examples: "Show me next year\'s fortune", "How\'s my luck in 2025", "I want to know about next year\'s developments"',
          "Avoid: Copying instructions content—that's for execution, not matching",
        ],
        semanticMatchingNote: "Semantic Matching Details",
        semanticMatchingDetails: [
          "Uses system-configured TEI service (BAAI/bge-m3) for embedding",
          "name, description, and example_queries are all indexed in Qdrant",
          "instructions do not participate in matching (they define execution behavior)",
          "Embeddings are automatically re-indexed when configuration is saved",
          "Threshold controls minimum similarity score (default 0.75 = 75% similar)",
          "Each skill can match multiple texts, only the highest score is used",
        ],
        validationErrors: "Found {count} validation error(s)",
        validationWarnings: "{count} warning(s)",
        andMore: "...and {count} more",
        newSkillName: "Skill {index}",
        keywordsCount: "{count} keywords",
        examplesCount: "{count} examples",
        errorsCount: "{count} error(s)",
        warningsCount: "{count} warning(s)",
        classificationRules: "Classification Rules",
        classificationRulesDesc:
          "Control message classification to reduce unnecessary LLM calls",
        llmFallbackEnabled: "LLM Classification Fallback",
        llmFallbackEnabledHint:
          "Use LLM for skill classification when rules don't match. Disable to reduce API costs.",
        customQuickKeywords: "Custom Quick Response Keywords",
        customQuickKeywordsHint:
          "Messages containing these keywords will be classified as quick response, skipping skill matching.",
        customQuickKeywordsPlaceholder: "Type keyword and press Enter",
        customQuickPatterns: "Custom Quick Response Patterns (Regex)",
        customQuickPatternsHint:
          "Regex patterns. Matching messages are classified as quick response.",
        customQuickPatternsPlaceholder: "e.g. ^(check balance|sign in)$",
        defaultPatternsInfo:
          "System defaults cover common greetings, confirmations, casual chat, laughter, and farewells. Add extra quick-response rules here.",
        addPattern: "Add Pattern",
        invalidRegex: "Invalid regular expression",
        builtinRules: "Built-in Rules",
        builtinRulesDesc: "These rules are always active and require no configuration. Custom rules are checked after built-in rules.",
        builtinMaxLength: "Rule matching only applies when message length ≤ 50 characters. Longer messages go directly to the DEEP path.",
        builtinSkillNote: "Quick replies only skip the skill matching process and heavy tools (MCP, memory tools, etc.), but skill tools are always retained. The Agent can still use get_skill_instructions to query and invoke configured skills even during quick replies.",
        builtinGreeting: "Greeting",
        builtinAck: "Acknowledgment",
        builtinCasual: "Casual Chat",
        builtinLaughter: "Laughter",
        builtinFarewell: "Farewell",
        builtinReaction: "Reaction",
      },

      // Schema Editor
      schemaEditor: {
        aiGenerate: "AI Generate Schema",
        aiGenerateDesc:
          "Input your expected JSON output example, AI will analyze and generate the corresponding JSON Schema",
        selectModel: "Select Model",
        selectModelPlaceholder: "-- Select a model --",
        inputExample: "Input JSON Example",
        generating: "Generating...",
        generate: "Generate Schema",
        aiExplanation: "AI Design Explanation",
        key: "Key",
        keyValidHint:
          "Unique identifier for API usage. Lowercase letters, numbers, and underscores only.",
        keyInvalidHint:
          "Must start with a letter and contain only lowercase letters, numbers, and underscores.",
        name: "Name",
        namePlaceholder: "Schema name",
        description: "Description",
        descriptionPlaceholder: "Brief description of this schema",
        jsonSchema: "JSON Schema",
        validJson: "Valid JSON",
        jsonSchemaHint:
          "Define the expected output structure using JSON Schema format",
        exampleOutput: "Example Output (Optional)",
        exampleOutputHint: "Provide a sample output for reference",
        formatInstruction: "Format Instruction",
        formatInstructionHint:
          "Use {schema} placeholder to inject JSON Schema at runtime",
        defaultSchema: "Default Schema",
        setAsDefault: "Set as Default",
        deleteSchema: "Delete Schema",
        confirmDelete: 'Are you sure you want to delete "{name}"?',
        alertExampleRequired: "Please provide an example JSON",
        alertModelRequired: "Please select a model first",
        alertGenerateFailed: "AI generation failed:",
      },

      // Template Selector
      templateSelector: {
        title: "Prompt Template",
        description:
          "Select a template to define how the system prompt is structured for this agent.",
        useDefault: "Use Default",
        default: "Default",
        recommended: "Recommended",
        systemDefault: "System Default",
        sections: "Template Sections",
        sectionsCount: "{count} sections",
        updated: "Updated",
        noTemplates: "No templates available",
        loadingTemplates: "Loading templates...",
        previewTemplate: "Preview Template",
      },

      // Knowledge Tab
      knowledge: {
        confirmDeleteDoc: "Are you sure you want to delete this document?",
        confirmClearAll:
          "Are you sure you want to clear all documents? This cannot be undone!",
      },

      // Test Chat
      testChat: {
        confirmDeleteConversation: "Delete this conversation?",
      },
    },

    // Chat Panel (Floating)
    chatPanel: {
      confirmDeleteTopic: 'Delete "{title}"? This cannot be undone.',
      confirmDeleteMemory: "Delete this memory?",
      confirmDeleteBirthChart:
        'Delete birth chart "{name}"? This cannot be undone.',
    },

    // Feature Group Card
    featureGroups: {
      virtualFeatures: "Virtual Features",
      confirmEnableDependencies:
        'Need to enable "{dependencies}" first. Enable them together?',
    },

    // Hooks
    hooks: {
      pipelineTitle: "Hook Pipeline",
      loading: "Loading hooks pipeline...",
      errorLoading: "Error Loading Pipeline",
      noData: "No pipeline data available",
      enabledCount: "{enabled} / {total} enabled",
      showHidden: "Show hidden ({count})",
      hideHidden: "Hide hidden",

      stages: {
        system: "System Stage",
        user: "User Stage",
        final: "Final Stage",
      },
      hooksCount: "{count} hooks",

      selectHook: "Select a hook to view details",
      description: "Description",
      noDescription: "No description",
      stage: "Stage",
      category: "Category",
      scope: "Scope",
      sortOrder: "Sort Order",
      dragToReorder: "Drag and drop to reorder hooks",
      configInAgentConfig:
        "Hook parameters are configured in the Agent Config section under the corresponding hook settings.",

      hookDefinitions: {
        // ═══ SYSTEM Stage ═══
        user_facts: {
          displayName: "User Facts",
          description:
            "Load permanent user preferences, info, and beliefs (e.g., prefers concise answers, is a programmer)",
          requires: "Memory System (memory.enabled)",
          note: "Injected into System Prompt at the front for KV Cache optimization",
        },
        datetime_context: {
          displayName: "Date & Time",
          description:
            "Inject current date/time context so the agent knows today's date and day of week",
        },
        astrology_plus: {
          displayName: "Astrology+",
          description:
            "Load user's birth chart data and current transit information",
          note: "Requires user to have set birth information",
        },
        first_message: {
          displayName: "First Message",
          description:
            "Detect if this is the first message of a new session for welcome or special handling",
        },
        classifier: {
          displayName: "Classification Engine",
          description:
            "Response type classification (quick/deep) + skill matching, unified decision engine",
          note: "Enabled by default. When disabled, skill configs and quick response model have no effect",
        },
        skill_dedup: {
          displayName: "Skill Dedup",
          description:
            "Check if the same skill is being called repeatedly in the conversation to avoid duplicate execution",
          requires: "Depends on classifier results",
        },

        // ═══ USER Stage ═══
        emotion_enhance: {
          displayName: "Emotion Sensing",
          description:
            "Analyze user message emotion and intent to adjust response style",
        },
        memory_context: {
          displayName: "Memory Retrieval",
          description:
            "Search for relevant user events and agent interaction memories based on user message",
          requires: "Memory System (memory.enabled)",
          note: "Retrieved results are injected into System Prompt for agent reference",
        },
        knowledge_context: {
          displayName: "Knowledge Retrieval",
          description:
            "Retrieve relevant document snippets from knowledge base for user's question",
          requires: "Knowledge Base (knowledge_enabled)",
        },
        memory_reinforce: {
          displayName: "Memory Reinforce",
          description:
            "Based on Ebbinghaus forgetting curve, reinforce accessed memories to delay forgetting",
          requires: "Depends on memory_context retrieval results",
          note: "Retrieved memories gain strength; memories not accessed for long periods gradually decay",
        },

        // ═══ FINAL Stage ═══
        memory_save: {
          displayName: "Memory Save",
          description:
            "After conversation ends, extract and save user memories and agent interaction memories",
          requires: "Memory System (memory.enabled)",
          note: "Dual extraction mode: separately extracts user facts and agent commitments/suggestions",
        },
        skill_tracking: {
          displayName: "Skill Tracking",
          description:
            "Record skills executed in this conversation for statistics and deduplication",
        },
      },
    },
    imageWorkflows: {
      executionPanel: {
        title: "Execution",
        execution: "Execution",
        progress: "Progress",
        nodes: "nodes",
        failed: "failed",
        duration: "Duration",
        error: "Error",
        clearAndClose: "Clear & Close",
        nodeDetail: "Node Detail",
      },
      executionOverview: {
        retrying: "Retrying...",
        retryFrom: "Retry from {node}",
        users: "{count} users",
        viewUser: "View user: {user}",
        targetUserDetails: "Target User Execution Details:",
        loading: "Loading...",
        totalUsers:
          "Total {total} users, {completed} completed, {failed} failed",
        completed: "completed",
        failedCount: "failed",
        currentView: "Current view: {user}",
        userError: "User {user} error",
      },
      // Config Renderers
      modelConfig: {
        resolution: "Resolution",
        aspectRatio: "Aspect Ratio",
        quality: "Quality",
        default: "Default",
        enableWebSearch: "Enable Web Search",
        webSearchDescription:
          "Allow model to search the web for reference materials",
      },
      executionHistory: {
        title: "Execution History",
        tab: "History",
        executionLabel: "Execution",
        nodes: "nodes",
        saveFirst: "Save the workflow to view execution history",
        noHistory: "No execution history",
        runFirst: "Run the workflow to see execution history",
        relativeTime: {
          justNow: "Just now",
          minutesAgo: "{n}m ago",
          hoursAgo: "{n}h ago",
          daysAgo: "{n}d ago",
        },
      },
      nodeDetail: {
        selectNode: "Click a node on the canvas to view details",
        noStepData: "No execution data for this node",
        status: "Status",
        duration: "Duration",
        order: "Step Order",
        startedAt: "Started",
        completedAt: "Completed",
      },
      stepResult: {
        error: "Error",
        variables: "Variables",
        noVariables: "No variables",
        mode: "Mode",
        generatedPrompt: "Generated Prompt",
        showLess: "Show less",
        showMore: "Show more",
        noPrompt: "No prompt generated",
        model: "Model",
        output: "Output",
        imageLoadError: "Failed to load image",
        noImage: "No image generated",
        storage: "Storage",
        saved: "Saved to Library",
        temporary: "Temporary",
        outputPending: "Output pending",
        noOutput: "No output data",
      },
      outputNode: {
        noArtifact: "No artifact (temporary file)",
      },
      runDialog: {
        title: "Execute Workflow",
        description: "Execute for selected users",
        targetUsers: "Target Users",
        cancel: "Cancel",
        execute: "Execute Workflow",
        executing: "Starting...",
        selectAtLeastOneUser: "Please select at least one user",
        workflowStarted: "Workflow started, target",
        allValidUsers: "All valid users",
        usersCount: "{count} users",
        executeFailed: "Failed to start workflow",
      },
      // Workflow Toolbar
      toolbar: {
        categories: {
          input: { label: "Input", description: "Text and prompt generation" },
          image: {
            label: "Image Processing",
            description: "Image generation, editing, fusion",
          },
          special: {
            label: "Special Features",
            description: "Specialized image features",
          },
          action: { label: "Actions", description: "Push notifications etc." },
          output: { label: "Output", description: "Save and export" },
        },
        jsonExport: {
          title: "Export Workflow JSON",
          copyToClipboard: "Copy to clipboard",
          downloadJson: "Download JSON",
          tip: "This JSON contains the complete workflow definition. Use it to import elsewhere or as backup.",
        },
        jsonImport: {
          title: "Import Workflow JSON",
          dropHint: "Drop JSON file here, or click to select",
          orPaste: "Or paste JSON directly",
          placeholder: "Paste workflow JSON...",
          tip: "Import will create a new workflow. Existing workflows will not be overwritten.",
          cancel: "Cancel",
          import: "Import",
          errors: {
            missingFields:
              "Invalid JSON: missing metadata, settings, or definition",
            missingMetadata: "Invalid JSON: metadata missing key or name",
            missingDefinition:
              "Invalid JSON: definition missing nodes or edges array",
            parseError: "JSON parse error: {error}",
            fileReadError: "Failed to read file",
            dropJsonFile: "Please drop a JSON file",
          },
        },
        addNode: "Add Node",
        nodePanel: "Node Panel",
        dragToAdd: "Drag to canvas to add",
        startNodeTip: "Start node is created automatically",
        nodeCount: "{count} nodes",
        importJson: "Import JSON",
        exportJson: "Export JSON",
        executionHistory: "Execution History",
        saveFirst: "Please save the workflow first",
        saving: "Saving...",
        save: "Save",
        running: "Running...",
        run: "Run",
        unsaved: "Unsaved",
        workflowName: "Workflow name",
        workflowKey: "workflow_key",
        keyValidation: {
          startWithLowercase:
            "Must start with lowercase letter, only lowercase letters, numbers and underscores allowed",
          maxLength: "Maximum 64 characters",
        },
        dragHint: "Drag to add {label} node",
        unsavedWarning:
          "You have unsaved changes. Are you sure you want to leave?",
      },
      // Workflow List
      list: {
        title: "Image Workflows",
        workflowCount: "{count} workflow(s)",
        newWorkflow: "New Workflow",
        loading: "Loading workflows...",
        loadFailed: "Failed to load workflows",
        noWorkflows: "No workflows yet",
        noWorkflowsHint: "Create your first workflow to get started",
        createWorkflow: "Create Workflow",
        copySuccess: "ID copied to clipboard",
        copyFailed: "Failed to copy",
        menu: {
          edit: "Edit",
          makePrivate: "Make Private",
          makePublic: "Make Public",
          delete: "Delete",
        },
        confirmDelete:
          'Are you sure you want to delete "{name}"? This action cannot be undone.',
        deleteSuccess: "Workflow deleted",
        deleteFailed: "Failed to delete workflow",
        updateSuccess: '"{name}" is now {status}',
        updateFailed: "Failed to update workflow",
        stats: {
          nodes: "nodes",
          runs: "runs",
        },
        tags: {
          public: "Public",
          agent: "Agent",
          rawLlm: "Raw LLM",
          library: "Library",
          temp: "Temp",
        },
      },
      // Workflow Editor
      editor: {
        loading: "Loading workflow...",
        back: "Back",
        editWorkflow: "Edit Workflow",
        newWorkflow: "New Workflow",
        unsavedWarning:
          "You have unsaved changes. Are you sure you want to leave?",
        validation: {
          keyRequired: "Key is required",
          keyInvalid:
            "Key must start with lowercase letter, only lowercase letters, numbers and underscores allowed",
          keyTooLong: "Key maximum 64 characters",
        },
        toast: {
          saveSuccess: "Workflow saved",
          createSuccess: "Workflow created",
          saveFailed: "Failed to save workflow",
          saveFirst: "Please save the workflow before running",
          executionComplete: "Workflow execution completed!",
          executionFailed: "Workflow execution failed",
          executionPartial: "Workflow execution partially completed",
          executionCancelled: "Workflow execution cancelled",
          resumeComplete: "Workflow resume completed!",
          resumeFailed: "Workflow resume failed",
          resumePartial: "Workflow resume partially completed",
          resumeCancelled: "Workflow resume cancelled",
          resumeError: "Failed to resume execution",
          modelRequired: "Node \"{node}\" requires a model selection, auto mode is no longer supported",
        },
      },
      // Config Field Renderer
      configField: {
        selectPlaceholder: "Select...",
        loadingOptions: "Loading...",
        unknownRenderer: "Unknown renderer: {renderer}",
      },
      // Node Config Panel
      nodeConfigPanel: {
        selectNodePrompt: "Select a node to configure",
        labelInput: "Label",
        labelPlaceholder: "Node label",
        inputFields: "Input Fields",
        outputFields: "Output Fields",
        executionResult: "Execution Result",
        status: {
          pending: "Pending",
          running: "Running",
          completed: "Completed",
          failed: "Failed",
          skipped: "Skipped",
        },
        timing: {
          started: "Started",
          completed: "Completed",
          duration: "Duration",
        },
        output: {
          title: "Output",
          prompt: "Prompt",
          generatedImage: "Generated Image",
          savedToLibrary: "Saved to Library",
          fileId: "File ID",
        },
        error: "Error",
        duplicate: "Duplicate",
        delete: "Delete",
        builtinVariables: {
          user_id: {
            label: "User ID",
            description: "Target user's unique identifier",
          },
          user_name: {
            label: "Username",
            description: "Target user's display name",
          },
          user_email: {
            label: "Email",
            description: "Target user's email address",
          },
          execution_id: {
            label: "Execution ID",
            description: "Current execution's unique identifier",
          },
          workflow_id: {
            label: "Workflow ID",
            description: "Current workflow's unique identifier",
          },
          workflow_name: {
            label: "Workflow Name",
            description: "Current workflow's name",
          },
          timestamp: {
            label: "Timestamp",
            description: "Execution start time (ISO format)",
          },
          date: { label: "Date", description: "Execution date (YYYY-MM-DD)" },
          time: { label: "Time", description: "Execution time (HH:mm:ss)" },
        },
      },
      // Input Fields Section
      inputFieldsSection: {
        title: "Input Fields",
        autoMap: "Auto Map",
        noFields: "No custom inputs. Click + to add",
        usageHint: "Use {{fieldName}} in config options below to reference",
        variableName: "Variable name",
        selectSource: "Select source...",
        fixedValue: "Enter fixed value",
        inputFixedValue: "Enter fixed value...",
        switchToSelect: "Select",
        deleteField: "Delete field",
        extractKey: "Extract key",
        entireObject: "(entire object)",
        contextVariables: "Context Variables",
        customVariables: "Custom Variables (var)",
        upstreamNode: "Upstream Node",
        selectableKey: "[selectable key]",
        clickToEdit: "Click to edit: {key}",
        keyPathPlaceholder: "e.g.: user_id, preferences.theme",
      },
      // Output Fields Section
      outputFieldsSection: {
        title: "Output Fields",
        noFields: "No output fields. Click + to add",
        variableName: "Variable name",
        deleteField: "Delete field",
        selectValueSource: "Select value source...",
        noMapping: "No mapping",
        schema: "[schema]",
        clickToEdit: "Click to edit: {key}",
      },
      // Field Source Toggle
      fieldSourceToggle: {
        mapped: "Mapped",
        fixedValue: "Fixed Value",
        switchToFixed: "Switch to fixed value",
        fromUpstream: "Get from upstream node",
        selectDataSource: "Select data source...",
        context: "Context",
        input: "Input",
        variable: "Variable",
        noAvailableSources: "No available data sources",
        extractKey: "Extract key",
        entireObject: "(entire object)",
        currentInputVars: "Current Input Variables",
        contextVars: "Context Variables",
        customVars: "Custom Variables (var)",
        optionalKey: "[optional key]",
        keyPathPlaceholder: "e.g.: user_id, preferences.theme",
      },
      // Runtime Options Form
      runtimeOptions: {
        title: "Runtime Options",
        disableMemoryTool: {
          label: "Disable Memory Tool",
          description: "Agent will not search/call user memory",
        },
        disableMemoryWrite: {
          label: "Disable Memory Write",
          description: "Conversation will not be saved to user memory",
        },
      },
      // Input Mode Renderer
      inputModeRenderer: {
        generationMethod: "Generation Method",
        manual: "Manual",
        agent: "Agent",
        llm: "LLM",
        outputFormat: "Output Format",
        loading: "Loading...",
        noConstraint: "No format constraint (free output)",
        manualModeHint:
          "Manual mode: Select a format to display the corresponding input form",
        agentLlmModeHint:
          "Agent/LLM mode: Select a format for AI to output in the specified structure",
        fillByFormat: "Fill by Format",
        enterLabel: "Enter {label}...",
        jsonFormat: "Enter {label} (JSON format)...",
        variableHint:
          "Supports variables: {{variableName}} to reference input fields",
        imageDescription: "Image Description",
        imageDescriptionPlaceholder:
          "Describe the image you want to generate...\nExample: A girl in a white dress under cherry blossoms\nSupports variables: {{variableName}}",
        customOutputFields: "Custom Output Fields",
        customOutputFieldsHint: "(no data source, manual input required)",
        customOutputFieldsTip:
          "Tip: Set data sources for these fields in Output Fields to skip manual input",
        selectAgent: "Select Agent",
        userIntent: "User Intent",
        userIntentPlaceholder:
          "Tell the Agent what you want...\nSupports variables: {{variableName}}",
        selectLlmModel: "Select LLM Model",
        selectModel: "Select model...",
        temperature: "Temperature",
        precise: "Precise (0)",
        balanced: "Balanced (1)",
        creative: "Creative (2)",
        systemPrompt: "System Prompt",
        customized: "Customized",
        selectTemplate: "Select template to load or edit directly",
        systemPromptPlaceholder: "System prompt...",
        templateTip:
          "Tip: You can edit after selecting a template, or enter a custom prompt directly",
        userMessage: "User Message",
        userMessagePlaceholder:
          "Enter your request...\nSupports variables: {{variableName}}",
      },
      // Artifact Config Renderer
      artifactConfig: {
        saveToLibrary: "Save to Library",
        imageSource: "Image Source",
        imageSourceDescription: "Select the image to save to library",
        imageSourcePlaceholder: "Select input variable to map image",
        category: "Category",
        categoryPlaceholder: "e.g.: portrait, landscape",
        variableHint: "Supports variables: {{variableName}}",
        filename: "Filename",
        filenamePlaceholder: "e.g.: {{user_name}}_portrait",
        optional: "Optional, supports variables",
        saveAsArtifact: "Save as Artifact Type",
        loading: "Loading...",
        artifactDescription:
          "Artifacts are automatically associated with target users, with rotation and expiration management",
        artifactFieldMapping: "Artifact Field Mapping",
        fieldsCount: "{count} fields",
        mapImagePath: "Map image file path",
        mapAudioPath: "Map audio file path",
        mapVideoPath: "Map video file path",
        mapDocumentPath: "Map document file path",
        mapMediaPath: "Map media file path",
        enterFieldLabel: "Enter {label}...",
        mappingTip:
          "💡 Define variables in Input Fields above, then select Current Input Variables here to map",
        artifactTitle: "Artifact Title",
        artifactTitlePlaceholder: "e.g.: {{date}} Lucky Charm",
        artifactDescriptionField: "Artifact Description",
        artifactDescriptionPlaceholder: "Generated based on your chart",
      },
      // Push Target Renderer
      pushTarget: {
        notificationTitle: "Notification Title",
        variableHint:
          "Supports variables: {{variableName}} to reference input fields",
        notificationTitlePlaceholder: "Notification title",
        notificationBody: "Notification Body",
        notificationBodyPlaceholder: "Notification content...",
        pushTarget: "Push Target",
        followStartConfig: "Follow Start Config",
        followStartConfigDesc: 'Use "Target User" settings from start config',
        currentUser: "Current User",
        currentUserDesc: "Push to the logged-in user when workflow executes",
        allUsers: "All Users",
        allUsersDesc: "Push to all users with registered push tokens",
        specificUsers: "Specific Users",
        specificUsersDesc: "Push to a specified list of users",
        userIdList: "User ID List",
        userIdListPlaceholder:
          "One user ID per line\ne.g.:\nuser_abc123\nuser_xyz456",
        userIdListHint: "One user ID per line, or map an array variable",
        notificationSound: "Notification Sound",
        default: "Default",
        silent: "Silent",
        badgeNumber: "Badge Number (iOS)",
        badgePlaceholder: "Leave empty to not update badge",
        quickConfigTab: "Quick Config",
        templateConfigTab: "Template Config",
        tapAction: "Tap Action",
        tapActionPlaceholder: "Select action when notification is tapped",
        tapActionNone: "No action",
        sendMessageContent: "Message Content",
        sendMessageContentHint: "This message will be auto-sent to start a new chat when the user taps the notification. Use {{field_name}} for input fields, {{relative_time}} is resolved client-side (today/yesterday/M/D)",
        sendMessageContentPlaceholder: "Enter the message to send...",
        selectTemplate: "Select Template",
        selectTemplatePlaceholder: "Choose notification template...",
        variableMappings: "Variable Mappings",
        variableMappingHint:
          "Map template variables to upstream node output fields",
        notMapped: "Not mapped",
        preview: "Preview",
        previewTitle: "Notification Preview",
        previewAction: "Tap Action",
        previewNoAction: "None",
        templateNotSelected: "Please select a template first",
      },
      // Charm Options Renderer
      charmOptions: {
        requiredFields: "Required",
        phraseContent: "Phrase Content",
        phraseHint:
          "Supports variables: {{variableName}} to reference input fields",
        phrasePlaceholder:
          "Enter phrase content, e.g.: Today is a good day for quiet contemplation...\nSupports variable substitution: {{phrase}}",
        imageGenOptions: "Image Generation Options",
        optional: "(Optional)",
        emotionTrend: "Emotion Trend",
        auto: "Auto",
        positive: "Positive",
        calm: "Calm",
        hopeful: "Hopeful",
        grateful: "Grateful",
        concerned: "Concerned",
        chartId: "Chart ID",
        chartIdPlaceholder: "Leave empty to use user's default chart",
        backgroundTheme: "Background Theme",
        loading: "Loading...",
        allThemes: "All Themes (Random)",
        imagesCount: "{count} images",
        backgroundImage: "Background Image",
        random: "Random",
        randomCurrentTheme: "Random (Current Theme)",
        font: "Font",
        defaultFont: "Default Font",
        overlayBadge: "Overlay",
        imagesUnit: "images",
        fontColorLabel: "Font color",
        autoColor: "Auto",
      },
      // Custom Variables Renderer
      customVariables: {
        title: "Custom Variables",
        description: "Define variables for use in subsequent nodes",
        addVariable: "Add Variable",
        noVariables: "No custom variables. Click the button above to add",
        variableName: "Variable name",
        variableValue: "Variable value",
        usageHint:
          "Use {{var.variableName}} in subsequent nodes to reference custom variables",
      },
      // Builtin Variables Renderer
      builtinVariables: {
        title: "Built-in Variables",
        selectToEnable: "Select variables to enable",
        userId: {
          label: "User ID",
          description: "Target user's unique identifier",
        },
        userName: {
          label: "Username",
          description: "Target user's display name",
        },
        userEmail: {
          label: "Email",
          description: "Target user's email address",
        },
        executionId: {
          label: "Execution ID",
          description: "Current execution's unique identifier",
        },
        workflowId: {
          label: "Workflow ID",
          description: "Current workflow's unique identifier",
        },
        workflowName: {
          label: "Workflow Name",
          description: "Current workflow's name",
        },
        timestamp: {
          label: "Timestamp",
          description: "Execution start time (ISO format)",
        },
        date: { label: "Date", description: "Execution date (YYYY-MM-DD)" },
        time: { label: "Time", description: "Execution time (HH:mm:ss)" },
      },
      // Node display labels
      nodes: {
        output: {
          artifact: "Artifact:",
          storage: "Storage:",
          artifactUser: "Artifact (User)",
          libraryPermanent: "Library (Permanent)",
          temp7days: "Temp (7 days)",
        },
        luckyCharm: {
          font: "Font",
          theme: "Theme:",
        },
        prompt: {
          manual: "Manual Input",
          agent: "Agent Generated",
          llm: "LLM Generated",
          clickToInput: "Click to input description",
          clickToConfigAgent: "Click to configure Agent generation",
          clickToConfigLlm: "Click to configure LLM generation",
          agentLabel: "Agent:",
          modelLabel: "Model:",
        },
        start: {
          builtinVariables: "Built-in vars:",
          customVariables: "Custom vars:",
          notEnabled: "Not enabled",
          notDefined: "Not defined",
        },
        push: {
          target: "Target:",
          userCount: "Users:",
          users: "users",
          sound: "Sound:",
          receivesPrompt: "← Receives Prompt node output",
          followStart: "Follow Start",
          currentUser: "Current User",
          allUsers: "All Users",
          specificUsers: "Specific Users",
          mode: "Mode:",
          quickMode: "Quick Config",
          templateMode: "Template Config",
          template: "Template:",
        },
        artifactTypes: {
          lucky_charm: "Daily Lucky Charm",
          soulmate: "Soulmate",
          soul_echo: "Daily Echo",
          daily_push: "Daily Push",
          natal_report: "Natal Report",
          transit_report: "Transit Report",
        },
        fieldMapping: {
          artifactField: "Artifact Field",
          itemField: "Batch Result Field",
          artifactFieldPlaceholder: "artifact field name",
          itemFieldPlaceholder: "batch result field name",
          hint: "Map batch result fields to artifact schema fields (e.g. push_title → push_title)",
        },
      },
    },

    // Resources
    resources: {
      pageTitle: "User Resources",
      tabs: {
        artifacts: "Artifacts",
        library: "Library",
        temp: "Temp Files",
      },
      types: {
        artifact: "Artifacts",
        library: "Library",
        temp: "Temp Files",
      },
      stats: {
        totalFiles: "Total Files",
        totalSize: "Total Size",
        uniqueUsers: "Users",
        byType: "By Type",
        bySubtype: "By Subtype",
        files: "files",
        users: "users",
        subtypes: "Subtypes",
        categories: "Categories",
        avgPerUser: "Avg per User",
        largestUsers: "Top Users by Storage",
        autoCleanup: "Auto cleanup in 7 days",
      },
      summary: {
        totalFiles: "Total Files",
        totalSize: "Total Size",
      },
      modal: {
        title: "User Resources",
        summary: "Resource Summary",
        noResources: "No resources",
        loading: "Loading...",
        error: "Failed to load",
        deleteWarning: "Delete operations cannot be undone",
      },
      status: {
        expired: "Expired",
        current: "Current",
      },
      actions: {
        viewResources: "View Resources",
        delete: "Delete",
        open: "Open",
        download: "Download",
        confirmDelete: "Confirm Delete",
        cancel: "Cancel",
        browseTools: "Browse Tools",
      },
      groups: {
        uncategorized: "Uncategorized",
        expandAll: "Expand All",
        collapseAll: "Collapse All",
      },
      deleteAll: {
        type: "Delete All {type}",
        all: "Delete All Resources",
        confirmType: "Delete all {type} for this user?",
        confirmAll:
          "Delete all resources for this user? This cannot be undone.",
      },
      deleting: "Deleting...",
      table: {
        userId: "User ID",
        totalFiles: "Files",
        totalSize: "Size",
      },
      cleanup: {
        title: "Cleanup Operations",
        tempTitle: "Cleanup Temp Files",
        tempDesc: "Delete temp files older than specified days",
        tempButton: "Cleanup Temp Files",
        artifactsTitle: "Cleanup Expired Artifacts",
        artifactsDesc: "Delete expired artifact files and database records",
        artifactsButton: "Cleanup Expired",
        daysOld: "Days old",
        days: "days",
        confirm: "Confirm Cleanup",
        cleaning: "Cleaning...",
        error: "Cleanup failed",
        successTemp: "Deleted {count} temp files, freed {size}",
        successArtifacts: "Deleted {count} expired artifacts, freed {size}",
      },
      lastUpdated: "Last updated",
      errors: {
        loadFailed: "Failed to load",
      },
    },

    // Tool Management page
    toolManagement: {
      pageTitle: "Tools & MCP",
      pageDescription:
        "Manage MCP servers, tool display and tool combo configurations",
      tabs: {
        mcpServers: "MCP Servers",
        toolEnhancement: "Tool Display",
        toolCombo: "Tool Combos",
        performance: "Performance",
      },
      perf: {
        title: "Tool Performance Monitor",
        description: "Real-time tool execution stats (since last pod restart)",
        totalCalls: "Total Calls",
        totalErrors: "Total Errors",
        trackedTools: "Tracked Tools",
        slowTools: "Slow Tools",
        noData: "No performance data yet",
        noDataHint: "Data will be collected automatically when tools are invoked",
        reset: "Reset",
        resetConfirm: "Reset all performance stats?",
        refresh: "Refresh",
        toolName: "Tool Name",
        calls: "Calls",
        errors: "Errors",
        avgMs: "Avg (ms)",
        maxMs: "Max (ms)",
        minMs: "Min (ms)",
        errorRate: "Error Rate",
        lastError: "Last Error",
        slow: "Slow",
        sortBy: "Sort by",
        loadError: "Failed to load performance data",
      },
    },

    // Business Templates page
    businessTemplates: {
      pageTitle: "Business Templates",
      pageDescription:
        "Manage prompt templates, artifact types and output schema definitions",
      tabs: {
        promptTemplates: "Prompt Templates",
        artifactTypes: "Artifact Types",
        outputPromptTemplates: "Output Prompts",
        outputSchemas: "Output Schemas",
      },
    },

    // Shared: User Selector
    userSelector: {
      searchPlaceholder: "Search by name, phone, email, or ID...",
      selectedCount: "{count} selected",
      selectAll: "Select All",
      deselectAll: "Deselect All",
      clearAll: "Clear",
      noUsers: "No users found",
      devices: "device(s)",
      showingCount: "Showing {from}-{to} of {total}",
    },

    // Shared: Workflow Target Selector
    workflowTarget: {
      currentUser: "Current User",
      currentUserDesc: "Run as the creator/executor",
      specificUsers: "Specific Users",
      specificUsersDesc: "Select specific users as targets",
      willRunAs: "Will run as",
    },

    // Feature Flags
    featureFlags: {
      pageTitle: "Feature Flags",
      pageDescription:
        "Manage feature flags, user overrides, and view change history.",
      createFlag: "Create Flag",
      version: "Version",
      tabs: {
        flags: "Flags",
        userOverrides: "User Overrides",
        auditLog: "Audit Log",
      },
      flagList: {
        noFlags: "No feature flags defined yet.",
        noFlagsHint: 'Click "Create Flag" to add your first flag.',
        rollout: "rollout",
        groups: "groups",
      },
      flagEditor: {
        createTitle: "Create Flag",
        editTitle: "Edit Flag",
        flagName: "Flag Name",
        flagNamePlaceholder: "my_new_feature",
        flagNameError: "Flag name is required",
        flagNameFormatError:
          "Flag name must start with a letter and contain only letters, numbers, and underscores",
        flagNameExistsError: "A flag with this name already exists",
        description: "Description",
        descriptionPlaceholder:
          "Brief description of what this flag controls...",
        descriptionLengthError: "Description cannot exceed 500 characters",
        enabledByDefault: "Enabled by default",
        platforms: "Platforms",
        platformsError: "At least one platform must be selected",
        rolloutPercentage: "Rollout Percentage",
        rolloutError: "Rollout percentage must be between 0 and 100",
        rolloutAllUsers: "All users will see this flag enabled",
        rolloutPartialUsers:
          "Only {percentage}% of users will see this flag enabled (using consistent hashing)",
        targetUserGroups: "Target User Groups",
        targetUserGroupsHelp:
          "Only users in these groups will see this flag enabled. Leave empty to target all users.",
        addGroupPlaceholder: "e.g., beta_testers, vip_users",
        saveChanges: "Save Changes",
      },
      deleteConfirm: {
        title: "Delete Flag",
        message:
          "Are you sure you want to delete {name}? This action cannot be undone.",
        confirm: "Delete",
      },
      toggleConfirm: {
        title: "Toggle Flag",
        message: "Push notification to all clients to take effect immediately?",
        withNotification: "Toggle & Push Notification",
        withoutNotification: "Toggle Only",
      },
      userOverrides: {
        title: "User Overrides",
        description: "Override feature flag status for specific users",
        searchPlaceholder: "Search user ID...",
        selectFlag: "Select Flag",
        selectFlagPlaceholder: "Select a flag...",
        noOverrides: "No user overrides defined",
        noOverridesHint:
          "Add overrides to enable/disable flags for specific users",
        addOverride: "Add Override",
        addTitle: "Add User Override",
        userId: "User ID",
        userIdPlaceholder: "user_123",
        flag: "Flag",
        overrideValue: "Override Value",
        enabled: "Enabled",
        disabled: "Disabled",
        deleteConfirm: "Are you sure you want to delete this user override?",
        clearAllConfirm: "Delete all overrides for user {userId}?",
        override: "override",
        overridesCount: "{count} overrides",
        errorLoading: "Error Loading Overrides",
        userIdRequired: "User ID is required",
        selectFlagRequired: "Please select a flag",
        addFailed: "Failed to add override",
      },
      auditLog: {
        title: "Audit Log",
        description: "View feature flag change history",
        noLogs: "No logs",
        noLogsHint: "Changes to feature flags will appear here",
        action: "Action",
        changedBy: "Changed By",
        reason: "Reason",
        timestamp: "Timestamp",
        version: "Version",
        page: "Page {page}",
        errorLoading: "Failed to load audit logs",
        actions: {
          create: "Created",
          update: "Updated",
          delete: "Deleted",
        },
      },
      errors: {
        loadingError: "Error loading feature flags",
      },
    },
    login: {
      title: "Admin Login",
      subtitle: "Sign in to manage agent configurations",
      modePassword: "Password",
      modePhone: "Phone",
      email: "Email Address",
      emailPlaceholder: "Enter your email",
      password: "Password",
      passwordPlaceholder: "Enter your password",
      phoneNumber: "Phone Number",
      phonePlaceholder: "Enter phone number",
      phoneHint: "Enter your admin-registered phone number",
      signingIn: "Signing in...",
      signIn: "Sign In",
      sendingCode: "Sending...",
      sendCode: "Send Verification Code",
      verificationTitle: "Enter Verification Code",
      codeSentTo: "Code sent to {phone}",
      resendIn: "Resend in {seconds}s",
      resendCode: "Resend Code",
      backToPhone: "Back to Phone Entry",
      enterAllDigits: "Please enter all 6 digits",
      invalidCode: "Invalid verification code",
      footer: "Protected area. Authorized access only.",
    },
    subscriptionPage: {
      pageTitle: "Subscription Management",
      pageDescription:
        "Configure subscriptions, view analytics, and manage products",
      tabs: {
        settings: "Settings",
        analytics: "Analytics",
        products: "Products",
      },
      settings: {
        refresh: "Refresh",
        saving: "Saving...",
        saveChanges: "Save Changes",
        errorLoading: "Error Loading Config",
        savedSuccess: "Settings saved successfully",
        saveFailed: "Failed to save",
        resetChanges: "Reset Changes",
        paywallSettings: "Paywall Settings",
        showOnLaunch: "Show on Launch",
        dismissable: "Dismissable",
        softWall: "Soft Wall (can skip)",
        delaySeconds: "Delay (seconds)",
        trialSettings: "Trial Settings",
        trialEnabled: "Trial Enabled",
        trialDays: "Trial Days",
        featuresByTier: "Features by Tier",
        featuresByTierHint:
          "Display only — not enforced for access control. Actual gating is based on role_level and message quota.",
        freeFeatures: "Free Features (comma-separated)",
        proFeatures: "Pro Features (comma-separated)",
        businessFeatures: "Business Features (comma-separated)",
        commaSeparated: "comma-separated",
        smartPaywall: "Smart Paywall",
        smartPaywallLabel: "Enable Smart Paywall (AI-driven payment guidance)",
        smartPaywallHardLimitHint: "Daily hard limits: free users are blocked (403) once exceeded, reset at local midnight. 0 = disabled",
        smartPaywallHardMessagesDaily: "Daily message hard limit",
        smartPaywallHardDeepDaily: "Daily deep analysis hard limit",
        paywallModeTitle: "Paywall Mode",
        paywallModeSmart: "Smart guidance (recommended)",
        paywallModeSmartDesc: "AI nudges users to upgrade naturally during chat; free users are only blocked past the hard limits",
        paywallModeLegacy: "Legacy quota",
        paywallModeLegacyDesc: "Block immediately once the free message quota is reached, no AI guidance",
        journeyTitle: "Free-user daily journey (live preview of current config)",
        journeyStart: "Start chatting",
        journeyDeepGuide: "{n} deep analyses → upgrade prompt",
        journeyFallbackGuide: "{n} messages → fallback prompt",
        journeyDeepBlock: "{n} deep analyses → deep features blocked",
        journeyMsgBlock: "{n} messages → blocked for the day",
        softSection: "Step 1 · Soft guidance (no blocking)",
        softSectionDesc: "Shows one dismissable upgrade prompt past the threshold. Max once per user per day; never in the first 2 turns of a topic (built-in, not configurable)",
        hardSection: "Step 2 · Hard limits (blocking)",
        hardSectionDesc: "Blocks further messages for the day once reached; resets at the user's local midnight. 0 = disabled",
        freeDeepDailyLabel: "Deep analysis guide line (per day)",
        freeDeepDailyHelp: "Starts upgrade guidance past this point; usage is not affected",
        fallbackDailyMessagesLabel: "Fallback prompt threshold (messages/day)",
        fallbackDailyMessagesHelp: "Guarantees one prompt even if the AI never nudges. 0 = off",
        hardMessagesHelp: "All messages. Suggested: 3x the fallback threshold",
        hardDeepHelp: "Deep analysis messages only. Suggested: 3x the guide line",
        hardBelowGuideWarning: "Hard limit is below the guide line: users get blocked before ever seeing an upgrade prompt. Set hard limit >= guide line",
        generalSection: "General",
        advancedSection: "Advanced (feature gating lists)",
        advancedSectionHint: "Controls client-side feature availability for free/Pro tiers; unrelated to paywall guidance. Keys must match client code — a typo disables the feature entirely",
        smartPaywallHint: "When enabled, AI naturally guides users to pay at the right moment. Message quota limits are bypassed. When disabled, message quota limits apply. The two strategies are mutually exclusive.",
        quotaSettings: "Free Quota Settings",
        quotaTotalMessages: "Total Message Limit",
        quotaDailyMessages: "Daily Message Limit",
        quotaUnlimitedHint: "Set to 0 for unlimited",
        quotaDisabledBySmartPaywall: "Disabled — Smart Paywall is active",
      },
      analytics: {
        title: "Subscription Analytics",
        refresh: "Refresh",
        totalUsers: "Total Users",
        activeSubscribers: "Active Subscribers",
        trialToPaidRate: "Trial-to-Paid Rate",
        churnRate: "Churn Rate",
        totalSubscribers: "Total Subscribers",
        trialUsers: "Trial Users",
        conversionRate: "Conversion Rate",
        monthlyRevenue: "Monthly Revenue",
        loading: "Loading...",
        errorLoading: "Error loading analytics",
        noData: "No data available",
        dateRanges: {
          days7: "7 Days",
          days30: "30 Days",
          days90: "90 Days",
          allTime: "All Time",
        },
        statusDistribution: "Status Distribution",
        productDistribution: "Product Distribution",
        noProductData: "No product data available",
        dailyTrends: "Daily Trends",
        date: "Date",
        newTrials: "New Trials",
        conversions: "Conversions",
        newSubscriptions: "New Subscriptions",
        cancellations: "Cancellations",
        recentEvents: "Recent Events",
        noRecentEvents: "No recent events",
        eventTypes: {
          trialStarted: "Trial Started",
          subscribed: "Subscribed",
          renewed: "Renewed",
          cancelled: "Cancelled",
          expired: "Expired",
          admin: "Admin",
        },
        statusLabels: {
          free: "Free",
          trial: "Trial",
          active: "Active",
          expired: "Expired",
          cancelled: "Cancelled",
        },
      },
      products: {
        title: "Product Management",
        subtitle: "Manage subscription products and store IDs",
        addProduct: "Add Product",
        editProduct: "Edit Product",
        deleteProduct: "Delete Product",
        deleteConfirm: "Are you sure you want to delete this product?",
        productId: "Product ID",
        productName: "Product Name",
        description: "Description",
        price: "Price",
        displayPrice: "Display Price",
        interval: "Interval",
        noProducts: "No Products Found",
        noProductsHint:
          "Products are created automatically when users make purchases",
        loading: "Loading...",
        errorLoading: "Error Loading Products",
        savedSuccess: "Product updated",
        saveFailed: "Failed to update",
        deletedSuccess: "Product deleted",
        deleteFailed: "Failed to delete",
        refresh: "Refresh",
        appleProductId: "Apple Product ID",
        googleProductId: "Google Product ID",
        sortOrder: "Sort Order",
        available: "Available",
        unavailable: "Unavailable",
        disableProduct: "Disable product",
        enableProduct: "Enable product",
        cancel: "Cancel",
        saving: "Saving...",
        saveChanges: "Save Changes",
        name: "Name",
        priceCents: "Charge Amount (cents)",
        priceYuan: "Charge Amount",
        currency: "Currency",
        alipaySection: "Alipay (Android)",
        alipayHint:
          "Price changes take effect immediately, no app update needed",
        appleSection: "Apple IAP (iOS)",
        appleHint: "Prices managed by App Store Connect",
      },
    },

    skillsPage: {
      pageTitle: "Skills Management",
      pageDescription: "Manage skills with drag-and-drop reordering",
      enabledCount: "{enabled} of {total} skills enabled",
      createSkill: "Create Skill",
      showAll: "Show All",
      showEnabledOnly: "Show Enabled Only",
      savingOrder: "Saving order...",
      errorLoading: "Error Loading Skills",
      loadingSkills: "Loading skills...",
      deleteSkill: "Delete Skill",
      deleteConfirm: "Are you sure you want to delete",
      cannotUndo: "This action cannot be undone.",
      translateAll: "Translate All to English",
      translating: "Translating...",
      translateSuccess: "Translation complete",
      translateError: "Translation failed",
      translations: "Translations",
      noTranslations: "No translations",
    },

    promptTemplatesPage: {
      pageTitle: "Prompt Templates",
      pageDescription:
        "Manage system prompt templates. Templates define how agent prompts are structured and assembled.",
      createTemplate: "Create Template",
      errorLoading: "Error Loading Templates",
      loadingTemplates: "Loading templates...",
      deleteTemplate: "Delete Template",
      deleteConfirm: "Are you sure you want to delete",
      cannotUndo: "This action cannot be undone.",
    },

    notificationTemplatesPage: {
      pageTitle: "Notification Templates",
      pageDescription:
        "Manage notification templates, configure channels and content, send test notifications",
      tabs: {
        templates: "Templates",
        sendTest: "Send Test",
      },
      sendTest: {
        title: "Send Test Notification",
        description:
          "Select a template or customize content to send test notification",
        target: "Target",
        specifyUser: "Specify User",
        allUsers: "All Users",
        userIdPlaceholder: "Enter User ID (UUID)",
        allUsersWarning:
          "This will send notifications to all registered users. Please proceed with caution!",
        sendButton: "Send Test Notification",
        selectTemplateOrContent:
          "Please select a template or fill in notification content",
        enterUserId: "Please enter target User ID",
        sendSuccess: "Sent successfully!",
        sendFailed: "Send failed",
        succeeded: "succeeded",
        failed: "failed",
        unknownError: "Unknown error",
      },
      rightPanel: {
        deviceTokens: "Device Tokens",
        sendLogs: "Send Logs",
      },
      tips: {
        selectTemplate:
          "Select template to auto-apply channel and content config",
        templateVariable:
          "Template variable {name} will be replaced by context value",
        silentPush: "Silent push only updates in background, no UI display",
      },
    },

    reportsPage: {
      pageTitle: "Operational Reports",
      pageDescription: "Cost analysis and operational insights",
      tabs: {
        overview: "Overview",
        users: "User Costs",
        pricing: "Pricing Analysis",
        trial: "Trial Risk",
        behavior: "User Behavior",
        revenue: 'Revenue',
      },
      loading: "Loading report data...",
      loadFailed: "Failed to load",
      fetchFailed: "Failed to fetch report data",
      retry: "Retry",
      dataUpdatedAt: "Data updated at",
      dateRange: "Date range",
      overview: {
        totalUsers: "Total Users",
        activeUsers: "Active Users",
        totalSessions: "Total Sessions",
        chatCount: "Chat Count",
        chatCost: "Chat Cost",
        dailyServiceCost: "Daily Service Cost",
        totalCost: "Total Cost",
        avgUserCost: "Avg User Cost",
        median: "Median",
        memberPrice: "Member Price",
        perMonth: "per month",
        profitableUsers: "Profitable Users",
        people: " users",
        ratio: "Rate",
        p75Cost: "P75 Cost",
        p75Subtitle: "75% users cost",
        p90Cost: "P90 Cost",
        p90Subtitle: "90% users cost",
        soulmateStats: "Soulmate Feature Stats",
        lifetimeContract: "Lifetime Contract",
        freeMatch: "Free Match",
        times: " times",
        cost: "Cost",
      },
      users: {
        costTierAnalysis: "User Cost Tier Analysis",
        userDetails: "User Details (Top 50)",
        user: "User",
        phone: "Phone",
        sessionCount: "Sessions",
        lifecycle: "Lifecycle",
        chatCost: "Chat Cost",
        serviceCost: "Service Cost",
        totalCost: "Total Cost",
        profitLoss: "Profit/Loss",
        days: " days",
      },
      pricing: {
        scenarioSimulation: "Pricing Scenario Simulation",
        suggestions: "Pricing Suggestions",
        tip1: "Current plan has low gross margin, consider optimizing cost structure or adjusting pricing",
        tip2: "Daily service cost is a high proportion of total cost, consider intelligent push to reduce costs",
        tip3: "High-cost user segment needs attention, avoid unlimited chat",
        tip4: "Recommend setting per-user cost cap or session limits",
      },
      trial: {
        riskAnalysis: "5-Day Trial Period Risk Analysis",
      },
      behavior: {
        posthogNotConfigured: "PostHog Not Configured",
        posthogConfigHint:
          "User behavior analysis requires PostHog configuration. Please set the following environment variables:",
        posthogHostHint: "PostHog host (default: https://us.i.posthog.com)",
        mobileConfigHint: "Also configure in mobile app:",
        loadingBehavior: "Loading behavior data...",
        fetchFailed: "Failed to fetch behavior data",
        noData: "No data available",
        userRetention: "User Retention",
        // BehaviorAnalysis charts
        topEvents: "Top 10 Events",
        triggerCount: "Trigger Count",
        uniqueUsers: "Unique Users",
        dailyTrend: "Daily Activity Trend",
        eventCount: "Event Count",
        activeUsers: "Active Users",
        featureDistribution: "Feature Usage Distribution",
        clickCount: "Click Count",
        featureDetails: "Feature Usage Details",
        featureName: "Feature Name",
        avgDuration: "Avg Duration",
        pageViews: "Page Views Ranking",
        viewCount: "View Count",
        userJourneyFunnel: "User Journey Funnel",
        step: "Step",
        userCount: "User Count",
        conversionRate: "Conversion Rate",
        retention: {
          d1: "D1 Retention",
          d7: "D7 Retention",
          d30: "D30 Retention",
          d1Desc: "Next-day retention",
          d7Desc: "7-day retention",
          d30Desc: "30-day retention",
        },
        summary: {
          totalEvents: "Total Events",
          activeUsers: "Active Users",
          eventsPerUser: "Events per User",
          behaviorEvents: "Behavior events",
          avgPerUser: "Avg per user",
        },
        // Event name mappings
        eventNames: {
          appOpened: "App Opened",
          chatStarted: "Chat Started",
          chatMessageSent: "Message Sent",
          featureClicked: "Feature Clicked",
          screenViewed: "Screen Viewed",
          subscriptionViewed: "Subscription Viewed",
          soulmateStarted: "Soulmate Started",
          pageview: "Page View",
          autocapture: "Auto Capture",
        },
        // Feature name mappings
        featureNames: {
          soulmate: "Soulmate",
          birthChart: "Birth Chart",
          dailyFortune: "Daily Fortune",
          luckyCharm: "Lucky Charm",
          settings: "Settings",
          subscription: "Subscription",
        },
      },
      revenue: {
        totalRevenue: 'Total Revenue',
        transactions: 'Transactions',
        payingUsers: 'Paying Users',
        arpu: 'ARPU',
        dailyRevenue: 'Daily Revenue Trend',
        channelDistribution: 'Channel Distribution',
        productDistribution: 'Product Distribution',
        conversionFunnel: 'Conversion Funnel',
        trialConversionTiming: 'Trial Conversion Timing',
        bestConversionDay: 'Best push day: Day {day}',
        expiringSoon: 'Expiring Soon',
        within3Days: 'Within 3 days',
        within7Days: 'Within 7 days',
        within14Days: 'Within 14 days',
        productMigrations: 'Product Migrations',
        cohortRetention: 'Cohort Retention (Apple)',
        funnelRegistered: 'Registered',
        funnelTrialed: 'Trialed',
        funnelPurchased: 'Purchased',
        funnelRenewed: 'Renewed',
        funnelChurned: 'Churned',
        renewedAppleOnly: 'Apple only',
        churnedAppleOnly: 'Apple only',
        noData: 'No data',
        upgrade: 'Upgrade',
        downgrade: 'Downgrade',
      },
      // DateRangeSelector
      dateRangeSelector: {
        last7Days: "Last 7 days",
        last30Days: "Last 30 days",
        last90Days: "Last 90 days",
        last1Year: "Last 1 year",
        to: "to",
      },
    },

    messageFeedbackPage: {
      pageTitle: "Message Feedback",
      pageDescription: "User like/dislike feedback on AI responses",
      like: "Like",
      dislike: "Dislike",
      likeShort: "Like",
      dislikeShort: "Dislike",
      total: "Total",
      likeRate: "Like Rate",
      byAgentStats: "Stats by Agent Type",
      filter: "Filter",
      allTypes: "All Types",
      allAgents: "All Agents",
      last7Days: "Last 7 days",
      last30Days: "Last 30 days",
      last90Days: "Last 90 days",
      last1Year: "Last 1 year",
      type: "Type",
      user: "User",
      userMessage: "User Message",
      aiReply: "AI Reply",
      time: "Time",
      noData: "No data",
      paginationInfo: "Total {total}, Page {current} / {pages}",
      feedbackDetail: "Feedback Detail",
      userNote: "User Note",
      none: "None",
      generatedImage: "Generated Image",
      loadingImage: "Loading image...",
      cannotLoadImage: "Cannot load image",
      contextData: "Context Data",
      title: "Title",
      mode: "Mode",
      generationDate: "Generation Date",
      viewRawJson: "View Raw JSON",
      deviceInfo: "Device Info",
    },

    // Annotation Batches page
    annotationBatchesPage: {
      pageTitle: "Annotation Management",
      createBatch: "Create Batch",
      pageDescription:
        "{count} annotation batches in total, manage your data annotation tasks",
      tabs: {
        batchManagement: "Batch Management",
        dataExport: "Data Export",
        statsReport: "Stats Report",
      },
      status: {
        all: "All",
        draft: "Draft",
        active: "Active",
        completed: "Completed",
        archived: "Archived",
      },
      loadFailed: "Failed to load batches",
      loadingBatches: "Loading batches...",
      noBatches: "No batches yet",
      createFirstBatch: "Create your first annotation batch to get started",
      deleteBatch: "Delete Batch",
      deleteConfirm: "Are you sure you want to delete batch",
      deleteWarning:
        "This action cannot be undone. All related annotation data will be deleted.",
      confirmDelete: "Confirm Delete",
      activateFailed: "Failed to activate batch",
      restoreFailed: "Failed to restore batch",
    },

    // Annotation Export page
    annotationExportPage: {
      pageTitle: "Data Export",
      pageDescription: "Export annotation data for training or analysis",
      previewFailed: "Failed to preview data",
      exportSuccess: "Export successful",
      exportFailed: "Export failed",
    },

    // Annotation Stats page
    annotationStatsPage: {
      pageTitle: "Stats Report",
      pageDescription:
        "View statistics and progress reports for annotation tasks",
      loadFailed: "Failed to load statistics",
      loadingStats: "Loading statistics...",
      exportFailed: "Export failed",
      exportSuccess: "Export successful",
      totalCases: "{count} cases in total",
      andMore: "and {count} more...",
      close: "Close",
      downloadJson: "Download JSON",
    },

    // Annotation Workbench page
    annotationWorkbenchPage: {
      pageTitle: "Annotation Workbench",
      pageDescription: "Annotate and review conversation data",
      allBatches: "All Batches",
      taskList: "Task List",
      totalTasks: "{count} tasks in total",
      pending: "Pending",
      completed: "Completed",
      skipped: "Skipped",
      annotated: "Annotated",
      loadFailed: "Failed to load tasks",
      loadingTasks: "Loading tasks...",
      noTasks: "No tasks",
      selectBatchHint: "Select a batch to start annotation work",
      back: "Back",
      sessionCompleted: "Completed this session",
      completedThisSession: "Completed this session",
      batchProgress: "Batch progress",
      submitFailed: "Submit failed",
      tasksCompleteTitle: "Great! Tasks completed",
      noTasksTitle: "No tasks to annotate",
      completedCountMessage: "Completed {count} annotations this session",
      noTasksInBatch: "No tasks available in current batch",
      returnToBatches: "Return to batches",
      viewStats: "View stats",
      pendingLabel: "Pending",
      wordCount: "Words",
      session: "Session",
      labelContent: "Label content",
      shortcuts: "Shortcuts",
      positive: "Positive",
      neutral: "Neutral",
      negative: "Negative",
      submit: "Submit",
      skip: "Skip",
    },

    // Annotation Form component
    annotationForm: {
      steps: {
        messageClassification: "Message Classification",
        emotionIntent: "Emotion/Intent",
        skipped: "Skipped",
      },
      messageType: {
        label: "Message Type",
        shortcutHint: "Shortcut: 1-8",
        simplePrompt: "Simple Prompt",
        fullAgent: "Full Agent",
      },
      expectedRouting: {
        label: "Expected Routing",
        autoSetHint: "Auto-set, can override",
      },
      noEmotionHint:
        "This message type does not require emotion/intent annotation, submit directly.",
      emotion: {
        valenceLabel: "Emotion Valence",
        valenceShortcut: "Shortcut: 1/2/3",
        positive: "Positive",
        neutral: "Neutral",
        negative: "Negative",
        specificLabel: "Specific Emotion",
        optional: "Optional",
      },
      intent: {
        label: "User Intent",
        multiSelectHint: "Multi-select",
      },
      needsSupport: {
        label: "Needs Emotional Support",
        hint: "User shows need for comfort/support",
      },
      confidence: {
        label: "Annotation Confidence",
        high: "High",
        highDesc: "Very sure",
        medium: "Medium",
        mediumDesc: "Fairly sure",
        low: "Low",
        lowDesc: "Not sure",
      },
      notes: {
        label: "Notes",
        placeholder: "Add notes for special cases...",
      },
      actions: {
        skip: "Skip",
        skipShortcut: "Esc",
        previous: "Previous",
        continueHint: "Enter to continue",
        submitHint: "⌘S/Ctrl+S to submit",
        next: "Next",
        submitLabel: "Submit Annotation",
      },
    },

    // Playground page
    playground: {
      modes: {
        generate: "Generate",
        edit: "Edit",
        fuse: "Fuse",
        luckyCharm: "Lucky Charm",
      },
      modeDescriptions: {
        generate: "Generate new images from text descriptions",
        edit: "Modify, adjust, or enhance existing images",
        fuse: "Combine multiple images into one new image",
        luckyCharm: "Generate personalized fortune lucky charm images",
      },
      labels: {
        selectEditImage: "Select image to edit",
        selectFuseImages: "Select images to fuse",
        selected: "Selected",
        uploadOrSelect: "Or select from history/library on the right",
        inputMode: "Input mode",
        agent: "Agent",
        llm: "LLM",
        manual: "Manual",
        selectAgent: "Select Agent",
        selectAgentPlaceholder: "Select Agent...",
        selectLLMModel: "Select LLM Model",
        selectModelPlaceholder: "Select model...",
        loading: "Loading...",
        noAgentsAvailable: "No agents available",
        noModelsAvailable: "No models available",
        outputSchema: "Output Schema",
        noSchemaConstraint: "No schema constraint (free output)",
        schemaDescription:
          "Agent will output in the specified JSON structure when selected",
        tellAgentWhatYouWant: "Tell Agent what you want",
        tellLLMWhatYouWant: "Tell LLM what you want",
        userPromptPlaceholder:
          "e.g., A girl in white dress under cherry blossoms...",
        temperature: "Temperature",
        precise: "Precise",
        balanced: "Balanced",
        creative: "Creative",
        generateDescription: "Generate description",
        generating: "Generating...",
        imageDescription: "Image description",
        editInstruction: "Edit instruction",
        fuseInstruction: "Fuse instruction",
        describeImageToGenerate: "Describe the image you want to generate...",
        describeEditInstruction: "Describe how you want to edit the image...",
        describeFuseInstruction:
          "Describe how you want to fuse these images...",
        processing: "Processing...",
        operationFailed: "Operation failed",
        generateFailed: "Failed to generate prompt",
        required: "Required",
        upload: "Upload",
        random: "Random",
        currentPack: "Current pack",
        defaultFont: "Default font",
        emotionTrend: "Emotion trend",
        emotionPositive: "Positive",
        emotionNeutral: "Neutral",
        emotionNegative: "Negative",
        emotionMixed: "Mixed",
        extraPrompt: "Extra prompt",
        extraPromptAgentPlaceholder:
          "Extra hints for Agent, e.g., emphasize wealth fortune...",
        extraPromptLLMPlaceholder:
          "Prompt for fortune phrase, e.g., Generate a positive fortune message...",
        optional: "Optional",
        generateText: "Generate text",
        fortunePhrase: "Fortune phrase",
        fortunePhrasePlaceholder:
          "Enter fortune phrase, e.g., Today is good for reflection, luck with helpers...",
        clickGenerateOrInput:
          "Click 'Generate text' button above, or enter directly...",
        editableAfterGenerate: "Can be edited after generation",
        customOptions: "Custom options",
        chartId: "Chart ID",
        chartIdPlaceholder: "Leave empty to use default chart",
        chartIdHint: "Provide chart data as context for fortune generation",
        backgroundPack: "Background pack",
        allPacks: "All packs",
        backgroundImage: "Background image",
        font: "Font",
        systemPrompt: "System Prompt",
        rawLLMHint: "Use raw LLM API for full control over system prompt",
        userPromptSent: "User Prompt Sent",
        taskFailed: "Task execution failed",
        pleaseSelectAgent: "Please select an Agent first",
        pleaseSelectLLMModel: "Please select an LLM model first",
        pleaseEnterPrompt: "Please enter a prompt",
        // ModelSelector and DynamicConfigSelector
        model: "Model",
        capabilityGenerate: "Generate",
        capabilityEdit: "Edit",
        capabilityFuse: "Fuse",
        maxImagesForFuse: "Max {count} images",
        resolution: "Resolution",
        quality: "Quality",
        supportsWebSearch: "Supports web search",
        supportsTransparency: "Supports transparency",
        pricePerImage: "Price",
        aspectRatio: "Aspect ratio",
        background: "Background",
        defaultOption: "Default",
        transparent: "Transparent",
        white: "White",
        auto: "Auto",
        enableWebSearch: "Enable web search",
        webSearchDescription:
          "Allow model to search the web for reference materials",
        maxGenerate: "Max {count} images",
        maxFuse: "Max {count} images to fuse",
        output: "Output",
        aspectRatioSquare: "1:1 (Square)",
        aspectRatioPortrait: "3:4 (Portrait)",
        aspectRatioLandscape: "4:3 (Landscape)",
        aspectRatioVertical: "9:16 (Vertical)",
        aspectRatioWide: "16:9 (Wide)",
        aspectRatioTallPortrait: "2:3 (Tall portrait)",
        aspectRatioTallLandscape: "3:2 (Tall landscape)",
        aspectRatioSocial: "4:5 (Social)",
        aspectRatioUltraWide: "21:9 (Ultra wide)",
        aspectRatioTall: "1:2 (Tall)",
        aspectRatioBanner: "2:1 (Banner)",
        qualityStandard: "Standard",
        qualityHD: "HD",
        qualityLow: "Low",
        qualityMedium: "Medium",
        qualityHigh: "High",
        qualityAuto: "Auto",
        sizeAuto: "Auto",
        liveBadge: "LIVE",
      },
      luckyCharm: {
        config: "Lucky Charm Configuration",
        inputMode: "Generation mode",
      },
      history: {
        title: "History",
        library: "Library",
        refresh: "Refresh",
        clear: "Clear",
        loading: "Loading...",
        noImages: "No images yet",
        noImagesHint: "Generated images will appear here",
        libraryEmpty: "Library is empty",
        libraryEmptyHint:
          "Save generated images to library for persistent storage",
        selectForEdit: "Select for edit",
        selectForFuse: "Select for fuse",
        selected: "Selected",
        deselect: "Deselect",
        view: "View",
        download: "Download",
        delete: "Delete",
        confirmDelete: "Are you sure you want to delete this history record?",
        confirmClear:
          "Are you sure you want to clear all history? This action cannot be undone.",
        confirmDeleteFromLibrary:
          "Are you sure you want to delete this image from library?",
        saveToLibrary: "Save to library",
        savedToLibrary: "Saved to library",
        saveFailed: "Save failed",
        deleteFailed: "Delete failed",
        saved: "Saved",
        saving: "Saving...",
        generating: "Generating...",
        alreadySaved: "Already saved to library",
        editFork: "Edit/Fork",
        imagePreview: "Image Preview",
        close: "Close",
        model: "Model",
        time: "Time",
        source: "Source",
        description: "Description",
        filterAll: "All",
        filterGenerate: "Generate",
        filterEdit: "Edit",
        filterFuse: "Fuse",
        filterLuckyCharm: "Lucky Charm",
      },
    },

    // Batch Card
    batchCard: {
      status: {
        draft: "Draft",
        active: "Active",
        completed: "Completed",
        archived: "Archived",
      },
      progress: "Progress",
      stats: {
        totalTasks: "Total Tasks",
        completed: "Completed",
        pending: "Pending",
      },
      actions: {
        activate: "Activate",
        activateTitle: "Activate batch",
        noTasksTitle: "No tasks in batch",
        startAnnotation: "Start Annotation",
        archive: "Archive",
        restore: "Restore",
      },
      metadata: {
        source: "Source",
        created: "Created",
      },
    },

    // Stats Overview
    statsOverview: {
      cards: {
        totalBatches: "Total Batches",
        activeBatches: "active",
        totalTasks: "Total Tasks",
        pendingTasks: "pending",
        completedTasks: "Completed",
        completionRate: "Completion Rate",
        annotators: "Annotators",
        avgTime: "Avg",
      },
      overallProgress: "Overall Progress",
      completed: "Completed",
      batchStats: {
        title: "Batch Statistics",
        batch: "Batch",
        totalTasks: "Total Tasks",
        completed: "Completed",
        pending: "Pending",
        progress: "Progress",
      },
      annotatorStats: {
        title: "Annotator Statistics",
        annotator: "Annotator",
        completed: "Completed",
        skipped: "Skipped",
        avgTime: "Avg Time",
        recentActivity: "Recent Activity",
        noData: "No annotation data yet",
      },
      export: {
        title: "Export Test Dataset",
        description:
          "Export completed annotations as emotion-service test case format",
        button: "Export Golden Cases",
      },
    },

    // Export Preview
    exportPreview: {
      presets: {
        title: "Quick Select Sampling Strategy",
        balanced: {
          name: "Balanced",
          description: "Balanced sampling from various users and conversations",
        },
        deepConversations: {
          name: "Deep Conversations",
          description: "Messages from multi-turn deep conversations",
        },
        newUsers: {
          name: "New User Experience",
          description: "First interactions from new users",
        },
        activeUsers: {
          name: "Active Users",
          description: "Messages from high-frequency users",
        },
        emotionalContent: {
          name: "Emotional Content",
          description: "Messages containing emotional keywords",
        },
      },
      filters: {
        title: "Filters",
        hasFilters: "Applied",
        reset: "Reset Filters",
        expandAdvanced: "Show Advanced",
        collapseAdvanced: "Hide Advanced",
        userActivity: "User Activity",
        conversationDepth: "Conversation Depth",
        userStage: "User Stage",
        messagePosition: "Message Position",
        messageLength: "Message Length",
        to: "to",
        characters: "chars",
        loadCount: "Load Count",
        maxCount: "max 500",
        includeKeywords: "Include Keywords",
        keywordPlaceholder: "Enter keyword...",
        add: "Add",
        excludeTemplates: "Exclude Template Messages",
        randomSample: "Random Sample",
        perPage: "Per Page:",
        excludeExported: "Exclude Exported",
        loadMessages: "Load Messages",
      },
      userActivityLabels: {
        single: "Single Use",
        light: "Light (2-5 times)",
        medium: "Medium (6-20 times)",
        heavy: "Heavy (20+ times)",
      },
      conversationDepthLabels: {
        singleTurn: "Single Turn",
        short: "Short (2-3 turns)",
        medium: "Medium (4-10 turns)",
        deep: "Deep (10+ turns)",
      },
      messagePositionLabels: {
        first: "First Message",
        early: "Early (2-3 turns)",
        middle: "Middle",
        last: "Last Message",
      },
      userStageLabels: {
        new: "New (0-1 days)",
        recent: "Recent (2-7 days)",
        active: "Active (8-30 days)",
        veteran: "Veteran (30+ days)",
      },
      selection: {
        title: "Select Messages",
        loaded: "Loaded",
        of: "/",
        existing: "already exist",
        searchPlaceholder: "Search messages...",
        selectAll: "Select All",
        selectNonDuplicate: "Select Non-Duplicate",
        clear: "Clear",
        skipDuplicates: "Auto-skip annotated duplicates",
        selected: "Selected:",
        duplicatesSkipped: "duplicates will be skipped",
        effectiveExport: "Actual Export:",
        chars: "chars",
        copyMd5: "Copy MD5",
        alreadyExists: "Already exists",
        noMatchingMessages: "No matching messages found",
        noMessages: "No messages available",
      },
      pagination: {
        showing: "Showing",
        total: "Total",
        excludedExported: "exported excluded",
        previous: "Previous",
        page: "Page",
        next: "Next",
      },
      createBatch: {
        title: "Create Annotation Batch",
        batchTitle: "Batch Title",
        batchTitleRequired: "*",
        batchTitlePlaceholder: "e.g., Emotion Annotation - Dec 2024",
        description: "Description",
        descriptionOptional: "Optional",
        descriptionPlaceholder: "Batch description...",
        create: "Create Batch",
        enterTitle: "Please enter a batch title",
      },
    },

    // Traces page
    tracesPage: {
      pageTitle: 'Prompt Traces',
      tabList: 'Trace List',
      tabMetrics: 'Metrics',
      opikConsole: 'Opik Console',
      notConfigured: 'Opik Not Configured',
      notConfiguredDesc: 'Please set OPIK_API_KEY and OPIK_WORKSPACE environment variables to enable the trace viewer.',
      loading: 'Loading...',
      loadFailed: 'Load failed',
      searchPlaceholder: 'Search trace name...',
      search: 'Search',
      colName: 'Name',
      colTime: 'Time',
      colDuration: 'Duration',
      colTokens: 'Tokens',
      colCost: 'Cost',
      colSpans: 'Spans',
      noData: 'No trace data',
      pagination: 'Total {total}, Page {page}/{pages}',
      loadingSpans: 'Loading spans...',
      noSpans: 'No span data',
      showingSpans: 'Showing {shown} of {total} spans',
      spanInput: 'Input:',
      spanOutput: 'Output:',
      tokenPrompt: 'Prompt:',
      tokenCompletion: 'Completion:',
      tokenTotal: 'Total:',
      toolCallLabel: 'Tool Call',
      reasoningLabel: 'Reasoning',
      emptyContent: 'empty',
      emptyResponse: 'empty response',
      showRawJson: 'Show Raw JSON',
      hideRawJson: 'Hide Raw JSON',
      sectionInput: 'Input',
      sectionOutput: 'Output',
      sectionSpans: 'Spans ({count})',
      sectionMetadata: 'Metadata',
      tokens: 'tokens',
      errorLabel: 'Error',
      spanCount: '{count} spans',
      noModel: 'unknown',
      viewTimeline: 'Timeline',
      viewTree: 'Tree',
      totalDuration: 'Total: {duration}',
      selectSpan: 'Click a span to view details',
      spanDetail: 'Span Detail',
      metricsTitle: 'Project Metrics Overview',
      noStats: 'No statistics available',
      statTraceCount: 'Total Traces',
      statDuration: 'Duration P50 / P90',
      statCost: 'Total Cost',
      statTokens: 'Total Tokens',
      statErrorSub: 'error: {count}',
      statAvgCost: 'avg ${cost}/trace',
      statAvgTokens: 'avg {count}/trace',
      errorBanner: '{count} traces contain errors',
      chartTraceCount: 'Daily Trace Count',
      chartDuration: 'Daily Duration Distribution',
      chartTokenUsage: 'Daily Token Usage',
      tooltipTraces: 'Traces',
      tooltipPrompt: 'Prompt',
      tooltipCompletion: 'Completion',
      dataSource: 'Data source: Opik (Comet) — Visit Opik console for detailed charts',
      filterChatOnly: 'Chat Requests',
      filterAll: 'All Traces',
    },
    schedules: {
      timezoneWindow: {
        enable: 'Execute by user timezone',
        targetHour: 'Target hour',
        defaultTimezone: 'Default timezone',
        hint: 'Triggers hourly, only processes users whose local time is in the target window',
        cronHint: 'Recommend setting Cron to "0 * * * *" (hourly)',
      },
    },
    wishesPage: {
      pageTitle: 'Wish Pool',
      pageDescription: 'Manage wish clusters · Update status · Send notifications',
      allStatus: 'All',
      statusOpen: 'Open',
      statusPlanned: 'Planned',
      statusInProgress: 'In Progress',
      statusDone: 'Done',
      statusRejected: 'Rejected',
      wishCount: 'Wishes',
      totalClusters: '{total} clusters total',
      selectCluster: 'Select a cluster to view details',
      noData: 'No wish clusters',
      loading: 'Loading...',
      save: 'Save',
      title: 'Title',
      summary: 'Summary',
      status: 'Status',
      statusNote: 'Status Note',
      representativeExcerpt: 'Representative Excerpt',
      representativeWishes: 'Representative Wishes',
      notifyUsers: 'Notify Users',
      confirmNotify: 'Confirm Notification',
      confirmNotifyDesc: 'This will send notifications to all users who submitted wishes in this cluster and mark it as done. Continue?',
      mergeInto: 'Merge Into...',
      mergeTarget: 'Target Cluster',
      confirmMerge: 'Confirm Merge',
      mergeCurrentDesc: 'The current cluster will be merged into the target, all wishes transferred, and the current cluster deleted.',
      cancel: 'Cancel',
      language: 'Language',
      notifiedAt: 'Notified At',
      notNotified: 'Not notified',
      deleteWish: 'Delete',
    },
    financePage: {
      pageTitle: "Finance",
      pageDescription: "Contributions, expenses and revenue P&L (source: Feishu ledger, read-only)",
      tabs: {
        overview: "Overview",
        expenses: "Expenses",
        contributions: "Contributions",
      },
      revenue: "Revenue",
      expense: "Expense",
      net: "Net",
      committed: "Committed",
      received: "Paid-in",
      outstanding: "Outstanding",
      outstandingHint: "Committed but unpaid",
      totalExpense: "Total Expense",
      totalRevenue: "Total Revenue",
      capitalPool: "Capital Pool",
      capitalPoolHint: "Paid-in − Expense (actual cash)",
      monthlyBurn: "Monthly Burn",
      runway: "Runway",
      breakeven: "Break-even",
      perMonth: "mo",
      months: "months",
      monthlyPnl: "Monthly P&L",
      cashflow: "Cumulative Cash Flow (capital pool)",
      byChannel: "Revenue by Channel",
      accounts: "Accounts",
      account: "Account",
      contributed: "Contributed",
      spent: "Spent",
      balance: "Balance",
      byCategory: "Expense by Category",
      date: "Date",
      category: "Category",
      amount: "Amount",
      contributor: "Contributor",
      round: "Round",
      note: "Note",
      lastSync: "Last Sync",
      syncNow: "Sync Now",
      syncing: "Syncing…",
      reconWarn: "Synced data diverges from Feishu's reported figures, please verify the source sheet",
    },
  },
};

export default messages;
